import React, { useState, useEffect, useRef } from "react";
import { View, Text, Button, StyleSheet, Dimensions, TextInput, Pressable, ScrollView, TouchableOpacity, Platform, Image } from "react-native";
import { getFirestore, collection, getDocs, updateDoc, query, where, setDoc, addDoc, doc, deleteDoc, onSnapshot, getDoc, deleteField } from "firebase/firestore";
import { getAuth, signInWithEmailAndPassword, onAuthStateChanged, } from "firebase/auth";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { app } from "../firebase";
import { useUser } from "../Widgets/UserContext";
import Popup from "../Widgets/Popup";
import { sendPasswordResetEmail } from "firebase/auth";
import axios from 'axios';
import ECardStudio from './AdminWidgets/ECardStudio';
import Analytics from './AdminWidgets/Analytics'; // Import the Analytics component
import analyticsManager from '../services/AnalyticsManager'; // Import the Analytics manager
import UserList from '../components/UserList';
import { usePageTracking } from '../Hooks/usePageTracking'; // Import page tracking hook
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from "@react-navigation/native";
import EventsWidgetMonitor from './AdminWidgets/EventsWidgetMonitor';
import SpotlightTutorialAdminModal from './AdminWidgets/SpotlightTutorialAdminModal';
import { formatReferralForDisplay } from '../constants/ReferralSources';
import hubStyles from '../Styles/HubStyles';
import {
	FALLBACK_STANDARD_PROFILE_BACKGROUNDS,
	STANDARD_PROFILE_BACKGROUNDS_CONFIG,
	getImageSource,
	getStandardProfileBackgroundValue,
	normalizeStandardProfileBackgrounds,
	readBackgroundBoolean,
} from '../services/StandardProfileBackgroundService';

const auth = getAuth(app);

const isDevelopment = process.env.NODE_ENV === "development";

const db = getFirestore();
const s3 = (() => {
	if (Platform.OS === "web") {
		return null;
	}

	const AWS = require("aws-sdk");
	AWS.config.update({
		accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
		secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
		region: process.env.REACT_APP_AWS_REGION,
	});
	return new AWS.S3();
})();

// Add after imports
const { width } = Dimensions.get('window');
const isMobile = width < 768;
const isTablet = width >= 768 && width < 1024;
const isSmallDesktop = width >= 1024 && width < 1440;
const isLargeDesktop = width >= 1440;

const VALID_ADMIN_TABS = ['users', 'organizations', 'catalogue', 'blog', 'analytics', 'registry-audit', 'features', 'mytassel-plus', 'registry', 'ecard-studio', 'notifications', 'maintenance', 'widgets-monitor'];

const Admin = ({ route }) => {
	const navigation = useNavigation();
	
	const [organizations, setOrganizations] = useState([]);
	const [approvedOrgs, setApprovedOrgs] = useState([]);
	const [deniedOrgs, setDeniedOrgs] = useState([]);
	const [loading, setLoading] = useState(true);
	const [catalogueItems, setCatalogueItems] = useState([]); // New state for catalogue items
	const [error, setError] = useState(null); // Add error state

	const [currentPage, setCurrentPage] = useState(1);
	const itemsPerPage = 25;
	const [searchQuery, setSearchQuery] = useState("");

	const [affiliateUrl, setAffiliateUrl] = useState("");
	const [itemPreview, setItemPreview] = useState(null);

	const [loadingStep, setLoadingStep] = useState("");
	const [isFetching, setIsFetching] = useState(false);
	const [showStillWorking, setShowStillWorking] = useState(false);

	const [category, setCategory] = useState(""); // For selecting a category
	const categories = ["Bedroom", "Bathroom", "Decor", "Health & Personal Care", "Kitchen", "Laundry & Cleaning", "Electronics & Office Supplies", "Office Supplies", "Storage & Organization"];

	const [categoryError, setCategoryError] = useState(false);

	const [filter, setFilter] = useState("all"); // 'all', or any of the categories
	const [sort, setSort] = useState("none"); // 'none', 'priceLowHigh', 'priceHighLow'

	const [itemName, setItemName] = useState("");
	const [itemDescription, setItemDescription] = useState("");
	const [itemPrice, setItemPrice] = useState("");
	const [itemImage, setItemImage] = useState("");

	const [selectedPrice, setSelectedPrice] = useState('');
	const [priceUpdates, setPriceUpdates] = useState({}); // For individual edits
	const [selectedCategory, setSelectedCategory] = useState('');
	const [selectedProducts, setSelectedProducts] = useState([]);
	const [bulkPrice, setBulkPrice] = useState('');
	const [bulkDescription, setBulkDescription] = useState('');

	const initialTab = route?.params?.tab && VALID_ADMIN_TABS.includes(route.params.tab) ? route.params.tab : 'users';
	const [activeTab, setActiveTabRaw] = useState(initialTab);
	const setActiveTab = (tab) => {
		setActiveTabRaw(tab);
		if (typeof window !== 'undefined' && window.history?.replaceState) {
			window.history.replaceState(null, '', `/admin/${tab}`);
		}
	};
	const [sidebarOpen, setSidebarOpen] = useState(true);
	const [collapsedSidebarCategories, setCollapsedSidebarCategories] = useState({});

	// Sidebar navigation structure with categories and icons (no emojis)
	const sidebarCategories = [
		{
			id: 'user-management',
			label: 'User Management',
			icon: 'people',
			items: [
				{ id: 'users', label: 'Users', icon: 'person' },
				{ id: 'organizations', label: 'Organizations', icon: 'business' }
			]
		},
		{
			id: 'content',
			label: 'Content',
			icon: 'document-text',
			items: [
				{ id: 'catalogue', label: 'Catalogue', icon: 'grid' },
				{ id: 'blog', label: 'Blog', icon: 'newspaper' }
			]
		},
		{
			id: 'monitoring',
			label: 'Monitoring',
			icon: 'stats-chart',
			items: [
				{ id: 'analytics', label: 'Analytics', icon: 'analytics' },
				{ id: 'registry-audit', label: 'Registry Audit', icon: 'bug' },
				{ id: 'widgets-monitor', label: 'Widgets Monitor', icon: 'calendar' }
			]
		},
		{
			id: 'configuration',
			label: 'Configuration',
			icon: 'settings',
			items: [
				{ id: 'features', label: 'Feature Flags', icon: 'toggle' },
				{ id: 'mytassel-plus', label: 'MyTassel+', icon: 'star' },
				{ id: 'registry', label: 'Registry Contribution', icon: 'cash' },
				{ id: 'ecard-studio', label: 'eCard Studio', icon: 'film' }
			]
		},
		{
			id: 'communications',
			label: 'Communications',
			icon: 'mail',
			items: [
				{ id: 'notifications', label: 'Email & Notifications', icon: 'notifications' }
			]
		},
		{
			id: 'system',
			label: 'System',
			icon: 'construct',
			items: [
				{ id: 'maintenance', label: 'Maintenance', icon: 'hammer' }
			]
		}
	];

	// Toggle sidebar category collapse
	const toggleSidebarCategory = (categoryId) => {
		setCollapsedSidebarCategories(prev => ({
			...prev,
			[categoryId]: !prev[categoryId]
		}));
	};

	// Sync tab from route params on external navigation (back/forward)
	useEffect(() => {
		const paramTab = route?.params?.tab;
		if (paramTab && VALID_ADMIN_TABS.includes(paramTab) && paramTab !== activeTab) {
			setActiveTabRaw(paramTab);
		}
	}, [route?.params?.tab]);

	// On mount, update URL if it was just /admin with no tab
	useEffect(() => {
		if (typeof window !== 'undefined' && window.history?.replaceState) {
			const path = window.location.pathname;
			if (path === '/admin' || path === '/admin/') {
				window.history.replaceState(null, '', `/admin/${activeTab}`);
			}
		}
	}, []);

	// Legacy tabs array for backward compatibility
	const tabs = [
		{ id: "users", label: "Users" },
		{ id: "organizations", label: "Organizations" },
		{ id: "catalogue", label: "Catalogue" },
		{ id: "blog", label: "Blog" },
		{ id: "analytics", label: "Analytics" },
		{ id: "registry-audit", label: "Registry Audit" },
		{ id: "features", label: "Feature Flags" },
		{ id: "mytassel-plus", label: "MyTassel+" },
		{ id: "registry", label: "Registry Contribution" },
		{ id: "ecard-studio", label: "eCard Studio" },
		{ id: "notifications", label: "Email & Notifications" },
		{ id: "maintenance", label: "Maintenance" },
		{ id: "widgets-monitor", label: "Widgets Monitor" },
	];

	// Direct production Firebase Functions - no emulator, no AWS
	const SERVER_URL = "https://us-central1-my-tassel.cloudfunctions.net/api";

	const { currentUser, currentUserData } = useUser();
	
	// Track page views for admin
	usePageTracking('Admin', {
		pageContext: 'Admin Panel',
		userDisplayName: currentUserData?.firstName && currentUserData?.lastName 
			? `${currentUserData.firstName} ${currentUserData.lastName}`
			: currentUserData?.uid || 'Admin User',
		viewerType: 'admin'
	});
	
	const [showLoginPopup, setShowLoginPopup] = useState(false);
	const [showAdmin, setShowAdmin] = useState(false); // New state for showing admin content
	
	// Dark Mode State - persisted to localStorage
	const [isDarkMode, setIsDarkMode] = useState(() => {
		if (typeof window !== 'undefined') {
			const saved = localStorage.getItem('adminDarkMode');
			return saved === 'true';
		}
		return false;
	});
	
	// Toggle dark mode and persist
	const toggleDarkMode = () => {
		setIsDarkMode(prev => {
			const newValue = !prev;
			if (typeof window !== 'undefined') {
				localStorage.setItem('adminDarkMode', String(newValue));
			}
			return newValue;
		});
	};
	
	// Dark Mode Color System
	const theme = {
		// Backgrounds
		bg: isDarkMode ? '#0f172a' : '#f8fafc',
		bgCard: isDarkMode ? '#1e293b' : '#ffffff',
		bgCardHover: isDarkMode ? '#334155' : '#f1f5f9',
		bgInput: isDarkMode ? '#1e293b' : '#f9fafb',
		bgSection: isDarkMode ? '#1e293b' : '#ffffff',
		bgHeader: isDarkMode ? '#0f172a' : '#ffffff',
		bgModal: isDarkMode ? '#1e293b' : '#ffffff',
		bgModalOverlay: isDarkMode ? 'rgba(0,0,0,0.8)' : 'rgba(0,0,0,0.5)',
		
		// Text
		textPrimary: isDarkMode ? '#f1f5f9' : '#1f2937',
		textSecondary: isDarkMode ? '#94a3b8' : '#4b5563',
		textMuted: isDarkMode ? '#64748b' : '#6b7280',
		textInverse: isDarkMode ? '#1f2937' : '#ffffff',
		
		// Borders
		border: isDarkMode ? '#334155' : '#e2e8f0',
		borderLight: isDarkMode ? '#475569' : '#e5e7eb',
		
		// Accent Colors (same in both modes)
		primary: '#4c956c',
		primaryHover: '#3d7a57',
		danger: '#ef4444',
		dangerHover: '#dc2626',
		warning: '#f59e0b',
		info: '#3b82f6',
		success: '#22c55e',
		
		// Feature flag specific
		ffCardBg: isDarkMode ? '#1e293b' : '#f8fafc',
		ffCardBorder: isDarkMode ? '#334155' : '#e2e8f0',
		ffItemBg: isDarkMode ? '#0f172a' : '#ffffff',
		ffItemBorder: isDarkMode ? '#334155' : '#e5e7eb',
		
		// Table
		tableHeaderBg: isDarkMode ? '#1e293b' : '#f8fafc',
		tableRowBg: isDarkMode ? '#0f172a' : '#ffffff',
		tableRowHoverBg: isDarkMode ? '#1e293b' : '#f1f5f9',
		
		// Buttons
		btnSecondaryBg: isDarkMode ? '#334155' : '#f3f4f6',
		btnSecondaryText: isDarkMode ? '#e2e8f0' : '#4b5563',
		btnSecondaryBorder: isDarkMode ? '#475569' : '#d1d5db',
	};

	const [testEmail, setTestEmail] = useState("");
	const [emailType, setEmailType] = useState("genericWelcome");
	const [emailParams, setEmailParams] = useState({});
	const [emailTestResult, setEmailTestResult] = useState("");
	const [recipientEmail, setRecipientEmail] = useState("trevorhawkins@dsignable.com");
	const [batchTestRunning, setBatchTestRunning] = useState(false);
	const [batchTestProgress, setBatchTestProgress] = useState([]);
	const [testPhone, setTestPhone] = useState("");
	const [smsType, setSmsType] = useState("welcome");
	const [smsTestResult, setSmsTestResult] = useState("");

	const [isMaintenance, setIsMaintenance] = useState(false);
	const [mismatchedUsers, setMismatchedUsers] = useState([]);
	const [maintenanceMode, setMaintenanceMode] = useState(false);
	const [disableRegistration, setDisableRegistration] = useState(false);

	// Registry Audit State
	const [registryAuditLoading, setRegistryAuditLoading] = useState(false);
	const [registryAuditResults, setRegistryAuditResults] = useState(null);
	const [registryAuditFixing, setRegistryAuditFixing] = useState({}); // keyed by userEmail
	const [registryAuditFixAllLoading, setRegistryAuditFixAllLoading] = useState(false);
	const [registryAuditError, setRegistryAuditError] = useState(null);
	const [registryAuditExpandedUser, setRegistryAuditExpandedUser] = useState(null);
	const [registryAuditHistory, setRegistryAuditHistory] = useState([]);
	const [registryAuditHistoryLoading, setRegistryAuditHistoryLoading] = useState(false);
	const [registryAuditHistoryClearLoading, setRegistryAuditHistoryClearLoading] = useState(false);

	// Feature Flags State
	const [featureFlags, setFeatureFlags] = useState({});
	const [featureFlagsLoading, setFeatureFlagsLoading] = useState(true);
	const [showFeatureConfirmModal, setShowFeatureConfirmModal] = useState(false);
	const [pendingFeatureChange, setPendingFeatureChange] = useState(null);
	const [collapsedCategories, setCollapsedCategories] = useState({});
	const [standardProfileBackgrounds, setStandardProfileBackgrounds] = useState([]);
	const [standardBackgroundsLoading, setStandardBackgroundsLoading] = useState(false);
	const [standardBackgroundsSaving, setStandardBackgroundsSaving] = useState(false);
	const [standardBackgroundsStatus, setStandardBackgroundsStatus] = useState('');
	
	// Admin Notifications State
	const [adminNotificationConfig, setAdminNotificationConfig] = useState({
		enabled: true,
		newUserSignupEnabled: true,
		recipients: []
	});
	const [adminNotificationsLoading, setAdminNotificationsLoading] = useState(true);
	const [newAdminEmail, setNewAdminEmail] = useState('');
	const [newAdminName, setNewAdminName] = useState('');
	const [adminNotificationSaving, setAdminNotificationSaving] = useState(false);
	
	// Email Settings State
	const [emailSettings, setEmailSettings] = useState({});
	const [emailSettingsLoading, setEmailSettingsLoading] = useState(true);
	const [emailSettingsSaving, setEmailSettingsSaving] = useState(false);

	// Email Activity Log State
	const [emailActivity, setEmailActivity] = useState([]);
	const [emailActivityLoading, setEmailActivityLoading] = useState(false);
	const [emailActivityError, setEmailActivityError] = useState(null);
	const [emailActivityFilter, setEmailActivityFilter] = useState('all');
	const [emailActivityPage, setEmailActivityPage] = useState(1);
	const EMAIL_ACTIVITY_PER_PAGE = 25;
	const [expandedActivityMsgId, setExpandedActivityMsgId] = useState(null);

	// SMS Activity Log State
	const [smsActivity, setSmsActivity] = useState([]);
	const [smsActivityLoading, setSmsActivityLoading] = useState(false);
	const [smsActivityError, setSmsActivityError] = useState(null);
	const [smsActivityFilter, setSmsActivityFilter] = useState('all');
	const [smsActivitySearch, setSmsActivitySearch] = useState('');
	const [smsActivityPage, setSmsActivityPage] = useState(1);
	const SMS_ACTIVITY_PER_PAGE = 25;
	const [expandedSmsSid, setExpandedSmsSid] = useState(null);

	// Email Activity Search
	const [emailActivitySearch, setEmailActivitySearch] = useState('');
	const [activityMsgDetails, setActivityMsgDetails] = useState({});
	const [activityMsgDetailLoading, setActivityMsgDetailLoading] = useState(new Set());

	// Email Schedule Queue State
	const [emailSchedule, setEmailSchedule] = useState(null);
	const [emailScheduleLoading, setEmailScheduleLoading] = useState(false);
	const [emailScheduleError, setEmailScheduleError] = useState(null);
	const [scheduleFilter, setScheduleFilter] = useState('all');
	const [scheduleQueuePage, setScheduleQueuePage] = useState(1);
	const [scheduleGroupBy, setScheduleGroupBy] = useState('user');
	const [scheduleSort, setScheduleSort] = useState('soonest');
	const [scheduleSearchQuery, setScheduleSearchQuery] = useState('');
	const [expandedQueueKeys, setExpandedQueueKeys] = useState([]);
	const [expandedQueueUserKeys, setExpandedQueueUserKeys] = useState([]);
	const [scheduleTick, setScheduleTick] = useState(0);
	const [recentlySentOpen, setRecentlySentOpen] = useState(false);
	const [remindersOpen, setRemindersOpen] = useState(false);
	const [expandedReminderIds, setExpandedReminderIds] = useState([]);
	const [expandedEventGroups, setExpandedEventGroups] = useState([]);

	const toggleQueueItemExpand = (key) => {
		setExpandedQueueKeys(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
	};
	const [expandedGroupRecipients, setExpandedGroupRecipients] = useState([]);
	const [sendNowConfirm, setSendNowConfirm] = useState({}); // { [id]: 1|2 }
	const [sendNowLoading, setSendNowLoading] = useState({});
	const [rescheduleState, setRescheduleState] = useState({}); // { [id]: { newDate, confirming } }
	const [rescheduleLoading, setRescheduleLoading] = useState({});
	const [scanningReminders, setScanningReminders] = useState(false);
	const [scanResults, setScanResults] = useState(null);
	const [reminderFilter, setReminderFilter] = useState('all'); // 'all' | 'day_of' | 'days_before_1' | 'days_before_3' | 'days_before_7' | 'test'
	const [reminderSort, setReminderSort] = useState('soonest'); // 'soonest' | 'furthest' | 'nameAZ' | 'nameZA'
	const [confirmSkipKeys, setConfirmSkipKeys] = useState({}); // { [`${email}:${type}`]: true }

	// Collapsible sections in Notifications tab (all collapsed by default)
	const [notifSectionOpen, setNotifSectionOpen] = useState({
		adminNotifications: false,
		testEmails: false,
		emailActivity: false,
		scheduleQueue: false,
		allTemplates: false,
		thankYouTemplates: false,
		smsTemplates: false,
		reminderTiming: false,
	});

	// Thank You Card Message Templates State
	const DEFAULT_THANK_YOU_TEMPLATES = [
		{ id: 'heartfelt', name: 'Heartfelt', message: 'Dear {{name}},\n\nThank you so much for your incredibly generous gift! Your thoughtfulness and kindness mean the world to me. I\'m so grateful to have you in my life.\n\nWith love and appreciation' },
		{ id: 'casual', name: 'Casual', message: 'Hey {{name}}!\n\nJust wanted to say a huge thank you for the amazing gift! You really didn\'t have to, but I\'m so glad you did. You\'re the best!\n\nThanks again' },
		{ id: 'short', name: 'Short & Sweet', message: 'Dear {{name}},\n\nThank you for your generous gift! Your kindness is truly appreciated.\n\nWarmly' },
		{ id: 'formal', name: 'Formal', message: 'Dear {{name}},\n\nI wanted to extend my sincere gratitude for your thoughtful gift. Your generosity is deeply appreciated and will be cherished.\n\nWith heartfelt thanks' },
	];
	const [thankYouTemplates, setThankYouTemplates] = useState(DEFAULT_THANK_YOU_TEMPLATES);
	const [thankYouTemplatesLoading, setThankYouTemplatesLoading] = useState(false);
	const [expandedTyMsgIds, setExpandedTyMsgIds] = useState([]);
	const [showVarsLegend, setShowVarsLegend] = useState(false);
	const [thankYouTemplatesSaving, setThankYouTemplatesSaving] = useState(false);

	// Registry Contribution Presets State
	const [registryPresets, setRegistryPresets] = useState([25,50,100,250]);
	const [registryLoading, setRegistryLoading] = useState(true);
	const [registrySaving, setRegistrySaving] = useState(false);
	const [registryError, setRegistryError] = useState(null);
	
	// Email Preview State
	const [expandedEmailPreviewIds, setExpandedEmailPreviewIds] = useState([]);
	const [inlineEdits, setInlineEdits] = useState({});
	const [collapsedEmailCategories, setCollapsedEmailCategories] = useState({});
	const [editingTemplate, setEditingTemplate] = useState(null);
	const [editingTemplateContent, setEditingTemplateContent] = useState('');
	const [editingSubject, setEditingSubject] = useState('');
	const [useVisualEditor, setUseVisualEditor] = useState(true);
	const emailBodyEditorRef = React.useRef(null);
	const [emailPreviewData, setEmailPreviewData] = useState(null);
	const [showEmailPreviewModal, setShowEmailPreviewModal] = useState(false);
	const [showSmsBackfillPreviewModal, setShowSmsBackfillPreviewModal] = useState(false);
	const [smsBackfillPreviewPhone, setSmsBackfillPreviewPhone] = useState('');
	const [emailCadenceEditing, setEmailCadenceEditing] = useState(null);

	// SMS Settings State
	const [smsSettings, setSmsSettings] = useState({});
	const [expandedSmsPreviewIds, setExpandedSmsPreviewIds] = useState([]);
	const [inlineSmsEdits, setInlineSmsEdits] = useState({});
	const [collapsedSmsCategories, setCollapsedSmsCategories] = useState({});

	// Reminder Timing Configuration State
	const defaultReminderOptions = [
		{ value: 'days_before_10', label: 'Day of Event + 10 Days Before', daysBefore: 10, sendHourUTC: 10 },
		{ value: 'days_before_7', label: 'Day of Event + 7 Days Before', daysBefore: 7, sendHourUTC: 10 },
		{ value: 'days_before_3', label: 'Day of Event + 3 Days Before', daysBefore: 3, sendHourUTC: 10 },
		{ value: 'days_before_1', label: 'Day of Event + 1 Day Before', daysBefore: 1, sendHourUTC: 10 },
	];
	const [reminderTimingConfig, setReminderTimingConfig] = useState({
		defaultOptions: defaultReminderOptions,
		dayOfSendHourUTC: 9,
	});
	const [reminderTimingLoading, setReminderTimingLoading] = useState(false);
	const [reminderTimingSaving, setReminderTimingSaving] = useState(false);
	const sendTimeOptions = [
		{ utc: 5,  label: '12:00 AM EST (Midnight)' },
		{ utc: 6,  label: '1:00 AM EST' },
		{ utc: 7,  label: '2:00 AM EST' },
		{ utc: 8,  label: '3:00 AM EST' },
		{ utc: 9,  label: '4:00 AM EST' },
		{ utc: 10, label: '5:00 AM EST' },
		{ utc: 11, label: '6:00 AM EST' },
		{ utc: 12, label: '7:00 AM EST' },
		{ utc: 13, label: '8:00 AM EST' },
		{ utc: 14, label: '9:00 AM EST' },
		{ utc: 15, label: '10:00 AM EST' },
		{ utc: 16, label: '11:00 AM EST' },
		{ utc: 17, label: '12:00 PM EST (Noon)' },
		{ utc: 18, label: '1:00 PM EST' },
		{ utc: 19, label: '2:00 PM EST' },
		{ utc: 20, label: '3:00 PM EST' },
		{ utc: 21, label: '4:00 PM EST' },
		{ utc: 22, label: '5:00 PM EST' },
		{ utc: 23, label: '6:00 PM EST' },
		{ utc: 0,  label: '7:00 PM EST' },
		{ utc: 1,  label: '8:00 PM EST' },
		{ utc: 2,  label: '9:00 PM EST' },
		{ utc: 3,  label: '10:00 PM EST' },
		{ utc: 4,  label: '11:00 PM EST' },
	];
	const daysBeforeOptions = [
		{ days: 1,  label: '1 day before' },
		{ days: 2,  label: '2 days before' },
		{ days: 3,  label: '3 days before' },
		{ days: 5,  label: '5 days before' },
		{ days: 7,  label: '1 week before' },
		{ days: 10, label: '10 days before' },
		{ days: 14, label: '2 weeks before' },
		{ days: 21, label: '3 weeks before' },
		{ days: 30, label: '1 month before' },
	];
	const reminderSelectStyle = { padding: '8px 12px', borderRadius: 8, fontSize: 13, cursor: 'pointer', appearance: 'auto' };

	// All Email Definitions
	const emailDefinitions = {
		// Welcome Emails
		genericWelcome: {
			id: 'genericWelcome',
			name: 'Generic Welcome',
			category: 'Welcome',
			subject: '"Welcome to MyTassel {firstName}!"',
			trigger: 'Friends & Family selects account type',
			triggerType: 'event',
			description: 'Sent when a new user registers and selects Friends & Family as their account type.',
			canDisable: true
		},
		graduateWelcome: {
			id: 'graduateWelcome',
			name: 'Graduate Welcome',
			category: 'Welcome',
			subject: '"Welcome to MyTassel {firstName}!"',
			trigger: 'Graduate selects account type',
			triggerType: 'event',
			description: 'Sent when a new user registers and selects Graduate as their account type.',
			canDisable: true
		},
	// Registry Emails
	itemPurchased: {
			id: 'itemPurchased',
			name: 'Item Purchased',
			category: 'Registry',
			subject: '"An Item from Your Registry was Purchased!"',
			trigger: 'Registry item marked as purchased',
			triggerType: 'event',
			description: 'Sent to the graduate when someone purchases an item from their registry.',
			canDisable: true
		},
		goalReached: {
			id: 'goalReached',
			name: 'Goal Reached',
			category: 'Registry',
			subject: '"Congratulations! Goal Reached!"',
			trigger: 'Contribution goal met or exceeded',
			triggerType: 'event',
			description: 'Sent to the graduate when their contribution goal is reached.',
			canDisable: true
		},
		// Event Emails
		yearbookSigned: {
			id: 'yearbookSigned',
			name: 'Yearbook Signed',
			category: 'Event',
			subject: '"Someone just signed your Yearbook!"',
			trigger: 'New yearbook entry added',
			triggerType: 'event',
			description: 'Sent to the graduate when someone signs their digital yearbook.',
			canDisable: true
		},
		pageShare: {
			id: 'pageShare',
			name: 'Page Share Invitation',
			category: 'Event',
			subject: '"You\'re Invited to {firstName}\'s MyTassel!"',
			trigger: 'Graduate shares pages via email',
			triggerType: 'event',
			description: 'Sent to recipients when a graduate shares their pages.',
			canDisable: false
		},
		ecardSent: {
			id: 'ecardSent',
			name: 'eCard with Contribution',
			category: 'Event',
			subject: '"You\'ve received a contribution & an eCard!"',
			trigger: 'eCard sent with contribution',
			triggerType: 'event',
			description: 'Sent to the graduate when someone sends them an eCard with a contribution.',
			canDisable: true
		},
		eventReminder: {
			id: 'eventReminder',
			name: 'Event Reminder',
			category: 'Reminders',
			subject: '"Reminder: {eventName}"',
			trigger: 'Scheduled reminder time reached',
			triggerType: 'scheduled',
			schedule: '1 day, 1 week, or custom before event',
			description: 'Sent to guests who have RSVP\'d or been invited to remind them of upcoming events.',
			canDisable: true
		},
		// Access Emails
		pageAccessRequest: {
			id: 'pageAccessRequest',
			name: 'Page Access Request',
			category: 'Access',
			subject: '"Access Request for Your Page"',
			trigger: 'Someone requests private page access',
			triggerType: 'event',
			description: 'Sent to the page owner when someone requests access to their private page.',
			canDisable: true,
		},
		pageAccessRequestConfirmation: {
			id: 'pageAccessRequestConfirmation',
			name: 'Access Request Auto-Reply',
			category: 'Access',
			subject: '"Your access request has been sent"',
			trigger: 'Someone requests private page access',
			triggerType: 'event',
			description: 'Confirmation sent to the requester when they submit a page access request.',
			canDisable: true,
		},
		// Support Emails
		contactForm: {
			id: 'contactForm',
			name: 'Contact Form Submission',
			category: 'Support',
			subject: '"New Contact Form Submission"',
			trigger: 'Contact form submitted',
			triggerType: 'event',
			description: 'Sent to admin when someone submits the contact form.',
			canDisable: true,
		},
		contactFormConfirmation: {
			id: 'contactFormConfirmation',
			name: 'Contact Form Auto-Reply',
			category: 'Support',
			subject: '"Thank you for contacting MyTassel!"',
			trigger: 'Contact form submitted',
			triggerType: 'event',
			description: 'Confirmation sent to the person who submitted the contact form.',
			canDisable: true,
		},
		// Thank You Emails
		thankYouCard: {
			id: 'thankYouCard',
			name: 'Thank You Card',
			category: 'Thank You',
			subject: '"Thank you from {graduateName}!"',
			trigger: 'Graduate sends thank you',
			triggerType: 'event',
			description: 'Sent to contributors when the graduate sends them a thank you card.',
			canDisable: true,
		},
		// Engagement Emails - Profile Completion
	profileCompletion1: {
		id: 'profileCompletion1',
		name: 'Profile Reminder #1',
		category: 'Engagement',
		subject: '"{firstName}, complete your MyTassel profile! 🎓"',
		trigger: '5 days after signup, no pages created',
		triggerType: 'scheduled',
		schedule: 'Daily at 10 AM PT',
		description: 'First reminder to graduates who haven\'t set up their pages yet.',
		canDisable: true,
		isNew: true,
		configurableDays: { label: 'Days after signup', default: 5 },
	},
	profileCompletion2: {
		id: 'profileCompletion2',
		name: 'Profile Reminder #2',
		category: 'Engagement',
		subject: '"{firstName}, your graduation pages are waiting!"',
		trigger: '12 days after signup, no pages created',
		triggerType: 'scheduled',
		schedule: 'Daily at 10 AM PT',
		description: 'Second reminder highlighting "3x more contributions" stat.',
		canDisable: true,
		isNew: true,
		configurableDays: { label: 'Days after signup', default: 12 },
	},
	profileCompletion3: {
		id: 'profileCompletion3',
		name: 'Profile Reminder #3 (Final)',
		category: 'Engagement',
		subject: '"Last reminder: Set up your MyTassel, {firstName}"',
		trigger: '21 days after signup, no pages created',
		triggerType: 'scheduled',
		schedule: 'Daily at 10 AM PT',
		description: 'Final reminder before stopping outreach.',
		canDisable: true,
		isNew: true,
		configurableDays: { label: 'Days after signup', default: 21 },
	},
		// Engagement Emails - Registry Nudges
		registryNudge: {
			id: 'registryNudge',
			name: 'Set Up Registry',
			category: 'Engagement',
			subject: '"{firstName}, set up your gift registry! 🎁"',
			trigger: 'TRIGGER REMOVED - Email preserved but not sent',
			triggerType: 'disabled',
			schedule: 'N/A',
			description: 'Previously encouraged graduates to set up their registry after creating events page. Trigger removed by admin request.',
			canDisable: true,
			isNew: true
		},
		goalNudge: {
			id: 'goalNudge',
			name: 'Set Contribution Goal',
			category: 'Engagement',
			subject: '"{firstName}, set your contribution goal 💰"',
			trigger: 'Registry exists, no goal after 14 days',
			triggerType: 'scheduled',
			schedule: 'Daily at 10 AM PT',
			description: 'Highlights "40% more gifts" stat to encourage goal setting.',
			canDisable: true,
			isNew: true
		},
		itemsNudge: {
			id: 'itemsNudge',
			name: 'Add Registry Items',
			category: 'Engagement',
			subject: '"{firstName}, take one more look at your registry"',
			trigger: 'Registry exists with too few items after each configured day (day ≤7: <5 items, day >7: <8 items)',
			triggerType: 'scheduled',
			schedule: 'Daily at 10 AM PT',
			description: 'Sends up to once per configured day threshold when the registry has too few items. Multiple sends possible if multiple days are configured (e.g. day 5, 14, 30). Configure day thresholds in the Cadence section.',
			canDisable: true,
			isNew: true,
		},
		// Engagement Emails - Page Enhancement
		widgetsNudge: {
			id: 'widgetsNudge',
			name: 'Customize Page',
			category: 'Engagement',
			subject: '"{firstName}, customize your announcement page ✨"',
			trigger: 'TRIGGER REMOVED - Email preserved but not sent',
			triggerType: 'disabled',
			schedule: 'N/A',
			description: 'Previously encouraged adding widgets with "2x engagement" stat. Trigger removed by admin request.',
			canDisable: true,
			isNew: true
		},
		// Engagement Emails - Re-engagement
	reengagement14Days: {
		id: 'reengagement14Days',
		name: 'We Miss You – 30 Days',
		category: 'Engagement',
		subject: '"We miss you, {firstName}! 👋"',
		trigger: 'No login for 30 days',
		triggerType: 'scheduled',
		schedule: 'Daily at 10 AM PT',
		description: 'Re-engagement email showing page view stats.',
		canDisable: true,
		isNew: true,
		configurableDays: { label: 'Days without login', default: 30 },
	},
	reengagement30Days: {
		id: 'reengagement30Days',
		name: 'Page Waiting – 45 Days',
		category: 'Engagement',
		subject: '"{firstName}, your graduation page is waiting"',
		trigger: 'No login for 45 days',
		triggerType: 'scheduled',
		schedule: 'Daily at 10 AM PT',
		description: 'Final re-engagement attempt for inactive users.',
		canDisable: true,
		isNew: true,
		configurableDays: { label: 'Days without login', default: 45 },
	},
		// Milestone Emails
		firstContribution: {
			id: 'firstContribution',
			name: 'First Contribution!',
			category: 'Milestones',
			subject: '"🎉 {firstName}, you got your first contribution!"',
			trigger: 'TRIGGER REMOVED - Email preserved but not sent',
			triggerType: 'disabled',
			description: 'Previously celebrated first contribution and encouraged sharing. Trigger removed by admin request.',
			canDisable: true,
			isNew: true
		},
		firstRsvp: {
			id: 'firstRsvp',
			name: 'First RSVP!',
			category: 'Milestones',
			subject: '"🎊 {firstName}, someone RSVP\'d to your event!"',
			trigger: 'TRIGGER REMOVED - Email preserved but not sent',
			triggerType: 'disabled',
			description: 'Previously celebrated first RSVP with current count. Trigger removed by admin request.',
			canDisable: true,
			isNew: true
		},
		pageViewsMilestone: {
			id: 'pageViewsMilestone',
			name: 'Page Views Milestone',
			category: 'Milestones',
			subject: '"📈 {firstName}, your page hit {n} views!"',
			trigger: 'TRIGGER REMOVED - Email preserved but not sent',
			triggerType: 'disabled',
			description: 'Previously celebrated page view milestones. Trigger removed by admin request.',
			canDisable: true,
			isNew: true
		},
		// Follower Emails
		gradPageUpdated: {
			id: 'gradPageUpdated',
			name: 'Grad Updated Page',
			category: 'Followers',
			subject: '"{graduateName} updated their MyTassel! 🎓"',
			trigger: 'Major page update by graduate',
			triggerType: 'event',
			description: 'Sent to followers when a graduate makes significant updates.',
			canDisable: true,
			isNew: true
		},
		// Admin Emails
		adminNewUserSignup: {
			id: 'adminNewUserSignup',
			name: 'New User Signup (Admin)',
			category: 'Admin',
			subject: '"🎓 New Signup: {name} ({accountType})"',
			trigger: 'New user completes registration',
			triggerType: 'event',
			description: 'Notifies admins when a new user signs up with account type, SMS opt-in, etc.',
			canDisable: true,
			isNew: true
		}
	};

	// ─── SMS Definitions ───
	const smsDefinitions = {
		welcome: {
			id: 'welcome',
			name: 'Welcome Message',
			category: 'Welcome',
			trigger: 'New user completes registration',
			triggerType: 'event',
			description: 'Sent when a new user signs up for MyTassel.',
			canDisable: true,
			backendConnected: false
		},
		invitation: {
			id: 'invitation',
			name: 'Page Invitation',
			category: 'Sharing',
			trigger: 'Graduate shares page via SMS',
			triggerType: 'event',
			description: 'Sent when a graduate invites someone to view their MyTassel page.',
			canDisable: false,
			backendConnected: false
		},
		yearbook: {
			id: 'yearbook',
			name: 'Yearbook Notification',
			category: 'Engagement',
			trigger: 'Someone signs a yearbook',
			triggerType: 'event',
			description: 'Notifies the graduate when someone signs their yearbook.',
			canDisable: true,
			backendConnected: false
		},
		shareAnnouncement: {
			id: 'shareAnnouncement',
			name: 'Share Announcement',
			category: 'Sharing',
			trigger: 'Graduate shares announcement via SMS',
			triggerType: 'event',
			description: 'Full share message with announcement and registry links sent to invitees.',
			canDisable: false
		},
		eventReminder: {
			id: 'eventReminder',
			name: 'Event Reminder',
			category: 'Reminders',
			trigger: 'Scheduled before an event',
			triggerType: 'scheduled',
			description: 'Reminder sent to RSVPed guests before an upcoming graduation event.',
			canDisable: true
		},
		thankYouCard: {
			id: 'thankYouCard',
			name: 'Thank You Card',
			category: 'Thank You',
			trigger: 'Graduate sends a thank you card',
			triggerType: 'event',
			description: 'SMS sent to recipients when a graduate sends them a thank you card.',
			canDisable: true
		}
	};

	// SMS category config (icons and colors)
	const smsCategoryConfig = {
		Welcome: { icon: 'hand-left', color: '#10b981' },
		Sharing: { icon: 'share-social', color: '#3b82f6' },
		Engagement: { icon: 'chatbubble-ellipses', color: '#8b5cf6' },
		Reminders: { icon: 'alarm', color: '#f59e0b' },
		'Thank You': { icon: 'heart', color: '#ec4899' }
	};

	// Default SMS templates (original messages — no emojis on welcome/invitation/yearbook)
	const smsTemplates = {
		welcome: {
			body: `Welcome to MyTassel! We're excited to help you celebrate your graduation.`
		},
		invitation: {
			body: `You've been invited to view a MyTassel graduation page!`
		},
		yearbook: {
			body: `Someone just signed your MyTassel yearbook!`
		},
		shareAnnouncement: {
			body: `{senderFirstName} {senderLastName} is thrilled to announce their graduation! Join them in celebrating by visiting MyTassel.\n\nAnnouncement page:\n{eventsPageUrl}?uic={uic}\n\nRegistry page:\n{registryPageUrl}?uic={uic}\n\nReply HELP for help, STOP to unsubscribe.`
		},
		eventReminder: {
			body: `It's almost time for\n{eventTitle}\n\n{formattedDate} at {formattedTime}\n{eventLocation}\n\nThe moment that matters.\n\n\u2014 {hostName}\nMyTassel`
		},
		thankYouCard: {
			body: `Thank You from {senderName}\n\n{message}\n\nSent via MyTassel\nmytassel.com`
		}
	};

	// Actual email templates (client's original handwritten templates)
	const emailTemplates = {
		genericWelcome: {
			subject: 'MyTassel | Welcome to MyTassel {firstName}!',
			html: `<div style="display: block; text-align: center; max-width: 600px; margin: auto; font-size: 18px;">
	<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2FMyTassel%20Email%20Header%20Image.png?alt=media&token=5a134bcb-6ea8-4690-b15a-089c46130ec8" style="width: 100%; height: auto;" />
	<h3>Hello {firstName}, and welcome to MyTassel!</h3>
	<br>
	<p>Welcome to MyTassel! We're thrilled to have you on board and look forward to helping you celebrate your graduates with us!</p>
	<br>
	<p>Let's begin the celebration by completing <a href="https://www.mytassel.com/">your profile</a>. From there, we can find your graduates and stay up to date with their events and registries.</p>
	<br>
	<p>Here's to celebrating your graduates!</p>
	<p>The MyTassel Team</p>
	<div style="display: flex; margin-top: 10px; width: 100%; height: 20%; justify-content: center; align-items: center;">
		<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2Flogo.png?alt=media&token=8d69cd10-5bc6-4344-b266-b04dac2c80f0" style="max-width: 100px; height: 100px;" />
	</div>
</div>`
		},
		graduateWelcome: {
			subject: 'MyTassel | Welcome to MyTassel {firstName}!',
			html: `<div style="display: block; text-align: center; max-width: 600px; margin: auto; font-size: 18px;">
	<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2FMyTassel%20Email%20Header%20Image.png?alt=media&token=5a134bcb-6ea8-4690-b15a-089c46130ec8" style="width: 100%; height: auto;" />
	<h3>Hello {firstName}, and welcome to MyTassel!</h3>
	<br>
	<p>You did it and we are thrilled to be a part of this significant milestone! Your hard work, persistence and commitment are certainly something to applaud. MyTassel encourages your friends and family to share their pride in your accomplishment and participate in the kickstart of your next chapter.</p>
	<br>
	<p>Let's begin the celebration by completing <a href="https://www.mytassel.com/">your profile</a>. From there, we can personalize your page, create your registry, and announce the occasion.</p>
	<br>
	<p>Cheers to a job well done!</p>
	<p>The MyTassel Team</p>
	<div style="display: flex; margin-top: 10px; width: 100%; height: 20%; justify-content: center; align-items: center;">
		<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2Flogo.png?alt=media&token=8d69cd10-5bc6-4344-b266-b04dac2c80f0" style="max-width: 100px; height: 100px;" />
	</div>
</div>`
		},
	itemPurchased: {
			subject: 'MyTassel | An Item from Your Registry was Purchased!',
			html: `<div style="text-align: center; max-width: 600px; margin: auto; font-size: 18px;">
	<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2FMyTassel%20Email%20Header%20Image.png?alt=media&token=5a134bcb-6ea8-4690-b15a-089c46130ec8" style="width: 100%; height: auto;" />
	<h3>Hello {graduateName}, congratulations — you can mark something off the list!</h3>
	<br>
	<p>A purchase from your registry was made by <strong>{purchaserName}</strong>:</p>
	<br>
	<p><strong>Item:</strong> {itemName}</p>
	<p><strong>Price:</strong> \${itemPrice}</p>
	<br>
	<p><a href="{itemUrl}">View item details</a></p>
	<br>
	<p>We've updated your registry accordingly and require no further action from you!</p>
	<p>Thank you for using MyTassel!</p>
	<br>
	<p>Warm regards,</p>
	<p>The My Tassel Team</p>
	<div style="display: flex; margin-top: 10px; width: 100%; height: 20%; justify-content: center; align-items: center;">
		<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2Flogo.png?alt=media&token=8d69cd10-5bc6-4344-b266-b04dac2c80f0" style="max-width: 100px; height: 100px;" />
	</div>
</div>`
		},
		yearbookSigned: {
			subject: 'MyTassel | Someone just signed your Yearbook!',
			html: `<div style="text-align: center; max-width: 600px; margin: auto; font-size: 18px;">
	<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2FMyTassel%20Email%20Header%20Image.png?alt=media&token=5a134bcb-6ea8-4690-b15a-089c46130ec8" style="width: 100%; height: auto;" />
	<h3>Hello {graduateName},</h3>
	<br>
	<p>Well wishes are coming your way! <strong>{senderName}</strong> just signed your yearbook.</p>
	<br>
	<p>"{message}"</p>
	<br>
	<p><a href="{yearbookUrl}">View your yearbook</a></p>
	<br>
	<p>Warm regards,</p>
	<p>The My Tassel Team</p>
	<div style="display: flex; margin-top: 10px; width: 100%; height: 20%; justify-content: center; align-items: center;">
		<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2Flogo.png?alt=media&token=8d69cd10-5bc6-4344-b266-b04dac2c80f0" style="max-width: 100px; height: 100px;" />
	</div>
</div>`
		},
		pageShare: {
			subject: "MyTassel | You're Invited to {firstName}'s MyTassel!",
			html: `<div style="display: block; text-align: center; max-width: 600px; margin: auto; font-size: 18px;">
	<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2FMyTassel%20Email%20Header%20Image.png?alt=media&token=5a134bcb-6ea8-4690-b15a-089c46130ec8" style="width: 100%; height: auto;" />
	<p>Hello,</p>
	<p>{firstName} {lastName} is thrilled to announce their graduation! Join them in celebrating this milestone achievement by visiting {firstName}'s MyTassel graduation page and registry!</p>
	<p>There you can view {firstName}'s personalized graduation announcement, read details of celebration plans, and sign their digital yearbook. If {firstName} created a registry page, you are welcome to make a purchase or contribution!</p>
	<p>Thank you for sharing in this memorable event and exciting new chapter!</p>
	<br/>
	<p>With gratitude,</p>
	<p>{firstName} {lastName} and the MyTassel team</p>
	<div style="display: flex; margin-top: 10px; width: 100%; height: 20%; justify-content: center; align-items: center;">
		<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2Flogo.png?alt=media&token=8d69cd10-5bc6-4344-b266-b04dac2c80f0" style="max-width: 100px; height: 100px;" />
	</div>
</div>`
		},
		ecardSent: {
			subject: "MyTassel | You've received a contribution & an eCard!",
			html: `<div style="text-align: center; max-width: 600px; margin: auto; font-size: 18px;">
	<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2FMyTassel%20Email%20Header%20Image.png?alt=media&token=5a134bcb-6ea8-4690-b15a-089c46130ec8" style="width: 100%; height: auto;" />
	<h3>Hello {recipientName}!</h3>
	<p>You've received a special eCard from <strong>{senderName}</strong>!</p>
	<div>
		<img src="{frontImageUrl}" alt="eCard" style="max-width: 100%; height: auto;">
	</div>
	<div style="background-color: #f8f9fa; border-radius: 8px; padding: 20px; margin: 20px 0; border-left: 4px solid #4c956c;">
		<h4 style="color: #333; margin: 0 0 10px 0;">Personal Message:</h4>
		<p style="font-style: italic; margin: 0; color: #555;">{message}</p>
	</div>
	<p>In addition, a contribution of \${contributionAmount} has been made in your honor.</p>
	<p>Congratulations!</p>
	<br>
	<p>Warm regards,</p>
	<p>The My Tassel Team</p>
	<div style="display: flex; margin-top: 10px; width: 100%; height: 20%; justify-content: center; align-items: center;">
		<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2Flogo.png?alt=media&token=8d69cd10-5bc6-4344-b266-b04dac2c80f0" style="max-width: 100px; height: 100px;" />
	</div>
</div>`
		},
		goalReached: {
			subject: 'MyTassel | Congratulations! Goal Reached!',
			html: `<div style="text-align: center; max-width: 600px; margin: auto; font-size: 18px;">
	<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2FMyTassel%20Email%20Header%20Image.png?alt=media&token=5a134bcb-6ea8-4690-b15a-089c46130ec8" style="width: 100%; height: auto;" />
	<h3>Hello {graduateName},</h3>
	<p>Congratulations! Your contribution goal of \${goalAmount} has been reached!</p>
	<p>This is a significant milestone, and we are thrilled to see the support from your friends and family. Your hard work and dedication are truly inspiring.</p>
	<p>Thank you for using MyTassel to share your journey and achievements. We look forward to seeing what you will accomplish next!</p>
	<br>
	<p>Warm regards,</p>
	<p>The My Tassel Team</p>
	<div style="display: flex; margin-top: 10px; width: 100%; height: 20%; justify-content: center; align-items: center;">
		<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2Flogo.png?alt=media&token=8d69cd10-5bc6-4344-b266-b04dac2c80f0" style="max-width: 100px; height: 100px;" />
	</div>
</div>`
		},
		pageAccessRequest: {
			subject: 'MyTassel | Access Request',
			html: `<div style="text-align: center; max-width: 600px; margin: auto; font-size: 18px;">
	<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2FMyTassel%20Email%20Header%20Image.png?alt=media&token=5a134bcb-6ea8-4690-b15a-089c46130ec8" style="width: 100%; height: auto;" />
	<h3>Hello {firstName},</h3>
	<p>{requesterName} ({requesterEmail}) is requesting access to your private page.</p>
	<p>If you'd like to grant {requesterName} access to your pages, please visit your events page or registry and add {requesterName} as a friend in the requests section of your friends page. If you'd like to change your profile to public, please click the <a href="https://www.mytassel.com/hub">gear icon</a> in the top right corner of any page and toggle the "Privacy" option to public.</p>
	<br>
	<p>Warm regards,</p>
	<p>The My Tassel Team</p>
	<div style="display: flex; margin-top: 10px; width: 100%; height: 20%; justify-content: center; align-items: center;">
		<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2Flogo.png?alt=media&token=8d69cd10-5bc6-4344-b266-b04dac2c80f0" style="max-width: 100px; height: 100px;" />
	</div>
</div>`
		},
		pageAccessRequestConfirmation: {
			subject: 'MyTassel | Your access request has been sent',
			html: `<div style="text-align: center; max-width: 600px; margin: auto; font-size: 18px;">
	<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2FMyTassel%20Email%20Header%20Image.png?alt=media&token=5a134bcb-6ea8-4690-b15a-089c46130ec8" style="width: 100%; height: auto;" />
	<h3>Access Request Sent</h3>
	<p>Dear {requesterName},</p>
	<p>We've sent your access request to {pageOwnerName}. They can choose to grant you access or keep their page private.</p>
	<div style="text-align: center; margin: 30px 0;">
		<a href="https://mytassel.com" style="display: inline-block; background-color: #4c956c; color: white; padding: 15px 30px; text-decoration: none; border-radius: 25px; font-weight: bold;">
			Explore MyTassel
		</a>
	</div>
	<p>Thank you for using MyTassel!</p>
	<p>Best regards,<br>The MyTassel Team</p>
	<div style="display: flex; margin-top: 10px; width: 100%; height: 20%; justify-content: center; align-items: center;">
		<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2Flogo.png?alt=media&token=8d69cd10-5bc6-4344-b266-b04dac2c80f0" style="max-width: 100px; height: 100px;" />
	</div>
</div>`
		},
		contactForm: {
			subject: 'MyTassel Contact Form: {inquiryType}',
			html: `<div style="text-align: center; max-width: 600px; margin: auto; font-size: 16px; font-family: Arial, sans-serif;">
	<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2FMyTassel%20Email%20Header%20Image.png?alt=media&token=5a134bcb-6ea8-4690-b15a-089c46130ec8" style="width: 100%; height: auto;" />
	<h2 style="color: #4c956c;">New Contact Form Submission</h2>
	<div style="margin: 20px 0; text-align: left; background-color: #f5f5f5; padding: 15px; border-radius: 5px;">
		<p><strong>Name:</strong> {firstName} {lastName}</p>
		<p><strong>Email:</strong> {email}</p>
		<p><strong>Inquiry Type:</strong> {inquiryType}</p>
		<p><strong>Message:</strong></p>
		<p>{message}</p>
	</div>
	<p style="font-size: 12px; color: #888; margin-top: 20px;">This email was sent from the MyTassel Contact Form</p>
	<div style="display: flex; margin-top: 10px; width: 100%; justify-content: center; align-items: center;">
		<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2Flogo.png?alt=media&token=8d69cd10-5bc6-4344-b266-b04dac2c80f0" style="max-width: 100px; height: 100px;" />
	</div>
</div>`
		},
		contactFormConfirmation: {
			subject: 'Thank you for contacting MyTassel!',
			html: `<div style="text-align: center; max-width: 600px; margin: auto; font-size: 16px; font-family: Arial, sans-serif;">
	<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2FMyTassel%20Email%20Header%20Image.png?alt=media&token=5a134bcb-6ea8-4690-b15a-089c46130ec8" style="width: 100%; height: auto;" />
	<h2 style="color: #4c956c;">Thank you for contacting MyTassel!</h2>
	<p>Dear {name},</p>
	<p>We've received your message and will get back to you as soon as possible. Here's a copy of what you sent:</p>
	<div style="background-color: #f9f9f9; padding: 20px; border-radius: 10px; margin: 20px 0; text-align: left;">
		<p><strong>Subject:</strong> {inquiryType}</p>
		<p><strong>Your Message:</strong></p>
		<p style="white-space: pre-line; margin-top: 10px;">{message}</p>
	</div>
	<p>Thank you for your interest in MyTassel!</p>
	<p>Best regards,<br>The MyTassel Team</p>
	<div style="display: flex; margin-top: 10px; width: 100%; justify-content: center; align-items: center;">
		<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2Flogo.png?alt=media&token=8d69cd10-5bc6-4344-b266-b04dac2c80f0" style="max-width: 100px; height: 100px;" />
	</div>
</div>`
		},
		eventReminder: {
			subject: 'MyTassel | Reminder: {eventName}',
			html: `<div style="text-align: center; max-width: 600px; margin: auto; font-size: 18px;">
	<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2FMyTassel%20Email%20Header%20Image.png?alt=media&token=5a134bcb-6ea8-4690-b15a-089c46130ec8" style="width: 100%; height: auto;" />
	<h3>Hello {firstName},</h3>
	<p>This is a friendly reminder that <strong>{eventName}</strong> is coming up soon!</p>
	<br>
	<p>Date: {eventDate}</p>
	<p>Location: {eventLocation}</p>
	<br>
	<p>We hope to see you there to celebrate this special occasion. Don't forget to check the event page for any last-minute updates!</p>
	<br>
	<p>Warm regards,</p>
	<p>The My Tassel Team</p>
	<div style="display: flex; margin-top: 10px; width: 100%; height: 20%; justify-content: center; align-items: center;">
		<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2Flogo.png?alt=media&token=8d69cd10-5bc6-4344-b266-b04dac2c80f0" style="max-width: 100px; height: 100px;" />
	</div>
</div>`
		},
		thankYouCard: {
			subject: 'MyTassel | Thank you from {graduateName}!',
			html: `<div style="text-align: center; max-width: 600px; margin: auto; font-size: 18px;">
	<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2FMyTassel%20Email%20Header%20Image.png?alt=media&token=5a134bcb-6ea8-4690-b15a-089c46130ec8" style="width: 100%; height: auto;" />
	<h3>Hello {firstName},</h3>
	<p>{graduateName} wanted to send you a special thank you for your generous support!</p>
	<br>
	<p>"{message}"</p>
	<br>
	<p>Your kindness and encouragement mean the world. Thank you for being a part of this celebration!</p>
	<br>
	<p>With gratitude,</p>
	<p>{graduateName} &amp; The My Tassel Team</p>
	<div style="display: flex; margin-top: 10px; width: 100%; height: 20%; justify-content: center; align-items: center;">
		<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2Flogo.png?alt=media&token=8d69cd10-5bc6-4344-b266-b04dac2c80f0" style="max-width: 100px; height: 100px;" />
	</div>
</div>`
		},
		profileCompletion1: {
			subject: 'MyTassel | {firstName}, complete your MyTassel profile!',
			html: `<div style="text-align: center; max-width: 600px; margin: auto; font-size: 18px;">
	<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2FMyTassel%20Email%20Header%20Image.png?alt=media&token=5a134bcb-6ea8-4690-b15a-089c46130ec8" style="width: 100%; height: auto;" />
	<h3>Hello {firstName},</h3>
	<p>We noticed you haven't set up your graduation page yet — and we'd love to help you get started!</p>
	<br>
	<p>With MyTassel, you can create a personalized graduation announcement, set up a gift registry, and share the celebration with friends and family.</p>
	<br>
	<p>It only takes a few minutes to <a href="https://www.mytassel.com/hub">set up your page</a> and start receiving well wishes and gifts.</p>
	<br>
	<p>Here's to your next chapter!</p>
	<p>The My Tassel Team</p>
	<div style="display: flex; margin-top: 10px; width: 100%; height: 20%; justify-content: center; align-items: center;">
		<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2Flogo.png?alt=media&token=8d69cd10-5bc6-4344-b266-b04dac2c80f0" style="max-width: 100px; height: 100px;" />
	</div>
</div>`
		},
		profileCompletion2: {
			subject: 'MyTassel | {firstName}, your graduation pages are waiting!',
			html: `<div style="text-align: center; max-width: 600px; margin: auto; font-size: 18px;">
	<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2FMyTassel%20Email%20Header%20Image.png?alt=media&token=5a134bcb-6ea8-4690-b15a-089c46130ec8" style="width: 100%; height: auto;" />
	<h3>Hey {firstName},</h3>
	<p>Just a quick nudge — your MyTassel graduation page is still waiting for you!</p>
	<br>
	<p>Did you know? Graduates who complete their profiles receive <strong>3x more contributions</strong> from friends and family.</p>
	<br>
	<p>It only takes a few minutes to <a href="https://www.mytassel.com/hub">finish setting up</a>. Your supporters are ready to celebrate with you!</p>
	<br>
	<p>Cheers,</p>
	<p>The My Tassel Team</p>
	<div style="display: flex; margin-top: 10px; width: 100%; height: 20%; justify-content: center; align-items: center;">
		<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2Flogo.png?alt=media&token=8d69cd10-5bc6-4344-b266-b04dac2c80f0" style="max-width: 100px; height: 100px;" />
	</div>
</div>`
		},
		profileCompletion3: {
			subject: 'MyTassel | Last reminder: Set up your MyTassel, {firstName}',
			html: `<div style="text-align: center; max-width: 600px; margin: auto; font-size: 18px;">
	<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2FMyTassel%20Email%20Header%20Image.png?alt=media&token=5a134bcb-6ea8-4690-b15a-089c46130ec8" style="width: 100%; height: auto;" />
	<h3>Hi {firstName},</h3>
	<p>This is our last reminder — we don't want you to miss out!</p>
	<br>
	<p>Your MyTassel graduation page is ready and waiting. Set it up to:</p>
	<ul style="text-align: left; display: inline-block;">
		<li>Share your graduation announcement</li>
		<li>Create a gift registry</li>
		<li>Receive contributions toward your goals</li>
		<li>Collect well wishes in your digital yearbook</li>
	</ul>
	<br>
	<p><a href="https://www.mytassel.com/hub">Complete your profile now</a> — it only takes a few minutes!</p>
	<br>
	<p>Wishing you all the best,</p>
	<p>The My Tassel Team</p>
	<div style="display: flex; margin-top: 10px; width: 100%; height: 20%; justify-content: center; align-items: center;">
		<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2Flogo.png?alt=media&token=8d69cd10-5bc6-4344-b266-b04dac2c80f0" style="max-width: 100px; height: 100px;" />
	</div>
</div>`
		},
		registryNudge: {
			subject: 'MyTassel | {firstName}, set up your gift registry!',
			html: `<div style="text-align: center; max-width: 600px; margin: auto; font-size: 18px;">
	<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2FMyTassel%20Email%20Header%20Image.png?alt=media&token=5a134bcb-6ea8-4690-b15a-089c46130ec8" style="width: 100%; height: auto;" />
	<h3>Hello {firstName},</h3>
	<p>Your events page is looking great! Now it's time to set up your gift registry so friends and family know exactly how to celebrate you.</p>
	<br>
	<p>With a MyTassel registry, you can add items you'd love, set a contribution goal, and make it easy for supporters to give meaningful gifts.</p>
	<br>
	<p><a href="https://www.mytassel.com/hub">Set up your registry now</a> and let the gifting begin!</p>
	<br>
	<p>Warm regards,</p>
	<p>The My Tassel Team</p>
	<div style="display: flex; margin-top: 10px; width: 100%; height: 20%; justify-content: center; align-items: center;">
		<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2Flogo.png?alt=media&token=8d69cd10-5bc6-4344-b266-b04dac2c80f0" style="max-width: 100px; height: 100px;" />
	</div>
</div>`
		},
		itemsNudge: {
			subject: 'MyTassel | {firstName}, take one more look at your registry',
			html: `<div style="text-align: center; max-width: 600px; margin: auto; font-size: 18px;">
	<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2FMyTassel%20Email%20Header%20Image.png?alt=media&token=5a134bcb-6ea8-4690-b15a-089c46130ec8" style="width: 100%; height: auto;" />
	<h3>Hi {firstName}!</h3>
	<p>Before things move forward, it is worth taking one more look at your registry.</p>
	<br>
	<p>This is your chance to make sure it reflects what you actually want and will use as you head into what is next. A few small updates now can make a big difference.</p>
	<br>
	<p>Take a few minutes to review and make any final adjustments.</p>
	<br>
	<p><a href="https://www.mytassel.com/hub" style="background-color: #4c956c; color: white; padding: 12px 24px; text-decoration: none; border-radius: 25px; font-weight: bold;">Finish My Registry</a></p>
	<br>
	<p>Warm regards,</p>
	<p>The My Tassel Team</p>
	<div style="display: flex; margin-top: 10px; width: 100%; height: 20%; justify-content: center; align-items: center;">
		<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2Flogo.png?alt=media&token=8d69cd10-5bc6-4344-b266-b04dac2c80f0" style="max-width: 100px; height: 100px;" />
	</div>
</div>`
		},
		goalNudge: {
			subject: 'MyTassel | {firstName}, set your contribution goal',
			html: `<div style="text-align: center; max-width: 600px; margin: auto; font-size: 18px;">
	<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2FMyTassel%20Email%20Header%20Image.png?alt=media&token=5a134bcb-6ea8-4690-b15a-089c46130ec8" style="width: 100%; height: auto;" />
	<h3>Hey {firstName},</h3>
	<p>Your registry is set up — awesome! Here's a tip: graduates who set a contribution goal receive <strong>40% more gifts</strong>.</p>
	<br>
	<p>A contribution goal lets your friends and family know how they can help kickstart your next chapter, whether it's for textbooks, a new laptop, or moving expenses.</p>
	<br>
	<p><a href="https://www.mytassel.com/hub">Set your goal now</a> and watch the support roll in!</p>
	<br>
	<p>Cheers,</p>
	<p>The My Tassel Team</p>
	<div style="display: flex; margin-top: 10px; width: 100%; height: 20%; justify-content: center; align-items: center;">
		<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2Flogo.png?alt=media&token=8d69cd10-5bc6-4344-b266-b04dac2c80f0" style="max-width: 100px; height: 100px;" />
	</div>
</div>`
		},
		widgetsNudge: {
			subject: 'MyTassel | {firstName}, customize your announcement page',
			html: `<div style="text-align: center; max-width: 600px; margin: auto; font-size: 18px;">
	<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2FMyTassel%20Email%20Header%20Image.png?alt=media&token=5a134bcb-6ea8-4690-b15a-089c46130ec8" style="width: 100%; height: auto;" />
	<h3>Hello {firstName},</h3>
	<p>Your graduation page is live — nice! Want to make it even better?</p>
	<br>
	<p>Graduates who customize their pages with widgets get <strong>2x more engagement</strong> from visitors. Try adding:</p>
	<ul style="text-align: left; display: inline-block;">
		<li>A countdown to your ceremony</li>
		<li>Photo galleries from your journey</li>
		<li>A personal message or quote</li>
		<li>Your event details</li>
	</ul>
	<br>
	<p><a href="https://www.mytassel.com/hub">Customize your page now</a> and make it uniquely yours!</p>
	<br>
	<p>Warm regards,</p>
	<p>The My Tassel Team</p>
	<div style="display: flex; margin-top: 10px; width: 100%; height: 20%; justify-content: center; align-items: center;">
		<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2Flogo.png?alt=media&token=8d69cd10-5bc6-4344-b266-b04dac2c80f0" style="max-width: 100px; height: 100px;" />
	</div>
</div>`
		},
		reengagement14Days: {
			subject: 'MyTassel | We miss you, {firstName}!',
			html: `<div style="text-align: center; max-width: 600px; margin: auto; font-size: 18px;">
	<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2FMyTassel%20Email%20Header%20Image.png?alt=media&token=5a134bcb-6ea8-4690-b15a-089c46130ec8" style="width: 100%; height: auto;" />
	<h3>Hey {firstName},</h3>
	<p>It's been a little while since we've seen you on MyTassel, and we wanted to let you know — your page has been getting attention!</p>
	<br>
	<p>Your page has received <strong>{pageViews} views</strong> since your last visit. People are checking you out!</p>
	<br>
	<p>Pop back in to see what's new, update your page, or check out any messages in your yearbook.</p>
	<br>
	<p><a href="https://www.mytassel.com/hub">Visit your page</a></p>
	<br>
	<p>We're rooting for you!</p>
	<p>The My Tassel Team</p>
	<div style="display: flex; margin-top: 10px; width: 100%; height: 20%; justify-content: center; align-items: center;">
		<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2Flogo.png?alt=media&token=8d69cd10-5bc6-4344-b266-b04dac2c80f0" style="max-width: 100px; height: 100px;" />
	</div>
</div>`
		},
		reengagement30Days: {
			subject: 'MyTassel | {firstName}, your graduation page is waiting',
			html: `<div style="text-align: center; max-width: 600px; margin: auto; font-size: 18px;">
	<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2FMyTassel%20Email%20Header%20Image.png?alt=media&token=5a134bcb-6ea8-4690-b15a-089c46130ec8" style="width: 100%; height: auto;" />
	<h3>Hi {firstName},</h3>
	<p>It's been a month since your last visit and your graduation page is still waiting for you!</p>
	<br>
	<p>Here's what you still have left to do:</p>
	<ul style="text-align: left; display: inline-block;">
		<li>Complete your profile</li>
		<li>Set up your events page</li>
		<li>Create your gift registry</li>
		<li>Share with friends &amp; family</li>
	</ul>
	<br>
	<p>Your friends and family are eager to celebrate with you. <a href="https://www.mytassel.com/hub">Come back and finish setting up</a> — it only takes a few minutes!</p>
	<br>
	<p>Best wishes,</p>
	<p>The My Tassel Team</p>
	<div style="display: flex; margin-top: 10px; width: 100%; height: 20%; justify-content: center; align-items: center;">
		<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2Flogo.png?alt=media&token=8d69cd10-5bc6-4344-b266-b04dac2c80f0" style="max-width: 100px; height: 100px;" />
	</div>
</div>`
		},
		firstContribution: {
			subject: 'MyTassel | {firstName}, you got your first contribution!',
			html: `<div style="text-align: center; max-width: 600px; margin: auto; font-size: 18px;">
	<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2FMyTassel%20Email%20Header%20Image.png?alt=media&token=5a134bcb-6ea8-4690-b15a-089c46130ec8" style="width: 100%; height: auto;" />
	<h3>Congratulations, {firstName}! 🎉</h3>
	<p>You just received your very first contribution on MyTassel!</p>
	<br>
	<p>This is just the beginning. Share your page with more friends and family to keep the momentum going!</p>
	<br>
	<p>Tip: Graduates who share their page on social media receive <strong>5x more contributions</strong>. Spread the word!</p>
	<br>
	<p><a href="https://www.mytassel.com/hub">View your page &amp; share</a></p>
	<br>
	<p>We're so excited for you!</p>
	<p>The My Tassel Team</p>
	<div style="display: flex; margin-top: 10px; width: 100%; height: 20%; justify-content: center; align-items: center;">
		<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2Flogo.png?alt=media&token=8d69cd10-5bc6-4344-b266-b04dac2c80f0" style="max-width: 100px; height: 100px;" />
	</div>
</div>`
		},
		firstRsvp: {
			subject: 'MyTassel | {firstName}, someone RSVP\'d to your event!',
			html: `<div style="text-align: center; max-width: 600px; margin: auto; font-size: 18px;">
	<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2FMyTassel%20Email%20Header%20Image.png?alt=media&token=5a134bcb-6ea8-4690-b15a-089c46130ec8" style="width: 100%; height: auto;" />
	<h3>Exciting news, {firstName}! 🎊</h3>
	<p>Someone just RSVP'd to your event! The celebration is coming together.</p>
	<br>
	<p>Keep sharing your event page to get even more attendees. The more the merrier!</p>
	<br>
	<p><a href="https://www.mytassel.com/hub">Check your RSVPs</a></p>
	<br>
	<p>Here's to a wonderful celebration!</p>
	<p>The My Tassel Team</p>
	<div style="display: flex; margin-top: 10px; width: 100%; height: 20%; justify-content: center; align-items: center;">
		<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2Flogo.png?alt=media&token=8d69cd10-5bc6-4344-b266-b04dac2c80f0" style="max-width: 100px; height: 100px;" />
	</div>
</div>`
		},
		pageViewsMilestone: {
			subject: 'MyTassel | {firstName}, your page hit {n} views!',
			html: `<div style="text-align: center; max-width: 600px; margin: auto; font-size: 18px;">
	<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2FMyTassel%20Email%20Header%20Image.png?alt=media&token=5a134bcb-6ea8-4690-b15a-089c46130ec8" style="width: 100%; height: auto;" />
	<h3>Way to go, {firstName}! 📈</h3>
	<p>Your MyTassel graduation page just hit <strong>{n} views</strong>!</p>
	<br>
	<p>People are clearly interested in celebrating with you. Keep the momentum going by sharing your page and keeping it updated!</p>
	<br>
	<p><a href="https://www.mytassel.com/hub">Visit your page</a></p>
	<br>
	<p>Congratulations on this milestone!</p>
	<p>The My Tassel Team</p>
	<div style="display: flex; margin-top: 10px; width: 100%; height: 20%; justify-content: center; align-items: center;">
		<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2Flogo.png?alt=media&token=8d69cd10-5bc6-4344-b266-b04dac2c80f0" style="max-width: 100px; height: 100px;" />
	</div>
</div>`
		},
		gradPageUpdated: {
			subject: 'MyTassel | {graduateFirstName} updated their MyTassel!',
			html: `<div style="text-align: center; max-width: 600px; margin: auto; font-size: 18px;">
	<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2FMyTassel%20Email%20Header%20Image.png?alt=media&token=5a134bcb-6ea8-4690-b15a-089c46130ec8" style="width: 100%; height: auto;" />
	<h3>Good news!</h3>
	<p><strong>{graduateFirstName}</strong> just made some exciting updates to their MyTassel graduation page!</p>
	<br>
	<p>Head over to check out what's new — there might be new event details, registry items, or announcements waiting for you.</p>
	<br>
	<p><a href="{pageUrl}">View {graduateFullName}'s page</a></p>
	<br>
	<p>Thanks for being a great supporter!</p>
	<p>The My Tassel Team</p>
	<div style="display: flex; margin-top: 10px; width: 100%; height: 20%; justify-content: center; align-items: center;">
		<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2Flogo.png?alt=media&token=8d69cd10-5bc6-4344-b266-b04dac2c80f0" style="max-width: 100px; height: 100px;" />
	</div>
</div>`
		},
		adminNewUserSignup: {
			subject: 'MyTassel Admin | New Signup: {name} ({accountType})',
			html: `<div style="text-align: center; max-width: 600px; margin: auto; font-size: 16px; font-family: Arial, sans-serif;">
	<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2FMyTassel%20Email%20Header%20Image.png?alt=media&token=5a134bcb-6ea8-4690-b15a-089c46130ec8" style="width: 100%; height: auto;" />
	<h2 style="color: #4c956c;">New User Signup</h2>
	<div style="margin: 20px 0; text-align: left; background-color: #f5f5f5; padding: 15px; border-radius: 5px;">
		<p><strong>Name:</strong> {name}</p>
		<p><strong>Email:</strong> {email}</p>
		<p><strong>Account Type:</strong> {accountType}</p>
		<p><strong>SMS Opt-in:</strong> {smsOptIn}</p>
		<p><strong>Signup Date:</strong> {signupDate}</p>
	</div>
	<p style="font-size: 12px; color: #888; margin-top: 20px;">This is an automated admin notification from MyTassel</p>
	<div style="display: flex; margin-top: 10px; width: 100%; justify-content: center; align-items: center;">
		<img src="https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2Flogo.png?alt=media&token=8d69cd10-5bc6-4344-b266-b04dac2c80f0" style="max-width: 100px; height: 100px;" />
	</div>
</div>`
		},
		
		// New Registry Item Nudge Email (single configurable-day template)
	};

	// Default engagement email cadence (days between emails)
	const defaultEngagementCadence = {
		profileCompletion: [5, 12, 21], // Days after signup
		registryNudge: [3], // Days after events page created (DISABLED)
		goalNudge: [14], // Days after registry created
		itemsNudge: [5, 14, 30], // Days after registry created — multi-day array, admin-configurable
		widgetsNudge: [5], // Days after events page created (DISABLED)
		reengagement: [30, 45] // Days since last login
	};

	// Get emails grouped by category
	const getEmailsByCategory = () => {
		const categories = {};
		Object.values(emailDefinitions).forEach(email => {
			const category = email.category;
			if (!categories[category]) {
				categories[category] = [];
			}
			categories[category].push(email);
		});
		return categories;
	};

	// All email ids (for expand/collapse all)
	const getAllEmailIds = () => Object.values(getEmailsByCategory()).flat().map((e) => e.id);

	const toggleAllEmailPreviews = () => {
		const allIds = getAllEmailIds();
		const allExpanded = allIds.length > 0 && expandedEmailPreviewIds.length >= allIds.length;
		if (allExpanded) {
			setExpandedEmailPreviewIds([]);
			setInlineEdits({});
		} else {
			setExpandedEmailPreviewIds(allIds);
			const edits = {};
			allIds.forEach(id => {
				const tmpl = getEmailTemplate(id);
				if (tmpl) edits[id] = { subject: tmpl.subject || '', html: tmpl.html || '' };
			});
			setInlineEdits(prev => ({ ...prev, ...edits }));
		}
	};

	const collapseAllEmailPreviews = () => {
		setExpandedEmailPreviewIds([]);
		setInlineEdits({});
		setEditingTemplate(null);
		setEditingTemplateContent('');
		setEditingSubject('');
	};

	const toggleSingleEmailPreview = (emailId) => {
		const isCurrentlyExpanded = expandedEmailPreviewIds.includes(emailId);
		if (isCurrentlyExpanded) {
			setExpandedEmailPreviewIds(prev => prev.filter(id => id !== emailId));
			setInlineEdits(prev => {
				const { [emailId]: _, ...rest } = prev;
				return rest;
			});
		} else {
			setExpandedEmailPreviewIds(prev => [...prev, emailId]);
			const tmpl = getEmailTemplate(emailId);
			if (tmpl) {
				setInlineEdits(prev => ({
					...prev,
					[emailId]: { subject: tmpl.subject || '', html: tmpl.html || '' }
				}));
			}
		}
	};

	// Get template for an email
	const getEmailTemplate = (emailId) => {
		// First check if custom template exists in settings
		if (emailSettings.templates && emailSettings.templates[emailId]) {
			return emailSettings.templates[emailId];
		}
		// Fall back to default template
		return emailTemplates[emailId] || null;
	};

	// Reset a custom template — removes the Firestore override so the code default is used
	const resetEmailTemplate = async (emailId) => {
		setEmailSettingsSaving(true);
		try {
			const configRef = doc(db, 'config', 'emailSettings');
			await updateDoc(configRef, { [`templates.${emailId}`]: deleteField() });
			setEmailSettings(prev => {
				const newTemplates = { ...(prev.templates || {}) };
				delete newTemplates[emailId];
				return { ...prev, templates: newTemplates };
			});
		} catch (e) {
			console.error('resetEmailTemplate error', e);
		} finally {
			setEmailSettingsSaving(false);
		}
	};

	// Save template to Firestore
	const saveEmailTemplate = async (emailId, templateData) => {
		setEmailSettingsSaving(true);
		try {
			const configRef = doc(db, 'config', 'emailSettings');
			const updates = {
				templates: {
					...(emailSettings.templates || {}),
					[emailId]: templateData
				},
				updatedAt: new Date().toISOString()
			};
			await setDoc(configRef, updates, { merge: true });
			setEmailSettings(prev => ({
				...prev,
				templates: {
					...(prev.templates || {}),
					[emailId]: templateData
				}
			}));
			setEditingTemplate(null);
			setEditingTemplateContent('');
			setEditingSubject('');
		} catch (error) {
			console.error('Error saving template:', error);
			alert('Failed to save template');
		} finally {
			setEmailSettingsSaving(false);
		}
	};

	// Sanitize HTML from visual editor (allow only safe tags for email)
	const sanitizeEmailHtml = (html) => {
		if (typeof document === 'undefined' || !html) return html;
		const div = document.createElement('div');
		div.innerHTML = html;
		const allowed = ['P', 'BR', 'DIV', 'SPAN', 'B', 'STRONG', 'I', 'EM', 'U', 'A', 'H1', 'H2', 'H3', 'H4', 'UL', 'OL', 'LI', 'TABLE', 'TR', 'TD', 'TH', 'TBODY', 'THEAD', 'IMG'];
		const walk = (node) => {
			if (node.nodeType === 1) {
				const tag = node.tagName.toUpperCase();
				if (!allowed.includes(tag)) {
					const span = document.createElement('span');
					while (node.firstChild) span.appendChild(node.firstChild);
					node.parentNode.replaceChild(span, node);
					walk(span);
					return;
				}
				if (tag === 'A') {
					const href = node.getAttribute('href') || '#';
					if (href.toLowerCase().startsWith('javascript:')) node.setAttribute('href', '#');
				}
				if (tag === 'IMG') {
					const src = node.getAttribute('src') || '';
					if (src.toLowerCase().startsWith('javascript:')) node.setAttribute('src', '');
				}
				Array.from(node.childNodes).forEach(walk);
			}
		};
		walk(div);
		return div.innerHTML;
	};

	// ─── SMS Helper Functions ───
	const getSmsByCategory = () => {
		const categories = {};
		Object.values(smsDefinitions).forEach(sms => {
			const category = sms.category;
			if (!categories[category]) categories[category] = [];
			categories[category].push(sms);
		});
		return categories;
	};

	const getAllSmsIds = () => Object.values(smsDefinitions).map(s => s.id);

	const getSmsTemplate = (smsId) => {
		if (smsSettings.templates && smsSettings.templates[smsId]) {
			return smsSettings.templates[smsId];
		}
		return smsTemplates[smsId] || null;
	};

	const saveSmsTemplate = async (smsId, templateData) => {
		setEmailSettingsSaving(true);
		try {
			const configRef = doc(db, 'config', 'smsSettings');
			const updates = {
				templates: {
					...(smsSettings.templates || {}),
					[smsId]: templateData
				},
				updatedAt: new Date().toISOString()
			};
			await setDoc(configRef, updates, { merge: true });
			setSmsSettings(prev => ({
				...prev,
				templates: {
					...(prev.templates || {}),
					[smsId]: templateData
				}
			}));
		} catch (error) {
			console.error('Error saving SMS template:', error);
			alert('Failed to save SMS template');
		} finally {
			setEmailSettingsSaving(false);
		}
	};

	// Reset a single SMS template to its default (no-emoji) version
	const resetSmsTemplateToDefault = async (smsId) => {
		const defaultTemplate = smsTemplates[smsId];
		if (!defaultTemplate) return;
		await saveSmsTemplate(smsId, defaultTemplate);
		// Also update the inline edit if it's open
		setInlineSmsEdits(prev => ({
			...prev,
			[smsId]: { body: defaultTemplate.body || '' }
		}));
	};

	// Reset ALL SMS templates to defaults
	const resetAllSmsTemplatesToDefault = async () => {
		setEmailSettingsSaving(true);
		try {
			const configRef = doc(db, 'config', 'smsSettings');
			await setDoc(configRef, { templates: { ...smsTemplates }, updatedAt: new Date().toISOString() }, { merge: true });
			setSmsSettings(prev => ({ ...prev, templates: { ...smsTemplates } }));
			// Update any open inline edits
			const updatedEdits = {};
			Object.keys(smsTemplates).forEach(id => {
				if (expandedSmsPreviewIds.includes(id)) {
					updatedEdits[id] = { body: smsTemplates[id].body || '' };
				}
			});
			setInlineSmsEdits(prev => ({ ...prev, ...updatedEdits }));
			alert('All SMS templates have been reset to defaults.');
		} catch (error) {
			console.error('Error resetting SMS templates:', error);
			alert('Failed to reset SMS templates');
		} finally {
			setEmailSettingsSaving(false);
		}
	};

	const toggleSingleSmsPreview = (smsId) => {
		if (expandedSmsPreviewIds.includes(smsId)) {
			setExpandedSmsPreviewIds(prev => prev.filter(id => id !== smsId));
			setInlineSmsEdits(prev => {
				const next = { ...prev };
				delete next[smsId];
				return next;
			});
		} else {
			setExpandedSmsPreviewIds(prev => [...prev, smsId]);
			const tmpl = getSmsTemplate(smsId);
			if (tmpl) {
				setInlineSmsEdits(prev => ({
					...prev,
					[smsId]: { body: tmpl.body || '' }
				}));
			}
		}
	};

	const toggleAllSmsPreviews = () => {
		const allIds = getAllSmsIds();
		const allExpanded = allIds.length > 0 && expandedSmsPreviewIds.length >= allIds.length;
		if (allExpanded) {
			setExpandedSmsPreviewIds([]);
			setInlineSmsEdits({});
		} else {
			setExpandedSmsPreviewIds(allIds);
			const edits = {};
			allIds.forEach(id => {
				const tmpl = getSmsTemplate(id);
				if (tmpl) edits[id] = { body: tmpl.body || '' };
			});
			setInlineSmsEdits(edits);
		}
	};

	const toggleSmsEnabled = async (smsId) => {
		setEmailSettingsSaving(true);
		try {
			const configRef = doc(db, 'config', 'smsSettings');
			const currentEnabled = smsSettings[smsId]?.enabled !== false;
			const updates = {
				[smsId]: {
					...(smsSettings[smsId] || {}),
					enabled: !currentEnabled
				}
			};
			await setDoc(configRef, updates, { merge: true });
			setSmsSettings(prev => ({
				...prev,
				[smsId]: {
					...(prev[smsId] || {}),
					enabled: !currentEnabled
				}
			}));
		} catch (error) {
			console.error('Error toggling SMS:', error);
		} finally {
			setEmailSettingsSaving(false);
		}
	};

	// Insert a variable placeholder at cursor in the visual editor
	const insertEmailVariable = (variable) => {
		const el = emailBodyEditorRef.current;
		if (!el) return;
		el.focus();
		const sel = window.getSelection();
		const range = document.createRange();
		if (sel.rangeCount) range.setStart(sel.anchorNode, sel.anchorOffset);
		range.collapse(true);
		const span = document.createElement('span');
		span.contentEditable = 'false';
		span.style.backgroundColor = isDarkMode ? '#334155' : '#e2e8f0';
		span.style.padding = '2px 6px';
		span.style.borderRadius = '4px';
		span.style.fontFamily = 'monospace';
		span.style.fontSize = '12px';
		span.textContent = `{{${variable}}}`;
		span.setAttribute('data-variable', variable);
		range.insertNode(span);
		sel.removeAllRanges();
		const r2 = document.createRange();
		r2.setStartAfter(span);
		r2.collapse(true);
		sel.addRange(r2);
	};

	// Get body HTML from visual editor (expand variable spans back to {{name}})
	const getVisualEditorHtml = () => {
		const el = emailBodyEditorRef.current;
		if (!el) return editingTemplateContent;
		const clone = el.cloneNode(true);
		clone.querySelectorAll('[data-variable]').forEach((span) => {
			const v = span.getAttribute('data-variable');
			const text = document.createTextNode(`{{${v}}}`);
			span.parentNode.replaceChild(text, span);
		});
		return sanitizeEmailHtml(clone.innerHTML);
	};

	// List of variables admins can insert (non-technical labels)
	const emailVariableOptions = [
		{ value: 'firstName', label: 'First name' },
		{ value: 'lastName', label: 'Last name' },
		{ value: 'amount', label: 'Amount / number' },
		{ value: 'contributorName', label: 'Contributor name' },
		{ value: 'itemName', label: 'Item name' },
		{ value: 'itemsCount', label: 'Registry items count' },
		{ value: 'message', label: 'Message / note' },
		{ value: 'milestone', label: 'Milestone (e.g. 100 views)' },
		{ value: 'accountType', label: 'Account type' },
		{ value: 'graduateFirstName', label: 'Graduate first name' },
		{ value: 'graduateName', label: 'Graduate full name' },
		{ value: 'pageLink', label: 'Graduation page link (HTML)' },
		{ value: 'registryLink', label: 'Registry page link (HTML)' }
	];

	// Sync contentEditable when opening editor or switching to Visual mode
	React.useEffect(() => {
		if (!editingTemplate || !useVisualEditor) return;
		const el = emailBodyEditorRef.current;
		if (el && editingTemplateContent) {
			el.innerHTML = editingTemplateContent;
		}
	}, [editingTemplate, useVisualEditor, editingTemplateContent]);

	// Render email preview with sample data
	const renderEmailPreview = (emailId) => {
		const template = getEmailTemplate(emailId);
		if (!template) return null;
		
		// Sample data for preview — keys must match what the server actually passes
		const sampleData = {
			firstName: 'John',
			lastName: 'Smith',
			graduateName: 'John Smith',
			recipientName: 'John Smith',
			amount: '50.00',
			contributionAmount: '50.00',
			contributorName: 'Jane Doe',
			senderName: 'Jane Doe',
			senderEmail: 'jane@example.com',
			itemName: 'Laptop Stand',
			itemsCount: 3,
			itemPrice: '49.99',
			itemUrl: 'https://example.com',
			cardImageUrl: 'https://via.placeholder.com/300x200',
			message: 'Congratulations on your graduation!',
			goalAmount: '500.00',
			totalAmount: '525.00',
			requesterName: 'Alex Johnson',
			requesterEmail: 'alex@example.com',
			inquiryType: 'General Question',
			email: 'john@example.com',
			graduateFirstName: 'John',
			graduateFullName: 'John Smith',
			pageUrl: 'https://mytassel.com/share/preview',
			pageLink: '<a href="https://mytassel.com/share/preview">View Graduation Page</a>',
			registryLink: '<a href="https://mytassel.com/registry/preview">View Registry Page</a>',
			yearbookUrl: 'https://mytassel.com/yearbook/preview',
		};
		
		let html = template.html;
		// Replace both {{key}} and {key} variants
		Object.entries(sampleData).forEach(([key, value]) => {
			html = html.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value);
			html = html.replace(new RegExp(`\\{${key}\\}`, 'g'), value);
		});
		
		return html;
	};

	// Save cadence settings
	const saveCadenceSettings = async (cadenceType, days) => {
		setEmailSettingsSaving(true);
		try {
			const configRef = doc(db, 'config', 'emailSettings');
			const updates = {
				cadence: {
					...(emailSettings.cadence || defaultEngagementCadence),
					[cadenceType]: days
				},
				updatedAt: new Date().toISOString()
			};
			await setDoc(configRef, updates, { merge: true });
			setEmailSettings(prev => ({
				...prev,
				cadence: {
					...(prev.cadence || defaultEngagementCadence),
					[cadenceType]: days
				}
			}));
			setEmailCadenceEditing(null);
		} catch (error) {
			console.error('Error saving cadence:', error);
		} finally {
			setEmailSettingsSaving(false);
		}
	};

	// Category icons and order for emails
	const emailCategoryConfig = {
		'Welcome': { icon: 'hand-left', color: '#4c956c', order: 1 },
		'Registry': { icon: 'gift', color: '#f59e0b', order: 2 },
		'Event': { icon: 'calendar', color: '#3b82f6', order: 3 },
		'Reminders': { icon: 'alarm', color: '#8b5cf6', order: 4 },
		'Access': { icon: 'lock-closed', color: '#64748b', order: 5 },
		'Support': { icon: 'help-circle', color: '#06b6d4', order: 6 },
		'Thank You': { icon: 'heart', color: '#ec4899', order: 7 },
		'Engagement': { icon: 'trending-up', color: '#10b981', order: 8 },
		'Milestones': { icon: 'trophy', color: '#eab308', order: 9 },
		'Followers': { icon: 'people', color: '#6366f1', order: 10 },
		'Admin': { icon: 'shield-checkmark', color: '#ef4444', order: 11 }
	};

	// Load email settings from Firestore
	const loadEmailSettings = async () => {
		setEmailSettingsLoading(true);
		try {
			const configRef = doc(db, 'config', 'emailSettings');
			const configSnap = await getDoc(configRef);
			
			if (configSnap.exists()) {
				const data = configSnap.data();
				setEmailSettings(data);

				// Auto-sync: push default templates to Firestore for any email that doesn't have one yet
				const existingTemplates = data.templates || {};
				const missingTemplates = {};
				Object.keys(emailTemplates).forEach(emailId => {
					if (!existingTemplates[emailId]) {
						missingTemplates[emailId] = emailTemplates[emailId];
					}
				});
				
				if (Object.keys(missingTemplates).length > 0) {
					const merged = { ...existingTemplates, ...missingTemplates };
					await setDoc(configRef, { templates: merged, updatedAt: new Date().toISOString() }, { merge: true });
					setEmailSettings(prev => ({ ...prev, templates: merged }));
					console.log(`Synced ${Object.keys(missingTemplates).length} default template(s) to Firestore:`, Object.keys(missingTemplates));
				}
			} else {
				// Create default settings - all emails enabled + all default templates
				const defaultSettings = {};
				Object.keys(emailDefinitions).forEach(key => {
					defaultSettings[key] = { enabled: true };
				});
				defaultSettings.templates = { ...emailTemplates };
				defaultSettings.engagementSchedule = 'every day 10:00';
				defaultSettings.engagementTimezone = 'America/Los_Angeles';
				defaultSettings.createdAt = new Date().toISOString();
				await setDoc(configRef, defaultSettings);
				setEmailSettings(defaultSettings);
			}
		} catch (error) {
			console.error('Error loading email settings:', error);
		} finally {
			setEmailSettingsLoading(false);
		}
	};

	// Force refresh all email templates from code to Firestore
	const forceRefreshTemplates = async () => {
		setEmailSettingsSaving(true);
		try {
			const configRef = doc(db, 'config', 'emailSettings');
			
			// Get current state first
			const configSnap = await getDoc(configRef);
			const currentData = configSnap.exists() ? configSnap.data() : {};
			const currentTemplates = currentData.templates || {};
			
			// Only add missing templates, preserve admin edits
			const newTemplates = { ...currentTemplates };
			let addedCount = 0;
			
			Object.keys(emailTemplates).forEach(emailId => {
				if (!currentTemplates[emailId]) {
					newTemplates[emailId] = emailTemplates[emailId];
					addedCount++;
				}
			});
			
			if (addedCount > 0) {
				await setDoc(configRef, { 
					...currentData,
					templates: newTemplates,
					updatedAt: new Date().toISOString(),
					lastForceRefresh: new Date().toISOString()
				}, { merge: true });
				
				setEmailSettings(prev => ({ 
					...prev, 
					templates: newTemplates
				}));
				
				console.log(`✅ Added ${addedCount} missing email templates`);
				alert(`Success! Added ${addedCount} missing email templates. Your existing customizations are preserved.`);
			} else {
				alert('All templates are already present. No updates needed.');
			}
			
		} catch (error) {
			console.error('❌ Error force refreshing templates:', error);
			alert('Error refreshing templates: ' + error.message);
		} finally {
			setEmailSettingsSaving(false);
		}
	};

	// Load SMS settings from Firestore
	const loadSmsSettings = async () => {
		try {
			const configRef = doc(db, 'config', 'smsSettings');
			const configSnap = await getDoc(configRef);
			if (configSnap.exists()) {
				const data = configSnap.data();
				const existingTemplates = data.templates || {};

				// Only add default templates for IDs that don't exist yet in Firestore.
				// This preserves any admin-edited templates while ensuring new template types are added.
				const missingIds = Object.keys(smsTemplates).filter(id => !existingTemplates[id]);
				if (missingIds.length > 0) {
					const merged = { ...existingTemplates };
					missingIds.forEach(id => { merged[id] = smsTemplates[id]; });
					await setDoc(configRef, { templates: merged, updatedAt: new Date().toISOString() }, { merge: true });
					setSmsSettings({ ...data, templates: merged });
					console.log('Added missing SMS templates to Firestore:', missingIds);
				} else {
					setSmsSettings(data);
				}
			} else {
				const defaultSettings = { templates: { ...smsTemplates }, createdAt: new Date().toISOString() };
				Object.keys(smsDefinitions).forEach(key => {
					defaultSettings[key] = { enabled: true };
				});
				await setDoc(configRef, defaultSettings);
				setSmsSettings(defaultSettings);
			}
		} catch (error) {
			console.error('Error loading SMS settings:', error);
		}
	};

	// Save email settings
	const saveEmailSettings = async (updates) => {
		setEmailSettingsSaving(true);
		try {
			const configRef = doc(db, 'config', 'emailSettings');
			const newSettings = {
				...emailSettings,
				...updates,
				updatedAt: new Date().toISOString()
			};
			await setDoc(configRef, newSettings, { merge: true });
			setEmailSettings(newSettings);
		} catch (error) {
			console.error('Error saving email settings:', error);
		} finally {
			setEmailSettingsSaving(false);
		}
	};

	// Toggle email enabled/disabled
	const toggleEmailEnabled = async (emailId) => {
		const currentSetting = emailSettings[emailId] || { enabled: true };
		await saveEmailSettings({
			[emailId]: { ...currentSetting, enabled: !currentSetting.enabled }
		});
	};

	// Toggle email category collapse
	const toggleEmailCategoryCollapse = (category) => {
		setCollapsedEmailCategories(prev => ({
			...prev,
			[category]: !prev[category]
		}));
	};

	// Toggle category collapse
	const toggleCategoryCollapse = (category) => {
		setCollapsedCategories(prev => ({
			...prev,
			[category]: !prev[category]
		}));
	};
	
	// Feature flag definitions with descriptions
	const featureFlagDefinitions = {
		showDashboardButton: {
			label: "My Dashboard Button",
			description: "Shows/hides the 'My Dashboard' button on the Hub page for graduate users.",
			category: "Hub Page",
			impact: "high",
			version: "1.0.0"
		},
		showSmsPhoneBackfillPrompt: {
			label: "SMS Phone Backfill Prompt",
			description: "Shows/hides the Hub modal that prompts opted-in users without a phone number to add one or turn off SMS.",
			category: "Hub Page",
			impact: "high",
			version: "1.4.8",
			defaults: { staging: false, production: false },
			previewable: true,
		},
		showPollsWidget: {
			label: "Polls Widget",
			description: "Shows/hides the Polls widget. Users won't be able to create or view polls when disabled.",
			category: "Announcements Page",
			impact: "medium",
			version: "1.0.0"
		},
		showTimelineWidget: {
			label: "Timeline Widget",
			description: "Shows/hides the Timeline widget on the Announcements page.",
			category: "Announcements Page",
			impact: "medium",
			version: "1.0.0"
		},
		showEditorMap: {
			label: "Editor Map",
			description: "Shows/hides the interactive Mapbox map in the event creation/editing wizard (Step 3: Location). When disabled, users can still type a location but won't see the map.",
			category: "Announcements Page",
			impact: "medium",
			version: "1.0.0"
		},
		showVisitorMap: {
			label: "Visitor Map",
			description: "Shows/hides the embedded map on the visitor-facing Event widget. When disabled, the map preview beneath event details will not be shown to visitors.",
			category: "Announcements Page",
			impact: "medium",
			version: "1.0.0"
		},
		showRemindersSystem: {
			label: "Reminders System",
			description: "Shows/hides the Reminders feature for event notifications.",
			category: "Announcements Page",
			impact: "medium",
			version: "1.0.0"
		},
		// Dashboard Component Flags
		dashboardGiftTracking: {
			label: "Gift Tracking",
			description: "Primary feature showing who bought what items and cash gifts received.",
			category: "Graduate Dashboard",
			impact: "high",
			version: "1.0.0"
		},
		dashboardProfileOverview: {
			label: "Profile Overview",
			description: "Profile Overview card displaying basic profile information.",
			category: "Graduate Dashboard",
			impact: "low",
			version: "1.0.0"
		},
		dashboardAchievements: {
			label: "Achievements & XP",
			description: "Achievement Progress gamification section.",
			category: "Graduate Dashboard",
			impact: "low",
			version: "1.0.0"
		},
		dashboardEventManagement: {
			label: "Event Management",
			description: "Event Management section including RSVP tracking and reminders.",
			category: "Graduate Dashboard",
			impact: "medium",
			version: "1.0.0"
		},
		dashboardRegistryStats: {
			label: "Registry Statistics",
			description: "Registry Statistics summary cards.",
			category: "Graduate Dashboard",
			impact: "low",
			version: "1.0.0"
		},
		dashboardYearbookMessages: {
			label: "Yearbook Messages",
			description: "Digital Yearbook Messages section.",
			category: "Graduate Dashboard",
			impact: "low",
			version: "1.0.0"
		},
		dashboardPollResults: {
			label: "Poll Results",
			description: "Poll Results section on the dashboard.",
			category: "Graduate Dashboard",
			impact: "low",
			version: "1.0.0"
		},
		dashboardPageActivities: {
			label: "Page Activities",
			description: "Page Activities summary showing engagement data.",
			category: "Graduate Dashboard",
			impact: "low",
			version: "1.0.0"
		},
		shareToastNotifications: {
			label: "Share Toast Notifications",
			description: "Shows polished toast notifications when sharing via Email or SMS instead of browser alerts.",
			category: "Registry Page",
			impact: "low",
			version: "1.1.3"
		},
	};

	const getFeatureFlagDefaults = (flagKey) => (
		featureFlagDefinitions[flagKey]?.defaults || { staging: true, production: true }
	);

	const [blogTitle, setBlogTitle] = useState("");
	const [blogContent, setBlogContent] = useState("");
	const [blogCategory, setBlogCategory] = useState("");
	const [blogImage, setBlogImage] = useState("");
	const [blogTags, setBlogTags] = useState([]);
	const [blogPosts, setBlogPosts] = useState([]);
	const [editingBlogId, setEditingBlogId] = useState(null);
	const [publishDate, setPublishDate] = useState("");
	const [lastUpdatedDate, setLastUpdatedDate] = useState("");

	// Update the blogCategories array
	const blogCategories = [
		"Graduation Tips",
		"Life After Graduation",
		"Moving",
		"Money Matters",
		"Student Life",
		"Career Planning",
		"Parent's Corner"
	];

	// Add preset blog tags
	const presetBlogTags = [
		"Freshman Tips",
		"Senior Year",
		"Dorm Essentials",
		"Study Hacks",
		"Career Planning",
		"Money Management",
		"Campus Life",
		"Organization Tips",
		"Time Management",
		"College Success",
		"Graduation",
		"Student Resources",
		"Moving Guide",
		"College Prep",
		"Student Life Hacks"
	];

	// Add state for subcategories
	const [subCategories, setSubCategories] = useState({
		"Bedroom": [
			"Sheets", "Comforter", "Bed In a Bag", "Pillow", "Pillow Protector", "Body Pillow",
			"Mattress Topper", "Mattress Protector", "Throw Blanket", "Study Pillow",
			"Space Heaters", // Added new subcategory
			"Room Security", // Changed from "Room Alarm"
			"Misc"
		],
		"Bathroom": [
			"Towel Set", "Waterproof Speakers", // Changed from "Bluetooth Speaker"
			"Shower Caddy", "Shower Shoes", "Bathrobe", "Shower Wrap", "Hair Wrap",
			"Bathroom Rug", "Shower Curtain", "Tension Rod", "Cosmetic Organizer", // Moved from Bedroom
			"Misc"
		],
		"Laundry & Cleaning": [
			"Cleaning Bundle", "Laundry Basket", "Hamper", "Laundry Detergent",
			"Odor Eliminator", "Steamer", "Iron", // Added "Iron"
			"Ironing Board", "Vacuums", "Laundry Bag", "Broom & Dust Pan",
			"Garbage Can", "Misc"
		],
		"Electronics & Office Supplies": [
			"Television", "Laptop", "Chargers", "Flash Drive", "Hard Drive",
			"Extension Cords", "Headphones", "Printer", "Alarm Clock",
			"Bluetooth Speaker", "Personal Alarm",
			"Firestick", "Alexa/Amazon Speakers",
			"Day Planner", "Backpack", "Pens", "Pencils", "Highlighters",
			"Notebooks", "Stapler", "Scissors", "Printer Paper", "Sticky Notes",
			"Dry Erase Markers", "Desk Set", "Computer Mouse",
			"Misc"
		],
		"Decor": ["Throw Rug", "Throw Pillows", "Clip Lamps", "Desk Lamp", "Floor Lamp", "Lounge Seating", "Mirror", "Picture Frames", "String Lights", "Tension Rod", "Vanity Mirror", "Window Curtains", "Wall Safe Adhesive", "Dry Erase/Bulletin Board", "Standing Fan", "Bed Fan/Clip Fan", "Doorstopper", "Lap Desk", "Room Decor", "Faux Plants", "Floor Pillows", "Misc"],
		"Health & Personal Care": ["Hair Dryer", "Styling Tools", "Hair Removal", "First Aid Kit", "Pain Reliever", "Sewing Kit", "Exercise Equipment", "Humidifier", "Diffuser", "Diffuser Oils", "Air Purifier", "Noise Machine", "Tool Set", "Misc"],
		"Kitchen": ["Microwave", "Refrigerator", "Personal Refrigerator", "Coffee Maker", "Rice Cooker", "Cooking Tools", "Smoothie Maker", "Air Fryer", "Dishes", "Silverware", "Water Bottles", "Travel Mug", "Can Opener", "Water Filtering", "Mugs", "Snack Bundles", "Food Storage", "Pots/Pans", "Cooking Utensils", "Misc"],
		"Storage & Organization": ["Bedside Shelf/Caddy", "Closet Organization", "Shoe Storage/Racks", "Hangers", "Trunk/Storage", "Underbed Storage", "Drawers", "Over Door Organization", "Rolling Cart", "Desk Organizer", "Wall Key Organizer", "Moving Bags", "Luggage", "Misc"]
	});

	const [selectedSubCategory, setSelectedSubCategory] = useState("");

	// Add these state variables near the other state declarations
	const [users, setUsers] = useState([]);
	const [authUsers, setAuthUsers] = useState([]);
	const [userSearchQuery, setUserSearchQuery] = useState('');
	const [selectedUser, setSelectedUser] = useState(null);
	const [isUserModalOpen, setIsUserModalOpen] = useState(false);
	const [tutorialAdminUser, setTutorialAdminUser] = useState(null);
	const [userActionLoading, setUserActionLoading] = useState(false);
	const [shareAnalytics, setShareAnalytics] = useState({}); // Add this line for share analytics

	// Add these state variables
	const [filterAccountType, setFilterAccountType] = useState('all'); // all | graduate | friends | admin | tester
	const [filterStripe, setFilterStripe] = useState('all'); // all | connected | pending
	const [filterGoal, setFilterGoal] = useState('all'); // all | set | not_set
	const [filterPrivacy, setFilterPrivacy] = useState('all'); // all | public | private
	const [filterActivity, setFilterActivity] = useState('all'); // all | active_7d | active_30d | inactive_30d
	const [filterSmsOptIn, setFilterSmsOptIn] = useState('all'); // all | opted_in | not_opted_in
	const [userSort, setUserSort] = useState('newest'); // 'newest', 'oldest', 'alphabetical', 'most_active', 'least_active', 'goal_high', 'goal_low'

	// Add a new state for search input
	const [searchInput, setSearchInput] = useState('');

	// Add new state variables
	const [adminPassword, setAdminPassword] = useState("");
	const [showStripeResetConfirm, setShowStripeResetConfirm] = useState(false);
	const [userToResetStripe, setUserToResetStripe] = useState(null);

	// Add state for selected categories
	const [selectedCategories, setSelectedCategories] = useState([]);

	// Find & Fix user state
	const [findFixEmail, setFindFixEmail] = useState('');
	const [findFixLoading, setFindFixLoading] = useState(false);
	const [findFixResult, setFindFixResult] = useState(null);
	const [showFindFixModal, setShowFindFixModal] = useState(false);

	// Add search button handler
	const handleSearch = () => {
		if (searchInput.trim().length < 3) {
			alert('Please enter at least 3 characters to search');
			return;
		}
		setSearchQuery(searchInput);
	};

	// Add these functions for user management
	const fetchAllUsers = async () => {
		try {
			// Fetch database users
			const usersSnapshot = await getDocs(collection(db, "users"));
			const dbUsers = usersSnapshot.docs.map(doc => ({
				...doc.data(),
				id: doc.id,
				// Ensure email is always populated - doc ID IS the email but some docs may not store it as a field
				email: doc.data().email || doc.id,
				source: 'database'
			}));

			// Get the current user's ID token
			let token = null;
			try {
				if (auth.currentUser) {
					token = await auth.currentUser.getIdToken();
					
				} else {
					
				}
			} catch (tokenError) {
				
			}

			// Try to fetch authentication users, but don't fail if server is down
			let authUsers = [];
			try {
				
				
				const authUsersResponse = await fetch(`${SERVER_URL}/api/listUsers`, {
					method: 'GET',
					headers: {
						'Content-Type': 'application/json',
						'Authorization': `Bearer ${token}`
					}
				});
				
				
				
				
				if (authUsersResponse.ok) {
					const authUsersData = await authUsersResponse.json();
					
					authUsers = authUsersData.users || [];
					setAuthUsers(authUsers);
					
				} else {
					const errorText = await authUsersResponse.text();
					
				}
			} catch (serverError) {
				
				
			}

			// Merge auth data (including creation time) with database users
			const mergedUsers = dbUsers.map(dbUser => {
				const authUser = authUsers.find(au => au.email?.toLowerCase() === dbUser.email?.toLowerCase());
				return {
					...dbUser,
					// Prefer Firestore creationTime, then auth creationTime, then null
					creationTime: dbUser.creationTime || authUser?.creationTime || null,
					lastSignInTime: authUser?.lastSignInTime || null,
					emailVerified: authUser?.emailVerified || false,
					uid: authUser?.uid || dbUser.uid || dbUser.id
				};
			});

			// Include auth users that have no matching Firestore document (auth-only users)
			const firestoreEmails = new Set(dbUsers.map(u => u.email?.toLowerCase()).filter(Boolean));
			const authOnlyUsers = authUsers
				.filter(au => au.email && !firestoreEmails.has(au.email.toLowerCase()))
				.map(au => ({
					id: au.uid,
					email: au.email,
					firstName: au.displayName ? au.displayName.split(' ')[0] : '',
					lastName: au.displayName ? au.displayName.split(' ').slice(1).join(' ') : '',
					creationTime: au.creationTime,
					lastSignInTime: au.lastSignInTime,
					emailVerified: au.emailVerified,
					uid: au.uid,
					firestoreMissing: true,
					source: 'auth-only'
				}));

			setUsers([...mergedUsers, ...authOnlyUsers]);
			
		} catch (error) {
			
			// Set empty array to prevent infinite loading
			setUsers([]);
		}
	};

	// Find & Fix: comprehensive lookup for a user by email across Auth + Firestore
	const handleFindAndFixUser = async () => {
		const email = findFixEmail.trim().toLowerCase();
		if (!email) return;
		setFindFixLoading(true);
		setFindFixResult(null);
		try {
			// 1. Check direct Firestore doc at users/{email}
			const directDocRef = doc(db, 'users', email);
			const directDocSnap = await getDoc(directDocRef);

			// 2. Query docs where email field == this email (catches docs stored under UID)
			const emailFieldQuery = await getDocs(query(collection(db, 'users'), where('email', '==', email)));
			const emailFieldDocs = emailFieldQuery.docs.map(d => ({ docId: d.id, data: d.data() }));

			// 3. Find in already-fetched auth users
			const authMatch = authUsers.find(au => au.email?.toLowerCase() === email);

			setFindFixResult({
				email,
				directDocExists: directDocSnap.exists(),
				directDocData: directDocSnap.exists() ? directDocSnap.data() : null,
				emailFieldDocs,
				authMatch: authMatch || null,
			});
			setShowFindFixModal(true);
		} catch (err) {
			alert('Lookup failed: ' + err.message);
		} finally {
			setFindFixLoading(false);
		}
	};

	// Create a Firestore document at users/{email} for an auth-only user
	const handleCreateFirestoreDoc = async (userOrResult) => {
		const email = (userOrResult.email || '').toLowerCase();
		if (!email) { alert('No email found.'); return; }
		if (!window.confirm(`Create a Firestore document for ${email}? This will give them a basic profile that they can fill in.`)) return;
		try {
			const authMatch = authUsers.find(au => au.email?.toLowerCase() === email);
			const newDoc = {
				email: email,
				firstName: userOrResult.firstName || (authMatch?.displayName?.split(' ')[0] || ''),
				lastName: userOrResult.lastName || (authMatch?.displayName?.split(' ').slice(1).join(' ') || ''),
				accountType: 'Friends & Family',
				creationTime: userOrResult.creationTime || authMatch?.creationTime || new Date().toISOString(),
				uid: userOrResult.uid || authMatch?.uid || '',
				createdByAdmin: true,
			};
			await setDoc(doc(db, 'users', email), newDoc);
			alert(`Firestore document created for ${email}. Refreshing user list...`);
			setShowFindFixModal(false);
			await fetchAllUsers();
		} catch (err) {
			alert('Failed to create document: ' + err.message);
		}
	};

	const handleResetUserPassword = async (email) => {
		if (window.confirm(`Are you sure you want to send a password reset email to ${email}?`)) {
			try {
				setUserActionLoading(true);
				await sendPasswordResetEmail(auth, email);
				alert(`Password reset email sent to ${email}`);
			} catch (error) {
				alert(`Error: ${error.message}`);
			} finally {
				setUserActionLoading(false);
			}
		}
	};

	const handleUpdateUserEmail = async (userId, currentEmail) => {
		const newEmail = prompt("Enter new email address:", currentEmail);
		if (newEmail && newEmail !== currentEmail) {
			try {
				setUserActionLoading(true);
				await fetch(`${SERVER_URL}/api/updateUserEmail`, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json'
					},
					body: JSON.stringify({
						uid: userId,
						newEmail: newEmail
					})
				});
				
				// Update in Firestore
				const userRef = doc(db, "users", userId);
				await updateDoc(userRef, { email: newEmail });
				
				alert("Email updated successfully");
				fetchAllUsers();
			} catch (error) {
				alert(`Error: ${error.message}`);
			} finally {
				setUserActionLoading(false);
			}
		}
	};

	const handleDeleteUser = async (userId, email) => {
		// Create a more detailed confirmation message
		const confirmMessage = `Are you sure you want to delete user ${email}?\n\nThis will:\n- Delete their authentication account\n- Remove all their data from the database\n- This action CANNOT be undone\n\nPlease type DELETE to confirm:`;
		
		const userInput = prompt(confirmMessage);
		
		if (userInput === 'DELETE') {
			try {
				setUserActionLoading(true);
				
				// First try to delete from Firestore
				try {
					await deleteDoc(doc(db, "users", email.toLowerCase()));
					
				} catch (firestoreError) {
					
					throw new Error(`Failed to delete user data: ${firestoreError.message}`);
				}
				
				// Then try to delete from Authentication
				try {
					const token = await auth.currentUser.getIdToken();
					const authResponse = await fetch(`${SERVER_URL}/api/deleteUser`, {
						method: 'POST',
						headers: {
							'Content-Type': 'application/json',
							'Authorization': `Bearer ${token}`
						},
						body: JSON.stringify({ 
							uid: userId,
							email: email.toLowerCase() 
						})
					});

					if (!authResponse.ok) {
						const errorData = await authResponse.json();
						throw new Error(errorData.message || 'Failed to delete authentication account');
					}
				} catch (authError) {
					
					// Even if auth deletion fails, we've already deleted the Firestore data
					alert("Warning: User data was deleted but there was an issue removing their authentication account. Please contact support.");
					fetchAllUsers();
					return;
				}
				
				alert("User successfully deleted from both authentication and database.");
				fetchAllUsers();
			} catch (error) {
				
				alert(`Error during deletion process: ${error.message}\nPlease try again or contact support if the issue persists.`);
			} finally {
				setUserActionLoading(false);
			}
		} else if (userInput !== null) {
			alert("Deletion cancelled - incorrect confirmation text entered.");
		}
	};

	// Function to handle subcategory change
	const handleSubCategoryChange = async (itemId, newSubCategory) => {
		try {
			const itemRef = doc(db, "catalogueItems", itemId);
			await updateDoc(itemRef, { subCategory: newSubCategory });

			setCatalogueItems(
				catalogueItems.map((item) => {
					if (item.id === itemId) {
						return { ...item, subCategory: newSubCategory };
					}
					return item;
				})
			);

			// Automatically select the product
			setSelectedProducts((prevSelectedProducts) => {
				if (!prevSelectedProducts.includes(itemId)) {
					return [...prevSelectedProducts, itemId];
				}
				return prevSelectedProducts;
			});
		} catch (error) {
			
		}
	};

	// Admin email whitelist - these emails can access admin even without accountType === "admin"
	const ADMIN_EMAIL_WHITELIST = [
		'trevorhawkins@dsignable.com',
		'info@mytassel.com'
	];

	// Check if user has admin access (either by accountType or email whitelist)
	const hasAdminAccess = (userData) => {
		if (!userData) return false;
		const email = userData.email?.toLowerCase();
		return userData.accountType === "admin" || ADMIN_EMAIL_WHITELIST.includes(email);
	};

	useEffect(() => {
		if (isDevelopment) {
			setShowAdmin(true); // Bypass login and authorization checks in development
		} else {
			if (!currentUserData || !hasAdminAccess(currentUserData)) {
				setShowLoginPopup(true);
				setShowAdmin(false);
				setTestEmail(currentUserData?.email || currentUser?.email);
			} else {
				setShowAdmin(true);
			}
		}
	}, [currentUserData]);

	useEffect(() => {
		try {
			// Wrap initialization in try/catch
			const initializeAdmin = async () => {
				try {
					setLoading(true);
					await fetchOrganizations();
					await fetchCatalogueItems();
					await fetchVisibilitySettings();
					await fetchBlogPosts();
					await fetchAllUsers();
					setLoading(false);
				} catch (err) {
					
					setError(err.message || "Failed to initialize Admin page");
					setLoading(false);
				}
			};
			
			initializeAdmin();
		} catch (err) {
			
			setError(err.message || "Critical error in Admin component");
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchCatalogueItems();
	}, [filter, sort]);

	useEffect(() => {
		const statusDocRef = doc(db, "maintenance", "status"); // Ensure this path is correct
		const unsubscribe = onSnapshot(statusDocRef, (doc) => {
			if (doc.exists()) {
				setIsMaintenance(doc.data().isMaintenanceActive); // Corrected field name
				
			} else {
				
			}
		});
		return () => unsubscribe(); // Cleanup subscription on unmount
	}, []); // No need to add db as a dependency since it's imported

	useEffect(() => {
		scanForUidMismatches();
	}, []);

	useEffect(() => {
		if (activeTab === 'users') {
			fetchAllUsers();
			fetchShareAnalytics();
		}
	}, [activeTab]);

	// Auth users state updated
	useEffect(() => {
		// State update handled
	}, [authUsers]);

	// Initialize analytics when the analytics tab is active
	useEffect(() => {
		if (activeTab === 'analytics') {
			// This component will handle its own data loading
			
		}
	}, [activeTab]);

	// Load feature flags when the features tab is active
	useEffect(() => {
		if (activeTab === 'features') {
			loadFeatureFlags();
		}
	}, [activeTab]);

	useEffect(() => {
		if (activeTab === 'mytassel-plus') {
			loadStandardProfileBackgroundConfig();
		}
	}, [activeTab]);

	// Auto-load scan results and history when the registry-audit tab opens
	useEffect(() => {
		if (activeTab === 'registry-audit') {
			runRegistryAudit();
			loadRegistryAuditHistory();
		}
	}, [activeTab]);

	const loadReminderTimingConfig = async () => {
		setReminderTimingLoading(true);
		try {
			const configRef = doc(db, 'config', 'reminderSettings');
			const snap = await getDoc(configRef);
			if (snap.exists()) {
				const data = snap.data();
				setReminderTimingConfig({
					defaultOptions: data.defaultOptions || defaultReminderOptions,
					dayOfSendHourUTC: data.dayOfSendHourUTC ?? 9,
				});
			}
		} catch (err) {
			console.error('Failed to load reminder timing config:', err);
		} finally {
			setReminderTimingLoading(false);
		}
	};

	const saveReminderTimingConfig = async () => {
		setReminderTimingSaving(true);
		try {
			const configRef = doc(db, 'config', 'reminderSettings');
			await setDoc(configRef, {
				defaultOptions: reminderTimingConfig.defaultOptions,
				dayOfSendHourUTC: reminderTimingConfig.dayOfSendHourUTC,
				updatedAt: new Date().toISOString(),
			}, { merge: true });
			alert('Reminder timing configuration saved.');
		} catch (err) {
			console.error('Failed to save reminder timing config:', err);
			alert('Failed to save reminder timing: ' + err.message);
		} finally {
			setReminderTimingSaving(false);
		}
	};

	const loadThankYouTemplates = async () => {
		setThankYouTemplatesLoading(true);
		try {
			const snap = await getDoc(doc(db, 'config', 'thankYouCardTemplates'));
			if (snap.exists() && snap.data().templates?.length) {
				setThankYouTemplates(snap.data().templates);
			}
		} catch (err) {
			console.error('Failed to load thank you card templates:', err);
		} finally {
			setThankYouTemplatesLoading(false);
		}
	};

	const saveThankYouTemplates = async () => {
		setThankYouTemplatesSaving(true);
		try {
			await setDoc(doc(db, 'config', 'thankYouCardTemplates'), {
				templates: thankYouTemplates,
				updatedAt: new Date().toISOString(),
			}, { merge: true });
			alert('Thank you card templates saved successfully.');
		} catch (err) {
			console.error('Failed to save thank you card templates:', err);
			alert('Failed to save: ' + err.message);
		} finally {
			setThankYouTemplatesSaving(false);
		}
	};

	// Load admin notification config and email settings when the notifications tab is active
	useEffect(() => {
		if (activeTab === 'notifications') {
			loadAdminNotificationConfig();
			loadEmailSettings();
			loadSmsSettings();
			loadReminderTimingConfig();
			loadThankYouTemplates();
		}
	}, [activeTab]);

	// Load registry config when the registry tab is active
	useEffect(() => {
		if (activeTab === 'registry') {
			loadRegistryConfig();
		}
	}, [activeTab]);

	// Function to load feature flags from Firestore
	// Feature flags now use environment-specific structure: { staging: bool, production: bool }
	const loadFeatureFlags = async () => {
		setFeatureFlagsLoading(true);
		try {
			const configRef = doc(db, 'config', 'features');
			const configSnap = await getDoc(configRef);
			
			if (configSnap.exists()) {
				const existingFlags = configSnap.data();
				// Ensure all defined flags exist in Firestore with new structure
				const allFlagKeys = Object.keys(featureFlagDefinitions);
				const missingFlags = {};
				let hasMissing = false;
				
				allFlagKeys.forEach(key => {
					// Check if flag exists and has the new structure
					if (existingFlags[key] === undefined) {
						// New flag - add with env-specific structure
						missingFlags[key] = getFeatureFlagDefaults(key);
						hasMissing = true;
					} else if (typeof existingFlags[key] === 'boolean') {
						// Old format - migrate to new structure
						missingFlags[key] = { staging: existingFlags[key], production: existingFlags[key] };
						hasMissing = true;
					}
				});
				
				if (hasMissing) {
					// Update Firestore with missing/migrated flags
					await updateDoc(configRef, missingFlags);
					setFeatureFlags({ ...existingFlags, ...missingFlags });
					
				} else {
					setFeatureFlags(existingFlags);
					
				}
			} else {
				// Create default feature flags document if it doesn't exist
				const defaultFlags = {};
				Object.keys(featureFlagDefinitions).forEach(key => {
					defaultFlags[key] = getFeatureFlagDefaults(key);
				});
				await setDoc(configRef, defaultFlags);
				setFeatureFlags(defaultFlags);
				
			}
		} catch (error) {
			
		} finally {
			setFeatureFlagsLoading(false);
		}
	};

	const loadStandardProfileBackgroundConfig = async () => {
		setStandardBackgroundsLoading(true);
		setStandardBackgroundsStatus('');
		try {
			const configSnap = await getDoc(doc(
				db,
				STANDARD_PROFILE_BACKGROUNDS_CONFIG.collection,
				STANDARD_PROFILE_BACKGROUNDS_CONFIG.id
			));
			const configured = configSnap.exists()
				? normalizeStandardProfileBackgrounds(
					configSnap.data()?.[STANDARD_PROFILE_BACKGROUNDS_CONFIG.field] || [],
					{
						includeInactive: true,
						defaultStandardAvailability: (_background, index) => index < 5,
					}
				)
				: [];
			setStandardProfileBackgrounds(configured.length ? configured : FALLBACK_STANDARD_PROFILE_BACKGROUNDS);
		} catch (error) {
			setStandardProfileBackgrounds(FALLBACK_STANDARD_PROFILE_BACKGROUNDS);
			setStandardBackgroundsStatus(`Using fallback backgrounds. ${error.message || ''}`);
		} finally {
			setStandardBackgroundsLoading(false);
		}
	};

	const buildStandardProfileBackgroundPayload = (backgrounds) => (
		backgrounds.map((background, index) => ({
			id: background.id,
			label: background.label,
			imageUrl: background.imageUrl,
			storagePath: background.storagePath || '',
			active: readBackgroundBoolean(background, 'active', true),
			availableToStandard: readBackgroundBoolean(background, 'availableToStandard', true),
			availableToPlus: readBackgroundBoolean(background, 'availableToPlus', true),
			order: index,
		}))
	);

	const validateStandardBackgroundLimit = (backgrounds) => {
		const standardEnabledCount = backgrounds.filter((background) => (
			readBackgroundBoolean(background, 'active', true) &&
			readBackgroundBoolean(background, 'availableToStandard', true)
		)).length;

		return standardEnabledCount <= 5;
	};

	const saveStandardProfileBackgroundConfig = async (backgrounds = standardProfileBackgrounds) => {
		if (!currentUser?.getIdToken) {
			alert('Please sign in as an admin before saving MyTassel+ settings.');
			return;
		}

		setStandardBackgroundsSaving(true);
		setStandardBackgroundsStatus('');
		try {
			const standardProfileBackgroundsPayload = buildStandardProfileBackgroundPayload(backgrounds);
			if (!validateStandardBackgroundLimit(standardProfileBackgroundsPayload)) {
				throw new Error('Standard users can only see up to five backgrounds. Turn off Standard on another background before adding this one.');
			}

			const token = await currentUser.getIdToken();
			const response = await fetch(`${SERVER_URL}/admin/mytassel-plus/standard-backgrounds`, {
				method: 'POST',
				headers: {
					Authorization: `Bearer ${token}`,
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					standardProfileBackgrounds: standardProfileBackgroundsPayload,
				}),
			});
			const data = await response.json().catch(() => ({}));
			if (!response.ok) throw new Error(data.error || 'Failed to save Standard backgrounds.');

			setStandardProfileBackgrounds(normalizeStandardProfileBackgrounds(
				standardProfileBackgroundsPayload,
				{ includeInactive: true }
			));
			setStandardBackgroundsStatus('Standard backgrounds saved.');
		} catch (error) {
			setStandardBackgroundsStatus(error.message || 'Failed to save Standard backgrounds.');
		} finally {
			setStandardBackgroundsSaving(false);
		}
	};

	const addStandardProfileBackground = async (event) => {
		const file = event.target.files?.[0];
		event.target.value = '';
		if (!file) return;

		setStandardBackgroundsSaving(true);
		setStandardBackgroundsStatus('Uploading background...');
		try {
			const storage = getStorage();
			const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '-');
			const path = `${STANDARD_PROFILE_BACKGROUNDS_CONFIG.storagePath}/${Date.now()}-${safeName}`;
			const fileRef = ref(storage, path);
			await uploadBytes(fileRef, file, { contentType: file.type || 'image/jpeg' });
			const imageUrl = await getDownloadURL(fileRef);
			const next = [
				...standardProfileBackgrounds,
				{
					id: `standard-${Date.now()}`,
					label: `Background ${standardProfileBackgrounds.length + 1}`,
					imageUrl,
					storagePath: path,
					active: true,
					availableToStandard: true,
					availableToPlus: true,
					order: standardProfileBackgrounds.length,
				},
			];
			setStandardProfileBackgrounds(next);
			await saveStandardProfileBackgroundConfig(next);
		} catch (error) {
			setStandardBackgroundsStatus(error.message || 'Failed to upload background.');
		} finally {
			setStandardBackgroundsSaving(false);
		}
	};

	const updateStandardProfileBackground = (id, updates) => {
		setStandardProfileBackgrounds(prev => prev.map((background) => (
			background.id === id ? { ...background, ...updates } : background
		)));
	};

	const updateStandardProfileBackgroundAvailability = (id, updates) => {
		const nextBackgrounds = standardProfileBackgrounds.map((background) => (
			background.id === id ? { ...background, ...updates } : background
		));

		if (!validateStandardBackgroundLimit(nextBackgrounds)) {
			window.alert('Standard users can only see up to five backgrounds. Turn off Standard on another background before adding this one.');
			return;
		}

		setStandardProfileBackgrounds(nextBackgrounds);
	};

	const removeStandardProfileBackground = (id) => {
		const confirmed = window.confirm(
			'Remove this background from Admin background choices? This will not delete the Storage file.'
		);
		if (!confirmed) return;

		setStandardProfileBackgrounds(prev => prev.filter((background) => background.id !== id));
	};

	// Get feature flags grouped by category
	const getFeatureFlagsByCategory = () => {
		const categories = {};
		Object.entries(featureFlagDefinitions).forEach(([flagKey, flagDef]) => {
			const category = flagDef.category || 'Other';
			if (!categories[category]) {
				categories[category] = [];
			}
			categories[category].push({ key: flagKey, ...flagDef });
		});
		return categories;
	};

	// Load admin notification config directly from Firestore
	const loadAdminNotificationConfig = async () => {
		setAdminNotificationsLoading(true);
		try {
			const configRef = doc(db, 'config', 'adminNotifications');
			const configSnap = await getDoc(configRef);
			
			if (configSnap.exists()) {
				setAdminNotificationConfig(configSnap.data());
			} else {
				// Create default config if it doesn't exist
				const defaultConfig = {
					enabled: true,
					newUserSignupEnabled: true,
					recipients: [
						{ email: 'trevorhawkins@dsignable.com', name: 'Trevor Hawkins', enabled: true }
					],
					createdAt: new Date().toISOString()
				};
				await setDoc(configRef, defaultConfig);
				setAdminNotificationConfig(defaultConfig);
			}
		} catch (error) {
			console.error('Error loading admin notification config:', error);
		} finally {
			setAdminNotificationsLoading(false);
		}
	};

	// Save admin notification config directly to Firestore
	const saveAdminNotificationConfig = async (updates) => {
		setAdminNotificationSaving(true);
		try {
			const configRef = doc(db, 'config', 'adminNotifications');
			const newConfig = {
				...adminNotificationConfig,
				...updates,
				updatedAt: new Date().toISOString()
			};
			await setDoc(configRef, newConfig, { merge: true });
			setAdminNotificationConfig(newConfig);
			return { success: true, config: newConfig };
		} catch (error) {
			console.error('Error saving admin notification config:', error);
			throw error;
		} finally {
			setAdminNotificationSaving(false);
		}
	};

	// Load registry config from Firestore (preset contribution amounts)
	const loadRegistryConfig = async () => {
		setRegistryLoading(true);
		try {
			const configRef = doc(db, 'config', 'registry');
			const configSnap = await getDoc(configRef);
			if (configSnap.exists()) {
				const data = configSnap.data();
				const amounts = Array.isArray(data.presetAmounts) ? data.presetAmounts : [25,50,100,250];
				setRegistryPresets(amounts);
			} else {
				const defaultConfig = { presetAmounts: [25,50,100,250], createdAt: new Date().toISOString() };
				await setDoc(configRef, defaultConfig);
				setRegistryPresets(defaultConfig.presetAmounts);
			}
		} catch (error) {
			console.error('Error loading registry config:', error);
			setRegistryError(error.message || 'Failed to load registry config');
		} finally {
			setRegistryLoading(false);
		}
	};

	// Save registry config to Firestore
	const saveRegistryConfig = async (amounts) => {
		setRegistrySaving(true);
		try {
			const configRef = doc(db, 'config', 'registry');
			const newConfig = { presetAmounts: amounts, updatedAt: new Date().toISOString() };
			await setDoc(configRef, newConfig, { merge: true });
			setRegistryPresets(amounts);
		} catch (error) {
			console.error('Error saving registry config:', error);
			throw error;
		} finally {
			setRegistrySaving(false);
		}
	};

	// Registry preset helpers
	const DEFAULT_REGISTRY_PRESETS = [25, 50, 100, 250];

	const handlePresetChange = (index, value) => {
		const newPresets = [...registryPresets];
		newPresets[index] = String(value);
		setRegistryPresets(newPresets);
	};

	const addRegistryPreset = () => {
		setRegistryPresets(prev => ([...prev, '']));
	};

	const removeRegistryPreset = (index) => {
		setRegistryPresets(prev => prev.filter((_, i) => i !== index));
	};

	const resetRegistryToDefault = () => {
		setRegistryPresets(DEFAULT_REGISTRY_PRESETS);
	};

	const handleSaveRegistryPresets = async () => {
		try {
			const parsed = registryPresets
				.map(v => Number(String(v).replace(/[^0-9.]/g, '')))
				.filter(n => !Number.isNaN(n) && n > 0);
			if (parsed.length === 0) {
				alert('Please provide at least one positive preset amount.');
				return;
			}
			await saveRegistryConfig(parsed);
			alert('Registry presets saved.');
		} catch (err) {
			console.error('Failed to save presets:', err);
			alert('Failed to save presets: ' + (err.message || err));
		}
	};

	// Toggle admin notification enabled
	const toggleAdminNotificationsEnabled = async () => {
		await saveAdminNotificationConfig({ enabled: !adminNotificationConfig.enabled });
	};

	// Toggle new user signup notifications
	const toggleNewUserSignupNotifications = async () => {
		await saveAdminNotificationConfig({ newUserSignupEnabled: !adminNotificationConfig.newUserSignupEnabled });
	};

	// Toggle individual recipient
	const toggleAdminRecipient = async (email) => {
		const updatedRecipients = adminNotificationConfig.recipients.map(r => 
			r.email === email ? { ...r, enabled: !r.enabled } : r
		);
		await saveAdminNotificationConfig({ recipients: updatedRecipients });
	};

	// Add new admin recipient
	const addAdminRecipient = async () => {
		if (!newAdminEmail || !newAdminEmail.includes('@')) {
			Alert.alert('Invalid Email', 'Please enter a valid email address.');
			return;
		}
		// Check if email already exists
		if (adminNotificationConfig.recipients.some(r => r.email.toLowerCase() === newAdminEmail.toLowerCase())) {
			Alert.alert('Duplicate', 'This email is already in the list.');
			return;
		}
		const newRecipient = {
			email: newAdminEmail.toLowerCase(),
			name: newAdminName || newAdminEmail.split('@')[0],
			enabled: true,
			addedAt: new Date().toISOString()
		};
		await saveAdminNotificationConfig({ 
			recipients: [...adminNotificationConfig.recipients, newRecipient]
		});
		setNewAdminEmail('');
		setNewAdminName('');
	};

	// Remove admin recipient
	const removeAdminRecipient = async (email) => {
		const updatedRecipients = adminNotificationConfig.recipients.filter(r => r.email !== email);
		await saveAdminNotificationConfig({ recipients: updatedRecipients });
	};

	// Send test admin notification
	const sendTestAdminNotification = async () => {
		try {
			const testUserData = {
				firstName: 'Test',
				lastName: 'User',
				email: 'test@example.com',
				accountType: 'Graduate',
				creationTime: new Date().toISOString(),
				smsOptIn: true,
				organization: { name: 'Test University' }
			};
			const response = await fetch(`${SERVER_URL}/admin-new-user-notification`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ 
					userData: testUserData,
					skipConfigCheck: false
				})
			});
			const data = await response.json();
			if (data.success) {
				Alert.alert('Success', data.skipped 
					? `Notification skipped: ${data.reason}` 
					: `Test notification sent to ${data.recipientCount} recipient(s)`
				);
			} else {
				Alert.alert('Error', data.error || 'Failed to send test notification');
			}
		} catch (error) {
			Alert.alert('Error', error.message);
		}
	};

	// Category display order and icons
	const categoryConfig = {
		'Hub Page': { iconName: 'home-outline', order: 1, description: 'Hub page navigation and buttons' },
		'Announcements Page': { iconName: 'megaphone-outline', order: 2, description: 'Announcements page widgets' },
		'Graduate Dashboard': { iconName: 'grid-outline', order: 3, description: 'Graduate dashboard sections' },
		'Other': { iconName: 'settings-outline', order: 4, description: 'Other app features' }
	};

	// Direct staging toggle (no confirmation needed)
	const toggleStagingFlag = async (flagKey, newValue) => {
		try {
			const configRef = doc(db, 'config', 'features');
			const currentFlag = featureFlags[flagKey] || getFeatureFlagDefaults(flagKey);
			const updatedFlag = { ...currentFlag, staging: newValue };
			
			await updateDoc(configRef, { [flagKey]: updatedFlag });
			setFeatureFlags(prev => ({ ...prev, [flagKey]: updatedFlag }));
		} catch (error) {
			alert('Failed to update staging flag. Please try again.');
		}
	};

	// Production toggle requires confirmation
	const initiateProductionFlagChange = (flagKey, newValue) => {
		const flagDef = featureFlagDefinitions[flagKey];
		setPendingFeatureChange({
			key: flagKey,
			newValue,
			environment: 'production',
			label: flagDef?.label || flagKey,
			description: flagDef?.description || 'No description available',
			impact: flagDef?.impact || 'unknown',
			category: flagDef?.category || 'Other',
			action: newValue ? 'ENABLE' : 'DISABLE'
		});
		setShowFeatureConfirmModal(true);
	};

	// Function to confirm and execute feature flag change
	const confirmFeatureFlagChange = async () => {
		if (!pendingFeatureChange) return;
		
		try {
			const configRef = doc(db, 'config', 'features');
			const { key, newValue, environment } = pendingFeatureChange;
			
			// Get current flag value and update the specific environment
			const currentFlag = featureFlags[key] || getFeatureFlagDefaults(key);
			const updatedFlag = {
				...currentFlag,
				[environment]: newValue
			};
			
			await updateDoc(configRef, {
				[key]: updatedFlag
			});
			
			setFeatureFlags(prev => ({
				...prev,
				[key]: updatedFlag
			}));
			
			const envLabel = environment === 'staging' ? 'STAGING' : 'PRODUCTION';
			
			alert(`Feature "${pendingFeatureChange.label}" has been ${newValue ? 'ENABLED' : 'DISABLED'} on ${envLabel}.`);
		} catch (error) {
			
			alert('Failed to update feature flag. Please try again.');
		} finally {
			setShowFeatureConfirmModal(false);
			setPendingFeatureChange(null);
		}
	};

	// Function to cancel feature flag change
	const cancelFeatureFlagChange = () => {
		setShowFeatureConfirmModal(false);
		setPendingFeatureChange(null);
	};

	const toggleMaintenance = async () => {
		const password = prompt("Please retype the site password to confirm:");
		if (password === "Newtjhawk27") { // Check if the password matches
			const newMaintenanceStatus = !isMaintenance; // Toggle the maintenance status
			await updateDoc(doc(db, "maintenance", "status"), {
				isMaintenanceActive: newMaintenanceStatus,
				startTime: newMaintenanceStatus ? new Date().toLocaleString() : null, // Changed to standard language format
			});
		} else {
			alert("Incorrect password. Maintenance status not changed.");
		}
	};

	const fetchOrganizations = async () => {
		const querySnapshot = await getDocs(collection(db, "new organizations"));
		const orgs = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
		setOrganizations(orgs);
		setLoading(false);
	};

	// Adjusted fetchCatalogueItems to optionally filter and sort items
	const fetchCatalogueItems = async () => {
		let querySnapshot = await getDocs(collection(db, "catalogueItems"));
		let items = querySnapshot.docs.map((doc) => {
			const data = doc.data();
			return {
				id: doc.id,
				...data,
				subCategory: data.subCategory || "Default Subcategory" // Use default or existing subcategory
			};
		});

		// Filter items if a category is selected
		if (filter !== "all") {
			items = items.filter((item) => item.category === filter);
		}

		// Sort items based on the selected sorting method
		if (sort === "priceLowHigh") {
			items.sort((a, b) => parseFloat(a.price.replace("$", "")) - parseFloat(b.price.replace("$", "")));
		} else if (sort === "priceHighLow") {
			items.sort((a, b) => parseFloat(b.price.replace("$", "")) - parseFloat(a.price.replace("$", "")));
		}

		setCatalogueItems(items);
	};

	// Function to handle search input change
	const handleSearchChange = (e) => {
		setSearchQuery(e.target.value);
		setCurrentPage(1); // Reset to first page to start filtering from the beginning
	};

	// Filter and paginate items
	const filteredItems = catalogueItems.filter((item) => item.name.toLowerCase().includes(searchQuery.toLowerCase()));

	// Calculate total pages
	const totalPages = Math.ceil(filteredItems.length / itemsPerPage);

	// Function to change page
	const handlePageChange = (pageNumber) => {
		setCurrentPage(pageNumber);
	};

	// Calculate the items to display
	const indexOfLastItem = currentPage * itemsPerPage;
	const indexOfFirstItem = indexOfLastItem - itemsPerPage;
	const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);

	const handleApprove = (org) => {
		const updatedOrg = { ...org, approved: true, code: generateInstitutionCode(org.organizationType) };
		setApprovedOrgs([...approvedOrgs, updatedOrg]);
		setOrganizations(organizations.filter((o) => o.id !== org.id));
	};

	const handleDeny = (org) => {
		const updatedOrg = { ...org, approved: false };
		setDeniedOrgs([...deniedOrgs, updatedOrg]);
		setOrganizations(organizations.filter((o) => o.id !== org.id));
	};

	const uploadApproved = async () => {
		if (window.confirm("Are you sure you want to upload these entries?")) {
			try {
				// Upload each approved organization to Firestore
				for (const org of approvedOrgs) {
					await addDoc(collection(db, "approvedOrganizations"), formatOrgForUpload(org));
				}

				// Clear approved organizations
				setApprovedOrgs([]);

				// Update Firestore for each organization
				approvedOrgs.forEach((org) => {
					deleteDoc(doc(db, "new organizations", org.id));
				});
			} catch (error) {
				
			}
		}
	};

	const formatOrgForUpload = (org) => {
		return {
			name: org.name,
			domains: [org.website],
			web_pages: [org.website],
			country: org.location || "",
			alpha_two_code: org.location || "",
			"state-province": org.location || null,
			organizationCode: org.code,
			members: {},
		};
	};

	const purgeDenied = async () => {
		if (window.confirm("Are you sure you want to permanently delete these entries?")) {
			for (const org of deniedOrgs) {
				await deleteDoc(doc(db, "new organizations", org.name));
			}
			setDeniedOrgs([]);
		}
	};

	const handleEdit = (id, field, value) => {
		setOrganizations(
			organizations.map((org) => {
				if (org.id === id) {
					return { ...org, [field]: value };
				}
				return org;
			})
		);
	};

	const handleReevaluate = (org, listSetter, fromList) => {
		listSetter(fromList.filter((o) => o.id !== org.id));
		setOrganizations([...organizations, org]);
	};

	const fetchItemPreview = async () => {
		const cleanedAffiliateUrl = sanitizeAffiliateUrl(affiliateUrl);
		setAffiliateUrl(cleanedAffiliateUrl); // Update the state with the sanitized URL

		setLoadingStep("Checking for duplicates...");
		const isDuplicate = await checkForDuplicateLink(cleanedAffiliateUrl);

		if (isDuplicate) {
			setLoadingStep("This affiliate link is already in the database.");
			// Optionally, reset affiliateUrl or handle as needed
			setAffiliateUrl("");
			return; // Stop execution to prevent adding a duplicate
		}

		setLoadingStep("Fetching preview, please wait...");
		setIsFetching(true);
		setShowStillWorking(false); // Reset the "still working" message state

		// Set a timeout to show the "still working" message after 7 seconds
		const stillWorkingTimeout = setTimeout(() => {
			setShowStillWorking(true);
		}, 7000); // 7000 milliseconds = 7 seconds

		try {
			const response = await fetch(`${SERVER_URL}/fetch-preview`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({ url: affiliateUrl }),
			});
			clearTimeout(stillWorkingTimeout); // Clear the timeout upon fetch completion
			if (response.ok) {
				setLoadingStep("Processing data...");
				const previewData = await response.json();
				setItemPreview(previewData);
				setLoadingStep("Preview fetched successfully.");
			} else {
				
				throw new Error("Failed to fetch item preview");
			}
		} catch (error) {
			setLoadingStep(`Error: ${error.message}`);
			
		} finally {
			setIsFetching(false);
			setShowStillWorking(false); // Ensure "still working" message is hidden after fetch
		}
	};

	const checkForDuplicateLink = async (affiliateUrl) => {
		// Assuming you're using Firestore as indicated in previous snippets
		const querySnapshot = await getDocs(query(collection(db, "catalogueItems"), where("url", "==", affiliateUrl)));
		return !querySnapshot.empty; // Returns true if a duplicate exists, false otherwise
	};

	const sanitizeAffiliateUrl = (url) => {
		// Trim leading and trailing spaces
		let cleanedUrl = url.trim();

		// Further cleaning can go here, e.g., removing or replacing specific characters
		// For example, to remove all spaces (not just leading/trailing), you could use:
		// cleanedUrl = cleanedUrl.replace(/\s+/g, '');

		return cleanedUrl;
	};

	const submitItemToDatabase = async () => {
		if (!category) {
			alert("Please select a category.");
			setCategoryError(true);
			return;
		}

		if (!selectedSubCategory) {
			alert("Please select a subcategory.");
			setCategoryError(true);
			return;
		}

		// Determine if we're using fetched data or manual entry
		const itemData = manualEntry
			? {
				name: itemName,
				description: itemDescription,
				price: itemPrice,
				image: itemImage,
				url: affiliateUrl,
				category: category,
				subCategory: selectedSubCategory,
			}
			: {
				name: itemPreview.title,
				description: itemPreview.description,
				price: itemPreview.price,
				image: itemPreview.imageUrl,
				url: affiliateUrl,
				category: category,
				subCategory: selectedSubCategory,
			};

		try {
			await addDoc(collection(db, "catalogueItems"), itemData);

			setCatalogueItems([...catalogueItems, itemData]);
			setLoadingStep("Item added successfully to the catalogue.");

			// Reset the form fields
			setAffiliateUrl("");
			setItemPreview(null);
			setCategory("");
			setSelectedSubCategory("");
			setCategoryError(false);

			fetchCatalogueItems();
		} catch (error) {
			
			setLoadingStep("Failed to add item to catalogue.");
		}
	};

	const [manualEntry, setManualEntry] = useState(false); // New state to toggle manual entry

	// Toggle function
	const toggleManualEntry = () => {
		setManualEntry(!manualEntry);
		// Reset item preview and manual entry fields
		setItemPreview(null);
		setItemName("");
		setItemDescription("");
		setItemPrice("");
		setItemImage("");
	};

	// Function to delete an item
	const deleteItem = async (itemId) => {
		await deleteDoc(doc(db, "catalogueItems", itemId));
		fetchCatalogueItems(); // Refetch items after deletion
	};

	const handlePriceChange = (event) => {
		setSelectedPrice(event.target.value);
	};

	const handleIndividualPriceChange = (productId, newPrice) => {
		setPriceUpdates(prev => ({ ...prev, [productId]: newPrice }));
	};

	// Function to handle product selection
	const handleSelectProduct = (productId) => {
		setSelectedProducts(prevSelectedProducts =>
			prevSelectedProducts.includes(productId)
				? prevSelectedProducts.filter(id => id !== productId)
				: [...prevSelectedProducts, productId]
		);
	};

	const [showUpdatePopup, setShowUpdatePopup] = useState(false);
	const [estimatedTime, setEstimatedTime] = useState(0);

	const updateProducts = async () => {
		if (selectedProducts.length === 0) {
			alert('Please select at least one product.');
			return;
		}

		const updates = {};
		if (selectedCategory) updates.category = selectedCategory;
		if (bulkPrice) updates.price = bulkPrice;
		if (bulkDescription) updates.description = bulkDescription;

		// Estimate time based on the number of selected products
		const estimatedTimeInSeconds = selectedProducts.length * 2; // Example: 2 seconds per product
		setEstimatedTime(estimatedTimeInSeconds);
		setShowUpdatePopup(true);

		try {
			for (const productId of selectedProducts) {
				const productRef = doc(db, "catalogueItems", productId);
				await updateDoc(productRef, updates);
			}

			alert('Products updated successfully!');
			fetchCatalogueItems(); // Refetch products to get updated data
			setSelectedProducts([]); // Clear selections
			setSelectedCategory('');
			setBulkPrice('');
			setBulkDescription('');
		} catch (error) {
			
			alert('Error updating products');
		} finally {
			setShowUpdatePopup(false);
		}
	};

	// Add this function inside your component
	const toggleSelectAllProducts = () => {
		if (selectedProducts.length === catalogueItems.filter(item => item.category === filter).length) {
			setSelectedProducts([]);
		} else {
			setSelectedProducts(catalogueItems.filter(item => item.category === filter).map(item => item.id));
		}
	};

	// To make category dropdown red if there's an error
	const categorySelectStyle = categoryError ? { ...styles.input, borderColor: "red" } : styles.input;
	//     https://amzn.to/3UCGBby

	const handleEmailParamChange = (param, value) => {
		setEmailParams((prevParams) => ({
			...prevParams,
			[param]: value,
		}));
	};

	// Canonical test data with realistic values for all template variables
	const buildTestData = (overrides = {}) => ({
		firstName: 'Trevor',
		lastName: 'Hawkins',
		graduateName: 'Trevor Hawkins',
		graduateFirstName: 'Trevor',
		graduateFullName: 'Trevor Hawkins',
		recipientName: 'Trevor Hawkins',
		contributorName: 'Jane Doe',
		senderName: 'Jane Doe',
		senderEmail: 'jane@example.com',
		amount: '50.00',
		contributionAmount: '50.00',
		itemName: 'Laptop Stand',
		itemsCount: 3,
		itemPrice: '89.99',
		itemUrl: 'https://amazon.com/test-item',
		cardImageUrl: 'https://via.placeholder.com/400x600?text=eCard',
		message: 'Congratulations on your graduation! So proud of you!',
		goalAmount: '1000',
		totalAmount: '1050',
		contributorCount: 8,
		yearbookUrl: 'https://mytassel.com/yearbook/test',
		requesterEmail: 'friend@example.com',
		requesterName: 'Sarah Friend',
		inquiryType: 'General Question',
		email: recipientEmail || 'trevorhawkins@dsignable.com',
		eventName: 'Graduation Ceremony 2026',
		eventDate: 'May 15, 2026',
		eventLocation: 'Main Auditorium',
		pageViews: '42',
		milestone: '100',
		viewCount: 100,
		n: '100',
		widgetCount: 2,
		name: 'Trevor Hawkins',
		accountType: 'Graduate',
		smsOptIn: 'Yes',
		signupDate: new Date().toLocaleDateString(),
		rsvpName: 'Test RSVP Person',
		goingCount: 5,
		maybeCount: 2,
		missingItems: ['Registry not set up', 'No contribution goal'],
		shareUrl: 'https://mytassel.com/share/test',
		pageUrl: 'https://mytassel.com/share/test',
		pageLink: '<a href="https://mytassel.com/share/test">View Graduation Page</a>',
		registryLink: '<a href="https://mytassel.com/registry/test">View Registry Page</a>',
		subject: 'Test Contact',
		phone: '555-555-5555',
		...overrides,
	});

	// Map Admin emailDefinition IDs → route + payload format
	const TRANSACTIONAL_EMAIL_MAP = {
		genericWelcome:       { endpoint: '/test-email', type: 'generic-welcome' },
		graduateWelcome:      { endpoint: '/test-email', type: 'graduate-welcome' },
		itemPurchased:        { endpoint: '/test-email', type: 'purchase-confirmation' },
		yearbookSigned:       { endpoint: '/test-email', type: 'yearbook' },
		ecardSent:            { endpoint: '/test-email', type: 'ecard' },
		goalReached:          { endpoint: '/test-email', type: 'goal-reached' },
	};
	const ENGAGEMENT_TYPES = new Set([
		'profileCompletion1','profileCompletion2','profileCompletion3',
		'registryNudge','goalNudge','itemsNudge','widgetsNudge',
		'reengagement14Days','reengagement30Days',
		'firstContribution','firstRsvp','pageViewsMilestone','gradPageUpdated',
	]);

	const sendOneTestEmail = async (defId, toEmail) => {
		const testData = buildTestData({ ...emailParams });
		const trans = TRANSACTIONAL_EMAIL_MAP[defId];
		if (trans) {
			const res = await fetch(`${SERVER_URL}${trans.endpoint}`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ emailType: trans.type, recipientEmail: toEmail, testData }),
			});
			if (!res.ok) { const e = await res.json(); throw new Error(e.error || e.message || `HTTP ${res.status}`); }
			return await res.json();
		} else if (ENGAGEMENT_TYPES.has(defId)) {
			const res = await fetch(`${SERVER_URL}/send-engagement-email`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ emailType: defId, recipientEmail: toEmail, data: testData, skipDuplicateCheck: true }),
			});
			if (!res.ok) { const e = await res.json(); throw new Error(e.error || e.message || `HTTP ${res.status}`); }
			return await res.json();
		} else {
			throw new Error(`No test route mapped for email type: ${defId}`);
		}
	};

	// Send test email using the correct endpoint based on email type
	const handleTestEmailSubmit = async (event) => {
		event.preventDefault();
		const toEmail = recipientEmail || 'trevorhawkins@dsignable.com';
		setEmailTestResult('Sending...');
		try {
			await sendOneTestEmail(emailType, toEmail);
			setEmailTestResult(`✅ Sent "${emailDefinitions[emailType]?.name || emailType}" to ${toEmail} at ${new Date().toLocaleTimeString()}`);
		} catch (error) {
			setEmailTestResult(`❌ Failed: ${error.message}`);
		}
	};

	// Batch-test every email type in sequence with a 2s gap
	const handleBatchTestAllEmails = async () => {
		const toEmail = recipientEmail || 'trevorhawkins@dsignable.com';
		const allTypes = Object.keys(emailDefinitions).filter(id =>
			TRANSACTIONAL_EMAIL_MAP[id] || ENGAGEMENT_TYPES.has(id)
		);
		setBatchTestProgress(allTypes.map(id => ({ id, name: emailDefinitions[id]?.name || id, status: 'pending' })));
		setBatchTestRunning(true);

		for (let i = 0; i < allTypes.length; i++) {
			const id = allTypes[i];
			setBatchTestProgress(prev => prev.map(p => p.id === id ? { ...p, status: 'sending' } : p));
			try {
				await sendOneTestEmail(id, toEmail);
				setBatchTestProgress(prev => prev.map(p => p.id === id ? { ...p, status: 'success' } : p));
			} catch (err) {
				setBatchTestProgress(prev => prev.map(p => p.id === id ? { ...p, status: 'error', error: err.message } : p));
			}
			if (i < allTypes.length - 1) await new Promise(r => setTimeout(r, 2000));
		}
		setBatchTestRunning(false);
	};

	// Add a styled result display component
	const EmailTestResult = ({ result }) => {
		if (!result) return null;
		
		const isError = result.includes("Failed");
		
		return (
			<View style={[
				styles.testResultContainer,
				isError ? styles.errorResult : styles.successResult
			]}>
				<Text style={[
					styles.testResultText,
					isError ? styles.errorText : styles.successText
				]}>
					{result.split('\n').map((line, i) => (
						<Text key={i}>{line}{'\n'}</Text>
					))}
				</Text>
			</View>
		);
	};

	const scanForUidMismatches = async () => {
		try {
		const usersRef = collection(db, "users");
		const querySnapshot = await getDocs(usersRef);
		const mismatchedUsers = [];

		querySnapshot.forEach((doc) => {
				try {
			const userData = doc.data();
					const uid = userData?.uid;
					const eventsUrl = userData?.pages?.events;
					const registryUrl = userData?.pages?.registry;

					// Only process UIDs that exist and start with "G"
					if (uid && typeof uid === 'string' && uid.startsWith("G")) {
				if (
					(eventsUrl && !eventsUrl.includes(uid)) ||
					(registryUrl && !registryUrl.includes(uid))
				) {
					mismatchedUsers.push({
								firstName: userData.firstName || 'Unknown',
								lastName: userData.lastName || 'Unknown',
								email: userData.email || 'No Email',
						uid: uid,
						eventsUrl: eventsUrl || "N/A",
						registryUrl: registryUrl || "N/A",
					});
				}
					}
				} catch (err) {
					
			}
		});

		if (mismatchedUsers.length > 0) {
			await alertMismatchedUsers(mismatchedUsers);
			setMismatchedUsers(mismatchedUsers); // Update state to display on the admin page
			}
		} catch (error) {
			
		}
	};

	const alertMismatchedUsers = async (mismatchedUsers) => {
		try {
			const response = await fetch(`${SERVER_URL}/alert-mismatched-users`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({ mismatchedUsers }),
			});

			if (!response.ok) {
				throw new Error("Failed to send alert email");
			}
		} catch (error) {
			
		}
	};

	const toggleCatalogueVisibility = async (field) => {
		try {
			const docRef = doc(db, "maintenance", "catalogueVisibility");
			const docSnap = await getDoc(docRef);

			if (docSnap.exists()) {
				const currentData = docSnap.data();
				const newValue = !currentData[field];
				await updateDoc(docRef, {
					[field]: newValue
				});
				setVisibilitySettings((prevSettings) => ({
					...prevSettings,
					[field]: newValue
				}));
			} else {
				// If the document doesn't exist, create it with default values
				await setDoc(docRef, {
					hidePrice: false,
					hideImages: false,
					hideAll: false
				});
				fetchVisibilitySettings(); // Fetch again to update state
			}
		} catch (error) {
			
		}
	};

	const [visibilitySettings, setVisibilitySettings] = useState({
		hidePrice: false,
		hideImages: false,
		hideAll: false
	});

	const fetchVisibilitySettings = async () => {
		try {
			const docRef = doc(db, "maintenance", "catalogueVisibility");
			const docSnap = await getDoc(docRef);

			if (docSnap.exists()) {
				setVisibilitySettings(docSnap.data());
			}
		} catch (error) {
			
		}
	};

	useEffect(() => {
		fetchVisibilitySettings();
	}, []);

	// Button styles based on visibility settings
	const getButtonStyle = (field) => {
		return visibilitySettings[field] ? styles.buttonActive : styles.buttonInactive;
	};

	const handleBlogSubmit = async (e) => {
		if (e) e.preventDefault();
		
		// Validation: Check if title and content are provided
		if (!blogTitle || blogTitle.trim() === "") {
			alert("Please enter a blog title before publishing.");
			return;
		}
		
		if (!blogContent || blogContent.trim() === "") {
			alert("Please enter blog content before publishing.");
			return;
		}
		
		try {
			const blogData = {
				title: blogTitle,
				content: blogContent,
				categories: selectedCategories, // Changed from single category to array
				imageUrl: blogImage,
				tags: blogTags,
				author: currentUserData?.email || currentUser?.email,
				createdAt: publishDate || new Date().toISOString(),
				updatedAt: lastUpdatedDate || new Date().toISOString()
			};

			

			if (editingBlogId) {
				await updateDoc(doc(db, "blog", editingBlogId), blogData);
			} else {
				await addDoc(collection(db, "blog"), blogData);
			}

			resetForm();
			fetchBlogPosts();
		} catch (error) {
			
			alert("Error saving blog post. Please try again.");
		}
	};

	const resetForm = () => {
		setBlogTitle("");
		setBlogContent("");
		setBlogCategory("");
		setBlogImage("");
		setBlogTags([]);
		setPublishDate("");
		setLastUpdatedDate("");
		setEditingBlogId(null);
	};

	const fetchBlogPosts = async () => {
		const querySnapshot = await getDocs(collection(db, "blog"));
		const posts = querySnapshot.docs.map(doc => ({
			id: doc.id,
			...doc.data()
		}));
		setBlogPosts(posts);
	};

	useEffect(() => {
		fetchBlogPosts();
	}, []);

	const checkMismatchedUsers = async () => {
		const usersRef = collection(db, "users");
		const snapshot = await getDocs(usersRef);
		const mismatched = [];

		snapshot.forEach(doc => {
			const userData = doc.data();
			if (userData.email && userData.uid && userData.email.toLowerCase() !== doc.id.toLowerCase()) {
				mismatched.push({ ...userData, id: doc.id });
			}
		});

		setMismatchedUsers(mismatched);
	};

	// Registry Audit handlers
	const runRegistryAudit = async () => {
		setRegistryAuditLoading(true);
		setRegistryAuditError(null);
		try {
			const response = await fetch(`${SERVER_URL}/registry-audit`, {
				method: 'GET',
				headers: { 'Content-Type': 'application/json' }
			});
			const data = await response.json();
			if (!data.success) throw new Error(data.error || 'Scan failed');
			setRegistryAuditResults(data);
		} catch (err) {
			setRegistryAuditError(err.message);
		} finally {
			setRegistryAuditLoading(false);
		}
	};

	const loadRegistryAuditHistory = async () => {
		setRegistryAuditHistoryLoading(true);
		try {
			const response = await fetch(`${SERVER_URL}/registry-audit/history`);
			const data = await response.json();
			if (data.success) setRegistryAuditHistory(data.runs || []);
		} catch (err) {
			// non-critical — history is best-effort
		} finally {
			setRegistryAuditHistoryLoading(false);
		}
	};

	const clearRegistryAuditHistory = async () => {
		if (!window.confirm('Clear all run history? This cannot be undone.')) return;
		setRegistryAuditHistoryClearLoading(true);
		try {
			const response = await fetch(`${SERVER_URL}/registry-audit/history`, { method: 'DELETE' });
			const data = await response.json();
			if (!data.success) throw new Error(data.error || 'Clear failed');
			setRegistryAuditHistory([]);
		} catch (err) {
			alert(`Failed to clear history: ${err.message}`);
		} finally {
			setRegistryAuditHistoryClearLoading(false);
		}
	};

	const fixRegistryUser = async (userEmail) => {
		const MAX_RETRIES = 5;
		setRegistryAuditFixing(prev => ({ ...prev, [userEmail]: { active: true, attempt: 1 } }));
		try {
			let lastErrors = Infinity;
			for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
				setRegistryAuditFixing(prev => ({ ...prev, [userEmail]: { active: true, attempt } }));
				const response = await fetch(`${SERVER_URL}/registry-audit/fix`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ userEmail })
				});
				const data = await response.json();
				if (!data.success) throw new Error(data.error || 'Fix failed');
				// No remaining errors — done
				if (!data.errors || data.errors === 0) break;
				// No progress since last attempt — stop retrying to avoid infinite loops
				if (data.errors >= lastErrors) break;
				lastErrors = data.errors;
			}
			await runRegistryAudit();
			await loadRegistryAuditHistory();
		} catch (err) {
			alert(`Failed to fix ${userEmail}: ${err.message}`);
		} finally {
			setRegistryAuditFixing(prev => ({ ...prev, [userEmail]: { active: false, attempt: 0 } }));
		}
	};

	const fixAllRegistryIssues = async () => {
		if (!registryAuditResults?.report?.length) return;
		const confirm = window.confirm(
			`This will re-fetch Amazon product data for items with missing metadata across ${registryAuditResults.report.length} user(s). Proceed?`
		);
		if (!confirm) return;
		setRegistryAuditFixAllLoading(true);
		try {
			const response = await fetch(`${SERVER_URL}/registry-audit/fix`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ fixAll: true })
			});
			const data = await response.json();
			if (!data.success) throw new Error(data.error || 'Fix all failed');
			alert(`Done! Fixed ${data.totalFixed} items (${data.totalErrors} errors).`);
			await runRegistryAudit();
			await loadRegistryAuditHistory();
		} catch (err) {
			alert(`Fix all failed: ${err.message}`);
		} finally {
			setRegistryAuditFixAllLoading(false);
		}
	};

	const fixUserMismatch = async (docId) => {
		try {
			const userRef = doc(db, "users", docId);
			await updateDoc(userRef, {
				email: docId.toLowerCase()
			});
			await checkMismatchedUsers(); // Refresh the list
		} catch (error) {
			
		}
	};

	const sendTestEmail = async () => {
		try {
			// Implement your email sending logic here
			alert(`Test email would be sent to ${testEmail}`);
		} catch (error) {
			
		}
	};

	const fetchEmailActivity = async () => {
		setEmailActivityLoading(true);
		setEmailActivityError(null);
		try {
			const res = await fetch(`${SERVER_URL}/email-activity?limit=50`);
			const data = await res.json();
			if (data.success) {
				setEmailActivity(data.messages || []);
			} else if (data.permissionRequired) {
				setEmailActivityError(`Permission required: ${data.permissionRequired}`);
			} else {
				setEmailActivityError(data.error || 'Failed to fetch activity');
			}
		} catch (err) {
			setEmailActivityError(err.message);
		} finally {
			setEmailActivityLoading(false);
		}
	};

	const fetchSmsActivity = async () => {
		setSmsActivityLoading(true);
		setSmsActivityError(null);
		try {
			const res = await fetch(`${SERVER_URL}/sms-activity?limit=50`);
			const data = await res.json();
			if (data.success) {
				setSmsActivity(data.messages || []);
			} else {
				setSmsActivityError(data.error || 'Failed to fetch SMS activity');
			}
		} catch (err) {
			setSmsActivityError(err.message);
		} finally {
			setSmsActivityLoading(false);
		}
	};

	const fetchEmailSchedule = async () => {
		setEmailScheduleLoading(true);
		setEmailScheduleError(null);
		try {
			const res = await fetch(`${SERVER_URL}/email-schedule`);
			const data = await res.json();
			if (data.success) {
				setEmailSchedule(data);
			} else {
				setEmailScheduleError(data.error || 'Failed to fetch schedule');
			}
		} catch (err) {
			setEmailScheduleError(err.message);
		} finally {
			setEmailScheduleLoading(false);
		}
	};

	const handleSuppressEmail = async (userEmail, emailType, trackingKey, suppress) => {
		try {
			const res = await fetch(`${SERVER_URL}/suppress-scheduled-email`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ userEmail, emailType, trackingKey, suppress }),
			});
			const data = await res.json();
			if (!data.success) {
				console.error('Failed to suppress email:', data.error);
				alert(`Failed to ${suppress ? 'suppress' : 'allow'} email: ${data.error || 'Unknown error'}`);
				return;
			}
			// Patch by trackingKey so only the exact entry is updated (prevents itemsNudge multi-entry bleed)
			const resolvedKey = trackingKey || emailType;
			setEmailSchedule(prev => {
				if (!prev) return prev;
				const patch = (arr) => arr.map(e =>
					e.userEmail === userEmail && (e.trackingKey || e.emailType) === resolvedKey
						? { ...e, suppressed: suppress }
						: e
				);
				return {
					...prev,
					pendingSends: patch(prev.pendingSends || []),
					upcomingSends: patch(prev.upcomingSends || []),
				};
			});
		} catch (err) {
			console.error('Failed to toggle suppress:', err);
			alert(`Failed to ${suppress ? 'suppress' : 'allow'} email: ${err.message}`);
		}
	};

	// Tick every second for live countdowns — only while Notifications tab is active
	useEffect(() => {
		if (activeTab !== 'notifications') return;
		const id = setInterval(() => setScheduleTick(t => t + 1), 1000);
		return () => clearInterval(id);
	}, [activeTab]);

	// Auto-refresh schedule every 5 minutes when tab is active
	// Also auto-scan for missing reminders on first load so all reminder types show immediately
	const hasAutoScanned = useRef(false);
	useEffect(() => {
		if (activeTab !== 'notifications') return;
		if (!emailSchedule && !emailScheduleLoading) {
			fetchEmailSchedule().then(() => {
				if (!hasAutoScanned.current) {
					hasAutoScanned.current = true;
					(async () => {
						try {
							const { SERVER_URL } = await import('../config.js');
							const res = await fetch(`${SERVER_URL}/scan-all-reminders`, { method: 'POST', headers: { 'Content-Type': 'application/json' } });
							const data = await res.json();
							if (data.scheduled > 0) fetchEmailSchedule();
						} catch (e) { console.warn('Auto-scan failed:', e.message); }
					})();
				}
			});
		}
		const id = setInterval(fetchEmailSchedule, 5 * 60 * 1000);
		return () => clearInterval(id);
	}, [activeTab]);

	// Auto-load email activity when notifications tab is opened
	useEffect(() => {
		if (activeTab !== 'notifications') return;
		if (!emailActivity.length && !emailActivityLoading) fetchEmailActivity();
		if (!smsActivity.length && !smsActivityLoading) fetchSmsActivity();
	}, [activeTab]);

	const fetchEmailMessageDetail = async (msgId) => {
		if (activityMsgDetails[msgId]) return;
		setActivityMsgDetailLoading(s => new Set([...s, msgId]));
		try {
			const res = await fetch(`${SERVER_URL}/email-message?msgId=${encodeURIComponent(msgId)}`);
			const data = await res.json();
			if (data.success) {
				setActivityMsgDetails(d => ({ ...d, [msgId]: data.message }));
			}
		} catch (_) {
			// fail silently — detail is best-effort
		} finally {
			setActivityMsgDetailLoading(s => { const ns = new Set(s); ns.delete(msgId); return ns; });
		}
	};

	const toggleMaintenanceMode = async (enabled) => {
		try {
			const configRef = doc(db, "config", "maintenance");
			await setDoc(configRef, { enabled }, { merge: true });
			setMaintenanceMode(enabled);
		} catch (error) {
			
		}
	};

	const toggleRegistration = async (disabled) => {
		try {
			const configRef = doc(db, "config", "registration");
			await setDoc(configRef, { disabled }, { merge: true });
			setDisableRegistration(disabled);
		} catch (error) {
			
		}
	};

	// Add this state near the other blog states
	const [showPreview, setShowPreview] = useState(false);

	// Add this function to format the date
	const formatDate = (dateString) => {
		return new Date(dateString).toLocaleDateString();
	};

	// Add this function to handle tag input
	const handleTagInput = (e) => {
		if (e.key === 'Enter' && e.target.value) {
			e.preventDefault();
			const newTag = e.target.value.trim();
			if (!blogTags.includes(newTag)) {
				setBlogTags([...blogTags, newTag]);
			}
			e.target.value = '';
		} else if (e.key === 'Backspace' && e.target.value === '' && blogTags.length > 0) {
			setBlogTags(blogTags.slice(0, -1));
		}
	};

	const handlePresetTagClick = (tag) => {
		if (!blogTags.includes(tag)) {
			setBlogTags([...blogTags, tag]);
		}
	};

	const getStripeSetupStatus = (user) => {
		const isComplete = Boolean(user.stripeAccountId && user.stripeSetupComplete);
		if (isComplete) {
			return { label: 'Setup Complete', color: '#4ade80', isComplete: true };
		}
		if (user.stripeAccountId) {
			return { label: 'Needs Verification', color: '#f59e0b', isComplete: false };
		}
		return { label: 'Pending Setup', color: '#fbbf24', isComplete: false };
	};

	const isSmsOptedIn = (user) => {
		const value = user?.smsOptIn;
		if (value === true) return true;
		if (typeof value === 'string') {
			return ['true', 'yes', 'opted in', 'opted_in'].includes(value.trim().toLowerCase());
		}
		return false;
	};

	const getSmsPhoneNumber = (user) => (
		user?.phone ||
		user?.phoneNumber ||
		user?.smsPhoneNumber ||
		user?.mobilePhone ||
		''
	);

	const formatSmsOptInDate = (timestamp) => {
		if (!timestamp) return '';
		const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
		if (Number.isNaN(date.getTime())) return '';
		return date.toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'short',
			day: 'numeric'
		});
	};

	// Add this function to get filtered and sorted users
	const getFilteredUsers = () => {
		return users
			.filter(user => {
				const searchLower = userSearchQuery.trim().toLowerCase();
				if (searchLower) {
					const firstName = user.firstName || '';
					const lastName = user.lastName || '';
					const fullName = `${firstName} ${lastName}`.trim().replace(/\s+/g, ' ').toLowerCase();
					const reverseFullName = `${lastName} ${firstName}`.trim().replace(/\s+/g, ' ').toLowerCase();
					if (!(
						user.email?.toLowerCase().includes(searchLower) ||
						firstName.toLowerCase().includes(searchLower) ||
						lastName.toLowerCase().includes(searchLower) ||
						fullName.includes(searchLower) ||
						reverseFullName.includes(searchLower)
					)) return false;
				}

				if (filterAccountType !== 'all') {
					if (filterAccountType === 'graduate' && user.accountType !== 'Graduate') return false;
					if (filterAccountType === 'friends' && user.accountType !== 'Friends & Family') return false;
					if (filterAccountType === 'admin' && user.accountType !== 'admin') return false;
					if (filterAccountType === 'tester' && !user.isTester) return false;
				}

				if (filterStripe !== 'all') {
					const stripeStatus = getStripeSetupStatus(user);
					if (filterStripe === 'connected' && !stripeStatus.isComplete) return false;
					if (filterStripe === 'pending' && stripeStatus.isComplete) return false;
				}

				if (filterGoal !== 'all') {
					const goalAmount = user.pages?.registryPageConfig?.goalTracker?.targetAmount;
					const hasGoal = goalAmount !== undefined && goalAmount !== null && Number(goalAmount) > 0;
					if (filterGoal === 'set' && !hasGoal) return false;
					if (filterGoal === 'not_set' && hasGoal) return false;
				}

			if (filterPrivacy !== 'all') {
				if (filterPrivacy === 'public' && user.isFriendsListPrivate) return false;
				if (filterPrivacy === 'private' && !user.isFriendsListPrivate) return false;
			}

			if (filterActivity !== 'all') {
				const now = Date.now();
				const lastSeen = user.lastSignInTime ? new Date(user.lastSignInTime).getTime() : 0;
				const diff = now - lastSeen;
				const day = 86400000;
				if (filterActivity === 'active_7d' && diff > 7 * day) return false;
				if (filterActivity === 'active_30d' && diff > 30 * day) return false;
				if (filterActivity === 'inactive_30d' && diff <= 30 * day) return false;
			}

			if (filterSmsOptIn !== 'all') {
				const optedIn = isSmsOptedIn(user);
				if (filterSmsOptIn === 'opted_in' && !optedIn) return false;
				if (filterSmsOptIn === 'not_opted_in' && optedIn) return false;
			}

			return true;
		})
		.sort((a, b) => {
			switch (userSort) {
				case 'newest': {
					const dateA = a.creationTime ? new Date(a.creationTime) : new Date(0);
					const dateB = b.creationTime ? new Date(b.creationTime) : new Date(0);
					return dateB - dateA;
				}
				case 'oldest': {
					const dateA2 = a.creationTime ? new Date(a.creationTime) : new Date();
					const dateB2 = b.creationTime ? new Date(b.creationTime) : new Date();
					return dateA2 - dateB2;
				}
				case 'alphabetical':
					return `${a.firstName || ''} ${a.lastName || ''}`.localeCompare(`${b.firstName || ''} ${b.lastName || ''}`);
				case 'most_active': {
					const sa = a.lastSignInTime ? new Date(a.lastSignInTime).getTime() : 0;
					const sb = b.lastSignInTime ? new Date(b.lastSignInTime).getTime() : 0;
					return sb - sa;
				}
				case 'least_active': {
					const sa2 = a.lastSignInTime ? new Date(a.lastSignInTime).getTime() : 0;
					const sb2 = b.lastSignInTime ? new Date(b.lastSignInTime).getTime() : 0;
					return sa2 - sb2;
				}
				case 'goal_high': {
					const ga = Number(a.pages?.registryPageConfig?.goalTracker?.targetAmount) || 0;
					const gb = Number(b.pages?.registryPageConfig?.goalTracker?.targetAmount) || 0;
					return gb - ga;
				}
				case 'goal_low': {
					const ga2 = Number(a.pages?.registryPageConfig?.goalTracker?.targetAmount) || 0;
					const gb2 = Number(b.pages?.registryPageConfig?.goalTracker?.targetAmount) || 0;
					return ga2 - gb2;
				}
				default:
					return 0;
			}
		});
};

	// Add debug logging when auth users are fetched
	useEffect(() => {
		if (activeTab === 'users') {
			fetchAllUsers();
			fetchShareAnalytics();
			// Add debug log to see what auth users look like
			
		}
	}, [activeTab]);

	const handleToggleTester = async (user) => {
		try {
			const userRef = doc(db, "users", user.email.toLowerCase());
			const newValue = !user.isTester;
			await updateDoc(userRef, { isTester: newValue });
			setUsers(prev => prev.map(u => u.id === user.id ? { ...u, isTester: newValue } : u));
		} catch (error) {
			alert("Failed to update tester flag. Please try again.");
		}
	};

	const handleTutorialUserUpdated = (userId, fields) => {
		setUsers(prev => prev.map(u => u.id === userId ? { ...u, ...fields } : u));
		setTutorialAdminUser(prev => prev && prev.id === userId ? { ...prev, ...fields } : prev);
	};

	// Add function to handle Stripe reset
	const handleStripeReset = async (user) => {
		setUserToResetStripe(user);
		setShowStripeResetConfirm(true);
	};

	const confirmStripeReset = async () => {
		if (adminPassword !== "Newtjhawk27") {
			alert("Invalid admin password");
			return;
		}

		try {
			setUserActionLoading(true);
			// Use email as the document ID instead of uid
			const userRef = doc(db, "users", userToResetStripe.email);
			
			// First verify the document exists
			const userDoc = await getDoc(userRef);
			if (!userDoc.exists()) {
				throw new Error("User document not found");
			}

			// Update the document with reset Stripe fields
			await updateDoc(userRef, {
				stripeAccountId: "", // This will remove the field
				stripeSetupComplete: false
			});

			// Also make API call to disconnect from Stripe if needed
			try {
				await axios.post(`${SERVER_URL}/disconnect-stripe-account`, {
					userId: userToResetStripe.uid,
					stripeAccountId: userToResetStripe.stripeAccountId
				});
			} catch (stripeError) {
				
				// Continue anyway as we've already reset the local data
			}

			alert("Stripe configuration reset successfully");
			
			// Refresh the users list
			fetchAllUsers();
			
		} catch (error) {
			
			alert(`Error resetting Stripe configuration: ${error.message}`);
		} finally {
			setUserActionLoading(false);
			setShowStripeResetConfirm(false);
			setAdminPassword("");
			setUserToResetStripe(null);
		}
	};

	// Initialize analytics when the component mounts
	useEffect(() => {
		if (showAdmin) {
			const initAnalytics = async () => {
				try {
					await analyticsManager.init();
					
				} catch (error) {
					
				}
			};
			
			initAnalytics();
		}
		
		// Cleanup on unmount
		return () => {
			if (showAdmin) {
				try {
					analyticsManager.cleanup();
					
				} catch (error) {
					
				}
			}
		};
	}, [showAdmin]);

	// Add this function to fetch share analytics
	const fetchShareAnalytics = async () => {
		try {
			const shareAnalyticsRef = collection(db, "shareAnalytics");
			const shareAnalyticsSnapshot = await getDocs(shareAnalyticsRef);
			const analytics = {};
			
			shareAnalyticsSnapshot.forEach(doc => {
				const data = doc.data();
				if (data && data.userEmail) {
					// Use email as the key instead of userId
					analytics[data.userEmail.toLowerCase()] = {
						totalShares: data.totalShares || 0,
						emailShares: Array.isArray(data.emailShares) ? data.emailShares : [],
						smsShares: Array.isArray(data.smsShares) ? data.smsShares : [],
						lastShareDate: data.lastShareDate || null,
						userId: data.userId,
						firstName: data.firstName,
						lastName: data.lastName,
						userEmail: data.userEmail.toLowerCase()
					};
				}
			});
			
			
			setShareAnalytics(analytics);
		} catch (error) {
			
		}
	};

	// Add function to handle category selection
	const handleCategoryToggle = (category) => {
		setSelectedCategories(prev => {
			if (prev.includes(category)) {
				return prev.filter(c => c !== category);
			} else {
				return [...prev, category];
			}
		});
	};

	if (loading) {
		return <div style={styles.loading}>Loading...</div>;
	}

	if (error) {
	return (
			<View style={styles.errorContainer}>
				<Text style={styles.errorTitle}>Something went wrong</Text>
				<Text style={styles.errorText}>{error}</Text>
				<Pressable 
					style={styles.errorButton}
					onPress={() => window.location.reload()}
				>
					<Text style={styles.errorButtonText}>Reload Page</Text>
				</Pressable>
			</View>
		);
	}

	// Simplified render method
	return (
		<View style={[styles.container, { backgroundColor: theme.bg }]}>
			{!currentUserData || !hasAdminAccess(currentUserData) ? (
				<View style={[styles.loginContainer, { backgroundColor: theme.bgCard }]}>
					<Text style={[styles.loginText, { color: theme.textPrimary }]}>You must be an admin to access this page.</Text>
				</View>
									) : (
										<>
					{/* Main Layout with Sidebar */}
					<View style={{ flexDirection: 'row', flex: 1, height: '100vh', minHeight: '100vh', overflow: 'hidden' }}>
						{/* Sidebar */}
						<View style={{
							width: sidebarOpen ? 260 : 60,
							backgroundColor: isDarkMode ? '#0f172a' : '#1e293b',
							borderRightWidth: 1,
							borderRightColor: isDarkMode ? '#1e293b' : '#334155',
							transition: 'width 0.2s ease',
							overflow: 'hidden',
							flexShrink: 0
						}}>
							{/* Sidebar Header */}
							<View style={{
								padding: sidebarOpen ? 20 : 12,
								borderBottomWidth: 1,
								borderBottomColor: isDarkMode ? '#1e293b' : '#334155',
								flexDirection: 'row',
								alignItems: 'center',
								justifyContent: sidebarOpen ? 'space-between' : 'center'
							}}>
								{sidebarOpen && (
									<Text style={{ color: '#ffffff', fontSize: 18, fontWeight: '700' }}>
										Admin Panel
									</Text>
								)}
								<Pressable
									onPress={() => setSidebarOpen(!sidebarOpen)}
									style={{
										padding: 8,
										borderRadius: 6,
										backgroundColor: 'rgba(255,255,255,0.1)'
									}}
								>
									<Ionicons 
										name={sidebarOpen ? 'chevron-back' : 'menu'} 
										size={20} 
										color="#94a3b8" 
									/>
								</Pressable>
							</View>

							{/* Go to Hub Button */}
							<View style={{
								paddingHorizontal: sidebarOpen ? 16 : 0,
								paddingVertical: 10,
								justifyContent: sidebarOpen ? 'flex-start' : 'center'
							}}>
								<Pressable
									onPress={() => navigation.navigate("Hub")}
									style={{
										flexDirection: 'row',
										alignItems: 'center',
										paddingHorizontal: sidebarOpen ? 16 : 0,
										paddingVertical: 10,
										justifyContent: sidebarOpen ? 'flex-start' : 'center',
										backgroundColor: 'rgba(59, 130, 246, 0.15)',
										borderRadius: 6
									}}
								>
									<View style={{
										width: 32,
										height: 32,
										borderRadius: 6,
										backgroundColor: '#3b82f6',
										alignItems: 'center',
										justifyContent: 'center',
										marginRight: sidebarOpen ? 12 : 0
									}}>
										<Ionicons 
											name="home" 
											size={16} 
											color="#ffffff" 
										/>
									</View>
									{sidebarOpen && (
										<Text style={{ 
											color: '#ffffff', 
											fontSize: 12, 
											fontWeight: '600',
											textTransform: 'uppercase',
											letterSpacing: 0.5,
											flex: 1
										}}>
											Go to Hub
										</Text>
									)}
								</Pressable>
							</View>

							{/* Sidebar Navigation */}
							<ScrollView style={{ flex: 1, paddingVertical: 8 }}>
								{sidebarCategories.map((category) => {
									const isCategoryCollapsed = collapsedSidebarCategories[category.id];
									const hasActiveItem = category.items.some(item => item.id === activeTab);
									
									return (
										<View key={category.id} style={{ marginBottom: 4 }}>
											{/* Category Header */}
											<Pressable
												onPress={() => sidebarOpen && toggleSidebarCategory(category.id)}
												style={{
													flexDirection: 'row',
													alignItems: 'center',
													paddingHorizontal: sidebarOpen ? 16 : 0,
													paddingVertical: 10,
													justifyContent: sidebarOpen ? 'flex-start' : 'center',
													backgroundColor: hasActiveItem ? 'rgba(76, 149, 108, 0.15)' : 'transparent'
												}}
											>
												<View style={{
													width: 32,
													height: 32,
													borderRadius: 6,
													backgroundColor: hasActiveItem ? '#4c956c' : 'rgba(255,255,255,0.08)',
													alignItems: 'center',
													justifyContent: 'center',
													marginRight: sidebarOpen ? 12 : 0
												}}>
													<Ionicons 
														name={category.icon} 
														size={16} 
														color={hasActiveItem ? '#ffffff' : '#94a3b8'} 
													/>
												</View>
												{sidebarOpen && (
													<>
														<Text style={{ 
															color: hasActiveItem ? '#ffffff' : '#94a3b8', 
															fontSize: 12, 
															fontWeight: '600',
															textTransform: 'uppercase',
															letterSpacing: 0.5,
															flex: 1
														}}>
															{category.label}
														</Text>
														<Ionicons 
															name={isCategoryCollapsed ? 'chevron-forward' : 'chevron-down'} 
															size={14} 
															color="#64748b" 
														/>
													</>
												)}
											</Pressable>
											
											{/* Category Items */}
											{sidebarOpen && !isCategoryCollapsed && (
												<View style={{ paddingLeft: 12 }}>
													{category.items.map((item) => {
														const isActive = activeTab === item.id;
														return (
															<Pressable
																key={item.id}
																onPress={() => setActiveTab(item.id)}
																style={{
																	flexDirection: 'row',
																	alignItems: 'center',
																	paddingHorizontal: 16,
																	paddingVertical: 10,
																	marginVertical: 1,
																	borderRadius: 6,
																	marginRight: 12,
																	backgroundColor: isActive ? '#4c956c' : 'transparent'
																}}
															>
																<Ionicons 
																	name={item.icon} 
																	size={16} 
																	color={isActive ? '#ffffff' : '#64748b'}
																	style={{ marginRight: 12 }}
																/>
																<Text style={{ 
																	color: isActive ? '#ffffff' : '#cbd5e1', 
																	fontSize: 14,
																	fontWeight: isActive ? '600' : '400'
																}}>
																	{item.label}
																</Text>
															</Pressable>
														);
													})}
												</View>
											)}
										</View>
									);
								})}
							</ScrollView>

							{/* Sidebar Footer */}
							<View style={{
								padding: sidebarOpen ? 16 : 12,
								borderTopWidth: 1,
								borderTopColor: isDarkMode ? '#1e293b' : '#334155'
							}}>
								<Pressable
									onPress={toggleDarkMode}
									style={{
										flexDirection: 'row',
										alignItems: 'center',
										justifyContent: sidebarOpen ? 'flex-start' : 'center',
										padding: 10,
										borderRadius: 6,
										backgroundColor: 'rgba(255,255,255,0.08)'
									}}
								>
									<Ionicons 
										name={isDarkMode ? 'sunny' : 'moon'} 
										size={18} 
										color={isDarkMode ? '#fbbf24' : '#94a3b8'} 
									/>
									{sidebarOpen && (
										<Text style={{ color: '#94a3b8', fontSize: 14, marginLeft: 12 }}>
											{isDarkMode ? 'Light Mode' : 'Dark Mode'}
										</Text>
									)}
								</Pressable>
							</View>
						</View>

						{/* Main Content Area */}
						<View style={{ flex: 1, minWidth: 0, height: '100vh', overflowY: 'auto', overflowX: 'hidden', WebkitOverflowScrolling: 'touch' }}>
							{/* Page Header */}
							<View style={{
								padding: 24,
								backgroundColor: isDarkMode ? '#0f172a' : '#f8fafc',
								borderBottomWidth: 1,
								borderBottomColor: theme.border,
								flexDirection: 'row',
								alignItems: 'center',
								justifyContent: 'space-between'
							}}>
								<View>
									<Text style={{ color: theme.textPrimary, fontSize: 24, fontWeight: '700' }}>
										{tabs.find(tab => tab.id === activeTab)?.label || 'Admin Dashboard'}
									</Text>
									<Text style={{ color: theme.textMuted, fontSize: 14, marginTop: 4 }}>
										{activeTab === 'users' && 'Manage user accounts and permissions'}
										{activeTab === 'organizations' && 'Review and manage organization requests'}
										{activeTab === 'catalogue' && 'Manage product catalogue items'}
										{activeTab === 'blog' && 'Create and manage blog posts'}
										{activeTab === 'analytics' && 'View platform analytics and insights'}
										{activeTab === 'registry-audit' && 'Scan and repair Amazon product metadata across all user registries'}
										{activeTab === 'features' && 'Toggle feature flags for staging and production'}
										{activeTab === 'ecard-studio' && 'Create and publish animated eCards with dynamic text overlays'}
										{activeTab === 'notifications' && 'Manage email templates, notifications, and testing'}
										{activeTab === 'maintenance' && 'System maintenance and diagnostics'}
										{activeTab === 'widgets-monitor' && 'Monitor, audit, and repair all widget types across user accounts'}
									</Text>
								</View>
							</View>

							{/* Content Wrapper */}
							<View style={[styles.contentWrapper, { backgroundColor: theme.bg, padding: 24 }]}>
						
						{activeTab === "organizations" && (
							<View style={styles.section}>
								{/* Pending Organizations Section */}
								<Text style={styles.subHeader}>Pending Organizations</Text>
								<View style={styles.tableWrapper}>
									<table style={styles.table}>
										<thead>
											<tr>
												<th style={{
													...styles.tableHeader, 
													width: isMobile ? '150px' : '20%'
												}}>Name</th>
												<th style={{...styles.tableHeader, width: '20%'}}>Address</th>
												<th style={{...styles.tableHeader, width: '25%'}}>Website</th>
												<th style={{...styles.tableHeader, width: '15%'}}>Type</th>
												<th style={{...styles.tableHeader, width: '20%'}}>Actions</th>
											</tr>
										</thead>
										<tbody>
											{organizations.map((org) => (
												<tr key={org.id} style={styles.tableRow}>
													<td>{org.name}</td>
													<td>{org.location}</td>
													<td>{org.website}</td>
													<td>{org.organizationType}</td>
													
													<td>
														<button onClick={() => handleApprove(org)} style={styles.buttonApprove}>
															Approve
														</button>
														<button onClick={() => handleDeny(org)} style={styles.buttonDeny}>
															Deny
														</button>
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</View>

								{/* Approved Organizations Section */}
								<Text style={styles.subHeader}>Approved Organizations</Text>
								<View style={styles.tableWrapper}>
									<table style={styles.table}>
										<thead>
											<tr>
												<th style={{
													...styles.tableHeader, 
													width: isMobile ? '150px' : '20%'
												}}>Name</th>
												<th style={{...styles.tableHeader, width: '15%'}}>Address</th>
												<th style={{...styles.tableHeader, width: '25%'}}>Website</th>
												<th style={{...styles.tableHeader, width: '15%'}}>Type</th>
												<th style={{...styles.tableHeader, width: '10%'}}>Code</th>
												<th style={{...styles.tableHeader, width: '15%'}}>Reevaluate</th>
											</tr>
										</thead>
										<tbody>
											{approvedOrgs.map((org) => (
												<tr key={org.id} style={styles.tableRow}>
													<td>{org.name}</td>
													<td>{org.location}</td>
													<td>{org.website}</td>
													<td>{org.organizationType}</td>
													<td>{org.code}</td>
													<td>
														<button onClick={() => handleReevaluate(org, setApprovedOrgs, approvedOrgs)} style={styles.buttonReevaluate}>
															Reevaluate
														</button>
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</View>
								<button onClick={uploadApproved} style={styles.buttonApprove}>
									Upload Approved
								</button>

								{/* Denied Organizations Section */}
								<Text style={styles.subHeader}>Denied Organizations</Text>
								<View style={styles.tableWrapper}>
									<table style={styles.table}>
										<thead>
											<tr>
												<th style={{
													...styles.tableHeader, 
													width: isMobile ? '150px' : '25%'
												}}>Name</th>
												<th style={{...styles.tableHeader, width: '20%'}}>Address</th>
												<th style={{...styles.tableHeader, width: '25%'}}>Website</th>
												<th style={{...styles.tableHeader, width: '15%'}}>Type</th>
												<th style={{...styles.tableHeader, width: '15%'}}>Reevaluate</th>
											</tr>
										</thead>
										<tbody>
											{deniedOrgs.map((org) => (
												<tr key={org.id} style={styles.tableRow}>
													<td>{org.name}</td>
													<td>{org.location}</td>
													<td>{org.website}</td>
													<td>{org.organizationType}</td>
													<td>
														<button onClick={() => handleReevaluate(org, setDeniedOrgs, deniedOrgs)} style={styles.buttonReevaluate}>
															Reevaluate
														</button>
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</View>
								<button onClick={purgeDenied} style={styles.buttonDeny}>
									Purge Denied
								</button>
							</View>
						)}

						{activeTab === "users" && (
							<View style={styles.section}>
								<View style={styles.sectionHeader}>
									<Text style={styles.subHeader}>User Management</Text>
								<View style={styles.userStats}>
									<View style={styles.statCard}>
										<Text style={styles.statNumber}>{users.length}</Text>
										<Text style={styles.statLabel}>Total Users</Text>
									</View>
									<View style={styles.statCard}>
										<Text style={styles.statNumber}>
											{users.filter(user => user.accountType === 'Graduate' && !user.isTester).length}
										</Text>
										<Text style={styles.statLabel}>Graduates</Text>
									</View>
									<View style={styles.statCard}>
										<Text style={styles.statNumber}>
											{users.filter(user => user.accountType === 'Graduate' && !user.isTester && isSmsOptedIn(user)).length}
										</Text>
										<Text style={styles.statLabel}>SMS Opt-In Graduates</Text>
									</View>
									<View style={styles.statCard}>
										<Text style={styles.statNumber}>
											{users.filter(user => user.accountType === 'Friends & Family' && !user.isTester).length}
										</Text>
										<Text style={styles.statLabel}>Friends & Family</Text>
									</View>
									<View style={styles.statCard}>
										<Text style={[styles.statNumber, { color: '#f59e0b' }]}>
											{users.filter(user => user.isTester).length}
										</Text>
										<Text style={styles.statLabel}>Testers</Text>
									</View>
									<View style={styles.statCard}>
										<Text style={styles.statNumber}>
											{users.filter(user => user.emailVerified).length}
										</Text>
										<Text style={styles.statLabel}>Verified</Text>
									</View>
									<View style={styles.statCard}>
										<Text style={styles.statNumber}>
											{users.filter(user => user.accountType === 'admin').length}
										</Text>
										<Text style={styles.statLabel}>Admins</Text>
									</View>
								</View>
								</View>

								<View style={styles.controlsContainer}>
									<View style={styles.searchContainer}>
										<TextInput
											placeholder="Search users..."
											value={userSearchQuery}
											onChangeText={setUserSearchQuery}
											style={styles.searchInput}
										/>
									</View>

						{/* Find & Fix: deep lookup across Auth + Firestore */}
						<View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 10, marginBottom: 20, gap: 8 }}>
							<TextInput
								placeholder="Find & Fix user by exact email..."
								value={findFixEmail}
								onChangeText={setFindFixEmail}
								style={[styles.searchInput, { flex: 1 }]}
								onSubmitEditing={handleFindAndFixUser}
							/>
							<button
								onClick={handleFindAndFixUser}
								disabled={findFixLoading || !findFixEmail.trim()}
								style={{ padding: '8px 14px', backgroundColor: '#f59e0b', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 'bold', whiteSpace: 'nowrap' }}
							>
								{findFixLoading ? 'Searching...' : 'Find & Fix'}
							</button>
						</View>

							<View style={styles.filterPanel}>
								{/* Filters section */}
								<View style={styles.filterPanelSection}>
									<Text style={styles.filterPanelSectionTitle}>FILTER</Text>
									<View style={styles.filterPanelGroups}>
										{[
											{
												label: 'Account',
												state: filterAccountType,
												setter: setFilterAccountType,
												opts: [
													{ label: 'All', value: 'all' },
													{ label: 'Graduates', value: 'graduate' },
													{ label: 'Friends & Family', value: 'friends' },
													{ label: 'Admins', value: 'admin' },
													{ label: 'Testers', value: 'tester' },
												],
											},
											{
												label: 'Stripe',
												state: filterStripe,
												setter: setFilterStripe,
												opts: [
													{ label: 'All', value: 'all' },
													{ label: 'Setup Complete', value: 'connected' },
													{ label: 'Not Complete', value: 'pending' },
												],
											},
											{
												label: 'Goal',
												state: filterGoal,
												setter: setFilterGoal,
												opts: [
													{ label: 'All', value: 'all' },
													{ label: 'Goal Set', value: 'set' },
													{ label: 'No Goal', value: 'not_set' },
												],
											},
										{
											label: 'Privacy',
											state: filterPrivacy,
											setter: setFilterPrivacy,
											opts: [
												{ label: 'All', value: 'all' },
												{ label: 'Public', value: 'public' },
												{ label: 'Private', value: 'private' },
											],
										},
										{
											label: 'Activity',
											state: filterActivity,
											setter: setFilterActivity,
											opts: [
												{ label: 'All', value: 'all' },
												{ label: 'Active 7d', value: 'active_7d' },
												{ label: 'Active 30d', value: 'active_30d' },
												{ label: 'Inactive 30d+', value: 'inactive_30d' },
											],
										},
										{
											label: 'SMS',
											state: filterSmsOptIn,
											setter: setFilterSmsOptIn,
											opts: [
												{ label: 'All', value: 'all' },
												{ label: 'Opted In', value: 'opted_in' },
												{ label: 'Not Opted In', value: 'not_opted_in' },
											],
										},
									].map(group => (
											<View key={group.label} style={styles.filterGroup}>
												<Text style={styles.filterGroupLabel}>{group.label}</Text>
												<View style={styles.filterSegment}>
													{group.opts.map((opt, i) => (
														<Pressable
															key={opt.value}
															onPress={() => group.setter(opt.value)}
															style={[
																styles.filterSegmentBtn,
																i === 0 && styles.filterSegmentBtnFirst,
																i === group.opts.length - 1 && styles.filterSegmentBtnLast,
																group.state === opt.value && styles.filterSegmentBtnActive,
															]}
														>
															<Text style={[styles.filterSegmentBtnText, group.state === opt.value && styles.filterSegmentBtnTextActive]}>
																{opt.label}
															</Text>
														</Pressable>
													))}
												</View>
											</View>
										))}
									</View>
								</View>

								{/* Divider */}
								<View style={styles.filterPanelDivider} />

								{/* Sort section */}
								<View style={styles.filterPanelSection}>
									<Text style={styles.filterPanelSectionTitle}>SORT</Text>
									<View style={styles.filterSegment}>
								{[
										{ label: 'Newest', value: 'newest' },
										{ label: 'Oldest', value: 'oldest' },
										{ label: 'Name A–Z', value: 'alphabetical' },
										{ label: 'Most Active', value: 'most_active' },
										{ label: 'Least Active', value: 'least_active' },
										{ label: 'Goal High', value: 'goal_high' },
										{ label: 'Goal Low', value: 'goal_low' },
									].map((opt, i, arr) => (
											<Pressable
												key={opt.value}
												onPress={() => setUserSort(opt.value)}
												style={[
													styles.filterSegmentBtn,
													i === 0 && styles.filterSegmentBtnFirst,
													i === arr.length - 1 && styles.filterSegmentBtnLast,
													userSort === opt.value && styles.filterSegmentBtnActive,
												]}
											>
												<Text style={[styles.filterSegmentBtnText, userSort === opt.value && styles.filterSegmentBtnTextActive]}>
													{opt.label}
												</Text>
											</Pressable>
										))}
									</View>
								</View>

								{/* Active filter count + clear */}
								{(filterAccountType !== 'all' || filterStripe !== 'all' || filterGoal !== 'all' || filterPrivacy !== 'all' || filterActivity !== 'all' || filterSmsOptIn !== 'all') && (
									<View style={styles.filterPanelFooter}>
										<Text style={styles.filterResultCount}>
											{getFilteredUsers().length} result{getFilteredUsers().length !== 1 ? 's' : ''}
										</Text>
										<Pressable
											onPress={() => { setFilterAccountType('all'); setFilterStripe('all'); setFilterGoal('all'); setFilterPrivacy('all'); setFilterActivity('all'); setFilterSmsOptIn('all'); }}
											style={styles.filterClearBtn}
										>
											<Text style={styles.filterClearBtnText}>✕ Clear filters</Text>
										</Pressable>
									</View>
								)}
							</View>
								</View>

								<View style={styles.tableContainer}>
									<table style={{ ...styles.table, minWidth: isMobile ? 1100 : 1280 }}>
										<thead>
											<tr>
												<th style={styles.tableHeader}>Email</th>
												<th style={styles.tableHeader}>Name</th>
												<th style={styles.tableHeader}>Dates</th>
												<th style={styles.tableHeader}>Account Type</th>
												<th style={styles.tableHeader}>SMS Opt-In</th>
												<th style={styles.tableHeader}>Stripe Status</th>
												<th style={styles.tableHeader}>Privacy Status</th>
												<th style={styles.tableHeader}>Share Analytics</th>
												<th style={styles.tableHeader}>Actions</th>
											</tr>
										</thead>
										<tbody>
											{getFilteredUsers().map(user => (
												<tr key={user.id} style={styles.tableRow}>
												<td style={styles.emailCell}>{user.email}</td>
								<td style={styles.tableCell}>
												<span>{`${user.firstName || ''} ${user.lastName || ''}`.trim() || '—'}</span>
											</td>
												<td style={{ ...styles.tableCell, display: 'flex', flexDirection: 'column', gap: 4 }}>
													<span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Joined</span>
													{user.creationTime ? (
														<span style={{ color: '#475569', fontSize: '13px', fontWeight: '500' }}>
															{new Date(user.creationTime).toLocaleDateString('en-US', {
																year: 'numeric',
																month: 'short',
																day: 'numeric'
															})}
														</span>
													) : (
														<span style={{ color: '#9ca3af', fontSize: '13px', fontStyle: 'italic' }}>N/A</span>
													)}
													<span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: 4 }}>Last Seen</span>
													{user.lastSignInTime ? (
														<span style={{ color: '#475569', fontSize: '13px', fontWeight: '500' }}>
															{new Date(user.lastSignInTime).toLocaleDateString('en-US', {
																year: 'numeric',
																month: 'short',
																day: 'numeric'
															})}
														</span>
													) : (
														<span style={{ color: '#9ca3af', fontSize: '13px', fontStyle: 'italic' }}>N/A</span>
													)}
												</td>
													<td style={styles.tableCell}>
														<span style={{
															color: user.accountType === 'admin' ? '#2c6e49' : '#64748b',
															fontWeight: 'bold'
														}}>
															{user.accountType || 'user'}
														</span>
														{formatReferralForDisplay(user) ? (
															<span style={{ display: 'block', fontSize: '12px', color: '#64748b', marginTop: 4 }}>
																Heard via: {formatReferralForDisplay(user)}
															</span>
														) : null}
														{user.spotlightTutorial?.status ? (
															<span style={{ display: 'block', fontSize: '12px', color: '#64748b' }}>
																Tutorial: {user.spotlightTutorial.status}
															</span>
														) : null}
													</td>
													<td style={styles.tableCell}>
														{(() => {
															const optedIn = isSmsOptedIn(user);
															const phoneNumber = getSmsPhoneNumber(user);
															const optInDate = formatSmsOptInDate(user.smsOptInTimestamp);

															return (
																<View style={styles.smsOptInCell}>
																	<View style={[
																		styles.smsOptInBadge,
																		optedIn ? styles.smsOptInBadgeActive : styles.smsOptInBadgeInactive
																	]}>
																		<Text style={[
																			styles.smsOptInBadgeText,
																			optedIn ? styles.smsOptInBadgeTextActive : styles.smsOptInBadgeTextInactive
																		]}>
																			{optedIn ? 'Opted in' : 'Not opted in'}
																		</Text>
																	</View>
																	{optedIn && (
																		<>
																			<Text style={styles.smsOptInDetail}>
																				{phoneNumber ? phoneNumber : 'Consent saved, no phone on file'}
																			</Text>
																			{optInDate && (
																				<Text style={styles.smsOptInMeta}>
																					{optInDate}
																				</Text>
																			)}
																		</>
																	)}
																</View>
															);
														})()}
													</td>
											<td style={{ ...styles.tableCell, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
												{(() => {
													const stripeStatus = getStripeSetupStatus(user);
													return (
														<span style={{
															color: stripeStatus.color,
															fontWeight: 'bold',
														}}>
															{stripeStatus.label}
														</span>
													);
												})()}
												{(() => {
													const tracker = user.pages?.registryPageConfig?.goalTracker;
													const target = Number(tracker?.targetAmount) || 0;
													const raised = Number(tracker?.amountContributed) || 0;
													const isDefault = tracker && target === 1 && !tracker.title && !tracker.description;
													const isCustomized = target > 0 && !isDefault;
													if (isCustomized) return (
														<span style={{
															display: 'flex',
															flexDirection: 'column',
															gap: 2,
															paddingTop: 5,
															paddingBottom: 5,
															paddingLeft: 8,
															paddingRight: 8,
															borderRadius: 4,
															backgroundColor: 'rgba(74,222,128,0.08)',
															border: '1px solid rgba(74,222,128,0.3)',
															fontSize: '12px',
															lineHeight: '1.5',
														}}>
															<span style={{ color: '#15803d', fontWeight: '600' }}>
																Target: ${target.toLocaleString()}
															</span>
															<span style={{ color: '#64748b', fontWeight: '500' }}>
																Raised: ${raised.toLocaleString()}
															</span>
														</span>
													);
													if (isDefault) return (
														<span style={{
															display: 'flex',
															flexDirection: 'column',
															gap: 2,
															paddingTop: 5,
															paddingBottom: 5,
															paddingLeft: 8,
															paddingRight: 8,
															borderRadius: 4,
															backgroundColor: 'rgba(234,179,8,0.08)',
															border: '1px solid rgba(234,179,8,0.3)',
															fontSize: '12px',
															lineHeight: '1.5',
														}}>
															<span style={{ color: '#a16207', fontWeight: '600' }}>
																Target: $1 (default)
															</span>
															<span style={{ color: '#64748b', fontWeight: '500' }}>
																Raised: ${raised.toLocaleString()}
															</span>
															<span style={{ color: '#94a3b8', fontWeight: '500', fontStyle: 'italic' }}>
																Not customized yet
															</span>
														</span>
													);
													return (
														<span style={{
															paddingTop: 3,
															paddingBottom: 3,
															paddingLeft: 8,
															paddingRight: 8,
															borderRadius: 4,
															backgroundColor: 'rgba(148,163,184,0.1)',
															border: '1px solid rgba(148,163,184,0.25)',
															fontSize: '12px',
															color: '#94a3b8',
															fontWeight: '500',
														}}>
															No goal set
														</span>
													);
												})()}
											</td>
													<td style={styles.tableCell}>
														<span style={{
															color: user.isFriendsListPrivate ? '#ef4444' : '#4ade80',
															fontWeight: 'bold'
														}}>
															{user.isFriendsListPrivate ? 'Private' : 'Public'}
														</span>
													</td>
													<td style={styles.tableCell}>
														{shareAnalytics[user.email?.toLowerCase()] ? (
															<View style={styles.shareAnalyticsCell}>
																<View style={styles.shareAnalyticsPills}>
																	<View style={styles.sharePill}>
																		<Text style={styles.sharePillCount}>{shareAnalytics[user.email?.toLowerCase()]?.totalShares || 0}</Text>
																		<Text style={styles.sharePillLabel}>Total</Text>
																	</View>
																	<View style={styles.sharePill}>
																		<Text style={styles.sharePillCount}>{shareAnalytics[user.email?.toLowerCase()]?.emailShares?.length || 0}</Text>
																		<Text style={styles.sharePillLabel}>Email</Text>
																	</View>
																	<View style={styles.sharePill}>
																		<Text style={styles.sharePillCount}>{shareAnalytics[user.email?.toLowerCase()]?.smsShares?.length || 0}</Text>
																		<Text style={styles.sharePillLabel}>SMS</Text>
																	</View>
																</View>
																{shareAnalytics[user.email?.toLowerCase()]?.totalShares > 0 && (
																	<button
																		onClick={() => {
																			setSelectedUser(user);
																			setIsUserModalOpen(true);
																		}}
																		style={styles.viewDetailsButton}
																	>
																		View Details
																	</button>
																)}
															</View>
														) : (
															<Text style={styles.noShares}>No shares</Text>
														)}
													</td>
													<td style={styles.tableCell}>
														<View style={styles.actionButtons}>
														<button
															onClick={() => handleToggleTester(user)}
															style={{
																...styles.actionButton,
																backgroundColor: user.isTester ? '#fef3c7' : '#f1f5f9',
																color: user.isTester ? '#d97706' : '#64748b',
																fontWeight: user.isTester ? 'bold' : 'normal',
															}}
															title={user.isTester ? 'Remove tester flag' : 'Mark as tester'}
														>
															{user.isTester ? '🧪 Tester' : 'Mark Tester'}
														</button>
														<button
															onClick={() => setTutorialAdminUser(user)}
															style={styles.actionButton}
															title="Restart, reset or preview the Spotlight Tutorial"
														>
															Tutorial
														</button>
														<button
															onClick={() => handleResetUserPassword(user.email)}
															style={styles.actionButton}
															disabled={userActionLoading}
														>
															Reset Password
														</button>
															<button
																onClick={() => handleUpdateUserEmail(user.id, user.email)}
																style={styles.actionButton}
																disabled={userActionLoading}
															>
																Update Email
															</button>
															<button
																onClick={() => handleDeleteUser(user.id, user.email)}
																style={{...styles.actionButton, backgroundColor: '#fee2e2', color: '#ef4444'}}
																disabled={userActionLoading}
															>
																Delete
															</button>
															<button
																onClick={() => handleStripeReset(user)}
																style={{...styles.actionButton, color: '#ef4444'}}
																disabled={userActionLoading}
															>
																Reset Stripe
															</button>
															{(() => {
																// Rewrite stored URL to current origin so localhost/staging links stay local
																const toCurrentOrigin = (url) => {
																	try { return window.location.origin + new URL(url).pathname; } catch { return url; }
																};
																const withUic = (url) => {
																	if (!user.uic) return url;
																	const params = new URLSearchParams({ uic: user.uic });
																	return `${url}?${params}`;
																};
																const withOwnerPreview = (url) => {
																	const params = new URLSearchParams({ preview: 'owner' });
																	if (user.uic) params.set('uic', user.uic);
																	return `${url}?${params}`;
																};
																return (
																	<>
																		{user.pages?.registry && user.pages?.registryPageConfig && (
																			<button
																				onClick={() => window.open(withOwnerPreview(toCurrentOrigin(user.pages.registry)), '_blank')}
																				style={{...styles.actionButton, backgroundColor: '#eff6ff', color: '#2563eb'}}
																				title="View this registry page as the account owner"
																			>
																				View Registry
																			</button>
																		)}
																		{user.pages?.events && (
																			<button
																				onClick={() => window.open(withOwnerPreview(toCurrentOrigin(user.pages.events)), '_blank')}
																				style={{...styles.actionButton, backgroundColor: '#eff6ff', color: '#2563eb'}}
																				title="View this events page as the account owner"
																			>
																				View Events
																			</button>
																		)}
																		{user.uid && (user.pages?.events || user.pages?.registry) && (
																			<button
																				onClick={() => {
																					const params = new URLSearchParams({ preview: 'owner' });
																					if (user.uic) params.set('uic', user.uic);
																					if (user.email) params.set('target_email', user.email.toLowerCase());
																					const dashboardUrl = `${window.location.origin}/dashboard/${user.uid}?${params}`;
																					window.open(dashboardUrl, '_blank');
																				}}
																				style={{...styles.actionButton, backgroundColor: '#eff6ff', color: '#2563eb'}}
																				title="View this user's graduate dashboard as the account owner"
																			>
																				View Dashboard
																			</button>
																		)}
																	</>
																);
															})()}
														</View>
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</View>
							</View>
						)}

						{activeTab === "analytics" && (
							<View style={styles.analyticsContainer}>
								<Analytics />
								
								{/* Analytics Health Check Section */}
								<View style={styles.healthCheckContainer}>
									<Text style={styles.healthCheckTitle}>Analytics System Health</Text>
									<Pressable
										style={styles.healthCheckButton}
										onPress={async () => {
											try {
												const health = await analyticsManager.getHealthStatus();
												
												alert(JSON.stringify(health, null, 2));
											} catch (error) {
												
												alert('Error: ' + error.message);
											}
										}}
									>
										<Text style={styles.healthCheckButtonText}>Check System Health</Text>
									</Pressable>
									
									<Pressable
										style={styles.refreshButton}
										onPress={async () => {
											try {
												await analyticsManager.refreshAll();
												alert('Analytics data refreshed successfully');
											} catch (error) {
												
												alert('Error refreshing: ' + error.message);
											}
										}}
									>
										<Text style={styles.refreshButtonText}>Refresh Analytics Data</Text>
									</Pressable>
								</View>
							</View>
						)}

						{activeTab === "catalogue" && (
							<View style={styles.section}>
								{/* Add Affiliates Section */}
								<Text style={styles.subHeader}>Affiliate Link Details</Text>
								<View>
									<Text style={{fontSize: 18, marginBottom: 10}}>Toggle Catalogue Visibility</Text>
									<View style={styles.visibilityButtonContainer}>
										<button 
											onClick={() => toggleCatalogueVisibility('hidePrice')} 
											style={{
												...styles.visibilityButton,
												...(visibilitySettings.hidePrice && styles.visibilityButtonActive)
											}}
										>
											{visibilitySettings.hidePrice ? "Show Price" : "Hide Price"}
										</button>
										<button 
											onClick={() => toggleCatalogueVisibility('hideImages')} 
											style={{
												...styles.visibilityButton,
												...(visibilitySettings.hideImages && styles.visibilityButtonActive)
											}}
										>
											{visibilitySettings.hideImages ? "Show Images" : "Hide Images"}
										</button>
										<button 
											onClick={() => toggleCatalogueVisibility('hideAll')} 
											style={{
												...styles.visibilityButton,
												...(visibilitySettings.hideAll && styles.visibilityButtonActive)
											}}
										>
											{visibilitySettings.hideAll ? "Show All" : "Hide All"}
										</button>
									</View>
								</View>

								{/* Add to Catalogue Section */}
								<View>
									<TextInput placeholder="Affiliate URL" value={affiliateUrl} onChangeText={setAffiliateUrl} style={styles.input} />
									<View>
										<select
											value={category}
											onChange={(e) => {
												setCategory(e.target.value);
												setSelectedSubCategory("");
											}}
											style={categorySelectStyle}
										>
											<option value="">Select a Category</option>
											{categories.map((cat) => (
												<option key={cat} value={cat}>
													{cat}
												</option>
											))}
										</select>

										<select
											value={selectedSubCategory}
											onChange={(e) => setSelectedSubCategory(e.target.value)}
											style={styles.input}
										>
											<option value="">Select a Subcategory</option>
											{subCategories[category]?.map((subCat) => (
												<option key={subCat} value={subCat}>
													{subCat}
												</option>
											))}
										</select>
									</View>

									{!manualEntry ? (
										<>
											<button onClick={fetchItemPreview} style={styles.buttonApprove}>
												Fetch Preview
											</button>
											<button onClick={toggleManualEntry} style={styles.buttonDeny}>
												Enter Details Manually
											</button>
										</>
									) : (
										<>
											<TextInput placeholder="Item Name" value={itemName} onChangeText={setItemName} style={styles.input} />
											<TextInput placeholder="Item Description" value={itemDescription} onChangeText={setItemDescription} style={styles.input} multiline />
											<TextInput placeholder="Item Price" value={itemPrice} onChangeText={setItemPrice} style={styles.input} />
											<TextInput placeholder="Item Image URL" value={itemImage} onChangeText={setItemImage} style={styles.input} />
											<button onClick={toggleManualEntry} style={styles.buttonDeny}>
												Fetch Preview Instead
											</button>
											<button
												onClick={submitItemToDatabase}
												style={category && selectedSubCategory && itemName && itemDescription && itemPrice && itemImage ? styles.buttonApprove : styles.buttonDisabled}
												disabled={!category || !selectedSubCategory || !itemName || !itemDescription || !itemPrice || !itemImage}
											>
												Add to Catalogue
											</button>
										</>
									)}
									{!isFetching && itemPreview && (
										<button
											onClick={submitItemToDatabase}
											style={category && selectedSubCategory ? styles.buttonApprove : styles.buttonDisabled}
											disabled={!category || !selectedSubCategory}
										>
											Add to Catalogue
										</button>
									)}

									{loadingStep && <View style={{ padding: 10, backgroundColor: "#f0f0f0", margin: 10, borderRadius: 5 }}><Text>{loadingStep}</Text></View>}
									{showStillWorking && <View style={{ padding: 10, backgroundColor: "#f0f0f0", margin: 10, borderRadius: 5 }}><Text>Still working...</Text></View>}
								</View>

								{itemPreview && (
									<View>
										<Text style={{fontSize: 18, marginBottom: 10}}>Item Preview:</Text>
										<img src={itemPreview.imageUrl} alt="Preview" style={{ width: 250, height: 250 }} />
										<Text>Name: {itemPreview.title}</Text>
										<Text>Description: {itemPreview.description}</Text>
										<Text>Price: {itemPreview.price}</Text>
										<button onClick={submitItemToDatabase} style={styles.buttonApprove}>
											Add to Catalogue
										</button>
									</View>
								)}

								{/* Catalogue Items Section with delete button */}
								<Text style={styles.subHeader}>Catalogue Items</Text>

								{/* UI for selecting filters and sorting methods */}
								<View style={styles.filterContainer}>
								<select
									value={filter}
									onChange={(e) => {
										setFilter(e.target.value);
										setCurrentPage(1);
										}}
										style={styles.filterSelect}
									>
									<option value="all">All Categories</option>
									{categories.map((category) => (
										<option key={category} value={category}>
											{category}
										</option>
									))}
								</select>

									<select 
										value={sort} 
										onChange={(e) => setSort(e.target.value)}
										style={styles.filterSelect}
									>
										<option value="none">Sort by: Default</option>
									<option value="priceLowHigh">Price: Low to High</option>
									<option value="priceHighLow">Price: High to Low</option>
								</select>
									
									<View style={{ flexDirection: 'row', alignItems: 'center', flex: 2 }}>
										<TextInput
											style={[styles.input, { flex: 1, marginRight: 10, marginBottom: 0 }]}
											placeholder="Search items..."
											value={searchInput}
											onChangeText={setSearchInput}
										/>
										<button 
											onClick={handleSearch}
											disabled={searchInput.trim().length < 3}
											style={searchInput.trim().length < 3 ? styles.buttonDisabled : styles.buttonApprove}
										>
											Search
										</button>
									</View>
								</View>

								{/* Pagination Controls */}
								<View style={styles.paginationContainer}>
									{totalPages > 10 ? (
										<>
											{/* Compact pagination for many pages */}
											<button
												style={styles.pageButton}
												onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
												disabled={currentPage === 1}
											>
												&laquo;
											</button>
											
											{currentPage > 3 && (
												<button style={styles.pageButton} onClick={() => handlePageChange(1)}>1</button>
											)}
											
											{currentPage > 4 && <span style={styles.pageEllipsis}>...</span>}
											
											{Array.from({ length: 5 }, (_, i) => {
												const pageNum = Math.max(2, Math.min(currentPage - 2 + i, totalPages - 1));
												if (pageNum <= 1 || pageNum >= totalPages) return null;
												if (i === 0 && pageNum > 2) return null;
												if (i === 4 && pageNum < totalPages - 1) return null;
												return (
													<button
														key={pageNum}
														style={currentPage === pageNum ? styles.activePage : styles.pageButton}
														onClick={() => handlePageChange(pageNum)}
													>
														{pageNum}
													</button>
												);
											})}
											
											{currentPage < totalPages - 3 && <span style={styles.pageEllipsis}>...</span>}
											
											{currentPage < totalPages - 2 && (
												<button style={styles.pageButton} onClick={() => handlePageChange(totalPages)}>
													{totalPages}
												</button>
											)}
											
											<button
												style={styles.pageButton}
												onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
												disabled={currentPage === totalPages}
											>
												&raquo;
											</button>
										</>
									) : (
										// Simple pagination for few pages
										Array.from({ length: totalPages }, (_, index) => (
										<button
											key={index + 1}
											style={currentPage === index + 1 ? styles.activePage : styles.pageButton}
											onClick={() => handlePageChange(index + 1)}
										>
											{index + 1}
										</button>
										))
									)}
								</View>

								{/* Bulk Edit Section */}
								<View style={styles.bulkEdit}>
									<Text style={styles.bulkEditHeader}>Bulk Edit Selected Items</Text>
									
									<View style={{ display: 'grid', gridTemplateColumns: width < 768 ? '1fr' : 'repeat(2, 1fr)', gap: 10 }}>
									<select
										value={selectedCategory}
										onChange={(e) => setSelectedCategory(e.target.value)}
										style={styles.input}
									>
										<option value="">Change Category</option>
										{categories.map((category) => (
											<option key={category} value={category}>{category}</option>
										))}
									</select>

									<select
										value={selectedSubCategory}
										onChange={(e) => setSelectedSubCategory(e.target.value)}
										style={styles.input}
											disabled={!selectedCategory}
									>
										<option value="">Select a Subcategory</option>
										{subCategories[selectedCategory]?.map((subCat) => (
											<option key={subCat} value={subCat}>
												{subCat}
											</option>
										))}
									</select>
									</View>

									<View style={{ display: 'grid', gridTemplateColumns: width < 768 ? '1fr' : 'repeat(2, 1fr)', gap: 10 }}>
										<TextInput
										placeholder="New Price"
										value={bulkPrice}
											onChangeText={setBulkPrice}
										style={styles.input}
									/>
										<TextInput
										placeholder="New Description"
										value={bulkDescription}
											onChangeText={setBulkDescription}
										style={styles.input}
											multiline
										/>
									</View>
									
									<View style={styles.bulkEditButtons}>
										<button onClick={toggleSelectAllProducts} style={styles.buttonEdit}>
											{selectedProducts.length > 0 ? 'Deselect All' : 'Select All in Category'}
										</button>
										<button 
											onClick={updateProducts} 
											style={selectedProducts.length > 0 ? styles.buttonApprove : styles.buttonDisabled}
											disabled={selectedProducts.length === 0}
										>
											Apply Changes to {selectedProducts.length} item{selectedProducts.length !== 1 ? 's' : ''}
										</button>
								</View>
								</View>

								{/* Displaying Items */}
								<View style={styles.tableWrapper}>
								<table style={styles.table}>
										<thead>
											<tr>
												<th style={styles.tableHeader}>
													<input
														type="checkbox"
														checked={selectedProducts.length > 0 && selectedProducts.length === currentItems.length}
														onChange={toggleSelectAllProducts}
													/>
												</th>
												<th style={styles.tableHeader}>Image</th>
												<th style={styles.tableHeader}>Item Name</th>
												<th style={styles.tableHeader}>URL</th>
												<th style={styles.tableHeader}>Description</th>
												<th style={styles.tableHeader}>Price</th>
												<th style={styles.tableHeader}>Category</th>
												<th style={styles.tableHeader}>Subcategory</th>
												<th style={styles.tableHeader}>Action</th>
										</tr>
									</thead>
									<tbody>
											{currentItems.length > 0 ? (
												currentItems.map((item) => (
											<tr key={item.id} style={styles.tableRow}>
														<td style={styles.tableCell}>
													<input
														type="checkbox"
														checked={selectedProducts.includes(item.id)}
														onChange={() => handleSelectProduct(item.id)}
													/>
												</td>
														<td style={styles.tableCell}>
															<img src={item.image} alt={item.name} style={{ width: 80, height: 80, objectFit: 'cover' }} />
												</td>
														<td style={styles.tableCell}>{item.name}</td>
														<td style={styles.tableCell}>
															<a href={item.url} target="_blank" rel="noopener noreferrer" style={{ color: '#4c956c', textDecoration: 'underline' }}>
																{item.url.length > 30 ? item.url.substring(0, 30) + '...' : item.url}
															</a>
														</td>
														<td style={styles.tableCell}>{item.description.length > 50 ? `${item.description.substring(0, 50)}...` : item.description}</td>
														<td style={styles.tableCell}>{item.price.includes("$") ? item.price : `$${item.price}`}</td>
														<td style={styles.tableCell}>{item.category}</td>
														<td style={styles.tableCell}>
													<select
														value={item.subCategory || ""}
														onChange={(e) => handleSubCategoryChange(item.id, e.target.value)}
																style={styles.tableSelect}
													>
														<option value="">Select a Subcategory</option>
														{subCategories[item.category]?.map((subCat) => (
															<option key={subCat} value={subCat}>
																{subCat}
															</option>
														))}
													</select>
												</td>
														<td style={styles.tableCell}>
													<button
														onClick={() => deleteItem(item.id)}
														style={styles.buttonDeny}
													>
														Delete
													</button>
													<button
														onClick={() => window.open(item.url, '_blank')}
														style={styles.buttonApprove}
													>
																View
													</button>
												</td>
											</tr>
												))
											) : (
												<tr>
													<td colSpan="9" style={{ textAlign: 'center', padding: 20 }}>
														No items found. Try changing your search or filter criteria.
													</td>
												</tr>
											)}
									</tbody>
								</table>
								</View>
							</View>
						)}

						{activeTab === "blog" && (
							<View style={styles.section}>
								<View style={styles.blogSection}>
									{/* Blog Form */}
									<View style={styles.blogFormContainer}>
										<View style={styles.blogForm}>
											<Text style={{fontSize: 20, marginBottom: 15}}>{editingBlogId ? 'Edit Blog Post' : 'Create Blog Post'}</Text>
											<View>
												<TextInput
												placeholder="Blog Title"
												value={blogTitle}
													onChangeText={setBlogTitle}
												style={styles.input}
											/>
											
											<Text style={styles.categoryLabel}>Select Categories (Multiple)</Text>
											<View style={styles.categoriesGrid}>
												{blogCategories.map(category => (
													<Pressable
														key={category}
														style={[
															styles.categorySelectButton,
															selectedCategories.includes(category) && styles.categorySelectButtonActive
														]}
														onPress={() => handleCategoryToggle(category)}
													>
														<Text style={[
															styles.categorySelectText,
															selectedCategories.includes(category) && styles.categorySelectTextActive
														]}>{category}</Text>
													</Pressable>
												))}
											</View>

												<TextInput
												placeholder="Image URL"
												value={blogImage}
													onChangeText={setBlogImage}
												style={styles.input}
												/>

												<View style={styles.dateInputContainer}>
													<View style={styles.dateField}>
														<Text style={styles.dateLabel}>Publish Date:</Text>
													<input
														type="datetime-local"
														value={publishDate ? new Date(publishDate).toISOString().slice(0, 16) : ""}
														onChange={(e) => setPublishDate(new Date(e.target.value).toISOString())}
														style={styles.dateInput}
														/>
													</View>
													<View style={styles.dateField}>
														<Text style={styles.dateLabel}>Last Updated:</Text>
													<input
														type="datetime-local"
														value={lastUpdatedDate ? new Date(lastUpdatedDate).toISOString().slice(0, 16) : ""}
														onChange={(e) => setLastUpdatedDate(new Date(e.target.value).toISOString())}
														style={styles.dateInput}
													/>
													</View>
												</View>

												<View style={styles.blogContentContainer}>
													<TextInput
												placeholder="Blog content will appear here..."
												value={blogContent}
														onChangeText={setBlogContent}
														style={{...styles.input, minHeight: 300}}
														multiline
													/>
												</View>

											{/* Update the tag input section in the blog form */}
												<View style={styles.tagSection}>
													<Text style={styles.tagSectionLabel}>Tags</Text>
													<View style={styles.presetTagsContainer}>
													{presetBlogTags.map((tag) => (
														<button
															key={tag}
															type="button"
															onClick={() => handlePresetTagClick(tag)}
															style={{
																...styles.presetTagButton,
																	...(blogTags.includes(tag) && styles.presetTagButtonSelected)
															}}
														>
															{tag}
														</button>
													))}
													</View>
													<View style={styles.tagList}>
													{blogTags.map((tag, index) => (
															<View key={index} style={styles.tagPill}>
																<Text>{tag}</Text>
															<button 
																type="button"
																onClick={() => setBlogTags(blogTags.filter((_, i) => i !== index))}
																style={styles.tagRemove}
															>
																×
															</button>
															</View>
														))}
													</View>
													<TextInput
														placeholder="Add a tag and press Enter..."
														onKeyPress={handleTagInput}
														style={styles.tagInput}
													/>
												</View>

												<View style={styles.buttonRow}>
												<button 
														type="button"
														onClick={handleBlogSubmit}
													style={styles.primaryButton}
												>
													{editingBlogId ? 'Update Post' : 'Create Post'}
												</button>
												{editingBlogId && (
													<button 
														type="button"
															onClick={resetForm}
															style={styles.secondaryButton}
													>
														Cancel Edit
													</button>
												)}
												</View>
											</View>
										</View>

										{/* Blog Preview */}
										<View style={styles.blogPreview}>
											<Text style={{fontSize: 20, marginBottom: 15}}>Preview</Text>
											<View style={styles.previewContainer}>
											{blogImage && (
													<img 
														src={blogImage} 
														alt="Blog preview" 
														style={styles.previewImage} 
														onError={(e) => e.target.src = 'https://via.placeholder.com/800x400?text=Image+Not+Found'}
													/>
												)}
												<Text style={styles.previewTitle}>{blogTitle || 'Your Blog Title'}</Text>
												{blogCategory && <Text style={styles.previewCategory}>Category: {blogCategory}</Text>}
												
												<View style={styles.previewTagList}>
												{blogTags.map((tag, index) => (
														<View key={index} style={styles.previewTagPill}>
															<Text style={styles.previewTagText}>{tag}</Text>
														</View>
													))}
												</View>
												
												<View style={styles.previewContent}>
													{blogContent ? (
														<Text>{blogContent}</Text>
													) : (
														<Text style={styles.placeholderText}>Your blog content will appear here...</Text>
													)}
												</View>
												
												<View style={styles.previewMeta}>
													<Text style={styles.previewMetaText}>
														{publishDate ? `Published: ${new Date(publishDate).toLocaleDateString()}` : 'Not yet published'}
													</Text>
													{lastUpdatedDate && (
														<Text style={styles.previewMetaText}>
															Last updated: {new Date(lastUpdatedDate).toLocaleDateString()}
														</Text>
													)}
												</View>
											</View>
										</View>
									</View>

									{/* Blog List */}
									<View style={styles.blogList}>
										<Text style={{fontSize: 20, marginBottom: 15}}>Published Blog Posts</Text>
										{blogPosts.map((post) => (
											<View key={post.id} style={styles.blogPost}>
												<Text style={styles.blogTitle}>
													{post.title && post.title.trim() ? post.title : '[No Title]'}
												</Text>
												<Text style={styles.blogMeta}>
													ID: {post.id} | Categories: {post.categories?.join(', ') || 'No categories'} | Published: {formatDate(post.createdAt)}
												</Text>
												<Text style={styles.blogContentPreview}>
													Content: {post.content && post.content.trim() ? 
														(post.content.length > 100 ? post.content.substring(0, 100) + '...' : post.content) : 
														'[No Content]'}
												</Text>
												<View style={styles.tagList}>
													{post.tags?.map((tag, index) => (
														<View key={index} style={styles.tagPill}>
															<Text>{tag}</Text>
														</View>
													))}
												</View>
												<View style={styles.blogActions}>
												<button
													onClick={() => {
														setBlogTitle(post.title);
														setBlogContent(post.content);
														setBlogCategory(post.category);
														setBlogImage(post.imageUrl);
														setBlogTags(post.tags || []);
														setPublishDate(post.createdAt);
															setLastUpdatedDate(post.updatedAt || post.createdAt);
															setEditingBlogId(post.id);
													}}
													style={styles.buttonEdit}
												>
													Edit
												</button>
												<button
													onClick={async () => {
														if (window.confirm("Are you sure you want to delete this post?")) {
															await deleteDoc(doc(db, "blog", post.id));
															fetchBlogPosts();
														}
													}}
													style={styles.buttonDeny}
												>
													Delete
												</button>
												</View>
											</View>
									))}
									</View>
								</View>
							</View>
						)}

						{activeTab === "registry" && (
					<View style={[styles.section, { paddingTop: 22, paddingBottom: 22 }] }>
						<Text style={[styles.subHeader, { marginBottom: 12, textAlign: 'left' }]}>Registry Contribution</Text>
						<Text style={{ color: theme.textMuted, marginBottom: 16, textAlign: 'left' }}>Edit the quick contribution amounts shown to donors. Use the chips to edit values directly and keep the list concise for a simple donor experience.</Text>
						<View style={styles.registryPresetsRow}>
							{registryPresets.map((preset, idx) => (
								<View key={idx} style={styles.presetChip}>
									<TextInput
										style={styles.presetInput}
										value={String(preset)}
										keyboardType="numeric"
										onChangeText={(val) => handlePresetChange(idx, val)}
									/>
									<button type="button" onClick={() => removeRegistryPreset(idx)} style={styles.chipRemoveBtn}>✕</button>
								</View>
							))}
						</View>

						<View style={styles.registryActionsRow}>
							<button onClick={addRegistryPreset} style={styles.buttonBase}>+ Add Preset</button>
							<button onClick={resetRegistryToDefault} style={styles.buttonDeny}>Reset</button>
							<button onClick={handleSaveRegistryPresets} disabled={registrySaving} style={registrySaving ? styles.buttonDisabled : styles.buttonApprove}>
								{registrySaving ? 'Saving...' : 'Save Changes'}
							</button>
						</View>

						{registryError && <Text style={{ color: '#ef4444', marginTop: 8 }}>{registryError}</Text>}
					</View>
					)}

						{activeTab === "ecard-studio" && (
							<ECardStudio theme={theme} />
						)}

			{activeTab === "features" && (
							<View style={[styles.section, { backgroundColor: theme.bgSection }]}>
								<View style={styles.ffContainer}>
									<View style={styles.ffHeaderSection}>
										<Text style={[styles.ffMainTitle, { color: theme.textPrimary }]}>Feature Flags</Text>
										<Text style={[styles.ffSubtitle, { color: theme.textMuted }]}>
											Control app features per environment. Staging changes are instant for testing. Production changes require confirmation.
										</Text>
										<View style={styles.ffLegend}>
											<View style={styles.ffLegendItem}>
												<View style={[styles.ffLegendDot, { backgroundColor: '#eab308' }]} />
												<Text style={[styles.ffLegendText, { color: theme.textMuted }]}>Staging - Test changes safely</Text>
											</View>
											<View style={styles.ffLegendItem}>
												<View style={[styles.ffLegendDot, { backgroundColor: '#3b82f6' }]} />
												<Text style={[styles.ffLegendText, { color: theme.textMuted }]}>Production - Affects all live users</Text>
											</View>
										</View>
									</View>
									
									{featureFlagsLoading ? (
										<View style={styles.ffLoadingContainer}>
											<Text style={[styles.ffLoading, { color: theme.textMuted }]}>Loading feature flags...</Text>
										</View>
									) : (
										<View style={styles.ffCategoriesGrid}>
											{Object.entries(getFeatureFlagsByCategory())
												.sort((a, b) => (categoryConfig[a[0]]?.order || 99) - (categoryConfig[b[0]]?.order || 99))
												.map(([category, flags]) => {
													const config = categoryConfig[category] || { iconName: 'settings-outline', description: '' };
													const isCollapsed = collapsedCategories[category];
													
													return (
														<View key={category} style={[
															styles.ffCategorySection,
															{ backgroundColor: theme.ffCardBg, borderColor: theme.ffCardBorder },
															isCollapsed && styles.ffCategorySectionCollapsed
														]}>
															{/* Clickable Category Header */}
															<Pressable 
																onPress={() => toggleCategoryCollapse(category)}
																style={styles.ffCategoryHeaderClickable}
															>
																<View style={styles.ffCategoryHeaderLeft}>
																	<Ionicons name={config.iconName} size={16} color={theme.primary} />
																	<Text style={[styles.ffCategoryTitle, { color: theme.textPrimary }]}>{category}</Text>
																	<View style={styles.ffCategoryBadge}>
																		<Text style={styles.ffCategoryBadgeText}>{flags.length}</Text>
																	</View>
																</View>
																<View style={[styles.ffCollapseBtn, { backgroundColor: theme.btnSecondaryBg }, isCollapsed && styles.ffCollapseBtnCollapsed]}>
																	<Ionicons 
																		name={isCollapsed ? 'chevron-forward' : 'chevron-down'} 
																		size={14} 
																		color={isCollapsed ? '#fff' : theme.textMuted} 
																	/>
																</View>
															</Pressable>
															
															{/* Collapsible Content - includes description and flags */}
															{!isCollapsed && (
																<View style={styles.ffCollapsibleContent}>
																	<Text style={[styles.ffCategoryDesc, { color: theme.textMuted, borderBottomColor: theme.border }]}>{config.description}</Text>
																<View style={styles.ffCategoryFlags}>
																	{flags.map((flag) => {
																		const flagValue = featureFlags[flag.key] || getFeatureFlagDefaults(flag.key);
																		const stagingEnabled = typeof flagValue === 'boolean' ? flagValue : flagValue.staging;
																		const productionEnabled = typeof flagValue === 'boolean' ? flagValue : flagValue.production;
																		const impactColor = flag.impact === 'high' ? '#dc2626' : flag.impact === 'medium' ? '#f59e0b' : '#22c55e';
																		
																		return (
																			<View key={flag.key} style={[styles.ffCard, { backgroundColor: theme.ffItemBg, borderColor: theme.ffItemBorder }]}>
																				<View style={styles.ffCardContent}>
																					<View style={styles.ffCardInfo}>
																						<View style={styles.ffCardTitleRow}>
																							<Text style={[styles.ffCardTitle, { color: theme.textPrimary }]}>{flag.label}</Text>
																							<View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
																								{flag.version && (
																									<View style={[styles.ffImpactBadge, { backgroundColor: theme.textMuted + '15', borderColor: theme.textMuted + '30' }]}>
																										<Text style={[styles.ffImpactText, { color: theme.textMuted }]}>
																											v{flag.version}
																										</Text>
																									</View>
																								)}
																								<View style={[styles.ffImpactBadge, { backgroundColor: impactColor + '20', borderColor: impactColor }]}>
																									<Text style={[styles.ffImpactText, { color: impactColor }]}>
																										{flag.impact?.toUpperCase()}
																									</Text>
																								</View>
																							</View>
																						</View>
																						<Text style={[styles.ffCardDesc, { color: theme.textMuted }]}>{flag.description}</Text>
																					</View>
																					
																					<View style={styles.ffTogglesColumn}>
																						{/* Staging Toggle - No confirmation needed */}
																						<View style={styles.ffToggleRow}>
																							<View style={styles.ffToggleLabel}>
																								<View style={[styles.ffToggleDot, { backgroundColor: '#eab308' }]} />
																								<Text style={[styles.ffToggleLabelText, { color: theme.textMuted }]}>Staging</Text>
																							</View>
																							<button
																								onClick={() => toggleStagingFlag(flag.key, !stagingEnabled)}
																								style={{
																									...styles.ffToggleBtn,
																									...(stagingEnabled ? styles.ffToggleBtnOn : styles.ffToggleBtnOff)
																								}}
																							>
																								{stagingEnabled ? 'ON' : 'OFF'}
																							</button>
																						</View>
																						
																						{/* Production Toggle - Requires confirmation */}
																						<View style={styles.ffToggleRow}>
																							<View style={styles.ffToggleLabel}>
																								<View style={[styles.ffToggleDot, { backgroundColor: '#3b82f6' }]} />
																								<Text style={[styles.ffToggleLabelText, { color: theme.textMuted }]}>Production</Text>
																							</View>
																							<button
																								onClick={() => initiateProductionFlagChange(flag.key, !productionEnabled)}
																								style={{
																									...styles.ffToggleBtn,
																									...(productionEnabled ? styles.ffToggleBtnOnProd : styles.ffToggleBtnOffProd)
																								}}
																							>
																								{productionEnabled ? 'ON' : 'OFF'}
																							</button>
																						</View>
																					</View>

																					{flag.previewable && (
																						<button
																							type="button"
																							onClick={() => {
																								setSmsBackfillPreviewPhone('');
																								setShowSmsBackfillPreviewModal(true);
																							}}
																							style={styles.ffPreviewBtn}
																						>
																							Preview Modal
																						</button>
																					)}
																				</View>
																			</View>
																		);
																	})}
																</View>
															</View>
															)}
														</View>
													);
												})}
										</View>
									)}
								</View>

								{/* Production Confirmation Modal */}
								{showFeatureConfirmModal && pendingFeatureChange && (
									<div style={styles.ffModalOverlay}>
										<div style={styles.ffModalProd}>
											<View style={styles.ffModalProdHeader}>
												<Text style={styles.ffModalProdIcon}>
													{pendingFeatureChange.action === 'DISABLE' ? '⚠️' : '🚀'}
												</Text>
												<Text style={styles.ffModalProdTitle}>
													Production Change
												</Text>
											</View>
											
											<View style={styles.ffModalProdAlert}>
												<Text style={styles.ffModalProdAlertText}>
													🔵 This will affect ALL live users immediately
												</Text>
											</View>
											
											<View style={styles.ffModalProdContent}>
												<View style={styles.ffModalProdRow}>
													<Text style={styles.ffModalProdLabel}>Feature:</Text>
													<Text style={styles.ffModalProdValue}>{pendingFeatureChange.label}</Text>
												</View>
												<View style={styles.ffModalProdRow}>
													<Text style={styles.ffModalProdLabel}>Category:</Text>
													<Text style={styles.ffModalProdValue}>{pendingFeatureChange.category}</Text>
												</View>
												<View style={styles.ffModalProdRow}>
													<Text style={styles.ffModalProdLabel}>Action:</Text>
													<Text style={[
														styles.ffModalProdValue,
														{ color: pendingFeatureChange.action === 'DISABLE' ? '#dc2626' : '#22c55e', fontWeight: 'bold' }
													]}>
														{pendingFeatureChange.action}
													</Text>
												</View>
												<View style={styles.ffModalProdRow}>
													<Text style={styles.ffModalProdLabel}>Impact:</Text>
													<Text style={[
														styles.ffModalProdValue,
														{ color: pendingFeatureChange.impact === 'high' ? '#dc2626' : pendingFeatureChange.impact === 'medium' ? '#f59e0b' : '#22c55e' }
													]}>
														{pendingFeatureChange.impact?.toUpperCase()}
													</Text>
												</View>
											</View>
											
											<Text style={styles.ffModalProdDesc}>
												{pendingFeatureChange.description}
											</Text>
											
											{pendingFeatureChange.action === 'DISABLE' && (
												<View style={styles.ffModalProdWarning}>
													<Text style={styles.ffModalProdWarningText}>
														⚠️ Disabling this feature will immediately remove it from all production users.
													</Text>
												</View>
											)}
											
											<View style={styles.ffModalProdBtns}>
												<button onClick={cancelFeatureFlagChange} style={styles.ffModalProdCancelBtn}>
													Cancel
												</button>
												<button 
													onClick={confirmFeatureFlagChange} 
													style={pendingFeatureChange.action === 'DISABLE' ? styles.ffModalProdDisableBtn : styles.ffModalProdEnableBtn}
												>
													{pendingFeatureChange.action === 'DISABLE' ? '🔴 Disable on Production' : '🟢 Enable on Production'}
												</button>
											</View>
										</div>
									</div>
								)}

								{showSmsBackfillPreviewModal && (
									<div
										style={styles.ffModalOverlay}
										onClick={() => setShowSmsBackfillPreviewModal(false)}
									>
										<View
											style={hubStyles.centeredView}
											onStartShouldSetResponder={() => true}
										>
											<Pressable onPress={(e) => e?.stopPropagation?.()}>
												<View style={hubStyles.smsBackfillModalView}>
													<Text style={hubStyles.smsBackfillTitle}>Complete Your SMS Setup</Text>
													<Text style={[hubStyles.smsBackfillBody, { fontSize: 13, color: '#6b7280' }]}>
														Admin preview — this is how graduates will see the prompt on the Hub.
													</Text>
													<Text style={hubStyles.smsBackfillBody}>
														You previously opted in to SMS updates, but there is no phone number on your account yet.
													</Text>
													<Text style={hubStyles.smsBackfillBody}>
														Add your mobile number now to keep SMS updates on, or turn SMS off for this account.
													</Text>

													<TextInput
														style={hubStyles.smsBackfillInput}
														placeholder="Mobile phone number"
														placeholderTextColor="#8b8b7a"
														value={smsBackfillPreviewPhone}
														onChangeText={setSmsBackfillPreviewPhone}
														keyboardType="phone-pad"
														autoCapitalize="none"
														autoCorrect={false}
													/>

													<Text style={hubStyles.smsBackfillHelper}>
														Example: 123-456-7890
													</Text>

													<Pressable style={hubStyles.smsBackfillPrimaryButton}>
														<Text style={hubStyles.smsBackfillPrimaryButtonText}>Save My Number</Text>
													</Pressable>

													<Pressable style={hubStyles.smsBackfillSecondaryButton}>
														<Text style={hubStyles.smsBackfillSecondaryButtonText}>Turn Off SMS</Text>
													</Pressable>

													<Pressable
														onPress={() => setShowSmsBackfillPreviewModal(false)}
														style={{ marginTop: 14, alignItems: 'center' }}
													>
														<Text style={{ color: '#6b7280', fontSize: 14 }}>Close Preview</Text>
													</Pressable>
												</View>
											</Pressable>
										</View>
									</div>
								)}
							</View>
						)}

						{activeTab === "mytassel-plus" && (
							<View style={[styles.section, { backgroundColor: theme.bgSection }]}>
								<View style={styles.ffContainer}>
									<View style={styles.ffHeaderSection}>
										<Text style={[styles.ffMainTitle, { color: theme.textPrimary }]}>MyTassel+</Text>
										<Text style={[styles.ffSubtitle, { color: theme.textMuted }]}>
											Manage the Standard profile backgrounds graduates can select without a code deployment. Custom uploads remain a Plus feature.
										</Text>
									</View>

									{standardBackgroundsLoading ? (
										<Text style={[styles.ffLoading, { color: theme.textMuted }]}>Loading Standard backgrounds...</Text>
									) : (
										<View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 14 }}>
											{standardProfileBackgrounds.map((background, index) => {
												const backgroundValue = getStandardProfileBackgroundValue(background);
												return (
													<View key={background.id} style={[styles.ffCard, { width: isMobile ? '100%' : 240, backgroundColor: theme.ffItemBg, borderColor: background.active === false ? theme.border : theme.primary }]}>
														<Image source={getImageSource(backgroundValue)} style={{ width: '100%', height: 110, borderRadius: 8, marginBottom: 10 }} />
														<TextInput
															style={[styles.presetInput, { width: '100%', marginBottom: 8 }]}
															value={background.label}
															onChangeText={(label) => updateStandardProfileBackground(background.id, { label })}
														/>
														<Text style={{ color: theme.textMuted, fontSize: 12, marginBottom: 8 }}>
															Order {index + 1}{background.fallback ? ' · built-in fallback' : ''}
														</Text>
														<View style={{ gap: 6, marginBottom: 10 }}>
															<label style={{ display: 'flex', alignItems: 'center', gap: 8, color: theme.textPrimary, fontSize: 13 }}>
																<input
																	type="checkbox"
																	checked={background.active !== false}
																	onChange={(event) => updateStandardProfileBackgroundAvailability(background.id, { active: event.target.checked })}
																/>
																Active
															</label>
															<label style={{ display: 'flex', alignItems: 'center', gap: 8, color: theme.textPrimary, fontSize: 13 }}>
																<input
																	type="checkbox"
																	checked={background.availableToStandard !== false}
																	onChange={(event) => updateStandardProfileBackgroundAvailability(background.id, { availableToStandard: event.target.checked })}
																/>
																Standard
															</label>
															<label style={{ display: 'flex', alignItems: 'center', gap: 8, color: theme.textPrimary, fontSize: 13 }}>
																<input
																	type="checkbox"
																	checked={background.availableToPlus !== false}
																	onChange={(event) => updateStandardProfileBackground(background.id, { availableToPlus: event.target.checked })}
																/>
																Plus
															</label>
														</View>
														<View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
															<button
																type="button"
																style={styles.buttonBase}
																disabled={index === 0}
																onClick={() => {
																	const next = [...standardProfileBackgrounds];
																	[next[index - 1], next[index]] = [next[index], next[index - 1]];
																	setStandardProfileBackgrounds(next);
																}}
															>
																Up
															</button>
															<button
																type="button"
																style={styles.buttonBase}
																disabled={index === standardProfileBackgrounds.length - 1}
																onClick={() => {
																	const next = [...standardProfileBackgrounds];
																	[next[index + 1], next[index]] = [next[index], next[index + 1]];
																	setStandardProfileBackgrounds(next);
																}}
															>
																Down
															</button>
															<button
																type="button"
																style={{ ...styles.buttonReject, paddingHorizontal: 12, paddingVertical: 8 }}
																onClick={() => removeStandardProfileBackground(background.id)}
															>
																Delete
															</button>
														</View>
													</View>
												);
											})}
										</View>
									)}

									<View style={{ marginTop: 18, flexDirection: isMobile ? 'column' : 'row', gap: 10, alignItems: isMobile ? 'stretch' : 'center' }}>
										<label style={{ ...styles.buttonBase, display: 'inline-block', textAlign: 'center', cursor: 'pointer' }}>
											Upload Background
											<input type="file" accept="image/*" onChange={addStandardProfileBackground} style={{ display: 'none' }} />
										</label>
										<button type="button" style={styles.buttonBase} onClick={() => setStandardProfileBackgrounds(FALLBACK_STANDARD_PROFILE_BACKGROUNDS)}>
											Reset to Built-In Five
										</button>
										<button
											type="button"
											style={standardBackgroundsSaving ? styles.buttonDisabled : styles.buttonApprove}
											disabled={standardBackgroundsSaving}
											onClick={() => saveStandardProfileBackgroundConfig()}
										>
											{standardBackgroundsSaving ? 'Saving...' : 'Save Standard Backgrounds'}
										</button>
									</View>
									{standardBackgroundsStatus ? <Text style={{ color: standardBackgroundsStatus.includes('Failed') ? theme.danger : theme.success, marginTop: 10 }}>{standardBackgroundsStatus}</Text> : null}
								</View>
							</View>
						)}

						{activeTab === "notifications" && (
							<View style={[styles.section, { backgroundColor: theme.bgSection }]}>
					<View style={styles.ffContainer}>
								{/* ─── Section toggle helper ─── */}
								{/* Renders a tappable section header row */}

								{/* ─── Admin Notifications ─── */}
								<button
									onClick={() => setNotifSectionOpen(s => ({ ...s, adminNotifications: !s.adminNotifications }))}
									style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 10, background: 'none', border: 'none', cursor: 'pointer', padding: '16px 0', width: '100%', textAlign: 'left' }}
								>
									<Ionicons name={notifSectionOpen.adminNotifications ? 'chevron-down' : 'chevron-forward'} size={16} color={theme.textMuted} />
									<View style={{ flex: 1 }}>
										<Text style={[styles.ffMainTitle, { color: theme.textPrimary, marginBottom: 2 }]}>Admin Notifications</Text>
										<Text style={[styles.ffSubtitle, { color: theme.textMuted, marginTop: 0 }]}>
											Configure email notifications sent to administrators when important events occur.
										</Text>
									</View>
								</button>

								{notifSectionOpen.adminNotifications && (adminNotificationsLoading ? (
									<View style={styles.ffLoadingContainer}>
										<Text style={[styles.ffLoading, { color: theme.textMuted }]}>Loading notification settings...</Text>
									</View>
								) : (
									<View style={styles.ffCategoriesGrid}>
											{/* Master Controls */}
											<View style={[styles.ffCategorySection, { backgroundColor: theme.ffCardBg, borderColor: theme.ffCardBorder }]}>
												<View style={styles.ffCategoryHeaderClickable}>
													<View style={styles.ffCategoryHeaderLeft}>
														<Ionicons name="notifications" size={16} color={theme.primary} />
														<Text style={[styles.ffCategoryTitle, { color: theme.textPrimary }]}>Master Controls</Text>
														<View style={styles.ffCategoryBadge}>
															<Text style={styles.ffCategoryBadgeText}>2</Text>
														</View>
													</View>
												</View>
												
												<View style={styles.ffCollapsibleContent}>
													<Text style={[styles.ffCategoryDesc, { color: theme.textMuted, borderBottomColor: theme.border }]}>
														Global notification settings
													</Text>
													<View style={styles.ffCategoryFlags}>
														{/* All Admin Notifications */}
														<View style={[styles.ffCard, { backgroundColor: theme.ffItemBg, borderColor: theme.ffItemBorder }]}>
															<View style={styles.ffCardContent}>
																<View style={styles.ffCardInfo}>
																	<View style={styles.ffCardTitleRow}>
																		<Text style={[styles.ffCardTitle, { color: theme.textPrimary }]}>All Admin Notifications</Text>
																		<View style={[styles.ffImpactBadge, { backgroundColor: '#dc262620', borderColor: '#dc2626' }]}>
																			<Text style={[styles.ffImpactText, { color: '#dc2626' }]}>HIGH</Text>
																		</View>
																	</View>
																	<Text style={[styles.ffCardDesc, { color: theme.textMuted }]}>Master switch for all admin email notifications</Text>
																</View>
																<View style={styles.ffTogglesColumn}>
																	<View style={styles.ffToggleRow}>
																		<button
																			onClick={toggleAdminNotificationsEnabled}
																			disabled={adminNotificationSaving}
																			style={{
																				...styles.ffToggleBtn,
																				...(adminNotificationConfig.enabled ? styles.ffToggleBtnOn : styles.ffToggleBtnOff),
																				opacity: adminNotificationSaving ? 0.5 : 1
																			}}
																		>
																			{adminNotificationConfig.enabled ? 'ON' : 'OFF'}
																		</button>
																	</View>
																</View>
															</View>
														</View>

														{/* New User Signup Alerts */}
														<View style={[styles.ffCard, { backgroundColor: theme.ffItemBg, borderColor: theme.ffItemBorder, opacity: adminNotificationConfig.enabled ? 1 : 0.5 }]}>
															<View style={styles.ffCardContent}>
																<View style={styles.ffCardInfo}>
																	<View style={styles.ffCardTitleRow}>
																		<Text style={[styles.ffCardTitle, { color: theme.textPrimary }]}>New User Signup Alerts</Text>
																		<View style={[styles.ffImpactBadge, { backgroundColor: '#f59e0b20', borderColor: '#f59e0b' }]}>
																			<Text style={[styles.ffImpactText, { color: '#f59e0b' }]}>MEDIUM</Text>
																		</View>
																	</View>
																	<Text style={[styles.ffCardDesc, { color: theme.textMuted }]}>Send email when a new user registers</Text>
																</View>
																<View style={styles.ffTogglesColumn}>
																	<View style={styles.ffToggleRow}>
																		<button
																			onClick={toggleNewUserSignupNotifications}
																			disabled={adminNotificationSaving || !adminNotificationConfig.enabled}
																			style={{
																				...styles.ffToggleBtn,
																				...(adminNotificationConfig.newUserSignupEnabled ? styles.ffToggleBtnOn : styles.ffToggleBtnOff),
																				opacity: (adminNotificationSaving || !adminNotificationConfig.enabled) ? 0.5 : 1
																			}}
																		>
																			{adminNotificationConfig.newUserSignupEnabled ? 'ON' : 'OFF'}
																		</button>
																	</View>
																</View>
															</View>
														</View>
													</View>
												</View>
											</View>

											{/* Recipients */}
											<View style={[styles.ffCategorySection, { backgroundColor: theme.ffCardBg, borderColor: theme.ffCardBorder }]}>
												<View style={styles.ffCategoryHeaderClickable}>
													<View style={styles.ffCategoryHeaderLeft}>
														<Ionicons name="people" size={16} color="#3b82f6" />
														<Text style={[styles.ffCategoryTitle, { color: theme.textPrimary }]}>Recipients</Text>
														<View style={styles.ffCategoryBadge}>
															<Text style={styles.ffCategoryBadgeText}>{adminNotificationConfig.recipients?.filter(r => r.enabled).length || 0}</Text>
														</View>
													</View>
												</View>
												
												<View style={styles.ffCollapsibleContent}>
													<Text style={[styles.ffCategoryDesc, { color: theme.textMuted, borderBottomColor: theme.border }]}>
														Who receives admin notifications
													</Text>
													<View style={styles.ffCategoryFlags}>
														{/* Add Recipient Form */}
														<View style={[styles.ffCard, { backgroundColor: theme.ffItemBg, borderColor: theme.ffItemBorder }]}>
															<View style={{ flexDirection: 'row', gap: 10, alignItems: 'center', flexWrap: 'wrap', width: '100%' }}>
																<TextInput
																	style={{
																		flex: 2,
																		minWidth: 180,
																		backgroundColor: isDarkMode ? '#0f172a' : '#ffffff',
																		borderWidth: 1,
																		borderColor: theme.border,
																		borderRadius: 6,
																		paddingHorizontal: 12,
																		paddingVertical: 8,
																		color: theme.textPrimary,
																		fontSize: 13
																	}}
																	placeholder="Email address"
																	placeholderTextColor={theme.textMuted}
																	value={newAdminEmail}
																	onChangeText={setNewAdminEmail}
																	keyboardType="email-address"
																	autoCapitalize="none"
																/>
																<TextInput
																	style={{
																		flex: 1,
																		minWidth: 120,
																		backgroundColor: isDarkMode ? '#0f172a' : '#ffffff',
																		borderWidth: 1,
																		borderColor: theme.border,
																		borderRadius: 6,
																		paddingHorizontal: 12,
																		paddingVertical: 8,
																		color: theme.textPrimary,
																		fontSize: 13
																	}}
																	placeholder="Name"
																	placeholderTextColor={theme.textMuted}
																	value={newAdminName}
																	onChangeText={setNewAdminName}
																/>
																<button
																	onClick={addAdminRecipient}
																	disabled={adminNotificationSaving || !newAdminEmail}
																	style={{
																		...styles.ffToggleBtn,
																		...styles.ffToggleBtnOn,
																		opacity: (adminNotificationSaving || !newAdminEmail) ? 0.5 : 1,
																		padding: '6px 12px'
																	}}
																>
																	+ Add
																</button>
															</View>
														</View>

														{/* Recipients List */}
														{adminNotificationConfig.recipients?.length === 0 ? (
															<View style={[styles.ffCard, { backgroundColor: theme.ffItemBg, borderColor: theme.ffItemBorder, justifyContent: 'center', alignItems: 'center', padding: 24 }]}>
																<Ionicons name="mail-outline" size={28} color={theme.textMuted} style={{ opacity: 0.4, marginBottom: 8 }} />
																<Text style={{ color: theme.textMuted, fontSize: 12 }}>No recipients configured</Text>
															</View>
														) : (
															adminNotificationConfig.recipients?.map((recipient) => (
																<View key={recipient.email} style={[styles.ffCard, { backgroundColor: theme.ffItemBg, borderColor: theme.ffItemBorder }]}>
																	<View style={styles.ffCardContent}>
																		<View style={styles.ffCardInfo}>
																			<View style={styles.ffCardTitleRow}>
																				<Text style={[styles.ffCardTitle, { color: theme.textPrimary }]}>
																					{recipient.name || recipient.email.split('@')[0]}
																				</Text>
																				{recipient.enabled && (
																					<View style={[styles.ffImpactBadge, { backgroundColor: '#22c55e20', borderColor: '#22c55e' }]}>
																						<Text style={[styles.ffImpactText, { color: '#22c55e' }]}>ACTIVE</Text>
																					</View>
																				)}
																			</View>
																			<Text style={[styles.ffCardDesc, { color: theme.textMuted }]}>{recipient.email}</Text>
																		</View>
																		<View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
																			<button
																				onClick={() => toggleAdminRecipient(recipient.email)}
																				disabled={adminNotificationSaving}
																				style={{
																					...styles.ffToggleBtn,
																					...(recipient.enabled ? styles.ffToggleBtnOn : styles.ffToggleBtnOff),
																					opacity: adminNotificationSaving ? 0.5 : 1
																				}}
																			>
																				{recipient.enabled ? 'ON' : 'OFF'}
																			</button>
																			<button
																				onClick={() => {
																					if (window.confirm(`Remove ${recipient.email}?`)) {
																						removeAdminRecipient(recipient.email);
																					}
																				}}
																				style={{
																					...styles.ffToggleBtn,
																					backgroundColor: '#fee2e2',
																					color: '#dc2626'
																				}}
																			>
																				✕
																			</button>
																		</View>
																	</View>
																</View>
															))
														)}
													</View>
												</View>
											</View>

											{/* Testing */}
											<View style={[styles.ffCategorySection, { backgroundColor: theme.ffCardBg, borderColor: theme.ffCardBorder }]}>
												<View style={styles.ffCategoryHeaderClickable}>
													<View style={styles.ffCategoryHeaderLeft}>
														<Ionicons name="flask" size={16} color="#a855f7" />
														<Text style={[styles.ffCategoryTitle, { color: theme.textPrimary }]}>Testing</Text>
													</View>
												</View>
												
												<View style={styles.ffCollapsibleContent}>
													<Text style={[styles.ffCategoryDesc, { color: theme.textMuted, borderBottomColor: theme.border }]}>
														Verify your notification setup
													</Text>
													<View style={styles.ffCategoryFlags}>
														<View style={[styles.ffCard, { backgroundColor: theme.ffItemBg, borderColor: theme.ffItemBorder }]}>
															<View style={styles.ffCardContent}>
																<View style={styles.ffCardInfo}>
																	<View style={styles.ffCardTitleRow}>
																		<Text style={[styles.ffCardTitle, { color: theme.textPrimary }]}>Send Test Email</Text>
																	</View>
																	<Text style={[styles.ffCardDesc, { color: theme.textMuted }]}>
																		Send a test notification to all enabled recipients
																	</Text>
																</View>
																<button
																	onClick={sendTestAdminNotification}
																	disabled={adminNotificationSaving || (adminNotificationConfig.recipients?.filter(r => r.enabled).length || 0) === 0}
																	style={{
																		...styles.ffToggleBtn,
																		...styles.ffToggleBtnOnProd,
																		opacity: (adminNotificationSaving || (adminNotificationConfig.recipients?.filter(r => r.enabled).length || 0) === 0) ? 0.5 : 1,
																		padding: '6px 12px'
																	}}
																>
																	Send Test
																</button>
															</View>
														</View>
													</View>
												</View>
											</View>
								</View>
									))}

								{/* ─── Testing Tools Section ─── */}
								<View style={{ borderTopWidth: 1, borderTopColor: theme.border, marginTop: 20, paddingTop: 8 }}>
									<button
										onClick={() => setNotifSectionOpen(s => ({ ...s, testEmails: !s.testEmails }))}
										style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 10, background: 'none', border: 'none', cursor: 'pointer', padding: '16px 0', width: '100%', textAlign: 'left' }}
									>
										<Ionicons name={notifSectionOpen.testEmails ? 'chevron-down' : 'chevron-forward'} size={16} color={theme.textMuted} />
										<View style={{ flex: 1 }}>
											<Text style={[styles.ffMainTitle, { color: theme.textPrimary, marginBottom: 2 }]}>Testing Tools</Text>
											<Text style={[styles.ffSubtitle, { color: theme.textMuted, marginTop: 0 }]}>
												Send test emails and SMS messages to verify your templates and delivery.
											</Text>
										</View>
									</button>
								</View>

								{notifSectionOpen.testEmails && <View style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 16, width: '100%' }}>
										{/* Test Emails */}
										<View style={[styles.ffCategorySection, { backgroundColor: theme.ffCardBg, borderColor: theme.ffCardBorder, flex: 1, width: isMobile ? '100%' : 'auto' }]}>
											<View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 16 }}>
												<Ionicons name="mail" size={18} color="#4c956c" />
												<Text style={{ color: theme.textPrimary, fontSize: 15, fontWeight: '700' }}>Test Emails</Text>
											</View>
											<View id="email-test-form" style={styles.emailTestForm}>
												<View style={styles.formGroup}>
													<Text style={[styles.formLabel, { color: theme.textSecondary }]}>Recipient Email:</Text>
													<TextInput
														type="email"
														value={recipientEmail}
														placeholder="Enter recipient's email address"
														onChangeText={setRecipientEmail}
														style={[styles.input, { backgroundColor: theme.bgCard, color: theme.textPrimary, borderColor: theme.border }]}
													/>
												</View>
												<View style={styles.formGroup}>
													<Text style={[styles.formLabel, { color: theme.textSecondary }]}>Email Type:</Text>
													<select
														value={emailType}
														onChange={(e) => setEmailType(e.target.value)}
														style={{ ...styles.input, backgroundColor: theme.bgCard, color: theme.textPrimary, borderColor: theme.border }}
													>
														{Object.values(emailDefinitions).map((email) => {
															const isEnabled = emailSettings[email.id]?.enabled !== false;
															return (
																<option key={email.id} style={styles.option} value={email.id}>
																	{email.name} ({email.category}){!isEnabled ? ' [OFF]' : ''}
																</option>
															);
														})}
													</select>
												</View>
												{/* Conditional input fields based on email type */}
												<View style={styles.emailParamsContainer}>
													{emailType === "genericWelcome" && (
														<View style={styles.formGroup}>
															<Text style={[styles.formLabel, { color: theme.textSecondary }]}>First Name:</Text>
															<TextInput
																placeholder="First Name"
																onChangeText={(value) => handleEmailParamChange("firstName", value)}
																style={[styles.input, { backgroundColor: theme.bgCard, color: theme.textPrimary, borderColor: theme.border }]}
															/>
														</View>
													)}
													{emailType === "graduateWelcome" && (
														<View style={styles.formGroup}>
															<Text style={[styles.formLabel, { color: theme.textSecondary }]}>First Name:</Text>
															<TextInput
																placeholder="First Name"
																onChangeText={(value) => handleEmailParamChange("firstName", value)}
																style={[styles.input, { backgroundColor: theme.bgCard, color: theme.textPrimary, borderColor: theme.border }]}
															/>
														</View>
													)}
													{emailType === "contribution" && (
														<>
															<View style={styles.formGroup}>
																<Text style={[styles.formLabel, { color: theme.textSecondary }]}>Contribution Amount:</Text>
																<TextInput placeholder="Contribution Amount" onChangeText={(value) => handleEmailParamChange("contributionAmount", value)} style={[styles.input, { backgroundColor: theme.bgCard, color: theme.textPrimary, borderColor: theme.border }]} />
															</View>
															<View style={styles.formGroup}>
																<Text style={[styles.formLabel, { color: theme.textSecondary }]}>Contributor Name:</Text>
																<TextInput placeholder="Contributor Name" onChangeText={(value) => handleEmailParamChange("contributorName", value)} style={[styles.input, { backgroundColor: theme.bgCard, color: theme.textPrimary, borderColor: theme.border }]} />
															</View>
														</>
													)}
													{emailType === "purchaseConfirmation" && (
														<>
															<View style={styles.formGroup}>
																<Text style={[styles.formLabel, { color: theme.textSecondary }]}>Purchase Amount:</Text>
																<TextInput placeholder="Purchase Amount" onChangeText={(value) => handleEmailParamChange("purchaseAmount", value)} style={[styles.input, { backgroundColor: theme.bgCard, color: theme.textPrimary, borderColor: theme.border }]} />
															</View>
															<View style={styles.formGroup}>
																<Text style={[styles.formLabel, { color: theme.textSecondary }]}>Product Name:</Text>
																<TextInput placeholder="Product Name" onChangeText={(value) => handleEmailParamChange("productName", value)} style={[styles.input, { backgroundColor: theme.bgCard, color: theme.textPrimary, borderColor: theme.border }]} />
															</View>
														</>
													)}
													{emailType === "yearbookMessage" && (
														<>
															<View style={styles.formGroup}>
																<Text style={[styles.formLabel, { color: theme.textSecondary }]}>Yearbook Message:</Text>
																<TextInput placeholder="Yearbook Message" onChangeText={(value) => handleEmailParamChange("yearbookMessage", value)} style={[styles.input, { backgroundColor: theme.bgCard, color: theme.textPrimary, borderColor: theme.border }]} />
															</View>
															<View style={styles.formGroup}>
																<Text style={[styles.formLabel, { color: theme.textSecondary }]}>Recipient Name:</Text>
																<TextInput placeholder="Recipient Name" onChangeText={(value) => handleEmailParamChange("recipientName", value)} style={[styles.input, { backgroundColor: theme.bgCard, color: theme.textPrimary, borderColor: theme.border }]} />
															</View>
														</>
													)}
													{emailType === "accessRequest" && (
														<>
															<View style={styles.formGroup}>
																<Text style={[styles.formLabel, { color: theme.textSecondary }]}>Access Request:</Text>
																<TextInput placeholder="Access Request" onChangeText={(value) => handleEmailParamChange("accessRequest", value)} style={[styles.input, { backgroundColor: theme.bgCard, color: theme.textPrimary, borderColor: theme.border }]} />
															</View>
															<View style={styles.formGroup}>
																<Text style={[styles.formLabel, { color: theme.textSecondary }]}>Recipient Name:</Text>
																<TextInput placeholder="Recipient Name" onChangeText={(value) => handleEmailParamChange("recipientName", value)} style={[styles.input, { backgroundColor: theme.bgCard, color: theme.textPrimary, borderColor: theme.border }]} />
															</View>
														</>
													)}
													{emailType === "goalReached" && (
														<>
															<View style={styles.formGroup}>
																<Text style={[styles.formLabel, { color: theme.textSecondary }]}>Graduation Fund Reached:</Text>
																<TextInput placeholder="Graduation Fund Reached" onChangeText={(value) => handleEmailParamChange("goalReached", value)} style={[styles.input, { backgroundColor: theme.bgCard, color: theme.textPrimary, borderColor: theme.border }]} />
															</View>
															<View style={styles.formGroup}>
																<Text style={[styles.formLabel, { color: theme.textSecondary }]}>Recipient Name:</Text>
																<TextInput placeholder="Recipient Name" onChangeText={(value) => handleEmailParamChange("recipientName", value)} style={[styles.input, { backgroundColor: theme.bgCard, color: theme.textPrimary, borderColor: theme.border }]} />
															</View>
														</>
													)}
													{emailType === "ecard" && (
														<>
															<View style={styles.formGroup}>
																<Text style={[styles.formLabel, { color: theme.textSecondary }]}>E-Card Message:</Text>
																<TextInput placeholder="E-Card Message" onChangeText={(value) => handleEmailParamChange("ecardMessage", value)} style={[styles.input, { backgroundColor: theme.bgCard, color: theme.textPrimary, borderColor: theme.border }]} />
															</View>
															<View style={styles.formGroup}>
																<Text style={[styles.formLabel, { color: theme.textSecondary }]}>Recipient Name:</Text>
																<TextInput placeholder="Recipient Name" onChangeText={(value) => handleEmailParamChange("recipientName", value)} style={[styles.input, { backgroundColor: theme.bgCard, color: theme.textPrimary, borderColor: theme.border }]} />
															</View>
														</>
													)}
													{emailType === "invitation" && (
														<>
															<View style={styles.formGroup}>
																<Text style={[styles.formLabel, { color: theme.textSecondary }]}>Invitation Message:</Text>
																<TextInput placeholder="Invitation Message" onChangeText={(value) => handleEmailParamChange("invitationMessage", value)} style={[styles.input, { backgroundColor: theme.bgCard, color: theme.textPrimary, borderColor: theme.border }]} />
															</View>
															<View style={styles.formGroup}>
																<Text style={[styles.formLabel, { color: theme.textSecondary }]}>Recipient Name:</Text>
																<TextInput placeholder="Recipient Name" onChangeText={(value) => handleEmailParamChange("recipientName", value)} style={[styles.input, { backgroundColor: theme.bgCard, color: theme.textPrimary, borderColor: theme.border }]} />
															</View>
														</>
													)}
													{emailType === "mismatchedUsersAlert" && (
														<>
															<View style={styles.formGroup}>
																<Text style={[styles.formLabel, { color: theme.textSecondary }]}>Mismatched Users Alert:</Text>
																<TextInput placeholder="Mismatched Users Alert" onChangeText={(value) => handleEmailParamChange("mismatchedUsersAlert", value)} style={[styles.input, { backgroundColor: theme.bgCard, color: theme.textPrimary, borderColor: theme.border }]} />
															</View>
															<View style={styles.formGroup}>
																<Text style={[styles.formLabel, { color: theme.textSecondary }]}>Recipient Name:</Text>
																<TextInput placeholder="Recipient Name" onChangeText={(value) => handleEmailParamChange("recipientName", value)} style={[styles.input, { backgroundColor: theme.bgCard, color: theme.textPrimary, borderColor: theme.border }]} />
															</View>
														</>
													)}
													{emailType === "contact" && (
														<View style={styles.emailParamsContainer}>
															<View style={styles.formGroup}>
																<Text style={[styles.formLabel, { color: theme.textSecondary }]}>Name:</Text>
																<TextInput placeholder="Test User Name" onChangeText={(value) => handleEmailParamChange("name", value)} style={[styles.input, { backgroundColor: theme.bgCard, color: theme.textPrimary, borderColor: theme.border }]} />
															</View>
															<View style={styles.formGroup}>
																<Text style={[styles.formLabel, { color: theme.textSecondary }]}>Subject:</Text>
																<TextInput placeholder="Test Subject" onChangeText={(value) => handleEmailParamChange("subject", value)} style={[styles.input, { backgroundColor: theme.bgCard, color: theme.textPrimary, borderColor: theme.border }]} />
															</View>
															<View style={styles.formGroup}>
																<Text style={[styles.formLabel, { color: theme.textSecondary }]}>Message:</Text>
																<TextInput placeholder="Test Message" onChangeText={(value) => handleEmailParamChange("message", value)} style={[styles.input, { backgroundColor: theme.bgCard, color: theme.textPrimary, borderColor: theme.border }]} multiline />
															</View>
															<View style={styles.formGroup}>
																<Text style={[styles.formLabel, { color: theme.textSecondary }]}>Phone:</Text>
																<TextInput placeholder="Test Phone Number" onChangeText={(value) => handleEmailParamChange("phone", value)} style={[styles.input, { backgroundColor: theme.bgCard, color: theme.textPrimary, borderColor: theme.border }]} />
															</View>
														</View>
													)}
												</View>
											{/* Single send + batch send row */}
											<View style={{ flexDirection: 'row', gap: 10, marginTop: 16, flexWrap: 'wrap', alignItems: 'center' }}>
												<button
													type="button"
													onClick={handleTestEmailSubmit}
													style={{ ...styles.ffToggleBtn, ...styles.ffToggleBtnOn, padding: '10px 20px' }}
												>
													Send This Email
												</button>
												<button
													type="button"
													onClick={handleBatchTestAllEmails}
													disabled={batchTestRunning}
													style={{ ...styles.ffToggleBtn, ...styles.ffToggleBtnOn, padding: '10px 20px', backgroundColor: '#7c3aed', opacity: batchTestRunning ? 0.6 : 1 }}
												>
													{batchTestRunning ? '⏳ Sending batch…' : '⚡ Send All Email Types'}
												</button>
											</View>

											{/* Single test result */}
											{emailTestResult && (
												<View style={{ backgroundColor: emailTestResult.startsWith('✅') ? '#f0fdf4' : '#fef2f2', borderRadius: 8, padding: 10, marginTop: 10, borderWidth: 1, borderColor: emailTestResult.startsWith('✅') ? '#86efac' : '#fca5a5' }}>
													<Text style={{ color: emailTestResult.startsWith('✅') ? '#166534' : '#991b1b', fontSize: 13 }}>{emailTestResult}</Text>
												</View>
											)}

											{/* Batch test live progress */}
											{batchTestProgress.length > 0 && (
												<View style={{ marginTop: 14, gap: 4 }}>
													<Text style={{ color: theme.textMuted, fontSize: 11, fontWeight: '700', textTransform: 'uppercase', marginBottom: 6 }}>
														Batch Progress — {recipientEmail || 'trevorhawkins@dsignable.com'}
													</Text>
													{batchTestProgress.map(({ id, name, status, error }) => {
														const icon = status === 'pending' ? '○' : status === 'sending' ? '⏳' : status === 'success' ? '✅' : '❌';
														const color = status === 'success' ? '#166534' : status === 'error' ? '#991b1b' : status === 'sending' ? '#1e40af' : theme.textMuted;
														const bg = status === 'success' ? '#f0fdf4' : status === 'error' ? '#fef2f2' : status === 'sending' ? '#eff6ff' : 'transparent';
														return (
															<View key={id} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: bg, borderRadius: 6, paddingVertical: 4, paddingHorizontal: 8 }}>
																<Text style={{ color, fontSize: 12, width: 16 }}>{icon}</Text>
																<Text style={{ color, fontSize: 12, flex: 1 }}>{name}</Text>
																{error && <Text style={{ color: '#991b1b', fontSize: 11 }} numberOfLines={1}>{error}</Text>}
															</View>
														);
													})}
													{!batchTestRunning && (
														<Text style={{ color: theme.textMuted, fontSize: 11, marginTop: 6, textAlign: 'right' }}>
															{batchTestProgress.filter(p => p.status === 'success').length}/{batchTestProgress.length} sent successfully
														</Text>
													)}
												</View>
											)}
										</View>
									</View>

										{/* Test SMS */}
										<View style={[styles.ffCategorySection, { backgroundColor: theme.ffCardBg, borderColor: theme.ffCardBorder, flex: 1, width: isMobile ? '100%' : 'auto' }]}>
											<View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 16 }}>
												<Ionicons name="chatbubble-ellipses" size={18} color="#4c956c" />
												<Text style={{ color: theme.textPrimary, fontSize: 15, fontWeight: '700' }}>Test SMS</Text>
											</View>
											<View style={styles.smsTestForm}>
												<View style={styles.formGroup}>
													<Text style={[styles.formLabel, { color: theme.textSecondary }]}>Test Phone Number:</Text>
													<TextInput
														placeholder="Enter test phone number"
														value={testPhone}
														onChangeText={setTestPhone}
														style={[styles.input, { backgroundColor: theme.bgCard, color: theme.textPrimary, borderColor: theme.border }]}
													/>
												</View>
												<View style={styles.formGroup}>
													<Text style={[styles.formLabel, { color: theme.textSecondary }]}>SMS Type:</Text>
													<select
														value={smsType}
														onChange={(e) => setSmsType(e.target.value)}
														style={{ ...styles.input, backgroundColor: theme.bgCard, color: theme.textPrimary, borderColor: theme.border }}
													>
														<option value="shareAnnouncement">Share Announcement (Email + SMS)</option>
														<option value="eventReminder">Event Reminder</option>
														<option value="welcome">Welcome (generic test)</option>
														<option value="invitation">Invitation (generic test)</option>
													</select>
												</View>
												<button
													onClick={async () => {
														try {
															const response = await fetch(`${SERVER_URL}/test-sms`, {
																method: "POST",
																headers: { "Content-Type": "application/json" },
																body: JSON.stringify({ phone: testPhone, smsType }),
															});
															const result = await response.text();
															setSmsTestResult(result);
														} catch (error) {
															setSmsTestResult(`Error: ${error.message}`);
														}
													}}
													style={{ ...styles.ffToggleBtn, ...styles.ffToggleBtnOn, padding: '10px 24px', marginTop: 16 }}
												>
													Send Test SMS
												</button>
												{smsTestResult && (
													<View style={[styles.testResultContainer, { backgroundColor: theme.bgCard, marginTop: 12 }]}>
														<Text style={[styles.testResultText, { color: theme.textPrimary }]}>{smsTestResult}</Text>
													</View>
												)}
											</View>
									</View>
								</View>
								}

						{/* ─── Delivery Activity (Email + SMS) ─── */}
							<View style={{ borderTopWidth: 1, borderTopColor: theme.border, marginTop: 20, paddingTop: 8 }}>
								{/* Header row */}
								<View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
									<button
										onClick={() => setNotifSectionOpen(s => ({ ...s, emailActivity: !s.emailActivity }))}
										style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 10, background: 'none', border: 'none', cursor: 'pointer', padding: '16px 0', flex: 1, textAlign: 'left' }}
									>
										<Ionicons name={notifSectionOpen.emailActivity ? 'chevron-down' : 'chevron-forward'} size={16} color={theme.textMuted} />
										<View style={{ flex: 1 }}>
											<Text style={[styles.ffMainTitle, { color: theme.textPrimary, marginBottom: 2 }]}>Delivery Activity</Text>
											<Text style={[styles.ffSubtitle, { color: theme.textMuted, marginTop: 0 }]}>
												MyTassel email &amp; SMS delivery events.
											</Text>
										</View>
									</button>
									<button
										onClick={() => { fetchEmailActivity(); fetchSmsActivity(); }}
										disabled={emailActivityLoading && smsActivityLoading}
										style={{ ...styles.ffToggleBtn, ...styles.ffToggleBtnOn, padding: '6px 14px', fontSize: 12, opacity: (emailActivityLoading && smsActivityLoading) ? 0.6 : 1, flexShrink: 0 }}
									>
										{(emailActivityLoading || smsActivityLoading) ? '⏳ Loading…' : '↻ Refresh'}
									</button>
								</View>

								{/* Expanded content — two-column: Email (left) + SMS (right) */}
								{notifSectionOpen.emailActivity && <>
								<div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 20, paddingTop: 8 }}>
								{/* ──── LEFT COLUMN: Email Activity ──── */}
								<div>
									<div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
										<Ionicons name="mail-outline" size={15} color="#3b82f6" />
										<span style={{ fontSize: 13, fontWeight: 700, color: theme.textPrimary }}>Email Activity</span>
										<span style={{ fontSize: 11, color: theme.textMuted }}>({emailActivity.filter(m => m.fromEmail && m.fromEmail.includes('mytassel.com')).length})</span>
									</div>
									{/* Email search */}
									<input
										type="text"
										placeholder="Search by recipient or subject…"
										value={emailActivitySearch}
										onChange={e => { setEmailActivitySearch(e.target.value); setEmailActivityPage(1); }}
										style={{ width: '100%', padding: '7px 10px', borderRadius: 8, border: `1px solid ${theme.border}`, backgroundColor: theme.ffCardBg, color: theme.textPrimary, fontSize: 12, marginBottom: 8, boxSizing: 'border-box' }}
									/>
									{/* Email filter pills */}
									<View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap', paddingBottom: 12, paddingTop: 4 }}>
										{['all', 'delivered', 'open', 'click', 'bounce', 'deferred', 'dropped'].map(f => (
											<button
												key={f}
												onClick={() => { setEmailActivityFilter(f); setEmailActivityPage(1); }}
												style={{
													padding: '4px 12px',
													borderRadius: 20,
													border: `1px solid ${emailActivityFilter === f ? theme.primary : theme.border}`,
													backgroundColor: emailActivityFilter === f ? theme.primary : 'transparent',
													color: emailActivityFilter === f ? '#fff' : theme.textMuted,
													cursor: 'pointer',
													fontSize: 12,
													fontWeight: '600',
													textTransform: 'capitalize',
												}}
											>{f}</button>
										))}
									</View>

									{emailActivityError && (
										<View style={[styles.ffCategorySection, { backgroundColor: '#fff3cd', borderColor: '#ffc107', marginBottom: 16 }]}>
											<Text style={{ color: '#856404', fontSize: 13, padding: 12 }}>
												⚠️ {emailActivityError}
												{emailActivityError.includes('permission') && (
													<Text style={{ fontWeight: '700' }}>{'\n\n'}Go to SendGrid → Settings → API Keys → Edit your key → Enable "Email Activity" → Read Access.</Text>
												)}
											</Text>
										</View>
									)}

									{emailActivity.length === 0 && !emailActivityLoading && !emailActivityError && (
										<View style={[styles.ffCategorySection, { backgroundColor: theme.ffCardBg, borderColor: theme.ffCardBorder, marginBottom: 16 }]}>
											<Text style={{ color: theme.textMuted, fontSize: 13, padding: 16, textAlign: 'center' }}>
												No activity loaded yet. Click Refresh to pull the latest events.
											</Text>
										</View>
									)}

									{emailActivity.length > 0 && (() => {
										const statusColors = {
											delivered: '#22c55e',
											open: '#3b82f6',
											click: '#8b5cf6',
											bounce: '#ef4444',
											deferred: '#f59e0b',
											dropped: '#6b7280',
											processed: '#94a3b8',
										};
										const mytasselOnly = emailActivity.filter(m => m.fromEmail && m.fromEmail.includes('mytassel.com'));
										const searchLower = emailActivitySearch.toLowerCase();
										const searched = searchLower ? mytasselOnly.filter(m => (m.toEmail || '').toLowerCase().includes(searchLower) || (m.subject || '').toLowerCase().includes(searchLower)) : mytasselOnly;
										const filtered = emailActivityFilter === 'all'
											? searched
											: searched.filter(m => m.status === emailActivityFilter);
										const totalPages = Math.max(1, Math.ceil(filtered.length / EMAIL_ACTIVITY_PER_PAGE));
										const safePage = Math.min(emailActivityPage, totalPages);
										const pageStart = (safePage - 1) * EMAIL_ACTIVITY_PER_PAGE;
										const pageSlice = filtered.slice(pageStart, pageStart + EMAIL_ACTIVITY_PER_PAGE);

										return (
											<View style={{ marginBottom: 20, gap: 16 }}>
												{filtered.length === 0 ? (
													<View style={[styles.ffCategorySection, { backgroundColor: theme.ffCardBg, borderColor: theme.ffCardBorder }]}>
														<Text style={{ color: theme.textMuted, fontSize: 13, padding: 16, textAlign: 'center' }}>No MyTassel events match the "{emailActivityFilter}" filter.</Text>
													</View>
												) : (
													<>
														{/* Email card list */}
														<div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
															{pageSlice.map((msg, idx) => {
																const color = statusColors[msg.status] || '#94a3b8';
																const time = msg.lastEventTime ? new Date(msg.lastEventTime).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
																const isExpanded = expandedActivityMsgId === msg.msgId;
																const detail = activityMsgDetails[msg.msgId];
																const detailLoading = activityMsgDetailLoading.has(msg.msgId);
																return (
																	<div key={msg.msgId || idx} style={{ borderRadius: 10, border: `1px solid ${isExpanded ? (color + '60') : theme.border}`, backgroundColor: theme.ffCardBg, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
																		{/* Card header */}
																		<button
																			onClick={() => {
																				const newId = isExpanded ? null : msg.msgId;
																				setExpandedActivityMsgId(newId);
																				if (newId) fetchEmailMessageDetail(newId);
																			}}
																			style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 14, background: 'none', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'left' }}
																		>
																			{/* Top: status + time */}
																			<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
																				<span style={{ backgroundColor: color + '22', borderRadius: 12, padding: '3px 10px', color, fontSize: 11, fontWeight: 700, textTransform: 'capitalize' }}>{msg.status || '—'}</span>
																				<span style={{ color: theme.textMuted, fontSize: 11 }}>{time}</span>
																			</div>
																			{/* To */}
																			<div style={{ fontSize: 13, fontWeight: 600, color: theme.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>{msg.toEmail || '—'}</div>
																			{/* Subject */}
																			<div style={{ fontSize: 12, color: theme.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>{msg.subject || '—'}</div>
																			{/* Footer: opens/clicks + chevron */}
																			<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', marginTop: 2 }}>
																				<div style={{ display: 'flex', gap: 12 }}>
																					<span style={{ color: theme.textMuted, fontSize: 11 }}>Opens: <span style={{ fontWeight: 600, color: theme.textPrimary }}>{msg.opensCount || 0}</span></span>
																					<span style={{ color: theme.textMuted, fontSize: 11 }}>Clicks: <span style={{ fontWeight: 600, color: theme.textPrimary }}>{msg.clicksCount || 0}</span></span>
																				</div>
																				<Ionicons name={isExpanded ? 'chevron-up' : 'chevron-down'} size={14} color={theme.textMuted} />
																			</div>
																		</button>

																		{/* Expanded detail */}
																		{isExpanded && (
																			<View style={{ backgroundColor: theme.bgSection, borderTopWidth: 1, borderTopColor: theme.border, padding: 14, gap: 12 }}>
																				<View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 14 }}>
																					<View style={{ minWidth: 100 }}>
																						<Text style={{ color: theme.textMuted, fontSize: 10, fontWeight: '700', textTransform: 'uppercase', marginBottom: 2 }}>From</Text>
																						<Text style={{ color: theme.textPrimary, fontSize: 12 }}>{msg.fromEmail || '—'}</Text>
																					</View>
																					<View style={{ flex: 1 }}>
																						<Text style={{ color: theme.textMuted, fontSize: 10, fontWeight: '700', textTransform: 'uppercase', marginBottom: 2 }}>Subject</Text>
																						<Text style={{ color: theme.textPrimary, fontSize: 12 }}>{msg.subject || '—'}</Text>
																					</View>
																				</View>

																				{detailLoading && (
																					<Text style={{ color: theme.textMuted, fontSize: 12 }}>Loading email content…</Text>
																				)}

																				{detail && !detailLoading && (() => {
																					const htmlBody = detail?.content?.find?.(c => c.type === 'text/html')?.value
																						|| detail?.body?.html_body
																						|| detail?.html_body;
																					const plainBody = detail?.content?.find?.(c => c.type === 'text/plain')?.value
																						|| detail?.body?.plain_body
																						|| detail?.plain_body;
																					const events = detail?.events || [];

																					return (
																						<View style={{ gap: 12 }}>
																							{events.length > 0 && (
																								<View>
																									<Text style={{ color: theme.textMuted, fontSize: 10, fontWeight: '700', textTransform: 'uppercase', marginBottom: 6 }}>Event Timeline</Text>
																									<View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
																										{events.map((ev, ei) => {
																											const evColor = statusColors[ev.event_name] || '#94a3b8';
																											return (
																												<View key={ei} style={{ backgroundColor: evColor + '18', borderRadius: 6, paddingHorizontal: 8, paddingVertical: 4, borderWidth: 1, borderColor: evColor + '40' }}>
																													<Text style={{ color: evColor, fontSize: 10, fontWeight: '700', textTransform: 'capitalize' }}>{ev.event_name}</Text>
																													<Text style={{ color: theme.textMuted, fontSize: 9 }}>{ev.processed ? new Date(ev.processed).toLocaleTimeString() : ''}</Text>
																												</View>
																											);
																										})}
																									</View>
																								</View>
																							)}

																							{(htmlBody || plainBody) && (
																								<View>
																									<Text style={{ color: theme.textMuted, fontSize: 10, fontWeight: '700', textTransform: 'uppercase', marginBottom: 6 }}>Email Body</Text>
																									{htmlBody ? (
																										<View style={{ borderWidth: 1, borderColor: theme.border, borderRadius: 8, overflow: 'hidden', maxHeight: 300 }}>
																											<div
																												style={{ padding: 12, overflowY: 'auto', maxHeight: 300, background: '#fff', fontSize: 12, lineHeight: 1.5 }}
																												dangerouslySetInnerHTML={{ __html: htmlBody }}
																											/>
																										</View>
																									) : (
																										<View style={{ borderWidth: 1, borderColor: theme.border, borderRadius: 8, padding: 12, maxHeight: 200, overflow: 'hidden' }}>
																											<Text style={{ color: theme.textPrimary, fontSize: 12, lineHeight: 18 }}>{plainBody}</Text>
																										</View>
																									)}
																								</View>
																							)}

																							{!htmlBody && !plainBody && (
																								<Text style={{ color: theme.textMuted, fontSize: 12 }}>Email body not available for this message.</Text>
																							)}
																						</View>
																					);
																				})()}

																				{!detail && !detailLoading && (
																					<Text style={{ color: theme.textMuted, fontSize: 12 }}>Email body could not be loaded.</Text>
																				)}
																			</View>
																		)}
																	</div>
																);
															})}
														</div>

														{/* Pagination controls */}
														<View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingTop: 4 }}>
															<Text style={{ color: theme.textMuted, fontSize: 12 }}>
																Showing {pageStart + 1}–{Math.min(pageStart + EMAIL_ACTIVITY_PER_PAGE, filtered.length)} of {filtered.length} events
															</Text>
															{totalPages > 1 && (
																<View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
																	<button
																		onClick={() => setEmailActivityPage(p => Math.max(1, p - 1))}
																		disabled={safePage <= 1}
																		style={{ padding: '5px 10px', borderRadius: 6, border: `1px solid ${theme.border}`, background: safePage <= 1 ? 'transparent' : theme.bgSection, color: safePage <= 1 ? theme.textMuted : theme.textPrimary, cursor: safePage <= 1 ? 'default' : 'pointer', fontSize: 12, opacity: safePage <= 1 ? 0.4 : 1 }}
																	>← Prev</button>
																	{Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
																		<button
																			key={p}
																			onClick={() => setEmailActivityPage(p)}
																			style={{ padding: '5px 9px', borderRadius: 6, border: `1px solid ${p === safePage ? theme.primary : theme.border}`, background: p === safePage ? theme.primary : 'transparent', color: p === safePage ? '#fff' : theme.textMuted, cursor: 'pointer', fontSize: 12, fontWeight: p === safePage ? 700 : 400 }}
																		>{p}</button>
																	))}
																	<button
																		onClick={() => setEmailActivityPage(p => Math.min(totalPages, p + 1))}
																		disabled={safePage >= totalPages}
																		style={{ padding: '5px 10px', borderRadius: 6, border: `1px solid ${theme.border}`, background: safePage >= totalPages ? 'transparent' : theme.bgSection, color: safePage >= totalPages ? theme.textMuted : theme.textPrimary, cursor: safePage >= totalPages ? 'default' : 'pointer', fontSize: 12, opacity: safePage >= totalPages ? 0.4 : 1 }}
																	>Next →</button>
																</View>
															)}
														</View>
													</>
												)}
											</View>
										);
									})()}
								</div>{/* end left column (Email) */}

								{/* ──── RIGHT COLUMN: SMS Activity ──── */}
								<div>
									<div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
										<Ionicons name="chatbubble-outline" size={15} color="#f59e0b" />
										<span style={{ fontSize: 13, fontWeight: 700, color: theme.textPrimary }}>SMS Activity</span>
										<span style={{ fontSize: 11, color: theme.textMuted }}>({smsActivity.length})</span>
									</div>
									{/* SMS search */}
									<input
										type="text"
										placeholder="Search by phone or message…"
										value={smsActivitySearch}
										onChange={e => { setSmsActivitySearch(e.target.value); setSmsActivityPage(1); }}
										style={{ width: '100%', padding: '7px 10px', borderRadius: 8, border: `1px solid ${theme.border}`, backgroundColor: theme.ffCardBg, color: theme.textPrimary, fontSize: 12, marginBottom: 8, boxSizing: 'border-box' }}
									/>
									{/* SMS filter pills */}
									<View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap', paddingBottom: 12 }}>
										{['all', 'delivered', 'sent', 'queued', 'failed', 'undelivered'].map(f => (
											<button key={f} onClick={() => { setSmsActivityFilter(f); setSmsActivityPage(1); }} style={{
												padding: '4px 10px', borderRadius: 20,
												border: `1px solid ${smsActivityFilter === f ? '#f59e0b' : theme.border}`,
												backgroundColor: smsActivityFilter === f ? '#f59e0b' : 'transparent',
												color: smsActivityFilter === f ? '#fff' : theme.textMuted,
												cursor: 'pointer', fontSize: 11, fontWeight: '600', textTransform: 'capitalize'
											}}>{f}</button>
										))}
									</View>

									{smsActivityError && (
										<View style={[styles.ffCategorySection, { backgroundColor: '#fff3cd', borderColor: '#ffc107', marginBottom: 12 }]}>
											<Text style={{ color: '#856404', fontSize: 12, padding: 10 }}>⚠️ {smsActivityError}</Text>
										</View>
									)}

									{smsActivity.length === 0 && !smsActivityLoading && !smsActivityError && (
										<View style={[styles.ffCategorySection, { backgroundColor: theme.ffCardBg, borderColor: theme.ffCardBorder, marginBottom: 12 }]}>
											<Text style={{ color: theme.textMuted, fontSize: 12, padding: 14, textAlign: 'center' }}>No SMS activity loaded yet. Click Refresh.</Text>
										</View>
									)}

									{smsActivityLoading && smsActivity.length === 0 && (
										<Text style={{ color: theme.textMuted, fontSize: 12, textAlign: 'center', padding: 20 }}>Loading SMS activity…</Text>
									)}

									{smsActivity.length > 0 && (() => {
										const smsStatusColors = { delivered: '#22c55e', sent: '#3b82f6', queued: '#94a3b8', failed: '#ef4444', undelivered: '#ef4444', accepted: '#8b5cf6', receiving: '#3b82f6' };
										const searchLower = smsActivitySearch.toLowerCase();
										const searched = searchLower ? smsActivity.filter(m => (m.to || '').includes(searchLower) || (m.body || '').toLowerCase().includes(searchLower)) : smsActivity;
										const filtered = smsActivityFilter === 'all' ? searched : searched.filter(m => m.status === smsActivityFilter);
										const totalPages = Math.max(1, Math.ceil(filtered.length / SMS_ACTIVITY_PER_PAGE));
										const safePage = Math.min(smsActivityPage, totalPages);
										const pageStart = (safePage - 1) * SMS_ACTIVITY_PER_PAGE;
										const pageSlice = filtered.slice(pageStart, pageStart + SMS_ACTIVITY_PER_PAGE);

										return (
											<View style={{ gap: 12 }}>
												{filtered.length === 0 ? (
													<View style={[styles.ffCategorySection, { backgroundColor: theme.ffCardBg, borderColor: theme.ffCardBorder }]}>
														<Text style={{ color: theme.textMuted, fontSize: 12, padding: 14, textAlign: 'center' }}>No SMS messages match the current filter.</Text>
													</View>
												) : (
													<>
														<div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
															{pageSlice.map((msg, idx) => {
																const color = smsStatusColors[msg.status] || '#94a3b8';
																const time = msg.dateSent ? new Date(msg.dateSent).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : msg.dateCreated ? new Date(msg.dateCreated).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
																const isExpanded = expandedSmsSid === msg.sid;
																const bodyPreview = msg.body ? (msg.body.length > 60 ? msg.body.slice(0, 60) + '…' : msg.body) : '—';
																return (
																	<div key={msg.sid || idx} style={{ borderRadius: 10, border: `1px solid ${isExpanded ? (color + '60') : theme.border}`, backgroundColor: theme.ffCardBg, overflow: 'hidden' }}>
																		<button
																			onClick={() => setExpandedSmsSid(isExpanded ? null : msg.sid)}
																			style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: 12, background: 'none', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'left' }}
																		>
																			<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
																				<span style={{ backgroundColor: color + '22', borderRadius: 12, padding: '3px 10px', color, fontSize: 11, fontWeight: 700, textTransform: 'capitalize' }}>{msg.status || '—'}</span>
																				<span style={{ color: theme.textMuted, fontSize: 11 }}>{time}</span>
																			</div>
																			<div style={{ fontSize: 13, fontWeight: 600, color: theme.textPrimary, width: '100%' }}>To: {msg.to || '—'}</div>
																			<div style={{ fontSize: 12, color: theme.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>{bodyPreview}</div>
																			<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
																				<span style={{ fontSize: 11, color: theme.textMuted }}>{msg.numSegments || 1} segment{(msg.numSegments || 1) !== '1' ? 's' : ''}{msg.price ? ` · $${Math.abs(parseFloat(msg.price)).toFixed(4)}` : ''}</span>
																				<Ionicons name={isExpanded ? 'chevron-up' : 'chevron-down'} size={14} color={theme.textMuted} />
																			</div>
																		</button>
																		{isExpanded && (
																			<View style={{ backgroundColor: theme.bgSection, borderTopWidth: 1, borderTopColor: theme.border, padding: 12, gap: 8 }}>
																				<View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12 }}>
																					<View><Text style={{ color: theme.textMuted, fontSize: 10, fontWeight: '700', textTransform: 'uppercase', marginBottom: 2 }}>From</Text><Text style={{ color: theme.textPrimary, fontSize: 12 }}>{msg.from || '—'}</Text></View>
																					<View><Text style={{ color: theme.textMuted, fontSize: 10, fontWeight: '700', textTransform: 'uppercase', marginBottom: 2 }}>Direction</Text><Text style={{ color: theme.textPrimary, fontSize: 12 }}>{msg.direction || '—'}</Text></View>
																					<View><Text style={{ color: theme.textMuted, fontSize: 10, fontWeight: '700', textTransform: 'uppercase', marginBottom: 2 }}>SID</Text><Text style={{ color: theme.textMuted, fontSize: 10, fontFamily: 'monospace' }}>{msg.sid}</Text></View>
																				</View>
																				{msg.errorCode && (
																					<View style={{ backgroundColor: '#ef444415', borderRadius: 6, padding: 8 }}>
																						<Text style={{ color: '#ef4444', fontSize: 11, fontWeight: '600' }}>Error {msg.errorCode}: {msg.errorMessage || 'Unknown'}</Text>
																					</View>
																				)}
																				<View>
																					<Text style={{ color: theme.textMuted, fontSize: 10, fontWeight: '700', textTransform: 'uppercase', marginBottom: 4 }}>Full Message</Text>
																					<View style={{ borderWidth: 1, borderColor: theme.border, borderRadius: 8, padding: 10, maxHeight: 200 }}>
																						<Text style={{ color: theme.textPrimary, fontSize: 12, lineHeight: 18 }}>{msg.body || 'No body'}</Text>
																					</View>
																				</View>
																			</View>
																		)}
																	</div>
																);
															})}
														</div>
														{/* SMS Pagination */}
														<View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingTop: 4 }}>
															<Text style={{ color: theme.textMuted, fontSize: 11 }}>
																{pageStart + 1}–{Math.min(pageStart + SMS_ACTIVITY_PER_PAGE, filtered.length)} of {filtered.length}
															</Text>
															{totalPages > 1 && (
																<View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
																	<button onClick={() => setSmsActivityPage(p => Math.max(1, p - 1))} disabled={safePage <= 1} style={{ padding: '4px 8px', borderRadius: 6, border: `1px solid ${theme.border}`, background: safePage <= 1 ? 'transparent' : theme.bgSection, color: safePage <= 1 ? theme.textMuted : theme.textPrimary, cursor: safePage <= 1 ? 'default' : 'pointer', fontSize: 11, opacity: safePage <= 1 ? 0.4 : 1 }}>←</button>
																	<span style={{ fontSize: 11, color: theme.textMuted }}>{safePage}/{totalPages}</span>
																	<button onClick={() => setSmsActivityPage(p => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages} style={{ padding: '4px 8px', borderRadius: 6, border: `1px solid ${theme.border}`, background: safePage >= totalPages ? 'transparent' : theme.bgSection, color: safePage >= totalPages ? theme.textMuted : theme.textPrimary, cursor: safePage >= totalPages ? 'default' : 'pointer', fontSize: 11, opacity: safePage >= totalPages ? 0.4 : 1 }}>→</button>
																</View>
															)}
														</View>
													</>
												)}
											</View>
										);
									})()}
								</div>{/* end right column (SMS) */}
								</div>{/* end two-column grid */}
								</>}
							</View>

							{/* ─── Scheduled Send Queue ─── */}
							{(() => {
									// ── Helpers ──────────────────────────────────────────────────
									const fmtCountdown = (isoTarget) => {
										const diff = new Date(isoTarget) - new Date();
										if (diff <= 0) return 'Next run';
										const totalSec = Math.floor(diff / 1000);
										const d = Math.floor(totalSec / 86400);
										const h = Math.floor((totalSec % 86400) / 3600);
										const m = Math.floor((totalSec % 3600) / 60);
										const s = totalSec % 60;
										if (d > 0) return `${d}d ${h}h ${m}m`;
										if (h > 0) return `${h}h ${m}m ${s}s`;
										return `${m}m ${s}s`;
									};

									const statusLabel = {
										profileCompletion1: 'Profile Completion 1',
										profileCompletion2: 'Profile Completion 2',
										profileCompletion3: 'Profile Completion 3',
										registryNudge: 'Registry Nudge',
										goalNudge: 'Goal Nudge',
										itemsNudge: 'Items Nudge',
										widgetsNudge: 'Widgets Nudge',
										reengagement14Days: 'Re-engage 14d',
										reengagement30Days: 'Re-engage 30d',
									};

								// Combine pending + upcoming, apply filter
								const allQueued = [
									...(emailSchedule?.pendingSends || []).map(e => ({ ...e, queueType: 'pending' })),
									...(emailSchedule?.upcomingSends || []).map(e => ({ ...e, queueType: 'upcoming' })),
								];

								const filteredByTime = allQueued.filter(e => {
									if (scheduleFilter === 'next') return e.queueType === 'pending';
									if (scheduleFilter === 'today') return e.daysUntil === 0;
									if (scheduleFilter === 'tomorrow') return e.daysUntil === 1;
									if (scheduleFilter === 'week') return e.daysUntil <= 7;
									return true;
								});

								const queueSearch = scheduleSearchQuery.trim().toLowerCase();
								const filtered = queueSearch ? filteredByTime.filter(e => {
										const userName = (e.userName || '').toLowerCase();
										const userEmail = (e.userEmail || '').toLowerCase();
										const userId = (e.userId || '').toLowerCase();
										const emailType = (e.emailType || '').toLowerCase();
										const reason = (e.reason || '').toLowerCase();
										return [userName, userEmail, userId, emailType, reason].some(field => field.includes(queueSearch));
								}) : filteredByTime;

								// Sort logic
								const sortComparator = (a, b) => {
									const aTime = a.firesAt ? new Date(a.firesAt).getTime() : 0;
									const bTime = b.firesAt ? new Date(b.firesAt).getTime() : 0;
									if (scheduleSort === 'soonest') return aTime - bTime;
									if (scheduleSort === 'furthest') return bTime - aTime;
									if (scheduleSort === 'nameAZ') return (a.userName || a.userEmail || '').localeCompare(b.userName || b.userEmail || '');
									if (scheduleSort === 'nameZA') return (b.userName || b.userEmail || '').localeCompare(a.userName || a.userEmail || '');
									return 0;
								};
								const sortedFiltered = [...filtered].sort(sortComparator);

								// Group logic
								let groups = [];
								if (scheduleGroupBy === 'emailType') {
									const map = {};
									sortedFiltered.forEach(e => {
										const key = e.emailType;
										if (!map[key]) map[key] = { label: statusLabel[key] || key, items: [] };
										map[key].items.push(e);
									});
									groups = Object.values(map).sort((a, b) => a.label.localeCompare(b.label));
								} else {
									// group by user
									const map = {};
									sortedFiltered.forEach(e => {
										const key = e.userEmail;
										if (!map[key]) map[key] = { label: e.userName || e.userEmail, items: [] };
										map[key].items.push(e);
									});
									// Sort groups by first item's firesAt (matching chosen sort)
									groups = Object.values(map).sort((a, b) => {
										const aTime = a.items[0]?.firesAt ? new Date(a.items[0].firesAt).getTime() : 0;
										const bTime = b.items[0]?.firesAt ? new Date(b.items[0].firesAt).getTime() : 0;
										if (scheduleSort === 'soonest') return aTime - bTime;
										if (scheduleSort === 'furthest') return bTime - aTime;
										if (scheduleSort === 'nameAZ') return a.label.localeCompare(b.label);
										if (scheduleSort === 'nameZA') return b.label.localeCompare(a.label);
										return 0;
									});
								}

									const summary = emailSchedule?.summary;
									const nextRunAt = emailSchedule?.nextRunAt;
									// Force re-render every second via scheduleTick
									void scheduleTick;

								return (
								<View style={{ borderTopWidth: 1, borderTopColor: theme.border, marginTop: 20, paddingTop: 8 }}>
									{/* Toggle header row */}
									<View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
										<button
											onClick={() => setNotifSectionOpen(s => ({ ...s, scheduleQueue: !s.scheduleQueue }))}
											style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 10, background: 'none', border: 'none', cursor: 'pointer', padding: '16px 0', flex: 1, textAlign: 'left' }}
										>
											<Ionicons name={notifSectionOpen.scheduleQueue ? 'chevron-down' : 'chevron-forward'} size={16} color={theme.textMuted} />
											<View style={{ flex: 1 }}>
												<Text style={[styles.ffMainTitle, { color: theme.textPrimary, marginBottom: 2 }]}>Scheduled Send Queue</Text>
												<Text style={[styles.ffSubtitle, { color: theme.textMuted, marginTop: 0 }]}>
													Engagement emails on the left · Event reminders on the right. Each side has independent filters.
												</Text>
											</View>
										</button>
										{notifSectionOpen.scheduleQueue && (
											<button
												onClick={fetchEmailSchedule}
												disabled={emailScheduleLoading}
												style={{ ...styles.ffToggleBtn, ...styles.ffToggleBtnOn, padding: '6px 16px', fontSize: 13, opacity: emailScheduleLoading ? 0.6 : 1 }}
											>
												{emailScheduleLoading ? '⏳ Loading…' : '↻ Refresh'}
											</button>
										)}
									</View>
								{!notifSectionOpen.scheduleQueue && null}
									{notifSectionOpen.scheduleQueue && <>

										{/* Next cron run banner */}
										{nextRunAt && (
												<View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: theme.primary + '18', borderRadius: 10, padding: '10px 16px', marginBottom: 16, flexWrap: 'wrap' }}>
													<Ionicons name="time-outline" size={16} color={theme.primary} />
													<Text style={{ color: theme.primary, fontWeight: '700', fontSize: 13 }}>
														Next batch run: {fmtCountdown(nextRunAt)}
													</Text>
													<Text style={{ color: theme.textMuted, fontSize: 12 }}>
														({new Date(nextRunAt).toLocaleString()} local)
													</Text>
												</View>
											)}

											{/* Summary pills */}
											{summary && (
												<View style={{ flexDirection: 'row', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
													{[
														{ label: `${summary.pendingCount} firing next run`, color: '#22c55e' },
														{ label: `${summary.upcomingCount} within 14 days`, color: '#f59e0b' },
														{ label: `${summary.disabledCount} disabled`, color: '#6b7280' },
														{ label: `${summary.suppressedCount} suppressed`, color: '#ef4444' },
														{ label: `${summary.reminderCount} event reminders`, color: '#3b82f6' },
													].map(({ label, color }) => (
														<View key={label} style={{ backgroundColor: color + '20', borderRadius: 20, paddingHorizontal: 12, paddingVertical: 4, borderWidth: 1, borderColor: color + '40' }}>
															<Text style={{ color, fontSize: 12, fontWeight: '600' }}>{label}</Text>
														</View>
													))}
												</View>
											)}

											{emailScheduleError && (
												<View style={[styles.ffCategorySection, { backgroundColor: '#fff3cd', borderColor: '#ffc107', marginBottom: 16 }]}>
													<Text style={{ color: '#856404', fontSize: 13, padding: 12 }}>⚠️ {emailScheduleError}</Text>
												</View>
											)}

											{!emailSchedule && !emailScheduleLoading && !emailScheduleError && (
												<View style={[styles.ffCategorySection, { backgroundColor: theme.ffCardBg, borderColor: theme.ffCardBorder, marginBottom: 16 }]}>
													<Text style={{ color: theme.textMuted, fontSize: 13, padding: 16, textAlign: 'center' }}>
														No schedule loaded yet. Click Refresh to compute upcoming sends.
													</Text>
												</View>
											)}

									{emailSchedule && (
										<View>
										<div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 24, alignItems: 'start' }}>

										{/* ── LEFT COLUMN: Engagement Emails ── */}
										<div>
											{/* Filter chips */}
											<View style={{ flexDirection: 'row', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
												{[
													{ key: 'all', label: 'All' },
													{ key: 'next', label: 'Next Run' },
													{ key: 'today', label: 'Today' },
													{ key: 'tomorrow', label: 'Tomorrow' },
													{ key: 'week', label: 'This Week' },
												].map(({ key, label }) => (
													<button key={key} onClick={() => { setScheduleFilter(key); setScheduleQueuePage(1); }} style={{ padding: '4px 14px', borderRadius: 20, border: `1px solid ${scheduleFilter === key ? theme.primary : theme.border}`, backgroundColor: scheduleFilter === key ? theme.primary : 'transparent', color: scheduleFilter === key ? '#fff' : theme.textMuted, cursor: 'pointer', fontSize: 12, fontWeight: '600' }}>{label}</button>
												))}
											</View>
											{/* Sort controls */}
											<View style={{ flexDirection: 'row', gap: 6, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
												<Text style={{ color: theme.textMuted, fontSize: 12, fontWeight: '600' }}>Sort:</Text>
												{[
													{ key: 'soonest', label: '↑ Soonest' },
													{ key: 'furthest', label: '↓ Furthest' },
													{ key: 'nameAZ', label: 'A→Z' },
													{ key: 'nameZA', label: 'Z→A' },
												].map(({ key, label }) => (
													<button key={key} onClick={() => setScheduleSort(key)} style={{ padding: '4px 12px', borderRadius: 20, border: `1px solid ${scheduleSort === key ? theme.primary : theme.border}`, backgroundColor: scheduleSort === key ? theme.primary + '22' : 'transparent', color: scheduleSort === key ? theme.primary : theme.textMuted, cursor: 'pointer', fontSize: 12, fontWeight: '600' }}>{label}</button>
												))}
											</View>

											{/* User-grouped engagement email cards */
                                                                               <div style={{ marginBottom: 16, display: 'flex', flexWrap: 'wrap' }}>
                                                                                   <input
                                                                                       type="search"
                                                                                       value={scheduleSearchQuery}
                                                                                       onChange={e => { setScheduleSearchQuery(e.target.value); setScheduleQueuePage(1); }}
                                                                                       placeholder="Search by name, email, user ID, or type"
                                                                                       style={{ width: '100%', minWidth: 240, padding: '10px 12px', borderRadius: 10, border: `1px solid ${theme.border}`, backgroundColor: theme.bgSection, color: theme.textPrimary, fontSize: 13 }}
                                                                                   />
                                                                               </div>}
											{sortedFiltered.length === 0 ? (
												<View style={[styles.ffCategorySection, { backgroundColor: theme.ffCardBg, borderColor: theme.ffCardBorder, marginBottom: 16 }]}>
													<Text style={{ color: theme.textMuted, fontSize: 13, padding: 16, textAlign: 'center' }}>No sends match the selected filter.</Text>
												</View>
											) : (() => {
												// Group by user
												const userMap = {};
												sortedFiltered.forEach(e => {
													if (!userMap[e.userEmail]) userMap[e.userEmail] = { name: e.userName || e.userEmail, email: e.userEmail, userId: e.userId || null, emails: [] };
													userMap[e.userEmail].emails.push(e);
												});
												const userList = Object.values(userMap);
												return (
													<View style={{ gap: 8, marginBottom: 16 }}>
														{userList.map(user => {
															const userExpanded = expandedQueueUserKeys.includes(user.email);
															return (
															<div key={user.email} style={{ borderRadius: 10, border: `1px solid ${theme.border}`, backgroundColor: theme.ffCardBg, overflow: 'hidden' }}>
																{/* ── User row (level 1) ── */}
																<button
																	onClick={() => setExpandedQueueUserKeys(prev => prev.includes(user.email) ? prev.filter(k => k !== user.email) : [...prev, user.email])}
																	style={{ width: '100%', background: isDarkMode ? '#1e293b' : '#f1f5f9', border: 'none', cursor: 'pointer', padding: '9px 12px', display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left', borderBottom: userExpanded ? `1px solid ${theme.border}` : 'none' }}
																>
																	<Ionicons name={userExpanded ? 'chevron-down' : 'chevron-forward'} size={14} color={theme.textMuted} />
																	<Ionicons name="person-circle-outline" size={16} color={theme.primary} />
																	<div style={{ flex: 1, minWidth: 0 }}>
																		<div style={{ fontSize: 12, fontWeight: 700, color: theme.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.name}</div>
																		{user.email !== user.name && <div style={{ fontSize: 10, color: theme.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.email}</div>}
																	</div>
																	<span style={{ backgroundColor: theme.primary + '22', borderRadius: 10, padding: '2px 8px', color: theme.primary, fontSize: 10, fontWeight: 700 }}>{user.emails.length}</span>
																</button>
																{/* ── Email cards (level 2, shown when user expanded) ── */}
																{userExpanded && (
																<div style={{ padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 6 }}>
																	{user.emails.map((entry, idx) => {
																		const statusColor = entry.suppressed ? '#ef4444' : !entry.enabled ? '#6b7280' : entry.queueType === 'pending' ? '#22c55e' : '#f59e0b';
																		const statusText = entry.suppressed ? 'Suppressed' : !entry.enabled ? 'Disabled' : entry.queueType === 'pending' ? 'Queued' : 'Soon';
																		const queueKey = `${entry.userEmail || ''}:${entry.emailType || ''}:${entry.firesAt || entry.queueType || idx}`;
																		const isExpanded = expandedQueueKeys.includes(queueKey);
																		const skipKey = `${entry.userEmail}:${entry.emailType}`;
																		const isPendingConfirm = confirmSkipKeys[skipKey];
																		return (
																		<div key={queueKey} style={{ borderRadius: 8, border: `1px solid ${theme.border}`, backgroundColor: theme.bgSection, overflow: 'hidden' }}>
																			{/* Email row (level 2) */}
																			<button onClick={() => toggleQueueItemExpand(queueKey)} style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: '8px 10px', display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left' }}>
																				<Ionicons name={isExpanded ? 'chevron-down' : 'chevron-forward'} size={13} color={theme.textMuted} />
																				<div style={{ flex: 1, minWidth: 0 }}>
																					<div style={{ fontSize: 12, fontWeight: 600, color: theme.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{statusLabel[entry.emailType] || entry.emailType}</div>
																					{entry.reason && <div style={{ fontSize: 10, color: theme.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.reason}</div>}
																				</div>
																				<div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
																					<span style={{ backgroundColor: statusColor + '22', borderRadius: 10, padding: '2px 9px', color: statusColor, fontSize: 10, fontWeight: 700 }}>{statusText}</span>
																					{!entry.suppressed && <span style={{ color: entry.queueType === 'pending' ? '#22c55e' : theme.textMuted, fontSize: 11, fontWeight: 600, fontVariant: 'tabular-nums' }}>{fmtCountdown(entry.firesAt)}</span>}
																				</div>
																			</button>
																			{/* Metadata + skip (level 3) */}
																			{isExpanded && (
																			<div style={{ padding: '10px 12px', borderTop: `1px solid ${theme.border}`, backgroundColor: isDarkMode ? '#0f172a' : '#f8fafc', display: 'flex', flexDirection: 'column', gap: 8 }}>
																				<div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 11, color: theme.textMuted }}>
																					<span><strong style={{ color: theme.textPrimary }}>UID:</strong> {entry.userId || '—'}</span>
																					{!entry.suppressed && <span><strong style={{ color: theme.textPrimary }}>Fires:</strong> {entry.firesAt ? new Date(entry.firesAt).toLocaleString() : 'Unknown'}</span>}
																					{!entry.suppressed && <span><strong style={{ color: theme.textPrimary }}>Days until:</strong> {entry.daysUntil ?? '—'}</span>}
																					{entry.suppressed && <span style={{ color: '#ef4444' }}>This email will not be sent.</span>}
																				</div>
																				<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6, flexWrap: 'wrap' }}>
																					{entry.suppressed ? (
																						<button onClick={() => handleSuppressEmail(entry.userEmail, entry.emailType, entry.trackingKey, false)} style={{ padding: '3px 9px', borderRadius: 6, fontSize: 10, cursor: 'pointer', border: '1px solid #22c55e44', backgroundColor: '#22c55e18', color: '#22c55e', fontWeight: '600' }}>Allow</button>
																					) : isPendingConfirm ? (
																						<>
																							<span style={{ fontSize: 10, color: '#ef4444', fontWeight: 600 }}>Skip this send?</span>
																							<button onClick={() => { handleSuppressEmail(entry.userEmail, entry.emailType, entry.trackingKey, true); setConfirmSkipKeys(p => ({ ...p, [skipKey]: false })); }} style={{ padding: '3px 9px', borderRadius: 6, fontSize: 10, cursor: 'pointer', border: '1px solid #ef444444', backgroundColor: '#ef444418', color: '#ef4444', fontWeight: '600' }}>Confirm</button>
																							<button onClick={() => setConfirmSkipKeys(p => ({ ...p, [skipKey]: false }))} style={{ padding: '3px 8px', borderRadius: 6, fontSize: 10, cursor: 'pointer', border: `1px solid ${theme.border}`, backgroundColor: 'transparent', color: theme.textMuted }}>Cancel</button>
																						</>
																					) : (
																						<button onClick={() => setConfirmSkipKeys(p => ({ ...p, [skipKey]: true }))} style={{ padding: '3px 9px', borderRadius: 6, fontSize: 10, cursor: 'pointer', border: '1px solid #ef444444', backgroundColor: '#ef444418', color: '#ef4444', fontWeight: '600' }}>Skip</button>
																					)}
																				</div>
																			</div>
																			)}
																		</div>
																		);
																	})}
																</div>
																)}
															</div>
															);
														})}
														<Text style={{ color: theme.textMuted, fontSize: 11, textAlign: 'right' }}>
															{sortedFiltered.length} send{sortedFiltered.length !== 1 ? 's' : ''} across {userList.length} user{userList.length !== 1 ? 's' : ''}
														</Text>
													</View>
												);
											})()}

											{/* Recently Sent (last 48h) */}
											<View style={[styles.ffCategorySection, { backgroundColor: theme.ffCardBg, borderColor: theme.ffCardBorder }]}>
												<button onClick={() => setRecentlySentOpen(o => !o)} style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', padding: '10px 12px', width: '100%', textAlign: 'left' }}>
													<Ionicons name={recentlySentOpen ? 'chevron-down' : 'chevron-forward'} size={14} color={theme.textMuted} />
													<Text style={{ color: theme.textPrimary, fontSize: 13, fontWeight: '700' }}>Recently Sent</Text>
													<View style={[styles.ffCategoryBadge, { marginLeft: 4 }]}><Text style={styles.ffCategoryBadgeText}>{(emailSchedule.recentlySent || []).length}</Text></View>
													<Text style={{ color: theme.textMuted, fontSize: 11, marginLeft: 4 }}>last 48h</Text>
												</button>
												{recentlySentOpen && (
													<View style={{ borderTopWidth: 1, borderTopColor: theme.border, padding: 10 }}>
														{(emailSchedule.recentlySent || []).length === 0 ? (
															<Text style={{ color: theme.textMuted, fontSize: 12, padding: 12, textAlign: 'center' }}>No emails sent in the last 48 hours.</Text>
														) : (
															<div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
																{(emailSchedule.recentlySent || []).map((e, idx) => (
																	<div key={`${e.userEmail}-${e.emailType}-${idx}`} style={{ borderRadius: 7, border: `1px solid ${theme.border}`, backgroundColor: theme.bgSection, padding: 8, display: 'flex', flexDirection: 'column', gap: 3 }}>
																		<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
																			<span style={{ backgroundColor: '#22c55e22', borderRadius: 10, padding: '2px 8px', color: '#22c55e', fontSize: 10, fontWeight: 700 }}>Sent</span>
																			<span style={{ color: theme.textMuted, fontSize: 10 }}>{e.sentAt ? new Date(e.sentAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}</span>
																		</div>
																		<div style={{ fontSize: 12, fontWeight: 600, color: theme.textPrimary }}>{e.userName || e.userEmail}</div>
																		<div style={{ fontSize: 11, color: theme.textMuted }}>{statusLabel[e.emailType] || e.emailType}</div>
																	</div>
																))}
															</div>
														)}
													</View>
												)}
											</View>
										</div>{/* end left column */}

										{/* ── RIGHT COLUMN: Event Reminders ── */}
										<div>
											{/* Header row */}
											<View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
												<Ionicons name="notifications-outline" size={15} color="#3b82f6" />
												<Text style={{ color: theme.textPrimary, fontSize: 14, fontWeight: '700' }}>Event Reminders</Text>
												{(emailSchedule.reminders || []).length > 0 && (
													<View style={[styles.ffCategoryBadge, { backgroundColor: '#3b82f620', borderColor: '#3b82f640' }]}>
														<Text style={[styles.ffCategoryBadgeText, { color: '#3b82f6' }]}>{(emailSchedule.reminders || []).length}</Text>
													</View>
												)}
												<button
													onClick={async () => {
														setScanningReminders(true); setScanResults(null);
														try {
															const { SERVER_URL } = await import('../config.js');
															const res = await fetch(`${SERVER_URL}/scan-all-reminders`, { method: 'POST', headers: { 'Content-Type': 'application/json' } });
															const data = await res.json();
															setScanResults(data);
															if (data.scheduled > 0) fetchEmailSchedule();
														} catch (e) { setScanResults({ error: e.message }); }
														finally { setScanningReminders(false); }
													}}
													disabled={scanningReminders}
													style={{ marginLeft: 'auto', padding: '4px 12px', borderRadius: 8, border: `1px solid #3b82f640`, backgroundColor: '#3b82f615', color: '#3b82f6', fontSize: 11, fontWeight: 700, cursor: 'pointer', opacity: scanningReminders ? 0.6 : 1 }}
												>
													{scanningReminders ? '⏳ Scanning…' : '⟳ Scan All Users'}
												</button>
											</View>

											{/* Reminder type filter chips */}
											<View style={{ flexDirection: 'row', gap: 8, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' }}>
												{[
													{ key: 'all', label: 'All' },
													{ key: 'scheduled', label: 'Scheduled' },
													{ key: 'sent', label: 'Sent' },
													{ key: 'day_of', label: 'Day-of' },
													{ key: 'days_before_1', label: '1d Before' },
													{ key: 'days_before_3', label: '3d Before' },
													{ key: 'days_before_7', label: '7d Before' },
													{ key: 'test', label: 'Test Only' },
												].map(({ key, label }) => (
													<button key={key} onClick={() => setReminderFilter(key)} style={{ padding: '4px 12px', borderRadius: 20, border: `1px solid ${reminderFilter === key ? '#3b82f6' : theme.border}`, backgroundColor: reminderFilter === key ? '#3b82f6' : 'transparent', color: reminderFilter === key ? '#fff' : theme.textMuted, cursor: 'pointer', fontSize: 11, fontWeight: '600' }}>{label}</button>
												))}
											</View>
											{/* Reminder sort controls */}
											<View style={{ flexDirection: 'row', gap: 6, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
												<Text style={{ color: theme.textMuted, fontSize: 12, fontWeight: '600' }}>Sort:</Text>
												{[
													{ key: 'soonest', label: '↑ Soonest' },
													{ key: 'furthest', label: '↓ Furthest' },
													{ key: 'nameAZ', label: 'A→Z' },
													{ key: 'nameZA', label: 'Z→A' },
												].map(({ key, label }) => (
													<button key={key} onClick={() => setReminderSort(key)} style={{ padding: '4px 12px', borderRadius: 20, border: `1px solid ${reminderSort === key ? '#3b82f6' : theme.border}`, backgroundColor: reminderSort === key ? '#3b82f622' : 'transparent', color: reminderSort === key ? '#3b82f6' : theme.textMuted, cursor: 'pointer', fontSize: 12, fontWeight: '600' }}>{label}</button>
												))}
											</View>

											{/* Scan results banner */}
											{scanResults && (
												<div style={{ borderRadius: 8, padding: '8px 12px', marginBottom: 10, backgroundColor: scanResults.error ? '#ef444415' : '#22c55e15', border: `1px solid ${scanResults.error ? '#ef444440' : '#22c55e40'}`, fontSize: 12, color: scanResults.error ? '#ef4444' : '#22c55e' }}>
													{scanResults.error ? `Scan error: ${scanResults.error}` : `Scanned ${scanResults.scanned} users — ${scanResults.scheduled} scheduled, ${scanResults.skipped} already had reminders`}
												</div>
											)}

											{/* Grouped + filtered reminder cards */}
											{(() => {
												const allReminders = emailSchedule.reminders || [];
												const filtered = allReminders.filter(r => {
													if (reminderFilter === 'all') return true;
													if (reminderFilter === 'test') return !!r.testMode;
													if (reminderFilter === 'scheduled') return r.status === 'scheduled';
													if (reminderFilter === 'sent') return r.status === 'sent' || r.status === 'completed' || r.status === 'failed';
													return r.reminderType === reminderFilter;
												});

												if (filtered.length === 0) return (
													<View style={[styles.ffCategorySection, { backgroundColor: theme.ffCardBg, borderColor: theme.ffCardBorder }]}>
														<Text style={{ color: theme.textMuted, fontSize: 13, padding: 16, textAlign: 'center' }}>
															{allReminders.length === 0
																? `No scheduled event reminders.\nClick "Scan All Users" to find missing ones.`
																: `No reminders match "${reminderFilter}" filter.`}
														</Text>
													</View>
												);

												const groupMap = {};
												filtered.forEach(r => {
													const gKey = `${(r.graduateEmail || r.eventOwnerId || '').toLowerCase()}::${(r.eventTitle || '').toLowerCase()}`;
													if (!groupMap[gKey]) groupMap[gKey] = { key: gKey, eventTitle: r.eventTitle, graduateName: r.graduateName, graduateEmail: r.graduateEmail, eventsPageUrl: r.eventsPageUrl, eventDate: r.eventDate, eventLocation: r.eventLocation, reminders: [] };
													groupMap[gKey].reminders.push(r);
												});
												const groups = Object.values(groupMap);
												groups.sort((a, b) => {
													const aFirst = a.reminders.find(r => r.status === 'scheduled') || a.reminders[0];
													const bFirst = b.reminders.find(r => r.status === 'scheduled') || b.reminders[0];
													const aTime = aFirst?.scheduledFor ? new Date(aFirst.scheduledFor).getTime() : Infinity;
													const bTime = bFirst?.scheduledFor ? new Date(bFirst.scheduledFor).getTime() : Infinity;
													const aName = (a.graduateName || a.graduateEmail || '').toLowerCase();
													const bName = (b.graduateName || b.graduateEmail || '').toLowerCase();
													if (reminderSort === 'furthest') return bTime - aTime;
													if (reminderSort === 'nameAZ') return aName.localeCompare(bName);
													if (reminderSort === 'nameZA') return bName.localeCompare(aName);
													return aTime - bTime;
												});

												const isInviteSource = s => s === 'shared' || s === 'invite' || s === 'sms_share' || s === 'email_share';
												const typePriority = { days_before_10: 0, days_before_7: 1, days_before_3: 2, days_before_1: 3, day_of: 4 };

												return (
													<View style={{ gap: 10 }}>
														{groups.map(g => {
															const isGroupOpen = expandedEventGroups.includes(g.key);
															const isRecipOpen = expandedGroupRecipients.includes(g.key);
															const scheduledCount = g.reminders.filter(r => r.status === 'scheduled').length;
															const sentCount = g.reminders.filter(r => r.status === 'sent' || r.status === 'completed').length;
															const failedCount = g.reminders.filter(r => r.status === 'failed').length;
															const bestRecipients = (g.reminders.find(r => (r.recipients || []).length > 0) || {}).recipients || [];
															const rsvpList = bestRecipients.filter(p => !isInviteSource(p.source));
															const inviteList = bestRecipients.filter(p => isInviteSource(p.source));
															const nextScheduled = g.reminders.filter(r => r.status === 'scheduled').sort((a, b) => new Date(a.scheduledFor) - new Date(b.scheduledFor))[0];
															const sortedReminders = [...g.reminders].sort((a, b) => (typePriority[a.reminderType] ?? 99) - (typePriority[b.reminderType] ?? 99));

															return (
																<div key={g.key} style={{ borderRadius: 10, border: `1px solid ${scheduledCount > 0 ? '#3b82f630' : sentCount > 0 ? '#22c55e30' : '#ef444430'}`, backgroundColor: isDarkMode ? '#111827' : '#f8fafc', overflow: 'hidden' }}>
																	{/* Group header — always visible */}
																	<button onClick={() => setExpandedEventGroups(prev => isGroupOpen ? prev.filter(x => x !== g.key) : [...prev, g.key])} style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: '10px 12px', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 5 }}>
																		<div style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%' }}>
																			<Ionicons name={isGroupOpen ? 'chevron-down' : 'chevron-forward'} size={13} color={theme.textMuted} />
																			<span style={{ fontSize: 13, fontWeight: 700, color: theme.textPrimary, flex: 1 }}>{g.eventTitle}</span>
																			{nextScheduled && <span style={{ color: '#3b82f6', fontSize: 11, fontWeight: 600, fontVariant: 'tabular-nums' }}>{fmtCountdown(nextScheduled.scheduledFor)}</span>}
																		</div>
																		<div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', paddingLeft: 19 }}>
																			<span style={{ fontSize: 11, color: theme.textMuted }}>{g.graduateName || g.graduateEmail}</span>
																			{g.eventDate && <span style={{ fontSize: 10, color: theme.textMuted }}>· {new Date(g.eventDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</span>}
																			<span style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
																				{scheduledCount > 0 && <span style={{ backgroundColor: '#3b82f620', borderRadius: 6, padding: '1px 6px', color: '#3b82f6', fontSize: 9, fontWeight: 700 }}>{scheduledCount} queued</span>}
																				{sentCount > 0 && <span style={{ backgroundColor: '#22c55e20', borderRadius: 6, padding: '1px 6px', color: '#22c55e', fontSize: 9, fontWeight: 700 }}>{sentCount} sent</span>}
																				{failedCount > 0 && <span style={{ backgroundColor: '#ef444420', borderRadius: 6, padding: '1px 6px', color: '#ef4444', fontSize: 9, fontWeight: 700 }}>{failedCount} failed</span>}
																			</span>
																		</div>
																	</button>

																	{/* Expanded: individual reminder rows + shared recipients */}
																	{isGroupOpen && (
																		<div style={{ borderTop: `1px solid ${theme.border}22` }}>
																			{sortedReminders.map((r, rIdx) => {
																				const cardKey = r.id || `${g.key}-${rIdx}`;
																				const sendNowStep = sendNowConfirm[cardKey] || 0;
																				const isSendingNow = sendNowLoading[cardKey];
																				const rs = rescheduleState[cardKey] || {};
																				const isRescheduling = rescheduleLoading[cardKey];
																				const scheduledDate = r.scheduledFor ? new Date(r.scheduledFor) : null;
																				const scheduledStr = scheduledDate ? scheduledDate.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : null;
																				const nowPlus5 = new Date(Date.now() + 5 * 60000);
																				const defaultReschedule = nowPlus5.toISOString().slice(0, 16);
																				const isSent = r.status === 'sent' || r.status === 'completed';
																				const isFailed = r.status === 'failed';
																				const rowBg = isSent ? (isDarkMode ? '#0a1e1408' : '#f0fdf408') : isFailed ? (isDarkMode ? '#1e0a0a08' : '#fef2f208') : 'transparent';
																				return (
																					<div key={cardKey} style={{ padding: '8px 12px', borderBottom: rIdx < sortedReminders.length - 1 ? `1px solid ${theme.border}15` : 'none', backgroundColor: rowBg, opacity: isSent ? 0.8 : 1 }}>
																						<div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
																							<span style={{ backgroundColor: '#3b82f622', borderRadius: 8, padding: '2px 8px', color: '#3b82f6', fontSize: 10, fontWeight: 700, minWidth: 65, textAlign: 'center' }}>
																								{r.reminderType === 'day_of' ? 'Day-of' : r.reminderType?.replace('days_before_', '') ? `${r.reminderType.replace('days_before_', '')}d before` : r.reminderType || '—'}
																							</span>
																							{isSent && <span style={{ backgroundColor: '#22c55e22', borderRadius: 6, padding: '1px 6px', color: '#22c55e', fontSize: 9, fontWeight: 700 }}>SENT</span>}
																							{isFailed && <span style={{ backgroundColor: '#ef444422', borderRadius: 6, padding: '1px 6px', color: '#ef4444', fontSize: 9, fontWeight: 700 }}>FAILED</span>}
																							{r.testMode && <span style={{ backgroundColor: '#f59e0b22', borderRadius: 6, padding: '1px 6px', color: '#f59e0b', fontSize: 9, fontWeight: 700 }}>TEST</span>}
																							{r.rescheduledByAdmin && <span style={{ backgroundColor: '#8b5cf622', borderRadius: 6, padding: '1px 6px', color: '#8b5cf6', fontSize: 9, fontWeight: 700 }}>RESCHEDULED</span>}
																							<span style={{ fontSize: 10, color: theme.textMuted, flex: 1, textAlign: 'right' }}>
																								{isSent ? 'Sent ' : isFailed ? 'Failed ' : 'Sends '}
																								<strong style={{ color: isSent ? '#22c55e' : isFailed ? '#ef4444' : theme.textPrimary }}>
																									{(isSent || isFailed) && r.processedAt ? new Date(r.processedAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : scheduledStr || '—'}
																								</strong>
																							</span>
																							{(isSent || isFailed) && (r.emailsSent > 0 || r.smsSent > 0) && <span style={{ fontSize: 9, color: theme.textMuted }}>{r.emailsSent > 0 ? `${r.emailsSent}✉` : ''}{r.smsSent > 0 ? ` ${r.smsSent}💬` : ''}</span>}
																							<span style={{ color: '#3b82f6', fontSize: 10, fontWeight: 600, fontVariant: 'tabular-nums' }}>{r.status === 'scheduled' && r.scheduledFor ? fmtCountdown(r.scheduledFor) : ''}</span>
																						</div>
																						{/* Admin actions — only for scheduled */}
																						{r.status === 'scheduled' && (
																							<div style={{ marginTop: 6, display: 'flex', gap: 5, flexWrap: 'wrap' }}>
																								{sendNowStep === 0 && <button onClick={() => setSendNowConfirm(p => ({ ...p, [cardKey]: 1 }))} style={{ flex: 1, padding: '4px 0', borderRadius: 5, border: '1px solid #22c55e44', backgroundColor: '#22c55e15', color: '#22c55e', fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>Send Now</button>}
																								{sendNowStep === 1 && (
																									<div style={{ width: '100%', backgroundColor: '#22c55e10', borderRadius: 6, padding: '6px 8px', border: '1px solid #22c55e44' }}>
																										<div style={{ fontSize: 11, color: '#22c55e', fontWeight: 600, marginBottom: 4 }}>Send to {r.recipientCount || '?'} recipient{(r.recipientCount || 0) !== 1 ? 's' : ''} now?</div>
																										<div style={{ display: 'flex', gap: 5 }}>
																											<button onClick={() => setSendNowConfirm(p => ({ ...p, [cardKey]: 2 }))} style={{ flex: 1, padding: '3px 0', borderRadius: 4, border: '1px solid #22c55e', backgroundColor: '#22c55e20', color: '#22c55e', fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>Yes, confirm</button>
																											<button onClick={() => setSendNowConfirm(p => ({ ...p, [cardKey]: 0 }))} style={{ padding: '3px 8px', borderRadius: 4, border: `1px solid ${theme.border}`, backgroundColor: 'transparent', color: theme.textMuted, fontSize: 10, cursor: 'pointer' }}>Cancel</button>
																										</div>
																									</div>
																								)}
																								{sendNowStep === 2 && (
																									<div style={{ width: '100%', backgroundColor: '#ef444410', borderRadius: 6, padding: '6px 8px', border: '1px solid #ef444444' }}>
																										<div style={{ fontSize: 11, color: '#ef4444', fontWeight: 700, marginBottom: 4 }}>FINAL — real emails/SMS send immediately.</div>
																										<div style={{ display: 'flex', gap: 5 }}>
																											<button onClick={async () => {
																												setSendNowLoading(p => ({ ...p, [cardKey]: true }));
																												try {
																													const { SERVER_URL } = await import('../config.js');
																													const res = await fetch(`${SERVER_URL}/send-reminder-now`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reminderId: r.id }) });
																													const data = await res.json();
																													if (data.success) { setSendNowConfirm(p => ({ ...p, [cardKey]: 0 })); fetchEmailSchedule(); }
																													else { alert(`Send failed: ${data.error}`); setSendNowConfirm(p => ({ ...p, [cardKey]: 0 })); }
																												} catch (e) { alert(`Error: ${e.message}`); setSendNowConfirm(p => ({ ...p, [cardKey]: 0 })); }
																												finally { setSendNowLoading(p => ({ ...p, [cardKey]: false })); }
																											}} disabled={isSendingNow} style={{ flex: 1, padding: '3px 0', borderRadius: 4, border: '1px solid #ef4444', backgroundColor: '#ef444420', color: '#ef4444', fontSize: 10, fontWeight: 700, cursor: 'pointer', opacity: isSendingNow ? 0.6 : 1 }}>
																												{isSendingNow ? 'Sending...' : 'Final Confirm'}
																											</button>
																											<button onClick={() => setSendNowConfirm(p => ({ ...p, [cardKey]: 0 }))} style={{ padding: '3px 8px', borderRadius: 4, border: `1px solid ${theme.border}`, backgroundColor: 'transparent', color: theme.textMuted, fontSize: 10, cursor: 'pointer' }}>Cancel</button>
																										</div>
																									</div>
																								)}
																								{sendNowStep === 0 && !rs.open && <button onClick={() => setRescheduleState(p => ({ ...p, [cardKey]: { open: true, newDate: defaultReschedule, confirming: false } }))} style={{ flex: 1, padding: '4px 0', borderRadius: 5, border: '1px solid #f59e0b44', backgroundColor: '#f59e0b15', color: '#f59e0b', fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>Reschedule</button>}
																								{rs.open && !rs.confirming && (
																									<div style={{ width: '100%', backgroundColor: '#f59e0b10', borderRadius: 6, padding: '6px 8px', border: '1px solid #f59e0b44' }}>
																										<div style={{ fontSize: 11, color: '#f59e0b', fontWeight: 600, marginBottom: 4 }}>New send time:</div>
																										<input type="datetime-local" value={rs.newDate || defaultReschedule} onChange={e => setRescheduleState(p => ({ ...p, [cardKey]: { ...p[cardKey], newDate: e.target.value } }))} style={{ width: '100%', padding: '5px 7px', borderRadius: 4, border: `1px solid ${theme.border}`, backgroundColor: theme.ffCardBg, color: theme.textPrimary, fontSize: 11, marginBottom: 5 }} />
																										<div style={{ display: 'flex', gap: 5 }}>
																											<button onClick={() => setRescheduleState(p => ({ ...p, [cardKey]: { ...p[cardKey], confirming: true } }))} style={{ flex: 1, padding: '3px 0', borderRadius: 4, border: '1px solid #f59e0b', backgroundColor: '#f59e0b20', color: '#f59e0b', fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>Set Time</button>
																											<button onClick={() => setRescheduleState(p => ({ ...p, [cardKey]: {} }))} style={{ padding: '3px 8px', borderRadius: 4, border: `1px solid ${theme.border}`, backgroundColor: 'transparent', color: theme.textMuted, fontSize: 10, cursor: 'pointer' }}>Cancel</button>
																										</div>
																									</div>
																								)}
																								{rs.open && rs.confirming && (
																									<div style={{ width: '100%', backgroundColor: '#ef444410', borderRadius: 6, padding: '6px 8px', border: '1px solid #ef444444' }}>
																										<div style={{ fontSize: 11, color: '#ef4444', fontWeight: 700, marginBottom: 4 }}>Confirm reschedule to {rs.newDate ? new Date(rs.newDate).toLocaleString() : '?'}?</div>
																										<div style={{ display: 'flex', gap: 5 }}>
																											<button onClick={async () => {
																												setRescheduleLoading(p => ({ ...p, [cardKey]: true }));
																												try {
																													const { SERVER_URL } = await import('../config.js');
																													const res = await fetch(`${SERVER_URL}/reschedule-single-reminder`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reminderId: r.id, newSendDate: new Date(rs.newDate).toISOString() }) });
																													const data = await res.json();
																													if (data.success) { setRescheduleState(p => ({ ...p, [cardKey]: {} })); fetchEmailSchedule(); }
																													else alert(`Reschedule failed: ${data.error}`);
																												} catch (e) { alert(`Error: ${e.message}`); }
																												finally { setRescheduleLoading(p => ({ ...p, [cardKey]: false })); }
																											}} disabled={isRescheduling} style={{ flex: 1, padding: '3px 0', borderRadius: 4, border: '1px solid #ef4444', backgroundColor: '#ef444420', color: '#ef4444', fontSize: 10, fontWeight: 700, cursor: 'pointer', opacity: isRescheduling ? 0.6 : 1 }}>
																												{isRescheduling ? 'Saving...' : 'Confirm'}
																											</button>
																											<button onClick={() => setRescheduleState(p => ({ ...p, [cardKey]: { ...p[cardKey], confirming: false } }))} style={{ padding: '3px 8px', borderRadius: 4, border: `1px solid ${theme.border}`, backgroundColor: 'transparent', color: theme.textMuted, fontSize: 10, cursor: 'pointer' }}>Back</button>
																										</div>
																									</div>
																								)}
																							</div>
																						)}
																					</div>
																				);
																			})}

																			{/* Shared recipients — collapsed by default */}
																			{bestRecipients.length > 0 && (
																				<div style={{ borderTop: `1px solid ${theme.border}22` }}>
																					<button onClick={() => setExpandedGroupRecipients(prev => isRecipOpen ? prev.filter(x => x !== g.key) : [...prev, g.key])} style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: '7px 12px', display: 'flex', alignItems: 'center', gap: 6 }}>
																						<Ionicons name={isRecipOpen ? 'chevron-down' : 'chevron-forward'} size={11} color={theme.textMuted} />
																						<span style={{ fontSize: 10, color: theme.textMuted, fontWeight: 600 }}>Recipients ({bestRecipients.length})</span>
																						{rsvpList.length > 0 && <span style={{ backgroundColor: '#22c55e20', borderRadius: 6, padding: '1px 6px', color: '#22c55e', fontSize: 9, fontWeight: 700 }}>{rsvpList.length} RSVPd</span>}
																						{inviteList.length > 0 && <span style={{ backgroundColor: '#8b5cf620', borderRadius: 6, padding: '1px 6px', color: '#8b5cf6', fontSize: 9, fontWeight: 700 }}>{inviteList.length} Invited</span>}
																					</button>
																					{isRecipOpen && (
																						<div style={{ padding: '0 12px 10px', backgroundColor: isDarkMode ? '#0a1628' : '#e8f0fe' }}>
																							{rsvpList.length > 0 && (
																								<div style={{ marginBottom: inviteList.length > 0 ? 8 : 0 }}>
																									<div style={{ fontSize: 9, fontWeight: 700, color: '#22c55e', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>RSVPd ({rsvpList.length})</div>
																									{rsvpList.map((p, pi) => {
																										const primary = p.name || p.email || p.phone || '—';
																										const showEmail = p.email && p.name;
																										const showPhone = p.phone && (p.name || p.email);
																										return (
																										<div key={pi} style={{ fontSize: 10, color: theme.textPrimary, padding: '2px 0', borderBottom: pi < rsvpList.length - 1 ? `1px solid ${theme.border}` : 'none' }}>
																											<span style={{ fontWeight: 600 }}>{primary}</span>
																											{showEmail && <span style={{ color: theme.textMuted, marginLeft: 5 }}>{p.email}</span>}
																											{showPhone && <span style={{ color: theme.textMuted, marginLeft: 5 }}>{p.phone}</span>}
																											{p.rsvpStatus && p.rsvpStatus !== 'Going' && <span style={{ color: '#f59e0b', marginLeft: 5, fontSize: 9 }}>{p.rsvpStatus}</span>}
																											{p.isUnsubscribed && <span style={{ color: '#ef4444', marginLeft: 5, fontSize: 9 }}>Unsubscribed</span>}
																										</div>
																									);})}
																								</div>
																							)}
																							{inviteList.length > 0 && (
																								<div>
																									<div style={{ fontSize: 9, fontWeight: 700, color: '#8b5cf6', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Invited ({inviteList.length})</div>
																									{inviteList.map((p, pi) => {
																										const primary = p.name || p.email || p.phone || '—';
																										const showEmail = p.email && p.name;
																										const showPhone = p.phone && (p.name || p.email);
																										const channelBadge = p.source === 'sms_share' ? 'SMS' : p.source === 'email_share' ? 'Email' : null;
																										return (
																										<div key={pi} style={{ fontSize: 10, color: theme.textPrimary, padding: '2px 0', borderBottom: pi < inviteList.length - 1 ? `1px solid ${theme.border}` : 'none', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 3 }}>
																											<span style={{ fontWeight: 600 }}>{primary}</span>
																											{showEmail && <span style={{ color: theme.textMuted }}>{p.email}</span>}
																											{showPhone && <span style={{ color: theme.textMuted }}>{p.phone}</span>}
																											{channelBadge && <span style={{ backgroundColor: p.source === 'sms_share' ? '#f59e0b20' : '#3b82f620', color: p.source === 'sms_share' ? '#f59e0b' : '#3b82f6', borderRadius: 5, padding: '0px 5px', fontSize: 9, fontWeight: 700 }}>{channelBadge}</span>}
																											{p.isUnsubscribed && <span style={{ color: '#ef4444', fontSize: 9 }}>Unsubscribed</span>}
																										</div>
																									);})}
																								</div>
																							)}
																						</div>
																					)}
																				</div>
																			)}
																		</div>
																	)}
																</div>
															);
														})}
														{filtered.length !== allReminders.length && (
															<Text style={{ color: theme.textMuted, fontSize: 11, textAlign: 'right' }}>
																Showing {filtered.length} of {allReminders.length} reminders
															</Text>
														)}
													</View>
												);
											})()}
										</div>{/* end right column */}

										</div>{/* end two-column grid */}
										</View>
									)}

							</>}
						</View>
						);
					})()}

					{/* ─── Email Management Section ─── */}
							<View style={{ borderTopWidth: 1, borderTopColor: theme.border, marginTop: 20, paddingTop: 8 }}>
								<button
									onClick={() => setNotifSectionOpen(s => ({ ...s, allTemplates: !s.allTemplates }))}
									style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 10, background: 'none', border: 'none', cursor: 'pointer', padding: '16px 0', width: '100%', textAlign: 'left' }}
								>
									<Ionicons name={notifSectionOpen.allTemplates ? 'chevron-down' : 'chevron-forward'} size={16} color={theme.textMuted} />
									<View style={{ flex: 1 }}>
										<Text style={[styles.ffMainTitle, { color: theme.textPrimary, marginBottom: 2 }]}>All Email Templates</Text>
										<Text style={[styles.ffSubtitle, { color: theme.textMuted, marginTop: 0 }]}>
											Manage all automated emails sent by the system. Toggle emails on/off, view templates, and configure schedules.
										</Text>
									</View>
								</button>
								
								{/* Force Refresh Templates Button 
								{notifSectionOpen.allTemplates && (
									<View style={{ paddingLeft: 26, paddingBottom: 12 }}>
										<button
											onClick={forceRefreshTemplates}
											disabled={emailSettingsSaving}
											style={{
												backgroundColor: '#f59e0b',
												color: 'white',
												border: 'none',
												borderRadius: 6,
												padding: '8px 16px',
												fontSize: 13,
												fontWeight: '600',
												cursor: emailSettingsSaving ? 'not-allowed' : 'pointer',
												opacity: emailSettingsSaving ? 0.6 : 1
											}}
										>
											{emailSettingsSaving ? 'Syncing...' : 'Sync Missing Templates'}
										</button>
										<Text style={{ color: theme.textMuted, fontSize: 11, marginTop: 4 }}>
											Adds missing email templates without overwriting your customizations
										</Text>
									</View>
								)}*/}
							</View>

							{notifSectionOpen.allTemplates && (emailSettingsLoading ? (
								<View style={styles.ffLoadingContainer}>
									<Text style={[styles.ffLoading, { color: theme.textMuted }]}>Loading email settings...</Text>
								</View>
							) : (
										<View style={styles.ffCategoriesGrid}>
											{/* Schedule Settings */}
											<View style={[styles.ffCategorySection, { backgroundColor: theme.ffCardBg, borderColor: theme.ffCardBorder, width: isMobile ? '100%' : 'calc(50% - 8px)' }]}>
												<View style={styles.ffCategoryHeaderClickable}>
													<View style={styles.ffCategoryHeaderLeft}>
														<Ionicons name="time" size={16} color="#f59e0b" />
														<Text style={[styles.ffCategoryTitle, { color: theme.textPrimary }]}>Schedule Settings</Text>
													</View>
												</View>
												
												<View style={styles.ffCollapsibleContent}>
													<Text style={[styles.ffCategoryDesc, { color: theme.textMuted, borderBottomColor: theme.border }]}>
														Configure when engagement emails are processed
													</Text>
													<View style={styles.ffCategoryFlags}>
														<View style={[styles.ffCard, { backgroundColor: theme.ffItemBg, borderColor: theme.ffItemBorder }]}>
															<View style={{ padding: 16 }}>
																<View style={{ marginBottom: 16 }}>
																	<Text style={{ color: theme.textPrimary, fontSize: 14, fontWeight: '600', marginBottom: 8 }}>
																		Engagement Email Schedule
																	</Text>
																	<Text style={{ color: theme.textMuted, fontSize: 12, marginBottom: 12 }}>
																		All scheduled engagement emails run once daily at the time and timezone you select.
																	</Text>
																	<View style={{ flexDirection: 'row', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
																		<View style={{ flex: 1, minWidth: 180 }}>
																			<Text style={{ color: theme.textMuted, fontSize: 11, marginBottom: 4 }}>Send Time</Text>
																			<select
																				value={(() => {
																					const sched = emailSettings.engagementSchedule || 'every day 10:00';
																					const match = sched.match(/(\d{1,2}):00/);
																					return match ? parseInt(match[1], 10) : 10;
																				})()}
																				onChange={(e) => {
																					const hour = parseInt(e.target.value, 10);
																					const padded = String(hour).padStart(2, '0');
																					setEmailSettings(prev => ({ ...prev, engagementSchedule: `every day ${padded}:00` }));
																				}}
																				style={{ padding: '8px 12px', borderRadius: 6, border: `1px solid ${theme.border}`, backgroundColor: isDarkMode ? '#0f172a' : '#ffffff', color: theme.textPrimary, fontSize: 13, cursor: 'pointer', appearance: 'auto', width: '100%' }}
																			>
																				{Array.from({ length: 24 }, (_, h) => {
																					const ampm = h < 12 ? 'AM' : 'PM';
																					const display = h === 0 ? 12 : h > 12 ? h - 12 : h;
																					return <option key={h} value={h}>{display}:00 {ampm}</option>;
																				})}
																			</select>
																		</View>
																		<View style={{ flex: 1, minWidth: 200 }}>
																			<Text style={{ color: theme.textMuted, fontSize: 11, marginBottom: 4 }}>Timezone</Text>
																			<select
																				value={emailSettings.engagementTimezone || 'America/Los_Angeles'}
																				onChange={(e) => setEmailSettings(prev => ({ ...prev, engagementTimezone: e.target.value }))}
																				style={{ padding: '8px 12px', borderRadius: 6, border: `1px solid ${theme.border}`, backgroundColor: isDarkMode ? '#0f172a' : '#ffffff', color: theme.textPrimary, fontSize: 13, cursor: 'pointer', appearance: 'auto', width: '100%' }}
																			>
																				<option value="America/New_York">Eastern (ET)</option>
																				<option value="America/Chicago">Central (CT)</option>
																				<option value="America/Denver">Mountain (MT)</option>
																				<option value="America/Los_Angeles">Pacific (PT)</option>
																				<option value="America/Anchorage">Alaska (AKT)</option>
																				<option value="Pacific/Honolulu">Hawaii (HT)</option>
																			</select>
																		</View>
																		<button
																			onClick={() => saveEmailSettings({
																				engagementSchedule: emailSettings.engagementSchedule,
																				engagementTimezone: emailSettings.engagementTimezone
																			})}
																			disabled={emailSettingsSaving}
																			style={{
																				...styles.ffToggleBtn,
																				...styles.ffToggleBtnOn,
																				opacity: emailSettingsSaving ? 0.5 : 1,
																				padding: '8px 16px',
																				alignSelf: 'flex-end'
																			}}
																		>
																			{emailSettingsSaving ? 'Saving...' : 'Save Schedule'}
																		</button>
																	</View>
																</View>
															</View>
														</View>
													</View>
												</View>
											</View>

											{/* Engagement Email Cadence */}
											<View style={[styles.ffCategorySection, { backgroundColor: theme.ffCardBg, borderColor: theme.ffCardBorder, width: isMobile ? '100%' : 'calc(50% - 8px)' }]}>
												<View style={styles.ffCategoryHeaderClickable}>
													<View style={styles.ffCategoryHeaderLeft}>
														<Ionicons name="calendar" size={16} color="#8b5cf6" />
														<Text style={[styles.ffCategoryTitle, { color: theme.textPrimary }]}>Email Cadence (Prevent Daily Spam)</Text>
													</View>
												</View>
												
												<View style={styles.ffCollapsibleContent}>
													<Text style={[styles.ffCategoryDesc, { color: theme.textMuted, borderBottomColor: theme.border }]}>
														Configure delays between engagement emails. Users will only receive one email at each configured day interval.
													</Text>
													<View style={styles.ffCategoryFlags}>
														{/* Profile Completion Cadence */}
														<View style={[styles.ffCard, { backgroundColor: theme.ffItemBg, borderColor: theme.ffItemBorder }]}>
															<View style={{ padding: 16 }}>
																<Text style={{ color: theme.textPrimary, fontSize: 14, fontWeight: '600', marginBottom: 4 }}>
																	Profile Completion Series
																</Text>
																<Text style={{ color: theme.textMuted, fontSize: 12, marginBottom: 12 }}>
																	Days after signup to send reminders (max 5). Users without pages set up.
																</Text>
																<View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
																	{(Array.isArray(emailSettings.cadence?.profileCompletion) ? emailSettings.cadence.profileCompletion : [3, 7, 14]).map((day, idx) => (
																		<View key={idx} style={{ 
																			backgroundColor: '#8b5cf620', 
																			paddingHorizontal: 12, 
																			paddingVertical: 6, 
																			borderRadius: 16,
																			flexDirection: 'row',
																			alignItems: 'center',
																			gap: 6
																		}}>
																			<Text style={{ color: '#8b5cf6', fontWeight: '600' }}>Day {day}</Text>
																		</View>
																	))}
																	<button
																		onClick={() => setEmailCadenceEditing('profileCompletion')}
																		style={{
																			...styles.ffToggleBtn,
																			padding: '4px 10px',
																			backgroundColor: 'transparent',
																			border: `1px dashed ${theme.border}`,
																			color: theme.textMuted
																		}}
																	>
																		Edit
																	</button>
																</View>
																{emailCadenceEditing === 'profileCompletion' && (
																	<View style={{ marginTop: 12, padding: 12, backgroundColor: isDarkMode ? '#0f172a' : '#f8fafc', borderRadius: 8 }}>
																		<Text style={{ color: theme.textMuted, fontSize: 11, marginBottom: 8 }}>
																			Enter days separated by commas (e.g., 3, 7, 14)
																		</Text>
																		<View style={{ flexDirection: 'row', gap: 8 }}>
																			<TextInput
																				style={{
																					flex: 1,
																					backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
																					borderWidth: 1,
																					borderColor: theme.border,
																					borderRadius: 6,
																					paddingHorizontal: 12,
																					paddingVertical: 8,
																					color: theme.textPrimary,
																					fontSize: 13
																				}}
																				defaultValue={(Array.isArray(emailSettings.cadence?.profileCompletion) ? emailSettings.cadence.profileCompletion : [3, 7, 14]).join(', ')}
																				placeholder="3, 7, 14"
																				placeholderTextColor={theme.textMuted}
																				id="cadence-profileCompletion"
																			/>
																			<button
																				onClick={() => {
																					const input = document.getElementById('cadence-profileCompletion');
																					const days = input.value.split(',').map(d => parseInt(d.trim())).filter(d => !isNaN(d) && d > 0).slice(0, 5);
																					if (days.length > 0) {
																						saveCadenceSettings('profileCompletion', days);
																					}
																				}}
																				style={{ ...styles.ffToggleBtn, ...styles.ffToggleBtnOn, padding: '8px 16px' }}
																			>
																				Save
																			</button>
																			<button
																				onClick={() => setEmailCadenceEditing(null)}
																				style={{ ...styles.ffToggleBtn, ...styles.ffToggleBtnOff, padding: '8px 16px' }}
																			>
																				Cancel
																			</button>
																		</View>
																	</View>
																)}
															</View>
														</View>

														{/* Goal Nudge Cadence */}
														<View style={[styles.ffCard, { backgroundColor: theme.ffItemBg, borderColor: theme.ffItemBorder }]}>
															<View style={{ padding: 16 }}>
																<Text style={{ color: theme.textPrimary, fontSize: 14, fontWeight: '600', marginBottom: 4 }}>
																	Goal Setting Nudge
																</Text>
																<Text style={{ color: theme.textMuted, fontSize: 12, marginBottom: 12 }}>
																	Days after registry creation to remind about setting contribution goals.
																</Text>
																<View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
																	{(Array.isArray(emailSettings.cadence?.goalNudge) ? emailSettings.cadence.goalNudge : [14]).map((day, idx) => (
																		<View key={idx} style={{ 
																			backgroundColor: '#8b5cf620', 
																			paddingHorizontal: 12, 
																			paddingVertical: 6, 
																			borderRadius: 16
																		}}>
																			<Text style={{ color: '#8b5cf6', fontWeight: '600' }}>Day {day}</Text>
																		</View>
																	))}
																	<button
																		onClick={() => setEmailCadenceEditing('goalNudge')}
																		style={{
																			...styles.ffToggleBtn,
																			padding: '4px 10px',
																			backgroundColor: 'transparent',
																			border: `1px dashed ${theme.border}`,
																			color: theme.textMuted
																		}}
																	>
																		Edit
																	</button>
																</View>
																{emailCadenceEditing === 'goalNudge' && (
																	<View style={{ marginTop: 12, padding: 12, backgroundColor: isDarkMode ? '#0f172a' : '#f8fafc', borderRadius: 8 }}>
																		<Text style={{ color: theme.textMuted, fontSize: 11, marginBottom: 8 }}>
																			Enter days separated by commas (e.g., 14)
																		</Text>
																		<View style={{ flexDirection: 'row', gap: 8 }}>
																			<TextInput
																				style={{
																					flex: 1,
																					backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
																					borderWidth: 1,
																					borderColor: theme.border,
																					borderRadius: 6,
																					paddingHorizontal: 12,
																					paddingVertical: 8,
																					color: theme.textPrimary,
																					fontSize: 13
																				}}
																				defaultValue={(Array.isArray(emailSettings.cadence?.goalNudge) ? emailSettings.cadence.goalNudge : [14]).join(', ')}
																				placeholder="14"
																				placeholderTextColor={theme.textMuted}
																				id="cadence-goalNudge"
																			/>
																			<button
																				onClick={() => {
																					const input = document.getElementById('cadence-goalNudge');
																					const days = input.value.split(',').map(d => parseInt(d.trim())).filter(d => !isNaN(d) && d > 0).slice(0, 5);
																					if (days.length > 0) {
																						saveCadenceSettings('goalNudge', days);
																					}
																				}}
																				style={{ ...styles.ffToggleBtn, ...styles.ffToggleBtnOn, padding: '8px 16px' }}
																			>
																				Save
																			</button>
																			<button
																				onClick={() => setEmailCadenceEditing(null)}
																				style={{ ...styles.ffToggleBtn, ...styles.ffToggleBtnOff, padding: '8px 16px' }}
																			>
																				Cancel
																			</button>
																		</View>
																	</View>
																)}
															</View>
														</View>

														{/* Items Nudge Cadence */}
														<View style={[styles.ffCard, { backgroundColor: theme.ffItemBg, borderColor: theme.ffItemBorder }]}>
															<View style={{ padding: 16 }}>
																<Text style={{ color: theme.textPrimary, fontSize: 14, fontWeight: '600', marginBottom: 4 }}>
																	Add Registry Items Nudge
																</Text>
																<Text style={{ color: theme.textMuted, fontSize: 12, marginBottom: 12 }}>
																	Days after registry creation to send nudge emails. Multiple days send separate emails (e.g. 5, 14, 30). Day ≤7 triggers if fewer than 5 items; day 8+ triggers if fewer than 8 items.
																</Text>
																<View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
																	{(Array.isArray(emailSettings.cadence?.itemsNudge) ? emailSettings.cadence.itemsNudge : [5, 14, 30]).map((day, idx) => (
																		<View key={idx} style={{ 
																			backgroundColor: '#f59e0b20', 
																			paddingHorizontal: 12, 
																			paddingVertical: 6, 
																			borderRadius: 16
																		}}>
																			<Text style={{ color: '#f59e0b', fontWeight: '600' }}>Day {day}</Text>
																		</View>
																	))}
																	<button
																		onClick={() => setEmailCadenceEditing('itemsNudge')}
																		style={{
																			...styles.ffToggleBtn,
																			padding: '4px 10px',
																			backgroundColor: 'transparent',
																			border: `1px dashed ${theme.border}`,
																			color: theme.textMuted
																		}}
																	>
																		Edit
																	</button>
																</View>
																{emailCadenceEditing === 'itemsNudge' && (
																	<View style={{ marginTop: 12, padding: 12, backgroundColor: isDarkMode ? '#0f172a' : '#f8fafc', borderRadius: 8 }}>
																		<Text style={{ color: theme.textMuted, fontSize: 11, marginBottom: 8 }}>
																			Enter days separated by commas (e.g., 5, 14, 30)
																		</Text>
																		<View style={{ flexDirection: 'row', gap: 8 }}>
																			<TextInput
																				style={{
																					flex: 1,
																					backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
																					borderWidth: 1,
																					borderColor: theme.border,
																					borderRadius: 6,
																					paddingHorizontal: 12,
																					paddingVertical: 8,
																					color: theme.textPrimary,
																					fontSize: 13
																				}}
																				defaultValue={(Array.isArray(emailSettings.cadence?.itemsNudge) ? emailSettings.cadence.itemsNudge : [5, 14, 30]).join(', ')}
																				placeholder="5, 14, 30"
																				placeholderTextColor={theme.textMuted}
																				id="cadence-itemsNudge"
																			/>
																			<button
																				onClick={() => {
																					const days = document.getElementById('cadence-itemsNudge').value
																						.split(',')
																						.map(n => parseInt(n.trim(), 10))
																						.filter(n => !isNaN(n) && n > 0);
																					if (days.length > 0) {
																						saveCadenceSettings('itemsNudge', days);
																					}
																				}}
																				style={{ ...styles.ffToggleBtn, ...styles.ffToggleBtnOn, padding: '8px 16px' }}
																			>
																				Save
																			</button>
																			<button
																				onClick={() => setEmailCadenceEditing(null)}
																				style={{ ...styles.ffToggleBtn, ...styles.ffToggleBtnOff, padding: '8px 16px' }}
																			>
																				Cancel
																			</button>
																		</View>
																	</View>
																)}
															</View>
														</View>

														{/* Re-engagement Cadence */}
														<View style={[styles.ffCard, { backgroundColor: theme.ffItemBg, borderColor: theme.ffItemBorder }]}>
															<View style={{ padding: 16 }}>
																<Text style={{ color: theme.textPrimary, fontSize: 14, fontWeight: '600', marginBottom: 4 }}>
																	Re-engagement Series
																</Text>
																<Text style={{ color: theme.textMuted, fontSize: 12, marginBottom: 12 }}>
																	Days since last login to send re-engagement emails.
																</Text>
																<View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
																	{(Array.isArray(emailSettings.cadence?.reengagement) ? emailSettings.cadence.reengagement : [30, 45]).map((day, idx) => (
																		<View key={idx} style={{ 
																			backgroundColor: '#10b98120', 
																			paddingHorizontal: 12, 
																			paddingVertical: 6, 
																			borderRadius: 16
																		}}>
																			<Text style={{ color: '#10b981', fontWeight: '600' }}>Day {day}</Text>
																		</View>
																	))}
																	<button
																		onClick={() => setEmailCadenceEditing('reengagement')}
																		style={{
																			...styles.ffToggleBtn,
																			padding: '4px 10px',
																			backgroundColor: 'transparent',
																			border: `1px dashed ${theme.border}`,
																			color: theme.textMuted
																		}}
																	>
																		Edit
																	</button>
																</View>
																{emailCadenceEditing === 'reengagement' && (
																	<View style={{ marginTop: 12, padding: 12, backgroundColor: isDarkMode ? '#0f172a' : '#f8fafc', borderRadius: 8 }}>
																		<Text style={{ color: theme.textMuted, fontSize: 11, marginBottom: 8 }}>
																			Enter days separated by commas (e.g., 30, 45)
																		</Text>
																		<View style={{ flexDirection: 'row', gap: 8 }}>
																			<TextInput
																				style={{
																					flex: 1,
																					backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
																					borderWidth: 1,
																					borderColor: theme.border,
																					borderRadius: 6,
																					paddingHorizontal: 12,
																					paddingVertical: 8,
																					color: theme.textPrimary,
																					fontSize: 13
																				}}
																				defaultValue={(Array.isArray(emailSettings.cadence?.reengagement) ? emailSettings.cadence.reengagement : [30, 45]).join(', ')}
																				placeholder="30, 45"
																				placeholderTextColor={theme.textMuted}
																				id="cadence-reengagement"
																			/>
																			<button
																				onClick={() => {
																					const input = document.getElementById('cadence-reengagement');
																					const days = input.value.split(',').map(d => parseInt(d.trim())).filter(d => !isNaN(d) && d > 0).slice(0, 5);
																					if (days.length > 0) {
																						saveCadenceSettings('reengagement', days);
																					}
																				}}
																				style={{ ...styles.ffToggleBtn, ...styles.ffToggleBtnOn, padding: '8px 16px' }}
																			>
																				Save
																			</button>
																			<button
																				onClick={() => setEmailCadenceEditing(null)}
																				style={{ ...styles.ffToggleBtn, ...styles.ffToggleBtnOff, padding: '8px 16px' }}
																			>
																				Cancel
																			</button>
																		</View>
																	</View>
																)}
															</View>
														</View>
													</View>
												</View>
											</View>

											{/* Toolbar row */}
											<View style={{ 
												width: '100%', 
												flexDirection: 'row', 
												alignItems: 'center', 
												gap: 10, 
												paddingVertical: 8, 
												paddingHorizontal: 12, 
												backgroundColor: isDarkMode ? '#1e293b' : '#f1f5f9', 
												borderRadius: 8, 
												marginBottom: 12,
												borderWidth: 1,
												borderColor: theme.border
											}}>
												<Pressable
													onPress={toggleAllEmailPreviews}
													style={{
														flexDirection: 'row',
														alignItems: 'center',
														gap: 5,
														paddingVertical: 5,
														paddingHorizontal: 10,
														borderRadius: 6,
														backgroundColor: expandedEmailPreviewIds.length > 0 ? '#ef444420' : theme.primary + '15',
														borderWidth: 1,
														borderColor: expandedEmailPreviewIds.length > 0 ? '#ef444460' : theme.primary + '40'
													}}
												>
													<Ionicons 
														name={expandedEmailPreviewIds.length >= getAllEmailIds().length ? "chevron-collapse-outline" : "expand-outline"} 
														size={14} 
														color={expandedEmailPreviewIds.length > 0 ? '#ef4444' : theme.primary} 
													/>
													<Text style={{ 
														color: expandedEmailPreviewIds.length > 0 ? '#ef4444' : theme.primary, 
														fontSize: 12, 
														fontWeight: '600' 
													}}>
														{expandedEmailPreviewIds.length >= getAllEmailIds().length ? 'Collapse all' : 'Expand all'}
													</Text>
												</Pressable>
												<Text style={{ color: theme.textMuted, fontSize: 11 }}>
													{expandedEmailPreviewIds.length > 0 
														? `${expandedEmailPreviewIds.length} template${expandedEmailPreviewIds.length !== 1 ? 's' : ''} open` 
														: 'Quick-edit templates inline'}
												</Text>
										</View>

										{/* Template Variables Legend */}
										<View style={{ width: '100%', marginBottom: 4 }}>
											<button
												onClick={() => setShowVarsLegend(v => !v)}
												style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8, background: 'none', border: `1px solid ${theme.border}`, borderRadius: 8, padding: '8px 14px', cursor: 'pointer', width: '100%', textAlign: 'left', backgroundColor: theme.ffCardBg }}
											>
												<Ionicons name="code-slash-outline" size={14} color={theme.primary} />
												<Text style={{ color: theme.primary, fontSize: 12, fontWeight: '700', flex: 1 }}>Template Variables Reference</Text>
												<Ionicons name={showVarsLegend ? 'chevron-up' : 'chevron-down'} size={13} color={theme.textMuted} />
											</button>
											{showVarsLegend && (
												<View style={{ marginTop: 8, borderRadius: 8, borderWidth: 1, borderColor: theme.border, backgroundColor: theme.ffCardBg, padding: 14, gap: 6 }}>
													<Text style={{ color: theme.textMuted, fontSize: 11, marginBottom: 6 }}>Use these placeholders in email/SMS templates. They are replaced with real values when the message is sent.</Text>
													<div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '6px 16px' }}>
														{[
															{ tag: '{{firstName}}', desc: "Recipient's first name" },
															{ tag: '{{graduateFirstName}}', desc: "Graduate's first name" },
															{ tag: '{{graduateName}}', desc: "Graduate's full name" },
															{ tag: '{{graduateFullName}}', desc: "Graduate's full name (alias)" },
															{ tag: '{{name}}', desc: "Recipient's name (thank you cards)" },
															{ tag: '{{userName}}', desc: "Sender's display name" },
															{ tag: '{{eventName}}', desc: 'Event title' },
															{ tag: '{{eventDate}}', desc: 'Event date (formatted)' },
															{ tag: '{{pageLink}}', desc: 'Graduation page link (HTML)' },
															{ tag: '{{registryLink}}', desc: 'Registry page link (HTML)' },
															{ tag: '{{amount}}', desc: 'Contribution dollar amount' },
															{ tag: '{{itemName}}', desc: 'Registry item name' },
															{ tag: '{{itemsCount}}', desc: 'Number of items in registry' },
															{ tag: '{{message}}', desc: 'Custom message body' },
															{ tag: '{{contributorName}}', desc: "Contributor's name" },
															{ tag: '{{goalAmount}}', desc: 'Fundraising goal amount' },
															{ tag: '{{currentAmount}}', desc: 'Current total raised' },
														].map(({ tag, desc }) => (
															<div key={tag} style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
																<code style={{ fontSize: 11, color: theme.primary, backgroundColor: theme.primary + '18', borderRadius: 4, padding: '2px 6px', fontFamily: 'monospace', whiteSpace: 'nowrap', flexShrink: 0 }}>{tag}</code>
																<span style={{ fontSize: 11, color: theme.textMuted }}>{desc}</span>
															</div>
														))}
													</div>
												</View>
											)}
										</View>

										{/* Email Categories */}
										{Object.entries(getEmailsByCategory()).map(([category, emails]) => {
												const categoryConfig = emailCategoryConfig[category] || { icon: 'mail', color: '#6b7280' };
												const enabledCount = emails.filter(e => emailSettings[e.id]?.enabled !== false).length;
												const isCollapsed = collapsedEmailCategories[category];
												
												return (
													<View key={category} style={[styles.ffCategorySection, { backgroundColor: theme.ffCardBg, borderColor: theme.ffCardBorder }]}>
														<Pressable 
															onPress={() => toggleEmailCategoryCollapse(category)}
															style={styles.ffCategoryHeaderClickable}
														>
															<View style={styles.ffCategoryHeaderLeft}>
																<Ionicons name={categoryConfig.icon} size={16} color={categoryConfig.color} />
																<Text style={[styles.ffCategoryTitle, { color: theme.textPrimary }]}>{category}</Text>
																<View style={styles.ffCategoryBadge}>
																	<Text style={styles.ffCategoryBadgeText}>{enabledCount}/{emails.length}</Text>
																</View>
															</View>
															<Ionicons 
																name={isCollapsed ? "chevron-down" : "chevron-up"} 
																size={18} 
																color={theme.textMuted} 
															/>
														</Pressable>
														
														{!isCollapsed && (
															<View style={styles.ffCollapsibleContent}>
																<Text style={[styles.ffCategoryDesc, { color: theme.textMuted, borderBottomColor: theme.border }]}>
																	{categoryConfig.description || `${category} email templates`}
																</Text>
																<View style={styles.ffCategoryFlags}>
																	{emails.map((email, emailIdx) => {
																		const isEnabled = emailSettings[email.id]?.enabled !== false;
																		const isExpanded = expandedEmailPreviewIds.includes(email.id);
																		const template = getEmailTemplate(email.id) || { subject: `MyTassel | ${email.name}`, html: '' };
																		const isEditing = editingTemplate === email.id;
																		const isConnected = email.backendConnected !== false;
																		
																		return (
																			<View key={email.id} style={[styles.ffCard, { backgroundColor: theme.ffItemBg, borderColor: !isConnected ? '#ef444440' : theme.ffItemBorder, opacity: isConnected ? (isEnabled ? 1 : 0.6) : 0.5 }]}>
																				<View style={styles.ffCardContent}>
																					<View style={[styles.ffCardInfo, { flex: 1 }]}>
																						<View style={styles.ffCardTitleRow}>
																							<Text style={[styles.ffCardTitle, { color: theme.textPrimary }]}>{email.name}</Text>
																							{!isConnected && (
																								<View style={[styles.ffImpactBadge, { backgroundColor: '#ef444420', borderColor: '#ef4444' }]}>
																									<Text style={[styles.ffImpactText, { color: '#ef4444' }]}>NOT CONNECTED</Text>
																								</View>
																							)}
																							{email.triggerType === 'scheduled' && (
																								<View style={[styles.ffImpactBadge, { backgroundColor: '#f59e0b20', borderColor: '#f59e0b' }]}>
																									<Text style={[styles.ffImpactText, { color: '#f59e0b' }]}>SCHEDULED</Text>
																								</View>
																							)}
																							{email.triggerType === 'event' && (
																								<View style={[styles.ffImpactBadge, { backgroundColor: '#3b82f620', borderColor: '#3b82f6' }]}>
																									<Text style={[styles.ffImpactText, { color: '#3b82f6' }]}>EVENT</Text>
																								</View>
																							)}
																							{email.isNew && (
																								<View style={[styles.ffImpactBadge, { backgroundColor: '#10b98120', borderColor: '#10b981' }]}>
																									<Text style={[styles.ffImpactText, { color: '#10b981' }]}>NEW</Text>
																								</View>
																							)}
																						</View>
																						<Text style={[styles.ffCardDesc, { color: theme.textMuted }]}>{email.description}</Text>
																						{!isConnected && (
																							<Text style={{ color: '#ef4444', fontSize: 11, marginTop: 4, fontWeight: '500' }}>
																								Template editing not available — backend uses a different sending path.
																							</Text>
																						)}
																						<View style={{ marginTop: 8, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
																							<Ionicons name="flash-outline" size={12} color={theme.textMuted} />
																							<Text style={{ color: theme.textMuted, fontSize: 11, fontStyle: 'italic' }}>
																								Trigger: {email.trigger}
																							</Text>
																						</View>
																						{email.triggerType === 'scheduled' && emailSchedule && (() => {
																							const pending = (emailSchedule.pendingSends || []).filter(e => e.emailType === email.id).length;
																							const upcoming = (emailSchedule.upcomingSends || []).filter(e => e.emailType === email.id).length;
																							if (pending === 0 && upcoming === 0) return null;
																							return (
																								<View style={{ marginTop: 6, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
																									<Ionicons name="mail-unread-outline" size={12} color={theme.primary} />
																									<View style={{ flexDirection: 'row', gap: 6 }}>
																										{pending > 0 && (
																											<View style={{ backgroundColor: '#22c55e20', borderRadius: 10, paddingHorizontal: 8, paddingVertical: 2 }}>
																												<Text style={{ color: '#22c55e', fontSize: 10, fontWeight: '700' }}>{pending} queued</Text>
																											</View>
																										)}
																										{upcoming > 0 && (
																											<View style={{ backgroundColor: '#f59e0b20', borderRadius: 10, paddingHorizontal: 8, paddingVertical: 2 }}>
																												<Text style={{ color: '#f59e0b', fontSize: 10, fontWeight: '700' }}>{upcoming} upcoming</Text>
																											</View>
																										)}
																									</View>
																								</View>
																							);
																						})()}
																					</View>
																					<View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
																						{isConnected && (
																						<Pressable
																							onPress={() => {
																								setEmailPreviewData({ id: email.id, name: email.name, html: renderEmailPreview(email.id), subject: template.subject });
																								setShowEmailPreviewModal(true);
																							}}
																							style={{
																								padding: 6,
																								borderRadius: 4,
																								backgroundColor: '#3b82f620'
																							}}
																						>
																							<Ionicons name="eye" size={18} color="#3b82f6" />
																						</Pressable>
																						)}
																						{isConnected && (
																						<Pressable
																							onPress={() => {
																								setEditingTemplate(email.id);
																								setEditingTemplateContent(template.html || '');
																								setEditingSubject(template.subject || '');
																								setUseVisualEditor(true);
																							}}
																							style={{
																								padding: 6,
																								borderRadius: 4,
																								backgroundColor: '#f59e0b20'
																							}}
																						>
																							<Ionicons name="create-outline" size={18} color="#f59e0b" />
																						</Pressable>
																						)}
																						{isConnected && emailSettings.templates?.[email.id] && (
																						<Pressable
																							onPress={() => {
																								if (window.confirm(`Reset "${email.name}" to the built-in default template? This cannot be undone.`)) {
																									resetEmailTemplate(email.id);
																								}
																							}}
																							style={{
																								padding: 6,
																								borderRadius: 4,
																								backgroundColor: '#ef444420'
																							}}
																						>
																							<Ionicons name="refresh-outline" size={18} color="#ef4444" />
																						</Pressable>
																						)}
																						{isConnected && (
																						<Pressable
																							onPress={() => toggleSingleEmailPreview(email.id)}
																							style={{
																								padding: 6,
																								borderRadius: 4,
																								backgroundColor: isExpanded ? theme.primary + '20' : 'transparent'
																							}}
																						>
																							<Ionicons 
																								name={isExpanded ? "code-slash" : "code-slash-outline"} 
																								size={18} 
																								color={isExpanded ? theme.primary : theme.textMuted} 
																							/>
																						</Pressable>
																						)}
																						{email.canDisable && isConnected && (
																							<button
																								onClick={() => toggleEmailEnabled(email.id)}
																								disabled={emailSettingsSaving}
																								style={{
																									...styles.ffToggleBtn,
																									...(isEnabled ? styles.ffToggleBtnOn : styles.ffToggleBtnOff),
																									opacity: emailSettingsSaving ? 0.5 : 1
																								}}
																							>
																								{isEnabled ? 'ON' : 'OFF'}
																							</button>
																						)}
																						{!email.canDisable && isConnected && (
																							<View style={{ 
																								padding: '4px 12px', 
																								backgroundColor: theme.textMuted + '20',
																								borderRadius: 4
																							}}>
																								<Text style={{ color: theme.textMuted, fontSize: 11 }}>REQUIRED</Text>
																							</View>
																						)}
																					</View>
																				</View>
																				
																				{/* Inline Visual Quick-Edit */}
																			{isExpanded && email.configurableDays && (
																				<View style={{ marginTop: 8, padding: 10, backgroundColor: isDarkMode ? '#0f172a22' : '#f0f9ff', borderRadius: 8, borderWidth: 1, borderColor: '#3b82f640' }}>
																					<View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
																						<Ionicons name="timer-outline" size={14} color="#3b82f6" />
																						<Text style={{ color: '#3b82f6', fontSize: 12, fontWeight: '700' }}>Trigger Threshold</Text>
																					</View>
																					<Text style={{ color: theme.textMuted, fontSize: 11, marginTop: 4, marginBottom: 8 }}>
																						{email.configurableDays.label} — sends after this many days.
																					</Text>
																					<View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
																						<TextInput
																							value={String(emailSettings[email.id]?.daysTrigger ?? email.configurableDays.default)}
																							onChangeText={(val) => {
																								const num = parseInt(val, 10);
																								if (!isNaN(num) && num > 0) {
																									setEmailSettings(prev => ({
																										...prev,
																										[email.id]: { ...(prev[email.id] || {}), daysTrigger: num }
																									}));
																								}
																							}}
																							keyboardType="numeric"
																							style={{
																								width: 80, padding: 8, backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
																								color: theme.textPrimary, borderWidth: 1, borderColor: '#3b82f660',
																								borderRadius: 6, fontSize: 14, fontWeight: '700', textAlign: 'center'
																							}}
																						/>
																						<Text style={{ color: theme.textMuted, fontSize: 12 }}>days</Text>
																						{(emailSettings[email.id]?.daysTrigger ?? email.configurableDays.default) !== email.configurableDays.default && (
																							<Text style={{ color: '#f59e0b', fontSize: 11 }}>
																								(default: {email.configurableDays.default})
																							</Text>
																						)}
																						<button
																							onClick={() => saveEmailSettings({ [email.id]: { ...(emailSettings[email.id] || {}), daysTrigger: emailSettings[email.id]?.daysTrigger ?? email.configurableDays.default } })}
																							disabled={emailSettingsSaving}
																							style={{ ...styles.ffToggleBtn, ...styles.ffToggleBtnOn, padding: '6px 14px', fontSize: 11, opacity: emailSettingsSaving ? 0.5 : 1, marginLeft: 'auto' }}
																						>
																							Save
																						</button>
																					</View>
																				</View>
																			)}

																			{isExpanded && template && (
																				<View style={{ 
																					marginTop: 8, 
																					padding: 10, 
																					backgroundColor: isDarkMode ? '#0f172a' : '#f8fafc',
																					borderRadius: 8,
																					borderWidth: 1,
																					borderColor: '#f59e0b40'
																				}}>
																					{/* Editable Subject */}
																						<View style={{ marginBottom: 8 }}>
																							<Text style={{ color: theme.textMuted, fontSize: 10, marginBottom: 3, fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.5 }}>Subject</Text>
																							<TextInput
																								value={inlineEdits[email.id]?.subject ?? template.subject ?? ''}
																								onChangeText={(text) => setInlineEdits(prev => ({
																									...prev,
																									[email.id]: { ...(prev[email.id] || { html: template.html || '' }), subject: text }
																								}))}
																								style={{
																									width: '100%',
																									padding: 8,
																									backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
																									color: theme.textPrimary,
																									borderWidth: 1,
																									borderColor: theme.border,
																									borderRadius: 6,
																									fontSize: 13
																								}}
																							/>
																						</View>
																						{/* Visual Editable Body */}
																						<View style={{ marginBottom: 8 }}>
																							<Text style={{ color: theme.textMuted, fontSize: 10, marginBottom: 3, fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.5 }}>Email Body</Text>
																							<div
																								contentEditable
																								suppressContentEditableWarning
																								ref={(el) => {
																									if (el && !el.dataset.initialized) {
																										el.innerHTML = inlineEdits[email.id]?.html ?? template.html ?? '';
																										el.dataset.initialized = 'true';
																									}
																								}}
																								onInput={(e) => {
																									const updatedHtml = e.currentTarget?.innerHTML || '';
																									setInlineEdits(prev => ({
																										...prev,
																										[email.id]: { ...(prev[email.id] || { subject: template.subject || '' }), html: updatedHtml }
																									}));
																								}}
																								style={{
																									width: '100%',
																									minHeight: 180,
																									maxHeight: 360,
																									overflowY: 'auto',
																									padding: 12,
																									backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
																									color: isDarkMode ? '#e2e8f0' : '#1e293b',
																									border: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`,
																									borderRadius: 6,
																									fontSize: 13,
																									lineHeight: '1.6',
																									outline: 'none',
																									boxSizing: 'border-box',
																									cursor: 'text'
																								}}
																							/>
																						</View>
																						{/* Save + Cancel */}
																						<View style={{ flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', gap: 6 }}>
																							<button
																								onClick={() => toggleSingleEmailPreview(email.id)}
																								style={{ ...styles.ffToggleBtn, ...styles.ffToggleBtnOff, padding: '4px 10px', fontSize: 11 }}
																							>
																								Cancel
																							</button>
																							<button
																								onClick={() => {
																									const edit = inlineEdits[email.id];
																									if (edit) {
																										saveEmailTemplate(email.id, { subject: edit.subject, html: edit.html });
																									}
																								}}
																								disabled={emailSettingsSaving}
																								style={{ ...styles.ffToggleBtn, ...styles.ffToggleBtnOn, padding: '4px 10px', fontSize: 11 }}
																							>
																								{emailSettingsSaving ? 'Saving...' : 'Save'}
																							</button>
																						</View>
																					</View>
																				)}

																				{/* Template Editor - admin-friendly visual or HTML */}
																				{isEditing && (
																					<View style={{ 
																						marginTop: 12, 
																						padding: 16, 
																						backgroundColor: isDarkMode ? '#0f172a' : '#f8fafc',
																						borderRadius: 8,
																						borderWidth: 2,
																						borderColor: '#f59e0b'
																					}}>
																						<View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
																							<Text style={{ color: theme.textPrimary, fontSize: 14, fontWeight: '600' }}>
																								Edit email: {email.name}
																							</Text>
																							<View style={{ flexDirection: 'row', gap: 8 }}>
																								<button
																									onClick={() => {
																										const subject = editingSubject.trim();
																										const html = useVisualEditor ? getVisualEditorHtml() : editingTemplateContent;
																										saveEmailTemplate(email.id, { subject: subject || template.subject, html });
																									}}
																									disabled={emailSettingsSaving}
																									style={{ ...styles.ffToggleBtn, ...styles.ffToggleBtnOn, padding: '6px 12px' }}
																								>
																									{emailSettingsSaving ? 'Saving...' : 'Save'}
																								</button>
																								<button
																									onClick={() => { setEditingTemplate(null); setEditingTemplateContent(''); setEditingSubject(''); }}
																									style={{ ...styles.ffToggleBtn, ...styles.ffToggleBtnOff, padding: '6px 12px' }}
																								>
																									Cancel
																								</button>
																							</View>
																						</View>

																						{/* Subject line - plain text */}
																						<View style={{ marginBottom: 12 }}>
																							<Text style={{ color: theme.textSecondary, fontSize: 12, fontWeight: '600', marginBottom: 4 }}>Subject line</Text>
																							<TextInput
																								value={editingSubject}
																								onChangeText={setEditingSubject}
																								placeholder="e.g. Hi {{firstName}}, complete your profile"
																								placeholderTextColor={theme.textMuted}
																								style={{
																									width: '100%',
																									padding: 10,
																									backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
																									color: theme.textPrimary,
																									borderWidth: 1,
																									borderColor: theme.border,
																									borderRadius: 8,
																									fontSize: 14
																								}}
																							/>
																							<Text style={{ color: theme.textMuted, fontSize: 11, marginTop: 4 }}>Use {'{{firstName}}'} etc. for personalization — insert from the dropdown below in the body.</Text>
																						</View>

																						{/* Visual vs HTML mode toggle */}
																						<View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 10, gap: 12 }}>
																							<Text style={{ color: theme.textSecondary, fontSize: 12, fontWeight: '600' }}>Email body:</Text>
																							<View style={{ flexDirection: 'row', backgroundColor: isDarkMode ? '#1e293b' : '#e2e8f0', borderRadius: 8, padding: 2 }}>
																								<button
																									type="button"
																									onClick={() => setUseVisualEditor(true)}
																									style={{
																										padding: '6px 12px',
																										borderRadius: 6,
																										fontSize: 12,
																										fontWeight: '500',
																										border: 'none',
																										backgroundColor: useVisualEditor ? (isDarkMode ? '#334155' : '#fff') : 'transparent',
																										color: useVisualEditor ? theme.textPrimary : theme.textMuted,
																										boxShadow: useVisualEditor ? '0 1px 2px rgba(0,0,0,0.05)' : 'none'
																									}}
																								>
																									Visual (recommended)
																								</button>
																								<button
																									type="button"
																									onClick={() => {
																										if (useVisualEditor && emailBodyEditorRef.current) {
																											setEditingTemplateContent(emailBodyEditorRef.current.innerHTML);
																										}
																										setUseVisualEditor(false);
																									}}
																									style={{
																										padding: '6px 12px',
																										borderRadius: 6,
																										fontSize: 12,
																										fontWeight: '500',
																										border: 'none',
																										backgroundColor: !useVisualEditor ? (isDarkMode ? '#334155' : '#fff') : 'transparent',
																										color: !useVisualEditor ? theme.textPrimary : theme.textMuted,
																										boxShadow: !useVisualEditor ? '0 1px 2px rgba(0,0,0,0.05)' : 'none'
																									}}
																								>
																									HTML (advanced)
																								</button>
																							</View>
																						</View>

																						{useVisualEditor ? (
																							<>
																								{/* Toolbar: formatting + insert variable */}
																								<View style={{ 
																									flexDirection: 'row', 
																									alignItems: 'center', 
																									flexWrap: 'wrap',
																									gap: 6, 
																									marginBottom: 8, 
																									padding: '8px 0',
																									borderBottomWidth: 1,
																									borderBottomColor: theme.border
																								}}>
																									<button type="button" onClick={() => document.execCommand('bold')} style={{ ...styles.ffToggleBtn, ...styles.ffToggleBtnOff, padding: '6px 10px', fontSize: 12 }}><b>B</b></button>
																									<button type="button" onClick={() => document.execCommand('italic')} style={{ ...styles.ffToggleBtn, ...styles.ffToggleBtnOff, padding: '6px 10px', fontSize: 12 }}><i>I</i></button>
																									<button type="button" onClick={() => document.execCommand('underline')} style={{ ...styles.ffToggleBtn, ...styles.ffToggleBtnOff, padding: '6px 10px', fontSize: 12 }}><u>U</u></button>
																									<button type="button" onClick={() => { const url = prompt('Link URL:'); if (url) document.execCommand('createLink', false, url); }} style={{ ...styles.ffToggleBtn, ...styles.ffToggleBtnOff, padding: '6px 10px', fontSize: 12 }}>Link</button>
																									<View style={{ width: 1, height: 20, backgroundColor: theme.border, marginHorizontal: 4 }} />
																									<Text style={{ color: theme.textMuted, fontSize: 12, marginRight: 4 }}>Insert:</Text>
																									<select
																										value=""
																										onChange={(e) => { const v = e.target.value; if (v) { insertEmailVariable(v); e.target.value = ''; } }}
																										style={{
																											padding: '6px 10px',
																											borderRadius: 6,
																											border: `1px solid ${theme.border}`,
																											backgroundColor: isDarkMode ? '#1e293b' : '#fff',
																											color: theme.textPrimary,
																											fontSize: 12,
																											minWidth: 140
																										}}
																									>
																										<option value="">Personalization...</option>
																										{emailVariableOptions.map((opt) => (
																											<option key={opt.value} value={opt.value}>{opt.label} ({`{{${opt.value}}}`})</option>
																										))}
																									</select>
																								</View>
																								{/* Visual editable area */}
																								<div
																									ref={emailBodyEditorRef}
																									contentEditable
																									suppressContentEditableWarning
																									style={{
																										minHeight: 280,
																										padding: 12,
																										backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
																										color: theme.textPrimary,
																										border: `1px solid ${theme.border}`,
																										borderRadius: 8,
																										fontSize: 14,
																										lineHeight: 1.5,
																										outline: 'none'
																									}}
																									onClick={() => {}}
																								/>
																								<Text style={{ color: theme.textMuted, fontSize: 11, marginTop: 8 }}>
																									Edit the email body above. Use the toolbar for bold/italic/link and the dropdown to insert personalization like first name.
																								</Text>
																							</>
																						) : (
																							<>
																								<Text style={{ color: theme.textMuted, fontSize: 11, marginBottom: 6 }}>HTML source — for advanced users only.</Text>
																								<textarea
																									value={editingTemplateContent}
																									onChange={(e) => setEditingTemplateContent(e.target.value)}
																									style={{
																										width: '100%',
																										minHeight: 280,
																										backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
																										color: theme.textPrimary,
																										border: `1px solid ${theme.border}`,
																										borderRadius: 8,
																										padding: 12,
																										fontFamily: 'monospace',
																										fontSize: 12,
																										resize: 'vertical'
																									}}
																								/>
																							</>
																						)}
																					</View>
																				)}
																			</View>
																		);
																	})}

																	{/* Default message templates — inside the Thank You category card */}
																	{category === 'Thank You' && (
																		<>
																			{thankYouTemplatesLoading ? null : thankYouTemplates.map((tpl, idx) => {
																				const isTplExpanded = expandedTyMsgIds.includes(tpl.id || idx);
																				return (
																					<View key={tpl.id || idx} style={[styles.ffCard, { backgroundColor: theme.ffItemBg, borderColor: theme.ffItemBorder }]}>
																						<View style={styles.ffCardContent}>
																							<View style={[styles.ffCardInfo, { flex: 1 }]}>
																								<View style={styles.ffCardTitleRow}>
																									<Text style={[styles.ffCardTitle, { color: theme.textPrimary }]}>{tpl.name}</Text>
																									<View style={[styles.ffImpactBadge, { backgroundColor: '#ec489920', borderColor: '#ec4899' }]}>
																										<Text style={[styles.ffImpactText, { color: '#ec4899' }]}>MESSAGE</Text>
																									</View>
																								</View>
																								<Text style={[styles.ffCardDesc, { color: theme.textMuted }]} numberOfLines={2}>
																									{tpl.message}
																								</Text>
																							</View>
																							<View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
																								<Pressable
																									onPress={() => setExpandedTyMsgIds(prev => isTplExpanded ? prev.filter(x => x !== (tpl.id || idx)) : [...prev, tpl.id || idx])}
																									style={{ padding: 6, borderRadius: 4, backgroundColor: isTplExpanded ? '#ec489920' : theme.primary + '20' }}
																								>
																									<Ionicons name={isTplExpanded ? 'chevron-up' : 'create-outline'} size={16} color={isTplExpanded ? '#ec4899' : theme.primary} />
																								</Pressable>
																								<Pressable
																									onPress={() => setThankYouTemplates(prev => prev.filter((_, i) => i !== idx))}
																									style={{ padding: 6, borderRadius: 4, backgroundColor: '#ef444420' }}
																								>
																									<Ionicons name="trash-outline" size={16} color="#ef4444" />
																								</Pressable>
																							</View>
																						</View>
																						{isTplExpanded && (
																							<View style={{ borderTopWidth: 1, borderTopColor: theme.border, padding: 12, gap: 10 }}>
																								<View>
																									<Text style={{ color: theme.textMuted, fontSize: 11, marginBottom: 4 }}>Template Name</Text>
																									<input
																										type="text"
																										value={tpl.name}
																										onChange={(e) => {
																											const updated = [...thankYouTemplates];
																											updated[idx] = { ...updated[idx], name: e.target.value };
																											setThankYouTemplates(updated);
																										}}
																										style={{ width: '100%', fontSize: 13, fontWeight: 600, color: theme.textPrimary, backgroundColor: isDarkMode ? '#0f172a' : '#ffffff', border: `1px solid ${theme.border}`, borderRadius: 6, padding: '8px 10px', outline: 'none' }}
																									/>
																								</View>
																								<View>
																									<Text style={{ color: theme.textMuted, fontSize: 11, marginBottom: 4 }}>Message — use {'{{name}}'} for recipient's name</Text>
																									<textarea
																										value={tpl.message}
																										onChange={(e) => {
																											const updated = [...thankYouTemplates];
																											updated[idx] = { ...updated[idx], message: e.target.value };
																											setThankYouTemplates(updated);
																										}}
																										rows={6}
																										style={{
																											width: '100%', fontSize: 13, color: theme.textPrimary,
																											backgroundColor: isDarkMode ? '#0f172a' : '#ffffff',
																											border: `1px solid ${theme.border}`, borderRadius: 6,
																											padding: 10, resize: 'vertical', fontFamily: 'inherit', lineHeight: '1.6',
																										}}
																									/>
																								</View>
																							</View>
																						)}
																					</View>
																				);
																			})}

																			{/* Add + Save/Reset row */}
																			<View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 4 }}>
																				<button
																					onClick={() => {
																						const newId = `custom_${Date.now()}`;
																						const newTpl = { id: newId, name: 'New Template', message: 'Dear {{name}},\n\nThank you!\n\nBest wishes' };
																						setThankYouTemplates(prev => [...prev, newTpl]);
																						setExpandedTyMsgIds(prev => [...prev, newId]);
																					}}
																					style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: `1px dashed ${theme.border}`, borderRadius: 8, padding: '8px 14px', cursor: 'pointer', color: theme.primary, fontSize: 12, fontWeight: '600' }}
																				>
																					<Ionicons name="add-circle-outline" size={14} color={theme.primary} />
																					Add Template
																				</button>
																				<button
																					onClick={saveThankYouTemplates}
																					disabled={thankYouTemplatesSaving}
																					style={{ ...styles.ffToggleBtn, ...styles.ffToggleBtnOn, padding: '8px 18px', fontSize: 12, opacity: thankYouTemplatesSaving ? 0.6 : 1 }}
																				>
																					{thankYouTemplatesSaving ? 'Saving…' : 'Save Templates'}
																				</button>
																				<button
																					onClick={() => { if (window.confirm('Reset to defaults?')) setThankYouTemplates(DEFAULT_THANK_YOU_TEMPLATES); }}
																					style={{ padding: '8px 18px', borderRadius: 8, border: `1px solid #f59e0b60`, backgroundColor: '#f59e0b20', color: '#f59e0b', cursor: 'pointer', fontSize: 12, fontWeight: '600' }}
																				>
																					Reset to Defaults
																				</button>
																			</View>
																		</>
																	)}
																</View>

															</View>
														)}
													</View>
												);
											})}
								</View>
								))}

						{/* ─── SMS Management Section ─── */}
								<View style={{ borderTopWidth: 1, borderTopColor: theme.border, marginTop: 20, paddingTop: 8 }}>
									<button
										onClick={() => setNotifSectionOpen(s => ({ ...s, smsTemplates: !s.smsTemplates }))}
										style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 10, background: 'none', border: 'none', cursor: 'pointer', padding: '16px 0', width: '100%', textAlign: 'left' }}
									>
										<Ionicons name={notifSectionOpen.smsTemplates ? 'chevron-down' : 'chevron-forward'} size={16} color={theme.textMuted} />
										<View style={{ flex: 1 }}>
											<Text style={[styles.ffMainTitle, { color: theme.textPrimary, marginBottom: 2 }]}>All SMS Templates</Text>
											<Text style={[styles.ffSubtitle, { color: theme.textMuted, marginTop: 0 }]}>
												Manage all automated SMS messages sent by the system. Toggle messages on/off and edit templates.
											</Text>
										</View>
									</button>

								{notifSectionOpen.smsTemplates && <View style={styles.ffCategoriesGrid}>
										{/* SMS Expand/Collapse Toggle */}
										<View style={{ flexDirection: 'row', gap: 6, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center', width: '100%' }}>
											<Pressable
												onPress={toggleAllSmsPreviews}
												style={{
													flexDirection: 'row',
													alignItems: 'center',
													gap: 5,
													paddingVertical: 5,
													paddingHorizontal: 10,
													borderRadius: 6,
													backgroundColor: expandedSmsPreviewIds.length > 0 ? '#ef444420' : theme.primary + '15',
													borderWidth: 1,
													borderColor: expandedSmsPreviewIds.length > 0 ? '#ef444460' : theme.primary + '40'
												}}
											>
												<Ionicons
													name={expandedSmsPreviewIds.length >= getAllSmsIds().length ? "chevron-collapse-outline" : "expand-outline"}
													size={14}
													color={expandedSmsPreviewIds.length > 0 ? '#ef4444' : theme.primary}
												/>
												<Text style={{
													color: expandedSmsPreviewIds.length > 0 ? '#ef4444' : theme.primary,
													fontSize: 12,
													fontWeight: '600'
												}}>
													{expandedSmsPreviewIds.length >= getAllSmsIds().length ? 'Collapse all' : 'Expand all'}
												</Text>
											</Pressable>
											<Text style={{ color: theme.textMuted, fontSize: 11 }}>
												{expandedSmsPreviewIds.length > 0
													? `${expandedSmsPreviewIds.length} template${expandedSmsPreviewIds.length !== 1 ? 's' : ''} open`
													: 'Quick-edit SMS templates inline'}
											</Text>
											<Pressable
												onPress={() => {
													if (window.confirm('Reset ALL SMS templates to their default (no-emoji) versions? Any custom edits will be lost.')) {
														resetAllSmsTemplatesToDefault();
													}
												}}
												style={{
													flexDirection: 'row',
													alignItems: 'center',
													gap: 5,
													paddingVertical: 5,
													paddingHorizontal: 10,
													borderRadius: 6,
													backgroundColor: '#f59e0b20',
													borderWidth: 1,
													borderColor: '#f59e0b60',
													marginLeft: 8,
												}}
											>
												<Ionicons name="refresh-outline" size={14} color="#f59e0b" />
												<Text style={{ color: '#f59e0b', fontSize: 12, fontWeight: '600' }}>
													Reset All to Defaults
												</Text>
											</Pressable>
										</View>

										{/* SMS Categories */}
										{Object.entries(getSmsByCategory()).map(([category, smsList]) => {
											const categoryConfig = smsCategoryConfig[category] || { icon: 'chatbubble', color: '#6b7280' };
											const enabledCount = smsList.filter(s => smsSettings[s.id]?.enabled !== false).length;
											const isSmsCollapsed = collapsedSmsCategories[category];

											return (
												<View key={category} style={[styles.ffCategorySection, { backgroundColor: theme.ffCardBg, borderColor: theme.ffCardBorder }]}>
													<Pressable
														onPress={() => setCollapsedSmsCategories(prev => ({ ...prev, [category]: !prev[category] }))}
														style={styles.ffCategoryHeaderClickable}
													>
														<View style={styles.ffCategoryHeaderLeft}>
															<Ionicons name={categoryConfig.icon} size={16} color={categoryConfig.color} />
															<Text style={[styles.ffCategoryTitle, { color: theme.textPrimary }]}>{category}</Text>
															<View style={[styles.ffCategoryCount, { backgroundColor: categoryConfig.color + '20' }]}>
																<Text style={[styles.ffCategoryCountText, { color: categoryConfig.color }]}>{enabledCount}/{smsList.length}</Text>
															</View>
														</View>
														<Ionicons
															name={isSmsCollapsed ? "chevron-forward" : "chevron-down"}
															size={16}
															color={theme.textMuted}
														/>
													</Pressable>

													{!isSmsCollapsed && (
														<View style={styles.ffCardList}>
															{smsList.map((sms) => {
																const isEnabled = smsSettings[sms.id]?.enabled !== false;
																const isSmsExpanded = expandedSmsPreviewIds.includes(sms.id);
																const template = getSmsTemplate(sms.id) || { body: '' };
																const isSmsConnected = sms.backendConnected !== false;

																return (
																	<View key={sms.id} style={[styles.ffCard, { backgroundColor: theme.ffItemBg, borderColor: !isSmsConnected ? '#ef444440' : theme.ffItemBorder, opacity: isSmsConnected ? (isEnabled ? 1 : 0.6) : 0.5 }]}>
																		<View style={styles.ffCardContent}>
																			<View style={[styles.ffCardInfo, { flex: 1 }]}>
																				<View style={styles.ffCardTitleRow}>
																					<Text style={[styles.ffCardTitle, { color: theme.textPrimary }]}>{sms.name}</Text>
																					{!isSmsConnected && (
																						<View style={[styles.ffImpactBadge, { backgroundColor: '#ef444420', borderColor: '#ef4444' }]}>
																							<Text style={[styles.ffImpactText, { color: '#ef4444' }]}>NOT CONNECTED</Text>
																						</View>
																					)}
																					{sms.triggerType === 'scheduled' && (
																						<View style={[styles.ffImpactBadge, { backgroundColor: '#f59e0b20', borderColor: '#f59e0b' }]}>
																							<Text style={[styles.ffImpactText, { color: '#f59e0b' }]}>SCHEDULED</Text>
																						</View>
																					)}
																					{sms.triggerType === 'event' && (
																						<View style={[styles.ffImpactBadge, { backgroundColor: '#3b82f620', borderColor: '#3b82f6' }]}>
																							<Text style={[styles.ffImpactText, { color: '#3b82f6' }]}>EVENT</Text>
																						</View>
																					)}
																				</View>
																				<Text style={[styles.ffCardDesc, { color: theme.textMuted }]}>{sms.description}</Text>
																				{!isSmsConnected && (
																					<Text style={{ color: '#ef4444', fontSize: 11, marginTop: 4, fontWeight: '500' }}>
																						Template editing not available — backend uses a different sending path.
																					</Text>
																				)}
																				<View style={{ marginTop: 8, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
																					<Ionicons name="flash-outline" size={12} color={theme.textMuted} />
																					<Text style={{ color: theme.textMuted, fontSize: 11, fontStyle: 'italic' }}>
																						Trigger: {sms.trigger}
																					</Text>
																				</View>
																			</View>
																			<View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
																				{isSmsConnected && (
																				<Pressable
																					onPress={() => toggleSingleSmsPreview(sms.id)}
																					style={{
																						padding: 6,
																						borderRadius: 4,
																						backgroundColor: isSmsExpanded ? '#4c956c20' : 'transparent'
																					}}
																				>
																					<Ionicons 
																						name={isSmsExpanded ? "create" : "create-outline"} 
																						size={18} 
																						color={isSmsExpanded ? '#4c956c' : theme.textMuted} 
																					/>
																				</Pressable>
																				)}
																				{sms.canDisable && isSmsConnected && (
																					<button
																						onClick={() => toggleSmsEnabled(sms.id)}
																						disabled={emailSettingsSaving}
																						style={{
																							...styles.ffToggleBtn,
																							...(isEnabled ? styles.ffToggleBtnOn : styles.ffToggleBtnOff),
																							opacity: emailSettingsSaving ? 0.5 : 1
																						}}
																					>
																						{isEnabled ? 'ON' : 'OFF'}
																					</button>
																				)}
																				{!sms.canDisable && isSmsConnected && (
																					<View style={{ 
																						padding: '4px 12px', 
																						backgroundColor: theme.textMuted + '20',
																						borderRadius: 4
																					}}>
																						<Text style={{ color: theme.textMuted, fontSize: 11 }}>REQUIRED</Text>
																					</View>
																				)}
																			</View>
																		</View>

																		{/* Inline SMS Quick-Edit */}
																		{isSmsExpanded && template && (
																			<View style={{ 
																				marginTop: 8, 
																				padding: 10, 
																				backgroundColor: isDarkMode ? '#0f172a' : '#f8fafc',
																				borderRadius: 8,
																				borderWidth: 1,
																				borderColor: '#4c956c40'
																			}}>
																				<View style={{ marginBottom: 8 }}>
																					<Text style={{ color: theme.textMuted, fontSize: 10, marginBottom: 3, fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.5 }}>Message Body</Text>
																					<textarea
																						value={inlineSmsEdits[sms.id]?.body ?? template.body ?? ''}
																						onChange={(e) => setInlineSmsEdits(prev => ({
																							...prev,
																							[sms.id]: { body: e.target.value }
																						}))}
																						rows={6}
																						style={{
																							width: '100%',
																							padding: 10,
																							backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
																							color: isDarkMode ? '#e2e8f0' : '#1e293b',
																							border: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`,
																							borderRadius: 6,
																							fontSize: 13,
																							lineHeight: '1.6',
																							fontFamily: 'inherit',
																							resize: 'vertical',
																							outline: 'none',
																							boxSizing: 'border-box'
																						}}
																					/>
																				</View>
																				<View style={{ flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', gap: 6 }}>
																					<button
																						onClick={() => resetSmsTemplateToDefault(sms.id)}
																						disabled={emailSettingsSaving}
																						style={{ ...styles.ffToggleBtn, ...styles.ffToggleBtnOff, padding: '4px 10px', fontSize: 11, color: '#f59e0b' }}
																					>
																						Reset to Default
																					</button>
																					<button
																						onClick={() => toggleSingleSmsPreview(sms.id)}
																						style={{ ...styles.ffToggleBtn, ...styles.ffToggleBtnOff, padding: '4px 10px', fontSize: 11 }}
																					>
																						Cancel
																					</button>
																				<button
																					onClick={() => {
																						const edit = inlineSmsEdits[sms.id];
																						const body = edit?.body ?? template.body ?? '';
																						saveSmsTemplate(sms.id, { body });
																						toggleSingleSmsPreview(sms.id);
																					}}
																					disabled={emailSettingsSaving}
																					style={{ ...styles.ffToggleBtn, ...styles.ffToggleBtnOn, padding: '4px 10px', fontSize: 11 }}
																				>
																					{emailSettingsSaving ? 'Saving...' : 'Save'}
																				</button>
																				</View>
																			</View>
																		)}
																	</View>
																);
															})}
														</View>
													)}
												</View>
											);
									})}
							</View>}
								</View>

					{/* ─── Reminder Timing Configuration ─── */}
					<View style={{ borderTopWidth: 1, borderTopColor: theme.border, marginTop: 20, paddingTop: 8 }}>
						<button
							onClick={() => setNotifSectionOpen(s => ({ ...s, reminderTiming: !s.reminderTiming }))}
							style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 10, background: 'none', border: 'none', cursor: 'pointer', padding: '16px 0', width: '100%', textAlign: 'left' }}
						>
							<Ionicons name={notifSectionOpen.reminderTiming ? 'chevron-down' : 'chevron-forward'} size={16} color={theme.textMuted} />
							<View style={{ flex: 1 }}>
								<Text style={[styles.ffMainTitle, { color: theme.textPrimary, marginBottom: 2 }]}>Reminder Timing Options</Text>
								<Text style={[styles.ffSubtitle, { color: theme.textMuted, marginTop: 0 }]}>
									Configure which reminder timing options appear for users on the events page, and set default send hours.
								</Text>
							</View>
						</button>

						{notifSectionOpen.reminderTiming && (reminderTimingLoading ? (
							<View style={styles.ffLoadingContainer}>
								<Text style={[styles.ffLoading, { color: theme.textMuted }]}>Loading reminder timing config...</Text>
							</View>
						) : (
							<View style={{ gap: 16, paddingBottom: 20 }}>
								{/* Day-of send time */}
								<View style={[styles.ffCategorySection, { backgroundColor: theme.ffCardBg, borderColor: theme.ffCardBorder }]}>
									<View style={{ padding: 16 }}>
										<Text style={{ fontWeight: '600', color: theme.textPrimary, fontSize: 14, marginBottom: 4 }}>Day-of Reminder Send Time</Text>
										<Text style={{ color: theme.textMuted, fontSize: 12, marginBottom: 10 }}>What time should the "Day of Event" reminder be sent to guests?</Text>
										<select
											value={reminderTimingConfig.dayOfSendHourUTC}
											onChange={(e) => setReminderTimingConfig(prev => ({ ...prev, dayOfSendHourUTC: parseInt(e.target.value, 10) }))}
											style={{ ...reminderSelectStyle, width: 260, border: `1px solid ${theme.border}`, backgroundColor: theme.bgSection, color: theme.textPrimary }}
										>
											{sendTimeOptions.map(t => (
												<option key={t.utc} value={t.utc}>{t.label}</option>
											))}
										</select>
									</View>
								</View>

								{/* Timing options list */}
								<View style={[styles.ffCategorySection, { backgroundColor: theme.ffCardBg, borderColor: theme.ffCardBorder }]}>
									<View style={{ padding: 16 }}>
										<Text style={{ fontWeight: '600', color: theme.textPrimary, fontSize: 14, marginBottom: 4 }}>Available Reminder Options</Text>
										<Text style={{ color: theme.textMuted, fontSize: 12, marginBottom: 14 }}>
											These are the options users see when setting up event reminders. Each option always includes a "Day of Event" reminder.
										</Text>

										{reminderTimingConfig.defaultOptions.map((opt, idx) => (
											<View key={opt.value || idx} style={{ flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center', gap: 10, paddingVertical: 12, borderBottomWidth: idx < reminderTimingConfig.defaultOptions.length - 1 ? 1 : 0, borderBottomColor: theme.border }}>
												<View style={{ flex: 1, gap: 8 }}>
													<View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
														<Text style={{ color: theme.textMuted, fontSize: 12, width: 85 }}>Display label</Text>
														<input
															type="text"
															value={opt.label}
															onChange={(e) => {
																const updated = [...reminderTimingConfig.defaultOptions];
																updated[idx] = { ...updated[idx], label: e.target.value };
																setReminderTimingConfig(prev => ({ ...prev, defaultOptions: updated }));
															}}
															style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: `1px solid ${theme.border}`, fontSize: 13, backgroundColor: theme.bgSection, color: theme.textPrimary }}
														/>
													</View>
													<View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
														<Text style={{ color: theme.textMuted, fontSize: 12, width: 85 }}>Send timing</Text>
														<select
															value={opt.daysBefore}
															onChange={(e) => {
																const updated = [...reminderTimingConfig.defaultOptions];
																const days = parseInt(e.target.value, 10);
																updated[idx] = { ...updated[idx], daysBefore: days, value: `days_before_${days}` };
																setReminderTimingConfig(prev => ({ ...prev, defaultOptions: updated }));
															}}
															style={{ ...reminderSelectStyle, border: `1px solid ${theme.border}`, backgroundColor: theme.bgSection, color: theme.textPrimary }}
														>
															{daysBeforeOptions.map(d => (
																<option key={d.days} value={d.days}>{d.label}</option>
															))}
														</select>
														<Text style={{ color: theme.textMuted, fontSize: 12 }}>at</Text>
														<select
															value={opt.sendHourUTC}
															onChange={(e) => {
																const updated = [...reminderTimingConfig.defaultOptions];
																updated[idx] = { ...updated[idx], sendHourUTC: parseInt(e.target.value, 10) };
																setReminderTimingConfig(prev => ({ ...prev, defaultOptions: updated }));
															}}
															style={{ ...reminderSelectStyle, border: `1px solid ${theme.border}`, backgroundColor: theme.bgSection, color: theme.textPrimary }}
														>
															{sendTimeOptions.map(t => (
																<option key={t.utc} value={t.utc}>{t.label}</option>
															))}
														</select>
													</View>
												</View>
												<button
													onClick={() => {
														const updated = reminderTimingConfig.defaultOptions.filter((_, i) => i !== idx);
														setReminderTimingConfig(prev => ({ ...prev, defaultOptions: updated }));
													}}
													style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, alignSelf: isMobile ? 'flex-end' : 'center' }}
													title="Remove this option"
												>
													<Ionicons name="trash-outline" size={16} color="#dc2626" />
												</button>
											</View>
										))}

										{/* Add new option */}
										<button
											onClick={() => {
												const existingDays = reminderTimingConfig.defaultOptions.map(o => o.daysBefore);
												const nextDays = daysBeforeOptions.find(d => !existingDays.includes(d.days))?.days || 7;
												setReminderTimingConfig(prev => ({
													...prev,
													defaultOptions: [...prev.defaultOptions, {
														value: `days_before_${nextDays}`,
														label: `Day of Event + ${nextDays === 7 ? '1 Week' : nextDays + ' Days'} Before`,
														daysBefore: nextDays,
														sendHourUTC: 15,
													}],
												}));
											}}
											style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: `1px dashed ${theme.border}`, borderRadius: 8, padding: '10px 16px', cursor: 'pointer', marginTop: 12, color: theme.primary, fontSize: 13, fontWeight: '500' }}
										>
											<Ionicons name="add-circle-outline" size={16} color={theme.primary} />
											Add Reminder Option
										</button>
									</View>
								</View>

								{/* Save button */}
								<button
									onClick={saveReminderTimingConfig}
									disabled={reminderTimingSaving}
									style={{
										...styles.ffToggleBtn,
										...styles.ffToggleBtnOn,
										padding: '12px 28px',
										fontSize: 14,
										alignSelf: 'flex-start',
										opacity: reminderTimingSaving ? 0.6 : 1,
									}}
								>
									{reminderTimingSaving ? 'Saving...' : 'Save Reminder Timing'}
								</button>
							</View>
						))}
					</View>

					{/* Email Preview Modal */}
								{showEmailPreviewModal && emailPreviewData && (
										<View style={{
											position: 'fixed',
											top: 0,
											left: 0,
											right: 0,
											bottom: 0,
											backgroundColor: 'rgba(0,0,0,0.7)',
											zIndex: 1000,
											display: 'flex',
											justifyContent: 'center',
											alignItems: 'center',
											padding: 20
										}}>
											<View style={{
												backgroundColor: theme.bgSection,
												borderRadius: 12,
												maxWidth: 700,
												width: '100%',
												maxHeight: '90vh',
												overflow: 'hidden',
												display: 'flex',
												flexDirection: 'column'
											}}>
												<View style={{
													padding: 20,
													borderBottomWidth: 1,
													borderBottomColor: theme.border,
													flexDirection: 'row',
													justifyContent: 'space-between',
													alignItems: 'center'
												}}>
													<View>
														<Text style={{ color: theme.textPrimary, fontSize: 18, fontWeight: '600' }}>
															Email Preview: {emailPreviewData.name}
														</Text>
														<Text style={{ color: theme.textMuted, fontSize: 13, marginTop: 4 }}>
															Subject: {emailPreviewData.subject}
														</Text>
													</View>
													<Pressable
														onPress={() => { setShowEmailPreviewModal(false); setEmailPreviewData(null); }}
														style={{ padding: 8 }}
													>
														<Ionicons name="close" size={24} color={theme.textMuted} />
													</Pressable>
												</View>
												<View style={{ flex: 1, overflow: 'auto', backgroundColor: '#ffffff' }}>
													<iframe
														srcDoc={emailPreviewData.html}
														style={{
															width: '100%',
															height: 500,
															border: 'none'
														}}
														title="Email Preview"
													/>
												</View>
												<View style={{
													padding: 16,
													borderTopWidth: 1,
													borderTopColor: theme.border,
													flexDirection: 'row',
													justifyContent: 'flex-end',
													gap: 12
												}}>
													<button
														onClick={() => { setShowEmailPreviewModal(false); setEmailPreviewData(null); }}
														style={{ ...styles.ffToggleBtn, ...styles.ffToggleBtnOff, padding: '8px 20px' }}
													>
														Close
													</button>
												</View>
											</View>
										</View>
									)}
									
							</View>
						</View>
					)}

					{activeTab === "registry-audit" && (
						<View style={styles.section}>
							<View style={styles.maintenanceTools}>

								{/* Automation Status Panel */}
								<View style={styles.maintenanceSection}>
									<Text style={styles.subHeader}>Automation Schedule</Text>
									{(() => {
										// Compute next Sunday 3 AM Pacific
										const now = new Date();
										const pacificOffset = -8 * 60; // PST (approx; no DST logic needed for display)
										const pacificNow = new Date(now.getTime() + (now.getTimezoneOffset() + pacificOffset) * 60000);
										const daysUntilSunday = (7 - pacificNow.getDay()) % 7 || 7;
										const nextRun = new Date(pacificNow);
										nextRun.setDate(nextRun.getDate() + daysUntilSunday);
										nextRun.setHours(3, 0, 0, 0);
										const lastRun = registryAuditHistory[0];
										return (
											<View style={{ flexDirection: 'row', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
												<View style={{ backgroundColor: '#f0f9ff', borderRadius: 8, padding: 14, flex: 1, minWidth: 180, borderWidth: 1, borderColor: '#bae6fd' }}>
													<Text style={{ fontSize: 11, color: '#0284c7', fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.5 }}>Schedule</Text>
													<Text style={{ fontSize: 14, color: '#0c4a6e', marginTop: 4, fontWeight: '600' }}>Every Sunday · 3:00 AM Pacific</Text>
													<Text style={{ fontSize: 12, color: '#0284c7', marginTop: 2 }}>Auto-scans and repairs missing Amazon metadata</Text>
												</View>
												<View style={{ backgroundColor: '#f8fafc', borderRadius: 8, padding: 14, flex: 1, minWidth: 180, borderWidth: 1, borderColor: '#e2e8f0' }}>
													<Text style={{ fontSize: 11, color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.5 }}>Next Scheduled Run</Text>
													<Text style={{ fontSize: 14, color: '#1e293b', marginTop: 4, fontWeight: '600' }}>{nextRun.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</Text>
													<Text style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>~{daysUntilSunday} day{daysUntilSunday !== 1 ? 's' : ''} from now</Text>
												</View>
												<View style={{ backgroundColor: lastRun ? '#f0fdf4' : '#f8fafc', borderRadius: 8, padding: 14, flex: 1, minWidth: 180, borderWidth: 1, borderColor: lastRun ? '#bbf7d0' : '#e2e8f0' }}>
													<Text style={{ fontSize: 11, color: lastRun ? '#16a34a' : '#94a3b8', fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.5 }}>Last Run</Text>
													{lastRun ? (
														<>
															<Text style={{ fontSize: 14, color: '#15803d', marginTop: 4, fontWeight: '600' }}>
																{new Date(lastRun.triggeredAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
															</Text>
															<Text style={{ fontSize: 12, color: '#16a34a', marginTop: 2 }}>
																{lastRun.triggeredBy === 'scheduled' ? 'Automated' : 'Manual'} · {lastRun.fixResults ? `${lastRun.fixResults.totalItemsFixed} fixed` : 'Scan only'}
															</Text>
														</>
													) : (
														<Text style={{ fontSize: 13, color: '#94a3b8', marginTop: 4 }}>No runs recorded yet</Text>
													)}
												</View>
											</View>
										);
									})()}
								</View>

								{/* Manual Scan + Actions */}
								<View style={styles.maintenanceSection}>
									<Text style={styles.subHeader}>Manual Scan</Text>
									<Text style={{ color: '#64748b', marginBottom: 16, fontSize: 14 }}>
										Scans every user's registry and detects Amazon products with missing or incomplete metadata (name, description, image, price, affiliate tag). Only items needing repair are updated.
									</Text>

									<View style={{ flexDirection: 'row', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
										<button
											onClick={runRegistryAudit}
											disabled={registryAuditLoading || registryAuditFixAllLoading}
											style={{ ...styles.buttonApprove, opacity: (registryAuditLoading || registryAuditFixAllLoading) ? 0.6 : 1 }}
										>
											{registryAuditLoading ? 'Scanning...' : 'Scan All Registries'}
										</button>
										{registryAuditResults?.report?.length > 0 && (
											<button
												onClick={fixAllRegistryIssues}
												disabled={registryAuditFixAllLoading || registryAuditLoading}
												style={{ ...styles.buttonDeny, background: '#f59e0b', borderColor: '#f59e0b', opacity: (registryAuditFixAllLoading || registryAuditLoading) ? 0.6 : 1 }}
											>
												{registryAuditFixAllLoading ? 'Fixing All...' : `Fix All Issues (${registryAuditResults.report.length} users)`}
											</button>
										)}
									</View>

									{registryAuditError && (
										<View style={{ backgroundColor: '#fef2f2', borderRadius: 8, padding: 12, marginBottom: 16 }}>
											<Text style={{ color: '#dc2626', fontSize: 14 }}>{registryAuditError}</Text>
										</View>
									)}


									{registryAuditResults && (
										<View style={{ flexDirection: 'row', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
											{[
												{ label: 'Users Scanned', value: registryAuditResults.summary.totalUsersScanned, color: '#3b82f6' },
												{ label: 'Users With Issues', value: registryAuditResults.summary.usersWithIssues, color: registryAuditResults.summary.usersWithIssues > 0 ? '#f59e0b' : '#22c55e' },
												{ label: 'Amazon Items', value: registryAuditResults.summary.totalAmazonItems, color: '#8b5cf6' },
												{ label: 'Items With Issues', value: registryAuditResults.summary.totalItemsWithIssues, color: registryAuditResults.summary.totalItemsWithIssues > 0 ? '#ef4444' : '#22c55e' },
											].map(stat => (
												<View key={stat.label} style={{ backgroundColor: '#f8fafc', borderRadius: 8, padding: 16, minWidth: 130, alignItems: 'center', borderWidth: 1, borderColor: '#e2e8f0' }}>
													<Text style={{ fontSize: 28, fontWeight: '700', color: stat.color }}>{stat.value}</Text>
													<Text style={{ fontSize: 12, color: '#64748b', textAlign: 'center', marginTop: 4 }}>{stat.label}</Text>
												</View>
											))}
											<View style={{ backgroundColor: '#f8fafc', borderRadius: 8, padding: 16, minWidth: 200, justifyContent: 'center', borderWidth: 1, borderColor: '#e2e8f0' }}>
												<Text style={{ fontSize: 11, color: '#94a3b8' }}>Scanned at</Text>
												<Text style={{ fontSize: 13, color: '#475569', marginTop: 2 }}>
													{new Date(registryAuditResults.summary.scannedAt).toLocaleString()}
												</Text>
											</View>
										</View>
									)}
								</View>

								{/* Results Table */}
								{registryAuditResults?.report?.length > 0 && (
									<View style={styles.maintenanceSection}>
										<Text style={styles.subHeader}>
											Users With Issues ({registryAuditResults.report.length})
										</Text>
										<View style={styles.tableWrapper}>
											<table style={styles.table}>
												<thead>
													<tr>
														<th style={styles.tableHeader}>User</th>
														<th style={styles.tableHeader}>Amazon Items</th>
														<th style={styles.tableHeader}>Items With Issues</th>
														<th style={styles.tableHeader}>Problems</th>
														<th style={styles.tableHeader}>Actions</th>
													</tr>
												</thead>
												<tbody>
													{registryAuditResults.report.map(entry => (
														<React.Fragment key={entry.userEmail}>
															<tr
																style={{ ...styles.tableRow, cursor: 'pointer' }}
																onClick={() => setRegistryAuditExpandedUser(
																	registryAuditExpandedUser === entry.userEmail ? null : entry.userEmail
																)}
															>
																<td style={styles.tableCell}>
																	<View>
																		<Text style={{ fontWeight: '600', color: '#1e293b', fontSize: 14 }}>{entry.displayName}</Text>
																		<Text style={{ color: '#94a3b8', fontSize: 12 }}>{entry.userEmail}</Text>
																	</View>
																</td>
																<td style={{ ...styles.tableCell, textAlign: 'center' }}>{entry.totalAmazonItems}</td>
																<td style={{ ...styles.tableCell, textAlign: 'center' }}>
																	<span style={{ background: '#fef2f2', color: '#dc2626', borderRadius: 999, padding: '2px 10px', fontSize: 13, fontWeight: '600' }}>
																		{entry.itemsWithIssues}
																	</span>
																</td>
																<td style={styles.tableCell}>
																	<View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 4 }}>
																		{[...new Set(entry.issues.flatMap(i => i.missingFields))].map(field => (
																			<span key={field} style={{ background: '#fef9c3', color: '#92400e', borderRadius: 4, padding: '1px 6px', fontSize: 11 }}>
																				{field}
																			</span>
																		))}
																	</View>
																</td>
																<td style={styles.tableCell}>
																	<View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
																		<button
																			onClick={(e) => { e.stopPropagation(); fixRegistryUser(entry.userEmail); }}
																			disabled={!!registryAuditFixing[entry.userEmail]?.active || registryAuditFixAllLoading}
																			style={{ ...styles.buttonApprove, fontSize: 13, padding: '6px 14px', opacity: (registryAuditFixing[entry.userEmail]?.active || registryAuditFixAllLoading) ? 0.6 : 1 }}
																		>
																			{registryAuditFixing[entry.userEmail]?.active
																				? `Fixing... (${registryAuditFixing[entry.userEmail].attempt}/5)`
																				: 'Fix User'}
																		</button>
																		{entry.userId && (
																			<a
																				href={`https://mytassel.com/registry/${entry.userId}`}
																				target="_blank"
																				rel="noopener noreferrer"
																				onClick={(e) => e.stopPropagation()}
																				style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 6, padding: '6px 12px', fontSize: 13, textDecoration: 'none', cursor: 'pointer', fontWeight: '500' }}
																			>
																				View Registry ↗
																			</a>
																		)}
																	</View>
																</td>
															</tr>
															{/* Expanded item details */}
															{registryAuditExpandedUser === entry.userEmail && (
																<tr>
																	<td colSpan={5} style={{ ...styles.tableCell, background: '#f8fafc', padding: 0 }}>
																		<View style={{ padding: 16 }}>
																			<Text style={{ fontWeight: '600', color: '#475569', marginBottom: 10, fontSize: 13 }}>
																				Items with issues for {entry.displayName}:
																			</Text>
																			<table style={{ width: '100%', borderCollapse: 'collapse' }}>
																				<thead>
																					<tr>
																						<th style={{ ...styles.tableHeader, fontSize: 12, padding: '6px 10px' }}>Item Name</th>
																						<th style={{ ...styles.tableHeader, fontSize: 12, padding: '6px 10px' }}>ASIN</th>
																						<th style={{ ...styles.tableHeader, fontSize: 12, padding: '6px 10px' }}>Missing Fields</th>
																						<th style={{ ...styles.tableHeader, fontSize: 12, padding: '6px 10px' }}>URL</th>
																					</tr>
																				</thead>
																				<tbody>
																					{entry.issues.map((issue, idx) => (
																						<tr key={idx} style={{ borderBottomWidth: 1, borderBottomColor: '#e2e8f0' }}>
																							<td style={{ ...styles.tableCell, fontSize: 13, padding: '6px 10px' }}>{issue.itemName}</td>
																							<td style={{ ...styles.tableCell, fontSize: 12, padding: '6px 10px', fontFamily: 'monospace' }}>{issue.asin || '—'}</td>
																							<td style={{ ...styles.tableCell, fontSize: 12, padding: '6px 10px' }}>
																								<View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 3 }}>
																									{issue.missingFields.map(f => (
																										<span key={f} style={{ background: '#fee2e2', color: '#b91c1c', borderRadius: 4, padding: '1px 6px', fontSize: 11 }}>{f}</span>
																									))}
																								</View>
																							</td>
																							<td style={{ ...styles.tableCell, fontSize: 11, padding: '6px 10px', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
																								{issue.url ? (
																									<a href={issue.url} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6' }}>
																										{issue.url.length > 50 ? issue.url.slice(0, 50) + '…' : issue.url}
																									</a>
																								) : '—'}
																							</td>
																						</tr>
																					))}
																				</tbody>
																			</table>
																		</View>
																	</td>
																</tr>
															)}
														</React.Fragment>
													))}
												</tbody>
											</table>
										</View>
									</View>
								)}

								{registryAuditResults?.report?.length === 0 && (
									<View style={styles.maintenanceSection}>
										<View style={{ backgroundColor: '#f0fdf4', borderRadius: 8, padding: 20, alignItems: 'center' }}>
											<Text style={{ color: '#16a34a', fontSize: 16, fontWeight: '600' }}>All Amazon registry items look good!</Text>
											<Text style={{ color: '#4ade80', fontSize: 13, marginTop: 4 }}>No missing metadata detected across any user's registry.</Text>
										</View>
									</View>
								)}

								{/* Automation Run History */}
								<View style={styles.maintenanceSection}>
									<View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
										<Text style={styles.subHeader}>Run History</Text>
										{registryAuditHistory.length > 0 && (
											<TouchableOpacity
												onPress={clearRegistryAuditHistory}
												disabled={registryAuditHistoryClearLoading}
												style={{ paddingHorizontal: 12, paddingVertical: 6, borderRadius: 6, borderWidth: 1, borderColor: '#fca5a5', backgroundColor: registryAuditHistoryClearLoading ? '#f8fafc' : '#fff', opacity: registryAuditHistoryClearLoading ? 0.6 : 1 }}
											>
												<Text style={{ color: '#dc2626', fontSize: 13, fontWeight: '500' }}>
													{registryAuditHistoryClearLoading ? 'Clearing...' : 'Clear History'}
												</Text>
											</TouchableOpacity>
										)}
									</View>
									{registryAuditHistoryLoading ? (
										<Text style={{ color: '#94a3b8', fontSize: 14, padding: 12 }}>Loading history...</Text>
									) : registryAuditHistory.length === 0 ? (
										<View style={{ backgroundColor: '#f8fafc', borderRadius: 8, padding: 20, alignItems: 'center', borderWidth: 1, borderColor: '#e2e8f0' }}>
											<Text style={{ color: '#94a3b8', fontSize: 14 }}>No audit runs recorded yet. The first entry will appear after the scheduled run or after you trigger a fix.</Text>
										</View>
									) : (
										<View style={styles.tableWrapper}>
											<table style={styles.table}>
												<thead>
													<tr>
														<th style={styles.tableHeader}>Date &amp; Time</th>
														<th style={styles.tableHeader}>Trigger</th>
														<th style={styles.tableHeader}>Users Scanned</th>
														<th style={styles.tableHeader}>Issues Found</th>
														<th style={styles.tableHeader}>Items Fixed</th>
														<th style={styles.tableHeader}>Status</th>
														<th style={styles.tableHeader}>Details</th>
													</tr>
												</thead>
												<tbody>
													{registryAuditHistory.map((run, idx) => {
														const statusColors = {
															clean: { bg: '#f0fdf4', text: '#16a34a', label: 'Clean' },
															fixed: { bg: '#eff6ff', text: '#1d4ed8', label: 'Fixed' },
															partial: { bg: '#fffbeb', text: '#b45309', label: 'Partial' },
															error: { bg: '#fef2f2', text: '#dc2626', label: 'Error' }
														};
														const sc = statusColors[run.status] || statusColors.error;
														const triggerLabel = run.triggeredBy === 'scheduled' ? 'Automated' : run.triggeredBy === 'manual-single' ? 'Manual (1 user)' : 'Manual';
														return (
															<tr key={run.runId || idx} style={styles.tableRow}>
																<td style={styles.tableCell}>
																	<Text style={{ fontSize: 13, color: '#1e293b' }}>
																		{new Date(run.triggeredAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
																	</Text>
																	<Text style={{ fontSize: 11, color: '#94a3b8' }}>
																		{new Date(run.triggeredAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
																	</Text>
																</td>
																<td style={{ ...styles.tableCell, fontSize: 13 }}>
																	<span style={{ background: run.triggeredBy === 'scheduled' ? '#eff6ff' : '#f5f3ff', color: run.triggeredBy === 'scheduled' ? '#1d4ed8' : '#7c3aed', borderRadius: 999, padding: '2px 8px', fontSize: 12 }}>
																		{triggerLabel}
																	</span>
																</td>
																<td style={{ ...styles.tableCell, textAlign: 'center', fontSize: 13 }}>{run.summary?.usersScanned ?? '—'}</td>
																<td style={{ ...styles.tableCell, textAlign: 'center', fontSize: 13 }}>
																	{run.summary?.itemsWithIssues > 0
																		? <span style={{ color: '#dc2626', fontWeight: '600' }}>{run.summary.itemsWithIssues}</span>
																		: <span style={{ color: '#16a34a' }}>{run.summary?.itemsWithIssues ?? '—'}</span>
																	}
																</td>
																<td style={{ ...styles.tableCell, textAlign: 'center', fontSize: 13 }}>
																	{run.fixResults != null
																		? <span style={{ color: run.fixResults.totalItemsFixed > 0 ? '#1d4ed8' : '#94a3b8', fontWeight: run.fixResults.totalItemsFixed > 0 ? '600' : 'normal' }}>
																			{run.fixResults.totalItemsFixed}
																		  </span>
																		: <span style={{ color: '#94a3b8' }}>—</span>
																	}
																</td>
																<td style={styles.tableCell}>
																	<span style={{ background: sc.bg, color: sc.text, borderRadius: 999, padding: '3px 10px', fontSize: 12, fontWeight: '600' }}>
																		{sc.label}
																	</span>
																</td>
																<td style={{ ...styles.tableCell, fontSize: 12, color: '#64748b', maxWidth: 200 }}>
																	{run.error
																		? <span style={{ color: '#dc2626' }}>{run.error}</span>
																		: run.fixResults?.totalErrors > 0
																			? <span style={{ color: '#b45309' }}>{run.fixResults.totalErrors} error{run.fixResults.totalErrors !== 1 ? 's' : ''}</span>
																			: run.fixResults != null
																				? <span style={{ color: '#16a34a' }}>No errors</span>
																				: <span style={{ color: '#94a3b8' }}>Scan only</span>
																	}
																</td>
															</tr>
														);
													})}
												</tbody>
											</table>
										</View>
									)}
								</View>

							</View>
						</View>
					)}

					{activeTab === "maintenance" && (
							<View style={styles.section}>
								<View style={styles.maintenanceTools}>
									{/* Maintenance Section */}
									<View style={styles.maintenanceSection}>
										<Text style={styles.subHeader}>System Maintenance</Text>
										<View style={styles.maintenanceStatusContainer}>
											{isMaintenance && (
												<View style={styles.maintenanceAlert}>
													<Text style={styles.maintenanceAlertText}>
														<span style={{ fontWeight: 'bold' }}>Maintenance Mode Active</span> - Started At: {new Date().toLocaleString()}
													</Text>
												</View>
											)}
											<button 
												style={isMaintenance ? styles.buttonDeny : styles.buttonApprove} 
												onClick={toggleMaintenance}
											>
												{isMaintenance ? "End Maintenance" : "Start Maintenance"}
											</button>
										</View>
										
										<View style={styles.maintenanceToggles}>
											<View style={styles.toggleItem}>
												<label style={styles.toggleLabel}>
													<input
														type="checkbox"
														checked={maintenanceMode}
														onChange={(e) => toggleMaintenanceMode(e.target.checked)}
														style={styles.toggleInput}
													/>
													<span style={styles.toggleText}>Maintenance Mode</span>
													<span style={styles.toggleDescription}>
														Display maintenance message to all users
													</span>
												</label>
											</View>
											<View style={styles.toggleItem}>
												<label style={styles.toggleLabel}>
													<input
														type="checkbox"
														checked={disableRegistration}
														onChange={(e) => toggleRegistration(e.target.checked)}
														style={styles.toggleInput}
													/>
													<span style={styles.toggleText}>Disable New Registrations</span>
													<span style={styles.toggleDescription}>
														Prevent new users from registering
													</span>
												</label>
											</View>
										</View>
									</View>

									<View style={styles.maintenanceSection}>
										{/* Mismatched Users Section */}
										<Text style={styles.subHeader}>Mismatched Users</Text>
										<View style={styles.mismatchedUsersContainer}>
											<button 
												onClick={checkMismatchedUsers} 
												style={styles.buttonApprove}
											>
												Scan for Mismatched Users
											</button>
											
											{mismatchedUsers.length > 0 ? (
												<View style={styles.tableWrapper}>
													<table style={styles.table}>
														<thead>
															<tr>
																<th style={styles.tableHeader}>First Name</th>
																<th style={styles.tableHeader}>Last Name</th>
																<th style={styles.tableHeader}>Email</th>
																<th style={styles.tableHeader}>UID</th>
																<th style={styles.tableHeader}>Events URL</th>
																<th style={styles.tableHeader}>Registry URL</th>
															</tr>
														</thead>
														<tbody>
															{mismatchedUsers.map((user) => (
																<tr key={user.uid} style={styles.tableRow}>
																	<td style={styles.tableCell}>{user.firstName}</td>
																	<td style={styles.tableCell}>{user.lastName}</td>
																	<td style={styles.tableCell}>{user.email}</td>
																	<td style={styles.tableCell}>{user.uid}</td>
																	<td style={styles.tableCell}>{user.eventsUrl}</td>
																	<td style={styles.tableCell}>{user.registryUrl}</td>
																</tr>
															))}
														</tbody>
													</table>
												</View>
											) : (
												<View style={styles.noDataContainer}>
													<Text style={styles.noDataText}>No mismatched users found</Text>
												</View>
											)}
										</View>
									</View>
								</View>
							</View>
						)}

						{activeTab === "widgets-monitor" && (
							<View style={styles.section}>
								<EventsWidgetMonitor
									adminId={currentUser?.uid}
									theme={theme}
								/>
							</View>
						)}

							{tutorialAdminUser && (
								<SpotlightTutorialAdminModal
									user={tutorialAdminUser}
									onClose={() => setTutorialAdminUser(null)}
									onUserUpdated={handleTutorialUserUpdated}
								/>
							)}

							{isUserModalOpen && selectedUser && (
								<View style={styles.modalOverlay}>
									<View style={styles.modalContent}>
										<Text style={styles.modalTitle}>Share Analytics for {selectedUser.firstName} {selectedUser.lastName}</Text>
										
										<View style={styles.shareStatsContainer}>
											<View style={styles.statPill}>
												<Text style={styles.statPillValue}>{shareAnalytics[selectedUser.email?.toLowerCase()]?.totalShares || 0}</Text>
												<Text style={styles.statPillLabel}>Total Shares</Text>
											</View>
											<View style={styles.statPill}>
												<Text style={styles.statPillValue}>{shareAnalytics[selectedUser.email?.toLowerCase()]?.emailShares?.length || 0}</Text>
												<Text style={styles.statPillLabel}>Email Shares</Text>
											</View>
											<View style={styles.statPill}>
												<Text style={styles.statPillValue}>{shareAnalytics[selectedUser.email?.toLowerCase()]?.smsShares?.length || 0}</Text>
												<Text style={styles.statPillLabel}>SMS Shares</Text>
											</View>
										</View>
										
										{shareAnalytics[selectedUser.email?.toLowerCase()]?.lastShareDate && (
											<Text style={styles.lastShareDate}>
												Last Share: {new Date(shareAnalytics[selectedUser.email?.toLowerCase()].lastShareDate).toLocaleDateString()} at {new Date(shareAnalytics[selectedUser.email?.toLowerCase()].lastShareDate).toLocaleTimeString()}
											</Text>
										)}
										
										<ScrollView style={styles.modalScrollView}>
											<View style={styles.shareTabs}>
												<View style={styles.shareTabsHeader}>
													<Text style={styles.shareTabTitle}>Email Shares</Text>
													<Text style={styles.shareTabTitle}>SMS Shares</Text>
												</View>
												
												<View style={styles.shareTabsContent}>
													{/* Email Shares Tab */}
													<View style={styles.shareTabPanel}>
														{shareAnalytics[selectedUser.email?.toLowerCase()]?.emailShares?.length > 0 ? (
															<View style={styles.shareList}>
																<View style={styles.shareListHeader}>
																	<Text style={styles.shareListHeaderCell}>Recipient</Text>
																	<Text style={styles.shareListHeaderCell}>Date</Text>
																	<Text style={styles.shareListHeaderCell}>Time</Text>
																</View>
																{shareAnalytics[selectedUser.email?.toLowerCase()].emailShares.map((share, index) => (
																	<View key={index} style={styles.shareItem}>
																		<Text style={styles.shareRecipient} numberOfLines={1}>{share.recipient}</Text>
																		<Text style={styles.shareDate}>{new Date(share.date).toLocaleDateString()}</Text>
																		<Text style={styles.shareTime}>{new Date(share.date).toLocaleTimeString()}</Text>
																	</View>
																))}
															</View>
														) : (
															<Text style={styles.noSharesText}>No email shares yet</Text>
														)}
													</View>
													
													{/* SMS Shares Tab */}
													<View style={styles.shareTabPanel}>
														{shareAnalytics[selectedUser.email?.toLowerCase()]?.smsShares?.length > 0 ? (
															<View style={styles.shareList}>
																<View style={styles.shareListHeader}>
																	<Text style={styles.shareListHeaderCell}>Recipient</Text>
																	<Text style={styles.shareListHeaderCell}>Date</Text>
																	<Text style={styles.shareListHeaderCell}>Time</Text>
																</View>
																{shareAnalytics[selectedUser.email?.toLowerCase()].smsShares.map((share, index) => (
																	<View key={index} style={styles.shareItem}>
																		<Text style={styles.shareRecipient} numberOfLines={1}>{share.recipient}</Text>
																		<Text style={styles.shareDate}>{new Date(share.date).toLocaleDateString()}</Text>
																		<Text style={styles.shareTime}>{new Date(share.date).toLocaleTimeString()}</Text>
																	</View>
																))}
															</View>
														) : (
															<Text style={styles.noSharesText}>No SMS shares yet</Text>
														)}
													</View>
												</View>
											</View>
										</ScrollView>
										
										<button
											onClick={() => setIsUserModalOpen(false)}
											style={styles.closeModalButton}
										>
											Close
										</button>
									</View>
								</View>
							)}
							</View>
						</View>
					</View>
				</>
			)}
		</View>
	);
				};

const styles = StyleSheet.create({
	container: {
		flex: 1,
		width: '100vw',
		maxWidth: '100%',
		minHeight: '100vh',
		backgroundColor: '#f1f5f9',
		overflowX: 'hidden',
	},

	// Sidebar styles
	sidebar: {
		height: '100vh',
		backgroundColor: '#1e293b',
		borderRightWidth: 1,
		borderRightColor: '#334155',
		transition: 'width 0.3s ease',
		position: 'sticky',
		top: 0,
		left: 0,
		zIndex: 100,
	},
	sidebarHeader: {
		padding: 16,
		borderBottomWidth: 1,
		borderBottomColor: '#334155',
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		minHeight: 64,
	},
	sidebarTitle: {
		fontSize: 18,
		fontWeight: '700',
		color: '#ffffff',
	},
	sidebarToggleBtn: {
		width: 32,
		height: 32,
		borderRadius: 8,
		backgroundColor: '#334155',
		justifyContent: 'center',
		alignItems: 'center',
		cursor: 'pointer',
	},
	sidebarNav: {
		flex: 1,
		padding: 8,
	},
	sidebarCategory: {
		marginBottom: 4,
	},
	sidebarCategoryHeader: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		padding: 12,
		borderRadius: 8,
		cursor: 'pointer',
	},
	sidebarCategoryHeaderActive: {
		backgroundColor: '#334155',
	},
	sidebarCategoryLeft: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 12,
	},
	sidebarCategoryLabel: {
		fontSize: 14,
		fontWeight: '600',
		color: '#94a3b8',
	},
	sidebarCategoryLabelActive: {
		color: '#ffffff',
	},
	sidebarCategoryItems: {
		marginLeft: 20,
		marginTop: 4,
	},
	sidebarItem: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 12,
		padding: 10,
		paddingLeft: 16,
		borderRadius: 8,
		cursor: 'pointer',
		marginBottom: 2,
	},
	sidebarItemActive: {
		backgroundColor: '#4c956c',
	},
	sidebarItemLabel: {
		fontSize: 13,
		fontWeight: '500',
		color: '#94a3b8',
	},
	sidebarItemLabelActive: {
		color: '#ffffff',
	},
	sidebarFooter: {
		padding: 16,
		borderTopWidth: 1,
		borderTopColor: '#334155',
	},
	sidebarDarkModeBtn: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'center',
		gap: 10,
		padding: 12,
		borderRadius: 8,
		backgroundColor: '#334155',
		cursor: 'pointer',
	},
	sidebarDarkModeText: {
		fontSize: 13,
		fontWeight: '500',
		color: '#94a3b8',
	},
	
	// Main content area styles
	mainContentArea: {
		flex: 1,
		minHeight: '100vh',
		backgroundColor: '#f1f5f9',
	},
	pageHeader: {
		padding: 24,
		paddingBottom: 16,
		borderBottomWidth: 1,
		borderBottomColor: '#e2e8f0',
		backgroundColor: '#ffffff',
	},
	pageTitle: {
		fontSize: 24,
		fontWeight: '700',
		color: '#1e293b',
		marginBottom: 4,
	},
	pageSubtitle: {
		fontSize: 14,
		color: '#64748b',
	},
	pageContent: {
		flex: 1,
		padding: 24,
	},

	contentWrapper: {
		width: '100%',
		maxWidth: '100%',
		marginVertical: 0,
		marginHorizontal: 'auto',
		display: 'flex',
		flexDirection: 'column',
		gap: 24,
	},

	section: {
		backgroundColor: 'white',
		padding: isMobile ? 20 : 30,
		borderRadius: 16,
		marginBottom: 24,
		boxShadow: '0 4px 10px rgba(0,0,0,0.05)',
		width: '100%',
		maxWidth: '100%',
		overflowX: 'auto',
	},

	// Update any nested containers to ensure they respect the full width
	mainContent: {
		width: '100%',
		maxWidth: '100%',
		display: 'flex',
		flexDirection: 'column',
		gap: 24,
	},

	header: {
		fontSize: 24,
		fontWeight: 'bold',
		color: '#2c6e49',
		marginBottom: 30,
		textAlign: 'center',
	},
	tabContainer: {
		flexDirection: 'row',
		marginBottom: 30,
		borderBottomWidth: 1,
		borderBottomColor: '#e2e8f0',
		flexWrap: 'wrap',
		justifyContent: 'center',
		alignItems: 'center',
		paddingVertical: 8,
		paddingHorizontal: 16,
		gap: 8,
	},
	tabButton: {
		paddingVertical: 10,
		paddingHorizontal: 16,
		borderRadius: 8,
		transition: 'all 0.2s ease',
	},
	activeTabButton: {
		backgroundColor: '#4c956c',
		borderBottomWidth: 0,
		boxShadow: '0 -4px 6px rgba(0,0,0,0.05)',
	},
	tabButtonText: {
		fontSize: 16,
		fontWeight: '600',
		color: '#64748b',
	},
	activeTabButtonText: {
		color: '#ffffff',
	},
	darkModeToggle: {
		width: 40,
		height: 40,
		borderRadius: 20,
		justifyContent: 'center',
		alignItems: 'center',
		marginLeft: 'auto',
		cursor: 'pointer',
		transition: 'all 0.2s ease',
	},
	tabContent: {
		display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			width: '100%',
	},
	subHeader: {
		fontSize: 20,
		fontWeight: 'bold',
		color: '#4b5563',
		textAlign: 'center',
		marginBottom: 30,
	},
	tableWrapper: {
		width: '100%',
		overflowX: 'auto',
		padding: isMobile ? 5 : '10px',
		marginBottom: '20px',
		borderRadius: 8,
	},
	table: {
		width: '100%',
		borderCollapse: 'separate',
		borderSpacing: '0 8px',
		marginTop: 25,
		tableLayout: 'fixed',
		minWidth: isMobile ? 800 : 'auto', // Ensures table doesn't get too squeezed on mobile
	},
	tableHeader: {
		backgroundColor: '#f8fafc',
		padding: isMobile ? '12px 16px' : '15px 20px',
		textAlign: 'left',
		fontSize: isMobile ? 14 : 16,
		color: '#4b5563',
		fontWeight: '600',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		borderTopLeftRadius: 8,
		borderTopRightRadius: 8,
	},
	tableRow: {
		backgroundColor: 'white',
		boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
		borderRadius: 8,
		transition: 'transform 0.2s ease, box-shadow 0.2s ease',
		':hover': {
			boxShadow: '0 4px 8px rgba(0,0,0,0.05)',
			transform: 'translateY(-2px)',
		}
	},
	tableCell: {
		paddingVertical: 12,
		paddingHorizontal: 16,
		fontSize: 14,
		color: '#4b5563',
		borderBottomWidth: 1,
		borderBottomColor: '#e5e7eb',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		maxWidth: '200px',
	},
	input: {
		width: '100%',
		height: 45,
		paddingVertical: 12,
		paddingHorizontal: 16,
		marginVertical: 8,
		marginHorizontal: 0,
		borderWidth: 1,
		borderColor: '#e2e8f0',
		borderRadius: 8,
		fontSize: 15,
		color: '#4b5563',
		backgroundColor: '#f9fafb',
		transition: 'all 0.2s ease',
		outlineStyle: 'none',
		':focus': {
			borderColor: '#4c956c',
			boxShadow: '0 0 0 3px rgba(76, 149, 108, 0.1)',
		},
	},

	// Registry presets chip layout
	registryPresetsRow: {
		display: 'flex',
		flexDirection: 'row',
		flexWrap: 'wrap',
		gap: 12,
		alignItems: 'center',
		marginTop: 8,
		marginBottom: 8,
	},
	presetChip: {
		backgroundColor: '#f3faf6',
		border: '1px solid #e6f3ea',
		paddingVertical: 8,
		paddingHorizontal: 12,
		borderRadius: 999,
		display: 'inline-flex',
		alignItems: 'center',
		gap: 8,
		boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
	},
	presetInput: {
		width: 72,
		height: 36,
		paddingVertical: 6,
		paddingHorizontal: 8,
		borderRadius: 8,
		border: '1px solid #e6f3ea',
		backgroundColor: 'white',
		textAlign: 'center',
		fontSize: 14,
		color: '#065f46',
	},
	chipRemoveBtn: {
		backgroundColor: 'transparent',
		border: 'none',
		color: '#9ca3af',
		cursor: 'pointer',
		fontSize: 14,
		padding: 4,
	},
	registryActionsRow: {
		display: 'flex',
		flexDirection: 'row',
		gap: 12,
		marginTop: 14,
		alignItems: 'center'
	},
	// Updated button base styles
	buttonBase: {
		paddingVertical: 12,
		paddingHorizontal: 24,
		borderRadius: 8,
		fontSize: 15,
		fontWeight: '600',
		cursor: 'pointer',
		transition: 'all 0.2s ease',
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		minWidth: 120,
		border: 'none',
		boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
		margin: 4,
		height: 45,
	},
	// Updated specific button styles
	buttonApprove: {
		backgroundColor: '#4c956c',
		color: 'white',
		paddingVertical: 12, paddingHorizontal: 24,
		borderRadius: 8,
		fontSize: 15,
		fontWeight: '600',
		cursor: 'pointer',
		transition: 'all 0.2s ease',
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		minWidth: 120,
		border: 'none',
		boxShadow: '0 2px 4px rgba(76, 149, 108, 0.2)',
		margin: 4,
		height: 45,
		':hover': {
			backgroundColor: '#3a7254',
			transform: 'translateY(-1px)',
		},
	},
	buttonDeny: {
		backgroundColor: '#ef4444',
		color: 'white',
		paddingVertical: 12, paddingHorizontal: 24,
		borderRadius: 8,
		fontSize: 15,
		fontWeight: '600',
		cursor: 'pointer',
		transition: 'all 0.2s ease',
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		minWidth: 120,
		border: 'none',
		boxShadow: '0 2px 4px rgba(239, 68, 68, 0.2)',
		margin: 4,
		height: 45,
		':hover': {
			backgroundColor: '#dc2626',
			transform: 'translateY(-1px)',
		},
	},
	buttonEdit: {
		backgroundColor: '#f59e0b',
		color: 'white',
		paddingVertical: 12, paddingHorizontal: 24,
		borderRadius: 8,
		fontSize: 15,
		fontWeight: '600',
		cursor: 'pointer',
		transition: 'all 0.2s ease',
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		minWidth: 120,
		border: 'none',
		boxShadow: '0 2px 4px rgba(245, 158, 11, 0.2)',
		margin: 4,
		height: 45,
		':hover': {
			backgroundColor: '#d97706',
			transform: 'translateY(-1px)',
		},
	},
	buttonDisabled: {
		backgroundColor: '#e5e7eb',
		color: '#9ca3af',
		paddingVertical: 12, paddingHorizontal: 24,
		borderRadius: 8,
		fontSize: 15,
		fontWeight: '600',
		cursor: 'not-allowed',
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		minWidth: 120,
		border: 'none',
		margin: 4,
		height: 45,
	},
	// Page navigation buttons
	pageButton: {
		backgroundColor: '#f9fafb',
		color: '#4b5563',
		paddingVertical: 8, paddingHorizontal: 12,
		border: '1px solid #e5e7eb',
		borderRadius: 6,
		cursor: 'pointer',
		fontSize: 14,
		fontWeight: '500',
		transition: 'all 0.2s ease',
		marginVertical: 0, marginHorizontal: 4,
		minWidth: 36,
		textAlign: 'center',
		':hover': {
			backgroundColor: '#f3f4f6',
			borderColor: '#d1d5db',
		},
	},
	activePage: {
		backgroundColor: '#4c956c',
		color: 'white',
		border: '1px solid #4c956c',
		paddingVertical: 8, paddingHorizontal: 12,
		borderRadius: 6,
		cursor: 'pointer',
		fontSize: 14,
		fontWeight: '500',
		marginVertical: 0, marginHorizontal: 4,
		minWidth: 36,
		textAlign: 'center',
	},
	maintenanceSection: {
		padding: 30,
		backgroundColor: 'white',
	},
	maintenanceToggles: {
		marginTop: 25,
		display: 'flex',
		flexDirection: 'column',
		gap: 20,
	},
	toggleItem: {
		flexDirection: 'row',
		alignItems: 'center',
		paddingVertical: 16, paddingHorizontal: 20,
		backgroundColor: '#f9fafb',
		borderRadius: 12,
		marginBottom: 15,
		boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
	},
	toggleLabel: {
		marginLeft: 15,
		fontSize: 16,
		color: '#4b5563',
		fontWeight: '500',
	},
	blogSection: {
		width: '100%',
	},
	blogFormContainer: {
		flexDirection: width < 1024 ? 'column' : 'row',
		justifyContent: 'space-between',
		marginBottom: 30,
		gap: 20,
	},
	blogForm: {
		flex: 1,
		marginRight: width < 1024 ? 0 : 20,
		padding: 25,
		backgroundColor: '#fff',
		borderRadius: 12,
		boxShadow: '0 4px 10px rgba(0,0,0,0.05)',
	},
	blogContentContainer: {
		marginBottom: 20,
	},
	blogPreview: {
		flex: 1,
		marginTop: width < 1024 ? 20 : 0,
		padding: 25,
		backgroundColor: '#fff',
		borderRadius: 12,
		boxShadow: '0 4px 10px rgba(0,0,0,0.05)',
	},
	previewContainer: {
		padding: 20,
		backgroundColor: '#f9fafb',
		borderRadius: 8,
		minHeight: 400,
	},
	previewImage: {
		width: '100%',
		height: 200,
		objectFit: 'cover',
		borderRadius: 8,
		marginBottom: 20,
	},
	previewTitle: {
		fontSize: 24,
		fontWeight: 'bold',
		marginBottom: 12,
		color: '#1f2937',
	},
	previewCategory: {
		fontSize: 16,
		color: '#4b5563',
		marginBottom: 16,
	},
	previewTagList: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		marginBottom: 20,
		gap: 8,
	},
	previewTagPill: {
		backgroundColor: '#ecfdf5',
		paddingVertical: 6,
		paddingHorizontal: 12,
		borderRadius: 20,
		display: 'inline-flex',
		alignItems: 'center',
	},
	previewTagText: {
		color: '#065f46',
		fontSize: 14,
		fontWeight: '500',
	},
	previewContent: {
		fontSize: 16,
		lineHeight: 1.6,
		color: '#4b5563',
		marginTop: 20,
	},
	// Tag input section
	tagSection: {
		marginTop: 20,
		marginBottom: 25,
	},
	tagSectionLabel: {
		fontSize: 16,
		fontWeight: '600',
		color: '#4b5563',
		marginBottom: 12,
	},
	presetTagsContainer: {
		display: 'flex',
		flexDirection: 'row',
		flexWrap: 'wrap',
		gap: 8,
		marginBottom: 16,
	},
	presetTagButton: {
		backgroundColor: '#f3f4f6',
		color: '#4b5563',
		paddingVertical: 6,
		paddingHorizontal: 12,
		borderRadius: 20,
		fontSize: 14,
		border: 'none',
		cursor: 'pointer',
		transition: 'all 0.2s ease',
		fontWeight: '500',
		':hover': {
			backgroundColor: '#e5e7eb',
		},
	},
	presetTagButtonSelected: {
		backgroundColor: '#dcfce7',
		color: '#065f46',
	},
	tagInputContainer: {
		marginBottom: isMobile ? 15 : 20,
	},
	tagList: {
		display: 'flex',
		flexDirection: 'row',
		flexWrap: 'wrap',
		gap: 8,
		marginBottom: 16,
	},
	tagPill: {
		backgroundColor: '#ecfdf5',
		paddingVertical: 6,
		paddingHorizontal: 12,
		borderRadius: 20,
		display: 'inline-flex',
		alignItems: 'center',
		gap: 8,
		flexDirection: 'row',
	},
	tagText: {
		color: '#065f46',
		fontSize: 14,
		fontWeight: '500',
	},
	tagRemove: {
		backgroundColor: 'transparent',
		border: 'none',
		color: '#059669',
		cursor: 'pointer',
		padding: 0,
		margin: 0,
		fontSize: 16,
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		':hover': {
			color: '#ef4444',
		},
	},
	tagInput: {
		width: '100%',
		paddingVertical: 12, paddingHorizontal: 16,
		border: '1px solid #e5e7eb',
		borderRadius: 8,
		fontSize: 14,
		backgroundColor: '#f9fafb',
		color: '#4b5563',
		outlineStyle: 'none',
		transition: 'all 0.2s ease',
		':focus': {
			borderColor: '#4c956c',
			boxShadow: '0 0 0 3px rgba(76, 149, 108, 0.1)',
		},
	},
	searchContainer: {
		marginBottom: 24,
	},
	searchInput: {
		width: '100%',
		paddingVertical: 12, paddingHorizontal: 16,
		border: '1px solid #e5e7eb',
		borderRadius: 8,
		fontSize: 15,
		backgroundColor: '#f9fafb',
		color: '#4b5563',
		outlineStyle: 'none',
		transition: 'all 0.2s ease',
		':focus': {
			borderColor: '#4c956c',
			boxShadow: '0 0 0 3px rgba(76, 149, 108, 0.1)',
		},
	},
	// Action buttons in tables
	actionButtons: {
		display: 'flex',
		gap: isMobile ? 8 : 10,
		justifyContent: 'flex-start',
		flexWrap: 'wrap',
		flexDirection: isMobile ? 'column' : 'row',
		width: isMobile ? '100%' : 'auto',
	},
	actionButton: {
		backgroundColor: '#f9fafb',
		color: '#4b5563',
		paddingVertical: 8, paddingHorizontal: 12,
		borderRadius: 6,
		fontSize: 14,
		fontWeight: '500',
		border: '1px solid #e5e7eb',
		cursor: 'pointer',
		transition: 'all 0.2s ease',
		textAlign: 'center',
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		marginTop: 0, marginRight: 0, marginBottom: 6, marginLeft: 0,
		minWidth: isMobile ? '100%' : 'auto',
		boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
		':hover': {
			backgroundColor: '#f3f4f6',
			borderColor: '#d1d5db',
		},
	},
	statusBadge: {
		paddingVertical: 4, paddingHorizontal: 10,
		borderRadius: 12,
		fontSize: 13,
		fontWeight: '600',
		color: 'white',
		display: 'inline-block',
	},
	sectionHeader: {
		marginBottom: 30,
	},
	userStats: {
		flexDirection: isMobile ? 'column' : 'row',
		flexWrap: 'wrap',
		gap: isMobile ? 15 : 20,
		marginTop: 20,
		marginBottom: 30,
	},
	statCard: {
		backgroundColor: 'white',
		padding: isMobile ? 20 : 25,
		borderRadius: 12,
		boxShadow: '0 4px 10px rgba(0,0,0,0.05)',
		flex: isMobile ? 'auto' : 1,
		minWidth: isMobile ? '100%' : 150,
		width: isMobile ? '100%' : 'auto',
	},
	statNumber: {
		fontSize: 32,
		fontWeight: 'bold',
		color: '#4c956c',
		marginBottom: 10,
	},
	statLabel: {
		fontSize: 16,
		fontWeight: '500',
		color: '#4b5563',
	},
	errorContainer: {
		flex: 1,
		padding: 40,
		alignItems: 'center',
		justifyContent: 'center',
	},
	errorTitle: {
		fontSize: 24,
		fontWeight: 'bold',
		color: '#ef4444',
		marginBottom: 12,
	},
	errorText: {
		fontSize: 16,
		color: '#4b5563',
		marginBottom: 20,
		textAlign: 'center',
	},
	errorButton: {
		backgroundColor: '#3b82f6',
		color: 'white',
		paddingVertical: 10, paddingHorizontal: 16,
		borderRadius: 8,
		fontSize: 15,
		fontWeight: '600',
	},
	errorButtonText: {
		color: '#ffffff',
	},
	tableContainer: {
		backgroundColor: 'white',
		borderRadius: 12,
		boxShadow: '0 4px 10px rgba(0,0,0,0.05)',
		overflow: 'hidden',
		marginBottom: 30,
	},
	tableHeader: {
		flexDirection: 'row',
		backgroundColor: '#f9fafb',
		paddingVertical: 12,
		paddingHorizontal: 16,
		borderBottomWidth: 1,
		borderBottomColor: '#e5e7eb',
	},
	tableHeaderCell: {
		fontSize: 14,
		fontWeight: 'bold',
		color: '#4b5563',
	},
	tableBody: {
		maxHeight: 500,
	},
	tableRow: {
		flexDirection: 'row',
		paddingVertical: 12,
		paddingHorizontal: 16,
		borderBottomWidth: 1,
		borderBottomColor: '#f3f4f6',
		transition: 'background-color 0.2s ease',
		':hover': {
			backgroundColor: '#f9fafb',
		},
	},
	tableCell: {
		fontSize: 14,
		color: '#4b5563',
	},
	// Visibility toggle buttons
	visibilityButtonContainer: {
		display: 'flex',
		flexDirection: isMobile ? 'column' : 'row',
		gap: 10,
		marginTop: 15,
		flexWrap: 'wrap',
	},
	visibilityButton: {
		backgroundColor: '#f9fafb',
		color: '#4b5563',
		paddingVertical: 10, paddingHorizontal: 16,
		borderRadius: 8,
		border: '1px solid #e5e7eb',
		fontSize: 15,
		fontWeight: '500',
		cursor: 'pointer',
		transition: 'all 0.2s ease',
		textAlign: 'center',
		marginTop: 0, marginRight: 0, marginBottom: 6, marginLeft: 0,
		minWidth: isMobile ? '100%' : 'auto',
		boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
		':hover': {
			backgroundColor: '#f3f4f6',
			borderColor: '#d1d5db',
		},
	},
	visibilityButtonActive: {
		backgroundColor: '#4c956c',
		color: 'white',
		borderColor: '#4c956c',
	},
	// Added styles for form layouts
	formGroup: {
		marginBottom: 20,
		width: '100%',
	},
	formLabel: {
		fontSize: 15,
		fontWeight: '600',
		color: '#4b5563',
		marginBottom: 8,
		display: 'block',
	},
	formInput: {
		width: '100%',
		paddingVertical: 12, paddingHorizontal: 16,
		border: '1px solid #e5e7eb',
		borderRadius: 8,
		fontSize: 15,
		backgroundColor: '#f9fafb',
		color: '#4b5563',
		outlineStyle: 'none',
		transition: 'all 0.2s ease',
		':focus': {
			borderColor: '#4c956c',
			boxShadow: '0 0 0 3px rgba(76, 149, 108, 0.1)',
		},
	},
	formSelect: {
		width: '100%',
		paddingVertical: 12, paddingHorizontal: 16,
		border: '1px solid #e5e7eb',
		borderRadius: 8,
		fontSize: 15,
		backgroundColor: '#f9fafb',
		color: '#4b5563',
		outlineStyle: 'none',
		appearance: 'none',
		transition: 'all 0.2s ease',
		':focus': {
			borderColor: '#4c956c',
			boxShadow: '0 0 0 3px rgba(76, 149, 108, 0.1)',
		},
	},
	contentContainer: {
		paddingVertical: 0, paddingHorizontal: 20,
		width: '100%',
		maxWidth: '100%',
		marginVertical: 0,
		marginHorizontal: 0,
	},
	analyticsContainer: {
		backgroundColor: 'white',
		padding: isMobile ? 20 : isTablet ? 30 : 40,
		borderRadius: 16,
		marginBottom: isMobile ? 20 : isTablet ? 30 : 40,
		boxShadow: '0 4px 10px rgba(0,0,0,0.05)',
		width: '100%',
	},
	stripeConfigContainer: {
		backgroundColor: 'white',
		padding: isMobile ? 20 : isTablet ? 30 : 40,
		borderRadius: 16,
		marginBottom: isMobile ? 20 : isTablet ? 30 : 40,
		boxShadow: '0 4px 10px rgba(0,0,0,0.05)',
		width: '100%',
	},
	loading: {
		textAlign: 'center',
		padding: 40,
		fontSize: 18,
		color: '#64748b',
	},
	loginContainer: {
		flex: 1,
		justifyContent: 'center',
		alignItems: 'center',
	},
	loginText: {
		fontSize: 18,
		color: '#4b5563',
		marginBottom: 20,
	},
	controlsContainer: {
		marginBottom: 20,
	},
	filterContainer: {
		flexDirection: isMobile ? 'column' : 'row',
		justifyContent: 'space-between',
		gap: 16,
	},
	filterPanel: {
		backgroundColor: '#ffffff',
		borderRadius: 12,
		borderWidth: 1,
		borderColor: '#e2e8f0',
		padding: 16,
		gap: 14,
		marginBottom: 4,
	},
	filterPanelSection: {
		gap: 10,
	},
	filterPanelSectionTitle: {
		fontSize: 10,
		fontWeight: '700',
		color: '#94a3b8',
		letterSpacing: 1.2,
	},
	filterPanelGroups: {
		gap: 8,
	},
	filterGroup: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 10,
	},
	filterGroupLabel: {
		fontSize: 12,
		fontWeight: '600',
		color: '#64748b',
		minWidth: 64,
	},
	filterSegment: {
		flexDirection: 'row',
		flex: 1,
		borderRadius: 8,
		borderWidth: 1,
		borderColor: '#e2e8f0',
		overflow: 'hidden',
	},
	filterSegmentBtn: {
		flex: 1,
		paddingVertical: 7,
		paddingHorizontal: 6,
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: '#f8fafc',
		borderRightWidth: 1,
		borderRightColor: '#e2e8f0',
	},
	filterSegmentBtnFirst: {
		borderTopLeftRadius: 7,
		borderBottomLeftRadius: 7,
	},
	filterSegmentBtnLast: {
		borderRightWidth: 0,
		borderTopRightRadius: 7,
		borderBottomRightRadius: 7,
	},
	filterSegmentBtnActive: {
		backgroundColor: '#4c956c',
	},
	filterSegmentBtnText: {
		fontSize: 12,
		color: '#64748b',
		fontWeight: '500',
		textAlign: 'center',
	},
	filterSegmentBtnTextActive: {
		color: '#ffffff',
		fontWeight: '600',
	},
	filterPanelDivider: {
		height: 1,
		backgroundColor: '#f1f5f9',
	},
	filterPanelFooter: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
	},
	filterResultCount: {
		fontSize: 12,
		color: '#64748b',
		fontWeight: '500',
	},
	filterClearBtn: {
		paddingHorizontal: 12,
		paddingVertical: 5,
		borderRadius: 6,
		backgroundColor: '#fef2f2',
		borderWidth: 1,
		borderColor: '#fecaca',
	},
	filterClearBtnText: {
		fontSize: 12,
		color: '#ef4444',
		fontWeight: '600',
	},
	filterRow: {
		flexDirection: 'row',
		alignItems: 'center',
		flexWrap: 'wrap',
		gap: 8,
	},
	filterPills: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		gap: 6,
	},
	filterPill: {
		paddingVertical: 5,
		paddingHorizontal: 12,
		borderRadius: 20,
		backgroundColor: '#f1f5f9',
		borderWidth: 1,
		borderColor: '#e2e8f0',
	},
	filterPillActive: {
		backgroundColor: '#4c956c',
		borderColor: '#4c956c',
	},
	filterPillText: {
		fontSize: 13,
		color: '#64748b',
		fontWeight: '500',
	},
	filterPillTextActive: {
		color: '#ffffff',
	},
	selectContainer: {
		flexDirection: 'row',
		alignItems: 'center',
		flexWrap: 'wrap',
		gap: 8,
	},
	selectLabel: {
		fontSize: 14,
		fontWeight: '500',
		color: '#64748b',
		marginRight: 8,
	},
	filterButton: {
		paddingVertical: 8,
		paddingHorizontal: 12,
		borderRadius: 6,
		backgroundColor: '#f8fafc',
		borderWidth: 1,
		borderColor: '#e2e8f0',
	},
	activeFilterButton: {
		backgroundColor: '#4c956c',
		borderColor: '#4c956c',
	},
	filterButtonText: {
		fontSize: 14,
		color: '#64748b',
	},
	activeFilterButtonText: {
		color: '#ffffff',
	},
	tableContainer: {
		backgroundColor: 'white',
		borderRadius: 12,
		shadowColor: '#000',
		shadowOffset: { width: 0, height: 1 },
		shadowOpacity: 0.1,
		shadowRadius: 2,
		elevation: 2,
		overflow: 'hidden',
	},
	tableHeader: {
		flexDirection: 'row',
		backgroundColor: '#f8fafc',
		paddingVertical: 12,
		paddingHorizontal: 16,
		borderBottomWidth: 1,
		borderBottomColor: '#e2e8f0',
	},
	tableHeaderCell: {
		fontSize: 14,
		fontWeight: 'bold',
		color: '#64748b',
	},
	tableBody: {
		maxHeight: 500,
	},
	tableRow: {
		flexDirection: 'row',
		paddingVertical: 12,
		paddingHorizontal: 16,
		borderBottomWidth: 1,
		borderBottomColor: '#f1f5f9',
	},
	tableCell: {
		fontSize: 14,
		color: '#334155',
	},
	actionButtons: {
		flexDirection: 'row',
		gap: 8,
		flexWrap: 'wrap',
		marginTop: 8,
		marginBottom: 8,
	},
	actionButton: {
		backgroundColor: '#f8fafc',
		paddingVertical: 6,
		paddingHorizontal: 10,
		borderRadius: 6,
		borderWidth: 1,
		borderColor: '#e2e8f0',
	},
	actionButtonText: {
		fontSize: 12,
		color: '#475569',
	},
	dangerButton: {
		backgroundColor: '#fee2e2',
		borderColor: '#fecaca',
	},
	dangerButtonText: {
		color: '#ef4444',
	},
	warningButton: {
		backgroundColor: '#fef3c7',
		borderColor: '#fde68a',
	},
	warningButtonText: {
		color: '#d97706',
	},
	dateInputContainer: {
		display: 'flex',
		gap: 20,
		marginBottom: 20,
		flexDirection: isMobile ? 'column' : 'row',
	},
	dateField: {
		flex: 1,
	},
	dateLabel: {
		display: 'block',
		marginBottom: 8,
		color: '#475569',
		fontSize: 14,
		fontWeight: '500',
	},
	dateInput: {
		width: '100%',
		paddingVertical: 12, paddingHorizontal: 16,
		border: '2px solid #e2e8f0',
		borderRadius: 8,
		fontSize: 14,
		color: '#334155',
		backgroundColor: '#f8fafc',
		transition: 'all 0.2s ease',
	},
	blogTitle: {
		fontSize: 24,
		color: '#334155',
		marginBottom: 15,
		fontWeight: '600',
	},
	blogMeta: {
		fontSize: 16,
		color: '#64748b',
		marginBottom: 20,
	},
	blogContentPreview: {
		fontSize: 14,
		color: '#64748b',
		marginBottom: 15,
		fontStyle: 'italic',
		backgroundColor: '#f8fafc',
		padding: 10,
		borderRadius: 4,
	},
	previewTitle: {
		fontSize: 28,
		color: '#2c6e49',
		marginBottom: 20,
		fontFamily: 'Orpheus Pro',
		fontWeight: '600',
	},
	previewContent: {
		fontSize: 16,
		lineHeight: 1.6,
		color: '#334155',
		marginBottom: 20,
	},
	previewImage: {
		width: '100%',
		height: 'auto',
		maxHeight: 300,
		objectFit: 'cover',
		borderRadius: 8,
		marginBottom: 20,
	},
	previewMeta: {
		display: 'flex',
		gap: 15,
		marginBottom: 20,
		color: '#64748b',
		fontSize: 14,
	},
	previewTags: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: 8,
		marginTop: 20,
	},
	select: {
		width: '100%',
		height: 45,
		paddingVertical: 12, paddingHorizontal: 16,
		marginVertical: 8, marginHorizontal: 0,
		border: '1px solid #e2e8f0',
		borderRadius: 8,
		fontSize: 15,
		color: '#4b5563',
		backgroundColor: '#f9fafb',
		transition: 'all 0.2s ease',
		cursor: 'pointer',
		appearance: 'none',
		backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
		backgroundRepeat: 'no-repeat',
		backgroundPositionX: 'right',
		backgroundPositionY: 'center',
		backgroundSize: '20px',
		':focus': {
			borderColor: '#4c956c',
			boxShadow: '0 0 0 3px rgba(76, 149, 108, 0.1)',
		},
	},
	toggleContainer: {
		display: 'flex',
		alignItems: 'center',
		gap: 12,
		marginBottom: 20,
	},
	toggle: {
		position: 'relative',
		width: 52,
		height: 28,
		borderRadius: 14,
		backgroundColor: '#e2e8f0',
		transition: 'background-color 0.2s ease',
		cursor: 'pointer',
	},
	toggleActive: {
		backgroundColor: '#2c6e49',
	},
	toggleHandle: {
		position: 'absolute',
		top: 2,
		left: 2,
		width: 24,
		height: 24,
		borderRadius: 12,
		backgroundColor: 'white',
		transition: 'transform 0.2s ease',
		boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
	},
	toggleHandleActive: {
		transform: 'translateX(24px)',
	},
	actionContainer: {
		display: 'flex',
		gap: 15,
		marginTop: 25,
		marginBottom: 25,
	},
	option: {
		paddingVertical: 12, paddingHorizontal: 20,
		textAlign: 'center',
		whiteSpace: 'normal',
		lineHeight: '1.4',
	},
	primaryButton: {
		backgroundColor: '#2c6e49',
		color: 'white',
		paddingVertical: 16, paddingHorizontal: 32,
		borderRadius: 8,
		border: 'none',
		fontSize: 16,
		fontWeight: '500',
		cursor: 'pointer',
		transition: 'all 0.2s ease',
		minWidth: 140,
		textAlign: 'center',
		boxShadow: '0 2px 4px rgba(44, 110, 73, 0.2)',
		':hover': {
			backgroundColor: '#235c3d',
			transform: 'translateY(-2px)',
		},
	},
	secondaryButton: {
		backgroundColor: '#f8fafc',
		color: '#475569',
		paddingVertical: 16, paddingHorizontal: 32,
		borderRadius: 8,
		border: '2px solid #e2e8f0',
		fontSize: 16,
		fontWeight: '500',
		cursor: 'pointer',
		transition: 'all 0.2s ease',
		minWidth: 140,
		textAlign: 'center',
		':hover': {
			backgroundColor: '#f1f5f9',
			borderColor: '#cbd5e1',
		},
	},
	paginationContainer: {
		flexDirection: 'row',
		justifyContent: 'center',
		alignItems: 'center',
		marginVertical: 15,
		flexWrap: 'nowrap',
		overflowX: 'auto',
		padding: 5,
	},
	pageEllipsis: {
		marginVertical: 0, marginHorizontal: 5,
		color: '#666',
	},
	filterContainer: {
		flexDirection: width < 768 ? 'column' : 'row',
		marginBottom: 15,
		alignItems: width < 768 ? 'stretch' : 'center',
		gap: 10,
	},
	filterSelect: {
		padding: 8,
		borderRadius: 4,
		borderWidth: 1,
		borderColor: '#ddd',
		backgroundColor: '#fff',
		marginRight: width < 768 ? 0 : 10,
		marginBottom: width < 768 ? 10 : 0,
		flex: width < 768 ? 'auto' : 1,
		maxWidth: width < 768 ? '100%' : 200,
	},
	bulkEdit: {
		backgroundColor: '#f8fafc',
		padding: 24,
		borderRadius: 12,
		marginBottom: 24,
		border: '1px solid #e2e8f0',
		display: 'grid',
		gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))',
		gap: 16,
	},
	bulkEditHeader: {
		fontSize: 16,
		fontWeight: 'bold',
		marginBottom: 10,
		gridColumn: '1 / -1',
	},
	bulkEditButtons: {
		display: 'flex',
		flexDirection: width < 768 ? 'column' : 'row',
		gap: 10,
		marginTop: 10,
		gridColumn: '1 / -1',
	},
	mismatchedUsersContainer: {
		marginTop: 15,
	},
	userListContainer: {
		padding: 20,
		backgroundColor: '#fff',
		borderRadius: 8,
		margin: 20,
	},
	userTable: {
		width: '100%',
		borderCollapse: 'collapse',
		marginTop: 20,
	},
	testResultContainer: {
		padding: 15,
		borderRadius: 8,
		marginTop: 15,
		marginBottom: 15,
	},
	successResult: {
		backgroundColor: '#ecfdf5',
		borderLeftWidth: 4, borderLeftColor: '#059669',
	},
	errorResult: {
		backgroundColor: '#fef2f2',
		borderLeftWidth: 4, borderLeftColor: '#dc2626',
	},
	testResultText: {
		fontSize: 14,
		lineHeight: 1.5,
		whiteSpace: 'pre-wrap',
	},
	successText: {
		color: '#065f46',
	},
	errorText: {
		color: '#991b1b',
	},
	shareAnalytics: {
		display: 'flex',
		alignItems: 'center',
		gap: 10,
	},
	shareCount: {
		fontSize: 14,
		fontWeight: 'bold',
		color: '#4b5563',
	},
	viewDetailsButton: {
		backgroundColor: '#4c956c',
		color: 'white',
		paddingVertical: 6, paddingHorizontal: 12,
		borderRadius: 4,
		fontSize: 12,
		fontWeight: 'bold',
		cursor: 'pointer',
		border: 'none',
		transition: 'all 0.2s ease',
		':hover': {
			backgroundColor: '#2c6e49',
			transform: 'translateY(-1px)',
		},
	},
	noShares: {
		color: '#94a3b8',
		fontSize: 14,
		fontStyle: 'italic',
	},
	smsOptInCell: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		gap: 6,
		minWidth: 0,
		whiteSpace: 'normal',
	},
	smsOptInBadge: {
		borderRadius: 999,
		paddingVertical: 4,
		paddingHorizontal: 10,
		borderWidth: 1,
	},
	smsOptInBadgeActive: {
		backgroundColor: '#ecfdf5',
		borderColor: '#bbf7d0',
	},
	smsOptInBadgeInactive: {
		backgroundColor: '#f8fafc',
		borderColor: '#e2e8f0',
	},
	smsOptInBadgeText: {
		fontSize: 12,
		fontWeight: '700',
	},
	smsOptInBadgeTextActive: {
		color: '#15803d',
	},
	smsOptInBadgeTextInactive: {
		color: '#64748b',
	},
	smsOptInDetail: {
		fontSize: 12,
		color: '#475569',
		fontWeight: '600',
		whiteSpace: 'normal',
		wordBreak: 'break-word',
	},
	smsOptInMeta: {
		fontSize: 11,
		color: '#94a3b8',
		fontWeight: '500',
	},
	modalOverlay: {
		position: 'fixed',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		backgroundColor: 'rgba(0, 0, 0, 0.5)',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		zIndex: 1000,
	},
	modalContent: {
		backgroundColor: '#fff',
		borderRadius: 12,
		padding: 24,
		width: isMobile ? '90%' : '600px',
		maxWidth: '90%',
		maxHeight: '90vh',
		boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
		display: 'flex',
		flexDirection: 'column',
	},
	modalTitle: {
		fontSize: 22,
		fontWeight: 'bold',
		color: '#2c6e49',
		marginBottom: 16,
		textAlign: 'center',
	},
	modalScrollView: {
		maxHeight: '60vh',
		marginBottom: 16,
	},
	closeModalButton: {
		marginTop: 16,
		paddingVertical: 10, paddingHorizontal: 0,
		backgroundColor: '#f8fafc',
		border: 'none',
		borderRadius: 8,
		color: '#4b5563',
		fontWeight: 'bold',
		cursor: 'pointer',
		transition: 'all 0.2s',
		':hover': {
			backgroundColor: '#e2e8f0',
		},
	},
	shareStatsContainer: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'space-between',
		marginBottom: 20,
		gap: 12,
	},
	statPill: {
		backgroundColor: '#f1f5f9',
		padding: 16,
		borderRadius: 10,
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		boxShadow: '0 2px 5px rgba(0,0,0,0.05)',
	},
	statPillValue: {
		fontSize: 24,
		fontWeight: 'bold',
		color: '#2c6e49',
		marginBottom: 8,
	},
	statPillLabel: {
		fontSize: 14,
		color: '#64748b',
		textAlign: 'center',
	},
	lastShareDate: {
		fontSize: 14,
		color: '#64748b',
		marginBottom: 20,
		textAlign: 'center',
		fontStyle: 'italic',
	},
	shareTabs: {
		display: 'flex',
		flexDirection: 'column',
		gap: 16,
	},
	shareTabsHeader: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'space-around',
		marginBottom: 10,
		paddingBottom: 10,
		borderBottomWidth: 1,
		borderBottomColor: '#e2e8f0',
	},
	shareTabTitle: {
		fontSize: 16,
		fontWeight: 'bold',
		color: '#4c956c',
		paddingVertical: 8, paddingHorizontal: 16,
		borderRadius: 8,
		backgroundColor: '#f1f5f9',
		textAlign: 'center',
	},
	shareTabsContent: {
		display: 'flex',
		flexDirection: 'column',
		gap: 24,
	},
	shareTabPanel: {
		backgroundColor: '#fff',
		padding: 16,
		borderRadius: 8,
		borderWidth: 1,
		borderColor: '#e2e8f0',
		boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
	},
	shareList: {
		maxHeight: 300,
		overflow: 'auto',
	},
	shareListHeader: {
		display: 'flex',
		flexDirection: 'row',
		paddingVertical: 8, paddingHorizontal: 0,
		borderBottomWidth: 2,
		borderBottomColor: '#e2e8f0',
		marginBottom: 8,
	},
	shareListHeaderCell: {
		flex: 1,
		fontSize: 14,
		fontWeight: 'bold',
		color: '#4b5563',
		textAlign: 'left',
	},
	shareItem: {
		display: 'flex',
		flexDirection: 'row',
		paddingVertical: 8, paddingHorizontal: 0,
		borderBottomWidth: 1,
		borderBottomColor: '#f1f5f9',
	},
	shareRecipient: {
		flex: 1,
		fontSize: 14,
		color: '#4b5563',
		textAlign: 'left',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
	shareDate: {
		flex: 1,
		fontSize: 14,
		color: '#64748b',
		textAlign: 'left',
	},
	shareTime: {
		flex: 1,
		fontSize: 14,
		color: '#64748b',
		textAlign: 'left',
	},
	noSharesText: {
		fontSize: 14,
		color: '#94a3b8',
		textAlign: 'center',
		padding: 16,
		fontStyle: 'italic',
	},
	shareAnalyticsCell: {
		display: 'flex',
		flexDirection: 'column',
		gap: 10,
		padding: 8,
		maxWidth: '100%',
		overflow: 'hidden',
	},
	shareAnalyticsPills: {
		display: 'grid',
		gridTemplateColumns: 'repeat(3, 1fr)',
		gap: 8,
		width: '100%',
	},
	sharePill: {
		backgroundColor: '#f1f5f9',
		padding: 6,
		borderRadius: 8,
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		minWidth: 0, // Allows shrinking below content size
	},
	sharePillCount: {
		fontSize: 14,
		fontWeight: 'bold',
		color: '#2c6e49',
		marginBottom: 2,
	},
	sharePillLabel: {
		fontSize: 12,
		color: '#64748b',
		textAlign: 'center',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
	viewDetailsButton: {
		backgroundColor: '#4c956c',
		color: 'white',
		paddingVertical: 4, paddingHorizontal: 8,
		borderRadius: 4,
		fontSize: 12,
		fontWeight: 'bold',
		cursor: 'pointer',
		border: 'none',
		transition: 'all 0.2s ease',
		alignSelf: 'flex-start',
		marginTop: 4,
	},
	categoryLabel: {
		fontSize: 16,
		fontWeight: '600',
		color: '#4b5563',
		marginBottom: 12,
		marginTop: 16,
	},
	categoriesGrid: {
		display: 'flex',
		flexDirection: 'row',
		flexWrap: 'wrap',
		gap: 12,
		marginBottom: 20,
	},
	categorySelectButton: {
		paddingVertical: 10, paddingHorizontal: 16,
		borderRadius: 20,
		backgroundColor: '#f3f4f6',
		borderWidth: 1,
		borderColor: '#e5e7eb',
		transition: 'all 0.2s ease',
	},
	categorySelectButtonActive: {
		backgroundColor: '#4c956c',
		borderColor: '#4c956c',
	},
	categorySelectText: {
		fontSize: 14,
		color: '#4b5563',
		fontWeight: '500',
	},
	categorySelectTextActive: {
		color: '#ffffff',
	},
	emailCell: {
		maxWidth: '180px',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		paddingVertical: 12, paddingHorizontal: 16,
		fontSize: 14,
		color: '#4b5563',
		borderBottomWidth: 1, borderBottomColor: '#e5e7eb',
	},
	// Analytics health check styles
	healthCheckContainer: {
		backgroundColor: 'white',
		borderRadius: 12,
		padding: 20,
		marginTop: 20,
		shadowColor: '#000',
		shadowOffset: { width: 0, height: 2 },
		shadowOpacity: 0.1,
		shadowRadius: 4,
		elevation: 3,
	},
	healthCheckTitle: {
		fontSize: 18,
		fontWeight: 'bold',
		color: '#1e293b',
		marginBottom: 15,
	},
	healthCheckButton: {
		backgroundColor: '#4c956c',
		paddingVertical: 12,
		paddingHorizontal: 20,
		borderRadius: 8,
		marginBottom: 10,
		alignItems: 'center',
	},
	healthCheckButtonText: {
		color: 'white',
		fontWeight: 'bold',
		fontSize: 14,
	},
	refreshButton: {
		backgroundColor: '#3b82f6',
		paddingVertical: 12,
		paddingHorizontal: 20,
		borderRadius: 8,
		alignItems: 'center',
	},
	refreshButtonText: {
		color: 'white',
		fontWeight: 'bold',
		fontSize: 14,
	},
	// Feature Flags Styles - full viewport width for more real estate
	ffContainer: {
		padding: 16,
		width: '100%',
		maxWidth: '100%',
	},
	ffHeaderSection: {
		marginBottom: 32,
		textAlign: 'center',
	},
	ffMainTitle: {
		fontSize: 24,
		fontWeight: 'bold',
		color: '#1f2937',
		marginBottom: 8,
	},
	ffSubtitle: {
		fontSize: 14,
		color: '#6b7280',
		marginBottom: 16,
	},
	ffLegend: {
		flexDirection: 'row',
		justifyContent: 'center',
		gap: 24,
		marginTop: 12,
	},
	ffLegendItem: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 8,
	},
	ffLegendDot: {
		width: 12,
		height: 12,
		borderRadius: 6,
	},
	ffLegendText: {
		fontSize: 13,
		color: '#6b7280',
	},
	ffLoadingContainer: {
		padding: 60,
		alignItems: 'center',
	},
	ffLoading: {
		fontSize: 14,
		color: '#6b7280',
	},
	ffCategoriesGrid: {
		display: 'flex',
		flexDirection: isMobile ? 'column' : 'row',
		flexWrap: 'wrap',
		gap: 16,
	},
	ffCategorySection: {
		backgroundColor: '#f8fafc',
		borderRadius: 12,
		padding: 16,
		borderWidth: 1,
		borderColor: '#e2e8f0',
		width: isMobile ? '100%' : 'calc(33.333% - 11px)',
		minWidth: isMobile ? 'auto' : 320,
		overflow: 'hidden',
	},
	ffCategorySectionCollapsed: {
		padding: 12,
	},
	ffCategoryHeaderClickable: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		cursor: 'pointer',
	},
	ffCategoryHeaderLeft: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 6,
		flex: 1,
	},
	ffCollapseBtn: {
		width: 24,
		height: 24,
		borderRadius: 4,
		backgroundColor: '#e2e8f0',
		justifyContent: 'center',
		alignItems: 'center',
	},
	ffCollapseBtnCollapsed: {
		backgroundColor: '#4c956c',
	},
	ffCollapseBtnText: {
		fontSize: 10,
		color: '#64748b',
	},
	ffCategoryIcon: {
		fontSize: 18,
	},
	ffCategoryTitle: {
		fontSize: 14,
		fontWeight: '600',
		color: '#1f2937',
	},
	ffCategoryBadge: {
		backgroundColor: '#4c956c',
		paddingVertical: 1,
		paddingHorizontal: 6,
		borderRadius: 8,
		marginLeft: 4,
	},
	ffCategoryBadgeText: {
		fontSize: 10,
		color: '#fff',
		fontWeight: '600',
	},
	ffCollapsibleContent: {
		marginTop: 12,
	},
	ffCategoryDesc: {
		fontSize: 11,
		color: '#6b7280',
		marginBottom: 10,
		paddingBottom: 10,
		borderBottomWidth: 1,
		borderBottomColor: '#e2e8f0',
	},
	ffCategoryFlags: {
		gap: 8,
	},
	ffCard: {
		backgroundColor: '#fff',
		borderRadius: 6,
		padding: 10,
		borderWidth: 1,
		borderColor: '#e5e7eb',
	},
	ffCardContent: {
		flexDirection: 'column',
		gap: 8,
	},
	ffCardInfo: {
		flex: 1,
	},
	ffCardTitleRow: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: 6,
		marginBottom: 4,
	},
	ffCardTitle: {
		fontSize: 13,
		fontWeight: '600',
		color: '#1f2937',
		flex: 1,
	},
	ffImpactBadge: {
		paddingVertical: 1,
		paddingHorizontal: 5,
		borderRadius: 3,
		borderWidth: 1,
	},
	ffImpactText: {
		fontSize: 8,
		fontWeight: '700',
	},
	ffCardDesc: {
		fontSize: 11,
		color: '#6b7280',
		lineHeight: 15,
		display: 'none', // Hide descriptions to save space in compact view
	},
	ffTogglesColumn: {
		flexDirection: 'row',
		gap: 12,
		justifyContent: 'flex-start',
	},
	ffToggleRow: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 4,
	},
	ffToggleLabel: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 3,
	},
	ffToggleDot: {
		width: 6,
		height: 6,
		borderRadius: 3,
	},
	ffToggleLabelText: {
		fontSize: 10,
		color: '#6b7280',
		fontWeight: '500',
	},
	ffPreviewBtn: {
		border: '1px solid #cbd5e1',
		borderRadius: 4,
		paddingVertical: 6,
		paddingHorizontal: 10,
		fontSize: 10,
		fontWeight: '600',
		cursor: 'pointer',
		width: '100%',
		marginTop: 8,
		backgroundColor: '#f8fafc',
		color: '#475569',
	},
	ffToggleBtn: {
		border: 'none',
		borderRadius: 3,
		paddingVertical: 4,
		paddingHorizontal: 8,
		fontSize: 9,
		fontWeight: '700',
		cursor: 'pointer',
		minWidth: 36,
		textAlign: 'center',
		transition: 'all 0.2s ease',
	},
	ffToggleBtnOn: {
		backgroundColor: '#22c55e',
		color: '#fff',
	},
	ffToggleBtnOff: {
		backgroundColor: '#f3f4f6',
		color: '#6b7280',
	},
	ffToggleBtnOnProd: {
		backgroundColor: '#3b82f6',
		color: '#fff',
	},
	ffToggleBtnOffProd: {
		backgroundColor: '#fee2e2',
		color: '#dc2626',
	},
	// Production Confirmation Modal
	ffModalOverlay: {
		position: 'fixed',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		backgroundColor: 'rgba(0,0,0,0.6)',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		zIndex: 9999,
	},
	ffModalProd: {
		backgroundColor: '#fff',
		borderRadius: 16,
		padding: 0,
		maxWidth: 480,
		width: '95%',
		overflow: 'hidden',
		boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
	},
	ffModalProdHeader: {
		backgroundColor: '#1e293b',
		padding: 20,
		alignItems: 'center',
	},
	ffModalProdIcon: {
		fontSize: 32,
		marginBottom: 8,
	},
	ffModalProdTitle: {
		fontSize: 20,
		fontWeight: 'bold',
		color: '#fff',
	},
	ffModalProdAlert: {
		backgroundColor: '#dbeafe',
		padding: 12,
		borderLeftWidth: 4,
		borderLeftColor: '#3b82f6',
	},
	ffModalProdAlertText: {
		fontSize: 14,
		color: '#1e40af',
		fontWeight: '500',
		textAlign: 'center',
	},
	ffModalProdContent: {
		padding: 20,
		gap: 12,
	},
	ffModalProdRow: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		paddingVertical: 8,
		borderBottomWidth: 1,
		borderBottomColor: '#f1f5f9',
	},
	ffModalProdLabel: {
		fontSize: 14,
		color: '#64748b',
		fontWeight: '500',
	},
	ffModalProdValue: {
		fontSize: 14,
		color: '#1e293b',
		fontWeight: '600',
	},
	ffModalProdDesc: {
		fontSize: 13,
		color: '#6b7280',
		paddingHorizontal: 20,
		paddingBottom: 16,
		lineHeight: 20,
	},
	ffModalProdWarning: {
		backgroundColor: '#fef2f2',
		marginHorizontal: 20,
		marginBottom: 20,
		padding: 12,
		borderRadius: 8,
		borderWidth: 1,
		borderColor: '#fecaca',
	},
	ffModalProdWarningText: {
		fontSize: 13,
		color: '#b91c1c',
		textAlign: 'center',
	},
	ffModalProdBtns: {
		flexDirection: 'row',
		borderTopWidth: 1,
		borderTopColor: '#e2e8f0',
	},
	ffModalProdCancelBtn: {
		flex: 1,
		backgroundColor: '#f8fafc',
		color: '#64748b',
		border: 'none',
		paddingVertical: 16,
		fontSize: 15,
		fontWeight: '600',
		cursor: 'pointer',
		borderRightWidth: 1,
		borderRightColor: '#e2e8f0',
	},
	ffModalProdEnableBtn: {
		flex: 1,
		backgroundColor: '#22c55e',
		color: '#fff',
		border: 'none',
		paddingVertical: 16,
		fontSize: 15,
		fontWeight: '600',
		cursor: 'pointer',
	},
	ffModalProdDisableBtn: {
		flex: 1,
		backgroundColor: '#dc2626',
		color: '#fff',
		border: 'none',
		paddingVertical: 16,
		fontSize: 15,
		fontWeight: '600',
		cursor: 'pointer',
	},
});

// Utility function to generate institution code
const generateInstitutionCode = (type) => {
	const prefix = type === "University or College" ? "U" : type === "High School" ? "H" : "I";
	return `${prefix}${Math.floor(10000 + Math.random() * 90000)}`;
};

export default Admin;
